import {
  mergePartsByClient,
  partReferencesForTeam,
  weightedInterleave,
} from "./clientUtils";
import {
  calculateHistoryEffectiveDays,
  buildEngineerDailyAverageRows,
  buildHistoryPerformanceCoach,
  getElapsedRotaDayLimit,
} from "./historyCoach";
import {
  buildEngineerAliasSuggestions,
  buildRepairUploadAliasTargets,
  buildHistoryConsistencyReport,
  countVerifiedEngineerAliases,
} from "./historyConsistency";
import { buildReconciliationReport } from "./repairReconciliation";
import {
  formatLondonShort,
  isLondonWeekend,
  londonDateKey,
  londonHourKey,
  londonWeekdayIndex,
  isRepairBonusWindow,
  monthKeyFromDate,
  parseRepairCompletedDate,
} from "./repairTime";
import { buildHistoryOperationalBrief } from "./historyInsights";
import { buildLiveRescueBoard } from "./liveRescue";
import {
  buildEngineerStatusIndex,
  isExcludedFromHistory,
} from "./engineerStatus";
import {
  historyBarFillStyle,
  historyStaggerStyle,
  mergeStyles,
} from "./motionUtils";
import {
  DEFAULT_TEAM_TARGETS,
  TEAM_TARGET_KEYS,
  buildHistoryTierMap,
  getTeamTargetTiers,
  sanitizeTeamTargets,
} from "./teamTargets";
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { supabase } from "./supabaseClient";
import StockTab, { StockSaveStatus, StockReportTime } from "./StockTab";
import StockParsedCapture from "./StockParsedCapture";
import { enqueueStockSource, enqueueStockCallsRevision } from "./stockCapture";
import { stockTeam, stockConfiguration } from "./stockHistory";
import { apiRoute, signIn, signOut, exportGrading } from "./supabaseApi";
import {
  GRADING_TEAM_OPTIONS,
  getEffectiveGradingTeams,
  normalizeGradingTeams,
  formatGradingTeams,
} from "./gradingTeams";
import {
  PART_TEAM_SOURCE,
  buildPartTeamAccess,
  buildCrossTeamReport,
} from "./partTeamAccess";

// Commission Config
const DEFAULT_GRADE_VALUES = {
  M: 0.1,
  A: 0.25,
  B: 0.5,
  C: 0.75,
  S: 1.25,
  X: 3.0,
  BER: 0.1,
};
const GRADE_LABELS = {
  M: "Cables/Misc",
  A: "Grade A",
  B: "Grade B",
  C: "Grade C",
  S: "Grade S",
  X: "Grade X",
  BER: "BER Units",
};
const DEFAULT_TIERS = [
  { daily: 18, bonus: 25 },
  { daily: 21, bonus: 50 },
  { daily: 24, bonus: 75 },
];

const TEAM_DEFAULT_TIERS = DEFAULT_TEAM_TARGETS;

const GRADE_ORDER = ["X", "S", "C", "B", "A", "M", "BER"];
const RC_TEAM_KEYS = [
  "RC PRINTERS & PERIPHERALS",
  "RC DCD",
  "RC PCB",
  "RC PC",
  "RC MCD",
  "RC PED",
];
const RC_TEAM_ALIASES = {
  "RC PRINTERS & PERIPHERALS": [
    "RC PRINTERS & PERIPHERALS",
    "PRINTERS",
    "PERIPHERALS",
    "PPR",
    "P&P",
    "P & P",
  ],
  "RC DCD": ["RC DCD", "DCD"],
  "RC PCB": ["RC PCB", "PCB"],
  "RC PC": ["RC PC", "PC", "PC SECTION", "PC ENGINEERS"],
  "RC MCD": ["RC MCD", "MCD"],
  "RC PED": ["RC PED", "PED"],
};
const GRADE_COLORS = {
  M: "#64748b",
  A: "#3b82f6",
  B: "#8b5cf6",
  C: "#f59e0b",
  S: "#ef4444",
  X: "#10b981",
  BER: "#94a3b8",
};
const TECH_COLORS = [
  "#4f8ef7",
  "#5de7ff",
  "#34d399",
  "#fb923c",
  "#f472b6",
  "#38bdf8",
  "#facc15",
  "#4ade80",
];

// Helpers
function parseCSV(text) {
  const lines = text
    .replace(/^\uFEFF/, "")
    .trim()
    .split(/\r?\n/)
    .filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const splitCSVLine = (line) => {
    const result = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        inQ = !inQ;
      } else if (line[i] === "," && !inQ) {
        result.push(cur.trim());
        cur = "";
      } else {
        cur += line[i];
      }
    }
    result.push(cur.trim());
    return result;
  };
  const headers = splitCSVLine(lines[0]).map((h) =>
    h.replace(/^"|"$/g, "").trim(),
  );
  const rows = lines.slice(1).map((line) => {
    const vals = splitCSVLine(line).map((v) => v.replace(/^"|"$/g, "").trim());
    const obj = {};
    headers.forEach((h, i) => (obj[h] = vals[i] || ""));
    return obj;
  });
  return { headers, rows };
}

function detectCol(headers, keywords) {
  for (const kw of keywords) {
    const found = headers.find((h) =>
      h.toLowerCase().includes(kw.toLowerCase()),
    );
    if (found) return found;
  }
  return "";
}

// Power BI style XLSX parse via SheetJS (loaded from CDN)
async function parseXLSX(file) {
  if (!window.XLSX) throw new Error("SheetJS not loaded");
  const ab = await file.arrayBuffer();
  const wb = window.XLSX.read(ab, { type: "array" });
  const result = {};
  wb.SheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    result[name] = window.XLSX.utils.sheet_to_json(ws, {
      header: 1,
      defval: "",
    });
  });
  return result;
}

// Rota XLSX parse returns { year, month, dow, engineers }
async function parseRotaXLSX(file) {
  const sheets = await parseXLSX(file);
  const rows = sheets[Object.keys(sheets)[0]];
  if (!rows || rows.length < 2) throw new Error("Empty rota file");

  // Row 0: [excelDateSerial, "W","T","F",...] day-of-week labels per day
  const headerRow = rows[0];
  const serial = Number(headerRow[0]);
  if (!serial || serial < 40000 || serial > 60000)
    throw new Error("Could not read date from rota header");
  const rotaDate = new Date(Date.UTC(1899, 11, 30) + serial * 86400 * 1000);
  const year = rotaDate.getUTCFullYear();
  const month = rotaDate.getUTCMonth() + 1;

  // Collect day-of-week labels until we hit a non-single-letter cell
  const dow = [];
  for (let i = 1; i <= 31; i++) {
    const v = String(headerRow[i] || "").trim();
    if (!v || v.length !== 1) break;
    dow.push(v);
  }
  const dayCount = dow.length;
  if (dayCount < 28) throw new Error("Could not read day columns from rota");

  const engineers = [];
  let currentSection = "";
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const name = String(row[0] || "").trim();
    if (!name) continue;
    // Section header row: col 1 is the number 1 (day numbers 1,2,3...)
    if (Number(row[1]) === 1 && !isNaN(Number(row[2]))) {
      currentSection = name;
      continue;
    }
    const days = [];
    for (let d = 1; d <= dayCount; d++) {
      days.push(String(row[d] || "").trim());
    }
    const holsFull = parseFloat(row[dayCount + 1]) || 0;
    const holsHalf = parseFloat(row[dayCount + 2]) || 0;
    const holsTotal = parseFloat(row[dayCount + 3]) || 0;
    engineers.push({
      engineer: name,
      section: currentSection,
      days,
      holsFull,
      holsHalf,
      holsTotal,
    });
  }
  if (engineers.length === 0) throw new Error("No engineer rows found in rota");
  return { year, month, dow, engineers };
}

// Parse whole-year rota XLSX returns [{year, month, dow, engineers}, ...] for each month sheet found
async function parseWholeYearRotaXLSX(file) {
  const MONTH_MAP = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };
  const sheets = await parseXLSX(file);
  const results = [];
  for (const [sheetName, rows] of Object.entries(sheets)) {
    const m = sheetName.trim().match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (!m) continue;
    const monthIdx = MONTH_MAP[m[1].toLowerCase()];
    if (!monthIdx) continue;
    const year = parseInt(m[2]);
    if (!rows || rows.length < 2) continue;
    try {
      const headerRow = rows[0];
      const dow = [];
      for (let i = 1; i <= 31; i++) {
        const v = String(headerRow[i] || "").trim();
        if (!v || v.length !== 1) break;
        dow.push(v);
      }
      if (dow.length < 28) continue;
      const dayCount = dow.length;
      const engineers = [];
      let currentSection = "";
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const name = String(row[0] || "").trim();
        if (!name) continue;
        if (Number(row[1]) === 1 && !isNaN(Number(row[2]))) {
          currentSection = name;
          continue;
        }
        const days = [];
        for (let d = 1; d <= dayCount; d++)
          days.push(String(row[d] || "").trim());
        const holsFull = parseFloat(row[dayCount + 1]) || 0;
        const holsHalf = parseFloat(row[dayCount + 2]) || 0;
        const holsTotal = parseFloat(row[dayCount + 3]) || 0;
        engineers.push({
          engineer: name,
          section: currentSection,
          days,
          holsFull,
          holsHalf,
          holsTotal,
        });
      }
      if (engineers.length === 0) continue;
      results.push({ year, month: monthIdx, dow, engineers });
    } catch {
      continue;
    }
  }
  return results;
}

function normalizeRotaCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase();
}

function rotaWorkedDayDelta(code, dow) {
  const c = normalizeRotaCode(code);
  const d = normalizeRotaCode(dow);
  if (d === "S" || c === "" || c === "OFF" || c === "BH" || c === "-") return 0;
  if (c === "W") return 1;
  if (c === "H/D" || c === "HD/S") return 0.5;
  if (
    [
      "O/D",
      "D/O",
      "HD/O",
      "O/HD",
      "H/O",
      "O/H",
      "HD/OTHER",
      "OTHER/HD",
      "HALF OTHER",
      "HALF-DAY OTHER",
    ].includes(c)
  )
    return -0.5;
  if (c === "O" || c === "OTHER") return -1;
  // Training (DCD, from Aug 2026). A T cell replaces the W, so the day is simply
  // not a worked day — it is not deducted the way OTHER is. Parts closed on a
  // training day still earn commission and still count: nothing about commission
  // reads the rota, so a smaller divisor is the only effect.
  if (c === "T" || c === "TRAINING") return 0;
  if (
    ["HD/T", "T/HD", "T/D", "D/T", "HALF TRAINING", "HALF-DAY TRAINING"].includes(
      c,
    )
  )
    return 0.5;
  return 0;
}

function calculateRotaWorkedDays(days, dow, dayLimit) {
  const max = Math.min(
    days?.length || 0,
    dayLimit == null ? days?.length || 0 : Math.max(0, dayLimit),
  );
  let count = 0;
  for (let i = 0; i < max; i++) {
    count += rotaWorkedDayDelta(days[i], dow?.[i]);
  }
  return Math.max(0, count);
}

function normalizeTeamText(value) {
  return ` ${String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9&]+/g, " ")} `;
}

function teamMatchesText(team, text) {
  const normalized = normalizeTeamText(text);
  const aliases = RC_TEAM_ALIASES[team] || [team];
  return aliases.some((alias) => normalized.includes(normalizeTeamText(alias)));
}

function normalizeSectionToTeam(section) {
  return RC_TEAM_KEYS.find((team) => teamMatchesText(team, section)) || null;
}

function getRepairHistoryTeamTiers(team, teamTargets = {}) {
  const normalizedTeam = normalizeSectionToTeam(team) || team || "ALL";
  return getTeamTargetTiers(normalizedTeam, teamTargets);
}

const ENGINEER_NAME_ALIASES = {
  danielhg: "danielhardingegrant",
  chrisstamaras: "christosstamataras",
};

function normalizeEngineerName(name) {
  const key = String(name || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  return ENGINEER_NAME_ALIASES[key] || key;
}

function namesMatchLoose(a, b) {
  const left = normalizeEngineerName(a);
  const right = normalizeEngineerName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.length >= 6 && right.includes(left)) return true;
  if (right.length >= 6 && left.includes(right)) return true;
  return false;
}

function findByLooseEngineerName(map, name) {
  const key = normalizeEngineerName(name);
  if (map[key] !== undefined) return map[key];
  const matchedKey = Object.keys(map).find((candidate) =>
    namesMatchLoose(candidate, key),
  );
  return matchedKey ? map[matchedKey] : undefined;
}

function chooseEngineerDisplayName(current, next) {
  const a = String(current || "").trim();
  const b = String(next || "").trim();
  if (!a || a === "Unknown") return b || "Unknown";
  if (!b || b === "Unknown") return a;
  const score = (value) => {
    const hasLower = /[a-z]/.test(value);
    const hasSpace = /\s/.test(value);
    const isCaps = /[A-Z]/.test(value) && !hasLower;
    return (
      (hasLower ? 4 : 0) +
      (hasSpace ? 2 : 0) -
      (isCaps ? 2 : 0) +
      Math.min(value.length, 40) / 100
    );
  };
  return score(b) > score(a) ? b : a;
}

function buildEngineerAliasIndex(engineers) {
  const index = {};
  (engineers || []).forEach((engineer) => {
    const canonical = String(engineer.engineer || "").trim();
    if (!canonical) return;
    index[normalizeEngineerName(canonical)] = canonical;
    (engineer.aliases || []).forEach((entry) => {
      const alias = typeof entry === "string" ? entry : entry?.alias;
      const key = normalizeEngineerName(alias);
      if (key) index[key] = canonical;
    });
  });
  return index;
}

function resolveEngineerIdentity(name, aliasIndex) {
  const key = normalizeEngineerName(name);
  return aliasIndex?.[key] || String(name || "").trim();
}

function looksLikeBER(...values) {
  return values.some((value) => {
    const raw = String(value || "").toUpperCase();
    const compact = raw.replace(/[^A-Z0-9]/g, "");
    return (
      /(^|[^A-Z])B\.?\s*E\.?\s*R\.?([^A-Z]|$)/.test(raw) ||
      compact === "BER" ||
      compact.startsWith("BER") ||
      compact.includes("BEYONDECONOMICALREPAIR") ||
      compact === "BEYONDECONOMICREPAIR"
    );
  });
}

// Parse Power BI export format
export function parsePowerBIData(sheetRows) {
  // Find header row - look for "Part ID"
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(sheetRows.length, 10); i++) {
    if (
      sheetRows[i].some(
        (cell) =>
          String(cell).toLowerCase().includes("part id") ||
          String(cell).toLowerCase().includes("part_id"),
      )
    ) {
      headerRowIdx = i;
      break;
    }
  }
  if (headerRowIdx === -1) return [];
  const headers = sheetRows[headerRowIdx].map((h) => String(h).trim());
  const partIdCol = headers.findIndex(
    (h) =>
      h.toLowerCase().includes("part id") ||
      h.toLowerCase().includes("part_id"),
  );
  const badCol = headers.findIndex(
    (h) => h.toLowerCase().includes("total bad") || h.toLowerCase() === "bad",
  );
  const repReqCol = headers.findIndex(
    (h) =>
      h.toLowerCase().includes("rep req") ||
      h.toLowerCase().includes("repair req"),
  );
  const goodCol = headers.findIndex(
    (h) => h.toLowerCase().includes("total good") || h.toLowerCase() === "good",
  );
  const customerCol = headers.findIndex((h) =>
    h.toLowerCase().includes("customer"),
  );
  const teamCol = headers.findIndex(
    (h) =>
      h.toLowerCase().includes("repair team") ||
      h.toLowerCase().includes("team"),
  );

  const rows = [];
  for (let i = headerRowIdx + 1; i < sheetRows.length; i++) {
    const row = sheetRows[i];
    const partId = String(row[partIdCol] || "").trim();
    if (
      !partId ||
      partId.toLowerCase() === "total" ||
      partId.toLowerCase() === "grand total"
    )
      continue;
    const bad = parseInt(row[badCol]) || 0;
    const repReq = parseInt(row[repReqCol]) || 0;
    const good = parseInt(row[goodCol]) || 0;
    const customer = String(row[customerCol] || "").trim();
    const team = String(row[teamCol] || "").trim();
    rows.push({ partId, bad, repReq, good, customer, team });
  }
  return rows;
}

// Parse grading spreadsheet to build part_id -> grade lookup
// ---------------------------------------------------------------------------
// Grading file column handling
//
// Two different exports arrive as "the grading reference":
//
//   A. The ERP export — a title row, then headers on the SECOND row:
//        part_id | part_class | descriptn | user_def16
//
//   B. The Power BI stock export with grades filled in — headers on the FIRST
//      row, different names:
//        Part ID | Part Class | Repair Team | Total Good | Total Bad | ...
//
// Rather than hard-coding a row index per format, find the header row by
// looking for a part-id column, and resolve the rest by alias. That handles
// both, plus RazerGrid's own export, without the caller caring which it has.
//
// Note on Repair Team: in format B it carries the values that user_def16
// carries in format A ("PROJECTS ONLY", "CONSUMABLES", …), which is what
// drives silent_exclude. So it maps to both team and user_def16.
// ---------------------------------------------------------------------------

function normalizeGradingHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_\-.]/g, "");
}

// Ordered by preference — "partid" wins over a bare "part" if both exist.
const GRADING_COLUMN_ALIASES = {
  partId: ["partid", "partcode", "partnumber", "part"],
  grade: ["partclass", "grade", "class"],
  description: ["descriptn", "description", "desc"],
  team: ["userdef16", "def16", "repairteam", "team", "section"],
};

/** Index of the first row that looks like a header, or -1. */
function findGradingHeaderRow(rows, maxScan = 10) {
  const limit = Math.min(rows?.length || 0, maxScan);
  for (let i = 0; i < limit; i++) {
    const cells = (rows[i] || []).map(normalizeGradingHeader);
    if (cells.some((c) => GRADING_COLUMN_ALIASES.partId.includes(c))) return i;
  }
  return -1;
}

/** Map a header row to column indexes. Missing columns come back as -1. */
function resolveGradingColumns(headerRow) {
  const norm = (headerRow || []).map(normalizeGradingHeader);
  const find = (aliases) => {
    for (const alias of aliases) {
      const idx = norm.indexOf(alias);
      if (idx !== -1) return idx;
    }
    return -1;
  };
  return {
    partId: find(GRADING_COLUMN_ALIASES.partId),
    grade: find(GRADING_COLUMN_ALIASES.grade),
    description: find(GRADING_COLUMN_ALIASES.description),
    team: find(GRADING_COLUMN_ALIASES.team),
  };
}

/**
 * Turn any supported grading sheet into upload records.
 * Returns { records, headerRowIdx, columns, skipped } — `skipped` counts rows
 * with no part id, which are always dropped.
 */
function buildGradingRecords(sheetRows) {
  const headerRowIdx = findGradingHeaderRow(sheetRows);
  if (headerRowIdx === -1) {
    return { records: [], headerRowIdx: -1, columns: null, skipped: 0 };
  }
  const columns = resolveGradingColumns(sheetRows[headerRowIdx]);
  if (columns.partId === -1) {
    return { records: [], headerRowIdx, columns, skipped: 0 };
  }

  const cell = (row, idx) => (idx >= 0 ? String(row[idx] ?? "").trim() : "");
  const records = [];
  let skipped = 0;

  for (let i = headerRowIdx + 1; i < sheetRows.length; i++) {
    const row = sheetRows[i] || [];
    const partId = cell(row, columns.partId).toUpperCase();
    if (!partId) {
      skipped++;
      continue;
    }
    const teamValue = cell(row, columns.team);
    records.push({
      part_id: partId,
      grade: cell(row, columns.grade).toUpperCase() || null,
      description: cell(row, columns.description),
      user_def16: teamValue,
      team: teamValue,
    });
  }

  return { records, headerRowIdx, columns, skipped };
}

function parseGradingData(sheetRows) {
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(sheetRows.length, 5); i++) {
    if (
      sheetRows[i].some(
        (cell) =>
          String(cell).toLowerCase().includes("part_id") ||
          String(cell).toLowerCase() === "part id",
      )
    ) {
      headerRowIdx = i;
      break;
    }
  }
  if (headerRowIdx === -1) return {};
  const headers = sheetRows[headerRowIdx].map((h) =>
    String(h).trim().toLowerCase(),
  );
  const partIdCol = headers.findIndex(
    (h) => h === "part_id" || h === "part id" || h === "part_code",
  );
  const partClassCol = headers.findIndex(
    (h) =>
      h === "part_class" ||
      h === "part class" ||
      h === "grade" ||
      h === "class",
  );
  const teamCol = headers.findIndex(
    (h) => h === "user_def16" || h === "team" || h === "section",
  );

  if (partIdCol === -1) return {};
  const lookup = {};
  for (let i = headerRowIdx + 1; i < sheetRows.length; i++) {
    const row = sheetRows[i];
    const partId = String(row[partIdCol] || "")
      .trim()
      .toUpperCase();
    const grade = String(row[partClassCol] || "")
      .trim()
      .toUpperCase();
    const team = String(row[teamCol] || "").trim();
    if (partId && grade && DEFAULT_GRADE_VALUES[grade] !== undefined) {
      lookup[partId] = { grade, team };
    }
  }
  return lookup;
}

// Core Allocation Algorithm
// 6-pass interleaved allocation so DCD and MCD work is naturally mixed:
// 1. DCD urgent call list 2. MCD urgent call list
// 3. DCD rep req > 0 4. MCD rep req > 0
// 5. DCD stock 6. MCD stock
// Each pass uses greedy fair-split (assign to engineer with lowest commission).
function allocate({
  parts,
  callListParts,
  techNames,
  targetDaily,
  tiers,
  workingDays,
  doaCount,
  gradeValues,
  activeClient,
  clientWeight,
  engineerClients,
}) {
  const techs = techNames.map((name, i) => ({
    id: i,
    name,
    parts: [],
    commission: 0,
  }));
  const weight =
    typeof clientWeight === "number"
      ? Math.max(0, Math.min(1, clientWeight))
      : 0;

  const assignToMin = (part, pool) => {
    const eligible = pool || techs;
    const target = eligible.reduce(
      (min, t) => (t.commission < min.commission ? t : min),
      eligible[0],
    );
    target.parts.push(part);
    target.commission += gradeValues[part.grade] || 0;
  };

  const sortByValue = (arr) =>
    [...arr].sort(
      (a, b) => (gradeValues[b.grade] || 0) - (gradeValues[a.grade] || 0),
    );

  // Round-robin across unique partIds (highest-value groups first) so no single part floods an engineer's queue
  const spreadByPartId = (arr) => {
    const sorted = sortByValue(arr);
    const groups = {};
    for (const p of sorted) {
      if (!groups[p.partId]) groups[p.partId] = [];
      groups[p.partId].push(p);
    }
    const groupList = Object.values(groups).sort(
      (a, b) => (gradeValues[b[0].grade] || 0) - (gradeValues[a[0].grade] || 0),
    );
    const result = [];
    const maxLen = groupList.reduce((m, g) => Math.max(m, g.length), 0);
    for (let i = 0; i < maxLen; i++) {
      for (const g of groupList) {
        if (i < g.length) result.push(g[i]);
      }
    }
    return result;
  };

  const buildResult = () =>
    techs.map((t) => {
      const totalUnits = t.parts.length;
      const dailyAvg = workingDays > 0 ? totalUnits / workingDays : 0;
      const activeTiers = tiers && tiers.length > 0 ? tiers : DEFAULT_TIERS;
      const bonusTier =
        [...activeTiers]
          .sort((a, b) => b.daily - a.daily)
          .find((ti) => dailyAvg >= ti.daily) || null;
      const doaDeduction = doaCount;
      const netCommission = Math.max(0, t.commission - doaDeduction);
      return {
        ...t,
        totalUnits,
        dailyAvg,
        bonusTier,
        doaDeduction,
        netCommission,
        totalEarnings: netCommission + (bonusTier?.bonus || 0),
      };
    });

  // When no specific client is active, allocate all parts together: calls priority stock
  if (!activeClient) {
    sortByValue(callListParts).forEach((p) =>
      assignToMin({ ...p, priority: "urgent" }, techs),
    );
    spreadByPartId(parts.filter((p) => p.repReq > 0)).forEach((p) =>
      assignToMin({ ...p, priority: "rep_req" }, techs),
    );
    spreadByPartId(parts.filter((p) => p.repReq <= 0)).forEach((p) =>
      assignToMin({ ...p, priority: "stock" }, techs),
    );
    return buildResult();
  }

  // Split engineers by client assignment
  const engByClient = {};
  const engUntagged = [];
  techs.forEach((t) => {
    const tag = engineerClients?.[t.name];
    if (tag) {
      if (!engByClient[tag]) engByClient[tag] = [];
      engByClient[tag].push(t);
    } else {
      engUntagged.push(t);
    }
  });

  // Call list urgent parts
  const teamCallParts = callListParts.filter((p) => p.source === "team");
  const clientCallParts = activeClient
    ? callListParts.filter((p) => p.source === activeClient)
    : [];

  // Assign call-list urgent parts
  sortByValue(clientCallParts).forEach((p) => {
    const pool = engByClient[activeClient]?.length
      ? engByClient[activeClient]
      : engUntagged;
    assignToMin({ ...p, priority: "urgent" }, pool.length ? pool : techs);
  });
  sortByValue(teamCallParts).forEach((p) => {
    assignToMin(
      { ...p, priority: "urgent" },
      engUntagged.length ? engUntagged : techs,
    );
  });

  // Assign tagged-engineer client stock
  if (activeClient && engByClient[activeClient]?.length) {
    const taggedClientRepReq = sortByValue(
      parts.filter((p) => p.source === activeClient && p.repReq > 0),
    );
    const taggedClientStock = sortByValue(
      parts.filter((p) => p.source === activeClient && p.repReq <= 0),
    );
    taggedClientRepReq.forEach((p) =>
      assignToMin({ ...p, priority: "rep_req" }, engByClient[activeClient]),
    );
    taggedClientStock.forEach((p) =>
      assignToMin({ ...p, priority: "stock" }, engByClient[activeClient]),
    );
  }

  // Assign untagged engineers: weighted rep_req then stock
  const teamRepReq = sortByValue(
    parts.filter((p) => p.source === "team" && p.repReq > 0),
  );
  const clientRepReq = activeClient
    ? sortByValue(
        parts.filter((p) => p.source === activeClient && p.repReq > 0),
      )
    : [];
  const teamStock = sortByValue(
    parts.filter((p) => p.source === "team" && p.repReq <= 0),
  );
  const clientStock = activeClient
    ? sortByValue(
        parts.filter((p) => p.source === activeClient && p.repReq <= 0),
      )
    : [];

  const pool = engUntagged.length ? engUntagged : techs;
  weightedInterleave(teamRepReq, clientRepReq, weight).forEach((p) =>
    assignToMin({ ...p, priority: "rep_req" }, pool),
  );
  weightedInterleave(teamStock, clientStock, weight).forEach((p) =>
    assignToMin({ ...p, priority: "stock" }, pool),
  );

  return buildResult();
}

// Styled Primitives
export function buildParsedPartReport(state) {
  const gradeValues = state.gradeValues || DEFAULT_GRADE_VALUES;
  const loadedParts = Array.isArray(state.mergedParts) ? state.mergedParts : [];
  const callList = Array.isArray(state.callList) ? state.callList : [];
  const groups = new Map();

  const ensureGroup = (partId) => {
    const key = String(partId || "")
      .trim()
      .toUpperCase();
    if (!key) return null;
    if (!groups.has(key)) {
      groups.set(key, {
        partId: key,
        backstockCount: 0,
        repairRequiredCount: 0,
        stockCount: 0,
        callListCount: 0,
        grades: {},
        teams: new Set(),
        customers: new Set(),
        sources: new Set(),
        examples: [],
        value: 0,
      });
    }
    return groups.get(key);
  };

  loadedParts.forEach((p) => {
    const group = ensureGroup(p.partId || p.ref || p.serial);
    if (!group) return;
    const grade = String(p.grade || "UNGRADED").toUpperCase();
    group.backstockCount += 1;
    if (Number(p.repReq || 0) > 0) group.repairRequiredCount += 1;
    else group.stockCount += 1;
    group.grades[grade] = (group.grades[grade] || 0) + 1;
    if (p.team) group.teams.add(p.team);
    if (p.customer) group.customers.add(p.customer);
    if (p.source) group.sources.add(p.source);
    group.value = Math.max(group.value, gradeValues[grade] || 0);
    group.examples.push({
      partId: group.partId,
      grade,
      requirement: Number(p.repReq || 0) > 0 ? "Repair Required" : "Stock",
      source: p.source || "",
      team: p.team || "",
      customer: p.customer || "",
      serial: p.serial || "",
      value: gradeValues[grade] || 0,
    });
  });

  callList.forEach((p) => {
    const group = ensureGroup(p.partId || p.ref || p.serial);
    if (!group) return;
    const grade = String(p.grade || "UNGRADED").toUpperCase();
    group.callListCount += 1;
    group.grades[grade] = (group.grades[grade] || 0) + 1;
    if (p.team) group.teams.add(p.team);
    if (p.customer) group.customers.add(p.customer);
    if (p.source) group.sources.add(p.source);
    group.value = Math.max(group.value, gradeValues[grade] || 0);
    group.examples.push({
      partId: group.partId,
      grade,
      requirement: "Call List",
      source: p.source || "",
      team: p.team || "",
      customer: p.customer || "",
      serial: p.serial || "",
      value: gradeValues[grade] || 0,
    });
  });

  const summary = Array.from(groups.values())
    .map((group) => {
      const priorityRank =
        group.callListCount > 0 ? 1 : group.repairRequiredCount > 0 ? 2 : 3;
      // A call with nothing in the Power BI stock upload is flagged so the
      // team can see at a glance there is no unit to pull for it.
      const noStockForCall =
        group.callListCount > 0 && group.backstockCount === 0;
      const requirement =
        priorityRank === 1
          ? noStockForCall
            ? "Call - none in stock"
            : "Call List"
          : priorityRank === 2
            ? "Repair Required"
            : "Stock";
      const gradeRank = (grade) => {
        const idx = GRADE_ORDER.indexOf(grade);
        return idx === -1 ? 99 : idx;
      };
      const gradeSummary = Object.entries(group.grades)
        .sort((a, b) => gradeRank(a[0]) - gradeRank(b[0]))
        .map(([grade, count]) => `${grade}: ${count}`)
        .join(", ");
      return {
        ...group,
        priorityRank,
        requirement,
        noStockForCall,
        gradeSummary,
        teamsText: Array.from(group.teams).sort().join(", "),
        customersText: Array.from(group.customers).sort().join(", "),
        sourcesText: Array.from(group.sources).sort().join(", "),
      };
    })
    .sort(
      (a, b) =>
        a.priorityRank - b.priorityRank ||
        b.repairRequiredCount - a.repairRequiredCount ||
        b.callListCount - a.callListCount ||
        b.backstockCount - a.backstockCount ||
        b.value - a.value ||
        a.partId.localeCompare(b.partId),
    );

  const details = summary.flatMap((group) => group.examples);
  return {
    summary,
    details,
    totalLoaded: loadedParts.length,
    totalCallList: callList.length,
  };
}

function getParsedReportClient(group, clients) {
  const partId = String(group?.partId || "").toUpperCase();
  const sourceSet = new Set(
    Array.from(group?.sources || []).map((source) =>
      String(source || "").toLowerCase(),
    ),
  );
  return (clients || []).find((client) => {
    const id = String(client.id || "").toLowerCase();
    const prefix = String(client.prefix || "").toUpperCase();
    return (id && sourceSet.has(id)) || (prefix && partId.startsWith(prefix));
  });
}

function currentMonthKey() {
  return monthKeyFromDate(new Date());
}

// Splits "YYYY-MM" into numbers. Returns null for anything unparseable so
// callers can fall back to the current month.
function parseMonthKey(key) {
  const m = String(key || "").match(/^(\d{4})-(\d{1,2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
}

function formatMonthKey(key) {
  const parsed = parseMonthKey(key);
  if (!parsed) return "";
  return `${ROTA_MONTH_NAMES[parsed.month - 1]} ${parsed.year}`;
}

// The month an uploaded KPI export belongs to: whichever month holds the most
// completed parts. Ties go to the later month. A file whose rows carry no usable
// completion date yields "" and callers fall back to the current month.
function pickDominantMonth(monthTally) {
  const entries = Object.entries(monthTally || {});
  if (!entries.length) return "";
  entries.sort((a, b) => b[1] - a[1] || b[0].localeCompare(a[0]));
  return entries[0][0];
}

function detectRepairUploadMonth(rows, closeDttmCol) {
  if (!closeDttmCol) return "";
  const tally = {};
  (rows || []).forEach((row) => {
    const date = parseRepairCompletedDate(row?.[closeDttmCol]);
    const month = monthKeyFromDate(date);
    if (month) tally[month] = (tally[month] || 0) + 1;
  });
  return pickDominantMonth(tally);
}

function summarizeRotaMonth(days, dow, year, month, bankHolidaySet, dayLimit) {
  const result = {
    workedDays: 0,
    fullWorkingDays: 0,
    halfDays: 0,
    holidays: 0,
    sick: 0,
    other: 0,
    halfOther: 0,
    training: 0,
    bankHolidays: 0,
  };
  const max = Math.min(
    days?.length || 0,
    dayLimit == null ? days?.length || 0 : Math.max(0, dayLimit),
  );
  for (let i = 0; i < max; i++) {
    const code = normalizeRotaCode(days[i]);
    const dayNum = i + 1;
    const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    const isBankHoliday = bankHolidaySet?.has(dateKey) || code === "BH";
    const delta = isBankHoliday ? 0 : rotaWorkedDayDelta(code, dow?.[i]);
    result.workedDays += delta;
    if (delta === 1) result.fullWorkingDays += 1;
    if (delta === 0.5) result.halfDays += 1;
    if (isBankHoliday) result.bankHolidays += 1;
    if (code === "H") result.holidays += 1;
    if (code === "H/D") result.holidays += 0.5;
    if (code === "S" || code === "SICK") result.sick += 1;
    if (code === "HD/S") result.sick += 0.5;
    if (code === "O" || code === "OTHER") result.other += 1;
    if (code === "T" || code === "TRAINING") result.training += 1;
    if (["HD/T", "T/HD", "T/D", "D/T"].includes(code)) result.training += 0.5;
    if (
      [
        "O/D",
        "D/O",
        "HD/O",
        "O/HD",
        "H/O",
        "O/H",
        "HD/OTHER",
        "OTHER/HD",
        "HALF OTHER",
        "HALF-DAY OTHER",
      ].includes(code)
    )
      result.halfOther += 1;
  }
  result.workedDays = Math.max(0, result.workedDays);
  return result;
}

function filterParsedPartReport(report, clients, filterMode) {
  if (!report || filterMode === "all") return report;
  const filteredSummary = (report.summary || []).filter((group) => {
    const client = getParsedReportClient(group, clients);
    if (filterMode === "clients-only") return !!client;
    if (filterMode === "clients-excluded") return !client;
    if (filterMode.startsWith("client:"))
      return client?.id === filterMode.slice("client:".length);
    return true;
  });
  return {
    ...report,
    summary: filteredSummary,
    details: filteredSummary.flatMap((group) => group.examples || []),
    totalLoaded: filteredSummary.reduce(
      (sum, group) => sum + (group.backstockCount || 0),
      0,
    ),
    totalCallList: filteredSummary.reduce(
      (sum, group) => sum + (group.callListCount || 0),
      0,
    ),
  };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const css = {
  card: {
    background:
      "linear-gradient(180deg, rgba(5,18,36,0.88), rgba(2,8,20,0.94))",
    border: "1px solid rgba(79,209,255,0.18)",
    borderRadius: 8,
    padding: 24,
    boxShadow:
      "0 22px 70px rgba(0,0,0,0.38), inset 0 1px 0 rgba(125,231,255,0.08)",
    backdropFilter: "blur(18px) saturate(170%)",
    WebkitBackdropFilter: "blur(18px) saturate(170%)",
  },
  label: {
    color: "#6be8ff",
    fontSize: 10,
    letterSpacing: 1.6,
    marginBottom: 8,
    textTransform: "uppercase",
    fontWeight: 700,
    display: "block",
  },
  input: {
    background: "rgba(1,8,20,0.72)",
    border: "1px solid rgba(87,190,255,0.2)",
    borderRadius: 8,
    color: "#edfaff",
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
    minHeight: 44,
  },
  select: {
    background: "rgba(1,8,20,0.72)",
    border: "1px solid rgba(87,190,255,0.2)",
    borderRadius: 8,
    color: "#edfaff",
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    minHeight: 44,
  },
};

// Global FX context components read this to decide whether to animate
let _fxEnabled = true;
const FX_LISTENERS = new Set();
function setGlobalFX(val) {
  _fxEnabled = val;
  FX_LISTENERS.forEach((fn) => fn(val));
}
function useFX() {
  const [fx, setFx] = useState(_fxEnabled);
  useEffect(() => {
    FX_LISTENERS.add(setFx);
    return () => FX_LISTENERS.delete(setFx);
  }, []);
  return fx;
}

function FaceGlyph({ size = 36, style }) {
  const fx = useFX();
  const [mood, setMood] = useState("idle");
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    let moodTimer;
    const flash = (next) => {
      setMood(next);
      window.clearTimeout(moodTimer);
      moodTimer = window.setTimeout(
        () => setMood("idle"),
        next === "typing" ? 420 : 620,
      );
    };
    const onKeyDown = () => flash("typing");
    const onPointerDown = () => flash("click");
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.clearTimeout(moodTimer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setBlink(true);
      window.setTimeout(() => setBlink(false), 120);
    }, 3600);
    return () => window.clearInterval(interval);
  }, []);

  const isTyping = mood === "typing";
  const isClick = mood === "click";
  const eyeHeight = blink ? 1.2 : isTyping ? 5 : isClick ? 7 : 4;
  const eyeY = isClick ? 21 : 20;
  const mouth = isTyping
    ? "M25 34 C29 36 33 36 37 34"
    : isClick
      ? "M24 33 C28 38 34 38 38 33"
      : "M23 34 C27 31 33 31 37 34";

  return (
    <div
      aria-label="Interactive face"
      role="img"
      className={`face-glyph ${fx ? "face-glyph-live" : ""} ${isTyping ? "face-glyph-typing" : ""} ${isClick ? "face-glyph-click" : ""}`}
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        ...style,
      }}
    >
      <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
        <defs>
          <linearGradient
            id="faceShell"
            x1="10"
            y1="8"
            x2="54"
            y2="58"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#0a2a46" />
            <stop offset="0.48" stopColor="#0b5fad" />
            <stop offset="1" stopColor="#5de7ff" />
          </linearGradient>
          <linearGradient
            id="faceCore"
            x1="16"
            y1="12"
            x2="50"
            y2="52"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#06111f" />
            <stop offset="1" stopColor="#020713" />
          </linearGradient>
          <filter id="faceGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.7" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d="M32 4 L52 13 L58 32 L49 53 L32 60 L15 53 L6 32 L12 13 Z"
          fill="url(#faceShell)"
          opacity="0.98"
        />
        <path
          d="M32 8 L48 16 L53 32 L45 49 L32 54 L19 49 L11 32 L16 16 Z"
          fill="url(#faceCore)"
          stroke={isClick ? "#20f6a6" : "#5de7ff"}
          strokeWidth="1.4"
        />
        <path
          d="M18 18 L26 14 M46 18 L38 14 M14 32 H9 M55 32 H50 M22 50 L17 55 M42 50 L47 55"
          stroke="#5de7ff"
          strokeWidth="1.2"
          opacity="0.55"
        />
        <rect
          x="20"
          y={eyeY}
          width={isClick ? 10 : 8}
          height={eyeHeight}
          rx="1.5"
          fill={isTyping ? "#20f6a6" : "#7df7ff"}
          filter="url(#faceGlow)"
        />
        <rect
          x={isClick ? 36 : 37}
          y={eyeY}
          width={isClick ? 10 : 8}
          height={eyeHeight}
          rx="1.5"
          fill={isTyping ? "#20f6a6" : "#7df7ff"}
          filter="url(#faceGlow)"
        />
        <path
          d={mouth}
          fill="none"
          stroke={isTyping ? "#20f6a6" : "#7df7ff"}
          strokeWidth="2"
          strokeLinecap="round"
          filter="url(#faceGlow)"
        />
        <path
          d="M24 43 H40"
          stroke="#5de7ff"
          strokeWidth="1"
          opacity={isTyping ? 0.7 : 0.25}
        />
      </svg>
    </div>
  );
}

function FaceLockup({ style }) {
  return (
    <div
      className="face-lockup"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
        ...style,
      }}
    >
      <FaceGlyph size={36} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          className="fx-shimmer-text"
          style={{
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 1.1,
            lineHeight: 1.1,
          }}
        >
          RazerGrid
        </div>
        <div
          style={{
            color: "#5de7ff",
            fontSize: 7,
            letterSpacing: 2,
            marginTop: 1,
          }}
        >
          COMMISSION OPTIMISER
        </div>
      </div>
    </div>
  );
}

function Card({ children, style, glow }) {
  const fx = useFX();
  return (
    <div
      className="card-mobile glass-card hud-corners"
      style={{
        ...css.card,
        ...style,
        ...(fx && glow
          ? {
              boxShadow: `0 0 42px ${glow}24, 0 22px 70px rgba(0,0,0,0.4), inset 0 1px 0 rgba(125,231,255,0.1)`,
            }
          : {}),
        transition: fx
          ? "border-color 0.25s, box-shadow 0.3s, transform 0.25s"
          : "none",
      }}
    >
      {children}
    </div>
  );
}
function Label({ children }) {
  return <div style={css.label}>{children}</div>;
}
function Input({ style, ...props }) {
  const fx = useFX();
  return (
    <input
      style={{ ...css.input, ...style }}
      onFocus={(e) => {
        if (fx) e.target.style.borderColor = "#4f8ef7";
        e.target.style.boxShadow = fx ? "0 0 0 3px #4f8ef722" : "none";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#1e3056";
        e.target.style.boxShadow = "none";
      }}
      {...props}
    />
  );
}
function Select({ style, children, ...props }) {
  return (
    <select style={{ ...css.select, ...style }} {...props}>
      {children}
    </select>
  );
}
function Btn({ children, variant = "primary", style, ...props }) {
  const fx = useFX();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const base = {
    border: "none",
    borderRadius: 8,
    padding: "10px 22px",
    fontSize: 12,
    fontFamily: "inherit",
    fontWeight: 700,
    letterSpacing: 1,
    cursor: "pointer",
    transition: fx
      ? "border-color 0.18s, background 0.18s, box-shadow 0.18s, transform 0.18s"
      : "none",
    transform: fx
      ? active
        ? "translateY(1px)"
        : hover
          ? "translateY(-1px)"
          : "none"
      : "none",
    position: "relative",
    overflow: "hidden",
  };
  const variants = {
    primary: {
      background: "linear-gradient(135deg,#0b4fbf 0%,#14b8ff 48%,#83f7ff 100%)",
      color: "#00111e",
      border: "1px solid rgba(125,231,255,0.42)",
      boxShadow:
        fx && hover
          ? "0 0 28px rgba(56,189,248,0.5)"
          : fx
            ? "0 0 16px rgba(56,189,248,0.25)"
            : "none",
    },
    secondary: {
      background: hover && fx ? "rgba(29,78,216,0.22)" : "rgba(8,18,38,0.78)",
      color: "#9bdcff",
      border: "1px solid rgba(91,180,255,0.24)",
    },
    danger: {
      background: hover && fx ? "#3d1830" : "#2a1020",
      color: "#f87171",
      border: "1px solid #3d1830",
      boxShadow: fx && hover ? "0 4px 16px #f8717133" : "none",
    },
    success: {
      background: "linear-gradient(135deg,#065f46,#059669)",
      color: "#fff",
      boxShadow:
        fx && hover
          ? "0 8px 24px #05966955"
          : fx
            ? "0 2px 8px #05966933"
            : "none",
    },
    warning: {
      background: "linear-gradient(135deg,#78350f,#d97706)",
      color: "#fff",
      boxShadow: fx && hover ? "0 8px 24px #d9770655" : "none",
    },
  };
  return (
    <button
      style={{ ...base, ...variants[variant], ...style }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...props}
    >
      {children}
    </button>
  );
}
// A part with no grade (blank, "?", "UNGRADED") needs attention: it earns
// nothing until someone grades it. BER parts are graded, so never count.
export function isUngradedGrade(grade) {
  const g = String(grade == null ? "" : grade)
    .trim()
    .toUpperCase();
  return (
    !g || g === "?" || g === "UNGRADED" || g === "UNKNOWN" || g === "N/A"
  );
}
export function isUngradedPart(p, grade) {
  if (!p || p.isBER || p.isBer) return false;
  return isUngradedGrade(grade === undefined ? p.grade : grade);
}
// Stable sort: ungraded parts first, everything else keeps its order.
export function ungradedFirst(parts, getGrade) {
  return (parts || [])
    .map((p, i) => [p, i])
    .sort(([a, ai], [b, bi]) => {
      const ua = isUngradedPart(a, getGrade ? getGrade(a) : undefined);
      const ub = isUngradedPart(b, getGrade ? getGrade(b) : undefined);
      return ua === ub ? ai - bi : ua ? -1 : 1;
    })
    .map(([p]) => p);
}
const UNGRADED_COLOR = "#f97316";

function GradeTag({ grade }) {
  const fx = useFX();
  if (isUngradedGrade(grade)) {
    return (
      <span
        title="Ungraded - needs a grade before it earns anything"
        style={{
          background: UNGRADED_COLOR,
          color: "#1a0a00",
          border: "1px solid #fdba74",
          borderRadius: 5,
          padding: "2px 7px",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.5,
          fontFamily: "monospace",
          whiteSpace: "nowrap",
          boxShadow: `0 0 10px ${UNGRADED_COLOR}88`,
        }}
      >
        ⚠ UNGRADED
      </span>
    );
  }
  const c = GRADE_COLORS[grade] || "#475569";
  return (
    <span
      style={{
        background: c + "22",
        color: c,
        border: `1px solid ${c}55`,
        borderRadius: 5,
        padding: "2px 9px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        fontFamily: "monospace",
        boxShadow: fx ? `0 0 8px ${c}33` : "none",
        transition: fx ? "box-shadow 0.2s" : "none",
      }}
    >
      {grade}
    </span>
  );
}
function StatBox({ label, value, sub, color = "#4f8ef7" }) {
  const fx = useFX();
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: fx && hover ? color + "18" : "#081224",
        border: `1px solid ${fx && hover ? color + "66" : color + "33"}`,
        borderRadius: 12,
        padding: "18px 20px",
        flex: 1,
        minWidth: 130,
        transition: fx ? "all 0.22s ease" : "none",
        transform: fx && hover ? "translateY(-2px)" : "none",
        boxShadow: fx && hover ? `0 8px 24px ${color}22` : "none",
      }}
    >
      <div
        style={{
          color,
          fontSize: 10,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: "#f0f6ff",
          fontSize: 24,
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ color: "#3d5280", fontSize: 11, marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function DropZone({
  label,
  sub,
  onFile,
  accept,
  color = "#4f8ef7",
  compact = false,
}) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  const fx = useFX();
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        e.dataTransfer.files[0] && onFile(e.dataTransfer.files[0]);
      }}
      onClick={() => ref.current.click()}
      style={{
        border: `2px dashed ${drag ? color : "#1e3056"}`,
        borderRadius: 12,
        padding: compact ? "16px 14px" : "32px 20px",
        textAlign: "center",
        cursor: "pointer",
        background: drag ? color + "18" : "#060e1e",
        transition: fx ? "all 0.22s ease" : "none",
        transform: fx && drag ? "scale(1.01)" : "none",
        boxShadow: fx && drag ? `0 0 28px ${color}33` : "none",
      }}
    >
      <div
        style={{
          color: drag ? color : "#4f6fa0",
          fontSize: compact ? 11 : 13,
          fontWeight: 600,
          transition: fx ? "color 0.2s" : "none",
        }}
      >
        {drag ? "Drop to upload" : label}
      </div>
      {sub && (
        <div style={{ color: "#3d5280", fontSize: 10, marginTop: 4 }}>
          {sub}
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
      />
    </div>
  );
}

function Badge({ children, color }) {
  return (
    <span
      style={{
        background: color + "22",
        color,
        border: `1px solid ${color}44`,
        borderRadius: 5,
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

// Step Indicator
function StepIndicator({ current, steps, onGo }) {
  const fx = useFX();
  return (
    <div
      className="step-indicator"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        padding: "0 16px",
        borderBottom: "1px solid #1a2744",
        overflowX: "auto",
        background: "#060e1e",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <button
            key={i}
            onClick={() => done && onGo(i)}
            style={{
              background: "none",
              border: "none",
              borderBottom: active
                ? "2px solid #4f8ef7"
                : done
                  ? "2px solid #22c55e"
                  : "2px solid transparent",
              color: active ? "#4f8ef7" : done ? "#22c55e" : "#3d5280",
              padding: "16px 20px",
              cursor: done ? "pointer" : "default",
              fontSize: 11,
              fontFamily: "inherit",
              fontWeight: active ? 700 : 400,
              letterSpacing: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
              transition: fx ? "all 0.2s" : "none",
              transform: fx && active ? "translateY(-1px)" : "none",
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: active
                  ? "linear-gradient(135deg,#1d4ed8,#4f8ef7)"
                  : done
                    ? "#22c55e"
                    : "#1a2744",
                color: active || done ? "#fff" : "#3d5280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
                boxShadow: fx && active ? "0 0 12px #4f8ef788" : "none",
                transition: fx ? "all 0.3s" : "none",
              }}
            >
              {done ? "" : i + 1}
            </span>
            {s}
          </button>
        );
      })}
    </div>
  );
}

// Step 0: Setup

// Login Screen
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fxEnabled = useFX();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError("");
    try {
      // Still a single shared password from the user's point of view — the
      // account email is fixed in supabaseApi.js, so the form is unchanged.
      const result = await signIn(password);
      if (!result.ok) {
        setError(result.error || "Incorrect password");
        setPassword("");
      } else {
        localStorage.setItem(RAZERGRID_AUTH_KEY, result.token);
        onLogin(result.token);
      }
    } catch {
      setError("Server unreachable - check connection");
    }
    setLoading(false);
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        background:
          "radial-gradient(ellipse 70% 50% at 50% 12%, rgba(17,77,134,0.42) 0%, rgba(2,10,24,0.92) 42%, #02040a 100%)",
        fontFamily:
          "'Share Tech Mono', 'Fira Code', 'IBM Plex Mono', monospace",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding:
          "16px env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 16px) env(safe-area-inset-left, 0px)",
        position: "relative",
      }}
    >
      <ParticleBackground fxEnabled={fxEnabled} />
      <div
        className="hud-corners"
        style={{
          background:
            "linear-gradient(180deg, #071428 0%, #03091a 60%, #010610 100%)",
          border: "1px solid rgba(93,231,255,0.28)",
          borderRadius: 10,
          padding: "36px 32px",
          width: "100%",
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          gap: 0,
          boxShadow:
            "0 0 70px rgba(93,231,255,0.16), 0 32px 80px #000000bb, inset 0 1px 0 rgba(255,255,255,0.08)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Brand */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            marginBottom: 32,
          }}
        >
          <FaceGlyph size={72} style={{ marginBottom: 8 }} />
          <div style={{ textAlign: "center" }}>
            <div
              className={fxEnabled ? "fx-shimmer-text" : ""}
              style={{
                fontWeight: 800,
                fontSize: 20,
                letterSpacing: 1.4,
                lineHeight: 1.2,
                ...(fxEnabled ? {} : { color: "#eef4ff" }),
              }}
            >
              RazerGrid
            </div>
            <div
              style={{
                fontSize: 9,
                color: "#5de7ff",
                letterSpacing: 2.8,
                marginTop: 5,
                fontWeight: 700,
              }}
            >
              REACTIVE LOGIC ENGINE
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background:
              "linear-gradient(90deg, transparent, #1a3060, transparent)",
            marginBottom: 28,
          }}
        />

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label
              style={{
                fontSize: 10,
                color: "#3a5a90",
                letterSpacing: 1.5,
                fontWeight: 600,
              }}
            >
              PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoFocus
              style={{
                background: "#060e1e",
                border: "1px solid #1a2f56",
                borderRadius: 10,
                color: "#dce9f8",
                fontSize: 14,
                padding: "13px 16px",
                fontFamily: "inherit",
                outline: "none",
                width: "100%",
                boxSizing: "border-box",
                minHeight: 48,
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#38bdf8";
                e.target.style.boxShadow = "0 0 0 3px #38bdf818";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#1a2f56";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {error && (
            <div
              style={{
                color: "#f87171",
                fontSize: 11,
                textAlign: "center",
                background: "#1a0808",
                border: "1px solid #7f1d1d44",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            style={{
              background:
                loading || !password
                  ? "#0d1a30"
                  : "linear-gradient(135deg, #0a4a9f 0%, #0e6dd4 50%, #38bdf8 100%)",
              border: `1px solid ${loading || !password ? "#162240" : "#38bdf840"}`,
              borderRadius: 10,
              color: loading || !password ? "#2e4470" : "#ffffff",
              fontSize: 12,
              fontFamily: "inherit",
              fontWeight: 700,
              padding: "14px 20px",
              cursor: loading || !password ? "not-allowed" : "pointer",
              letterSpacing: 1.5,
              minHeight: 48,
              width: "100%",
              transition: "all 0.2s",
              boxShadow: loading || !password ? "none" : "0 4px 16px #38bdf830",
            }}
          >
            {loading ? "VERIFYING..." : "SIGN IN"}
          </button>
        </form>

        <div
          style={{
            marginTop: 20,
            fontSize: 9,
            color: "#1e3050",
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          SECURED RAZERGRID v2
        </div>
      </div>
    </div>
  );
}

// ServerAdmin panel removed with the Express server.
// It managed API-key clients, the audit log, the Telegram bot and the
// server URL/key fields — all of which existed to police a shared HTTP API.
// Access is now enforced by Row Level Security in Postgres, and request logs
// live in the Supabase dashboard. Password changes moved to Supabase Auth
// (server.changeAppPassword), and bank holidays / engineer aliases / the
// Parts Grading Manager kept their own dedicated modals.

// Rota Manager
const ROTA_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function RotaManager({ onClose }) {
  const server = useServer();
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [wholeYearLoading, setWholeYearLoading] = useState(false);
  const [wholeYearMsg, setWholeYearMsg] = useState("");

  useEffect(() => {
    loadMonths();
  }, []);

  async function loadMonths() {
    const r = await server.fetchRotaMonths();
    if (r) setMonths(r);
  }

  async function handleUpload(file) {
    setMsg("");
    setLoading(true);
    try {
      const { year, month, dow, engineers } = await parseRotaXLSX(file);
      const r = await server.uploadRota(year, month, engineers, dow);
      if (r?.ok) {
        setMsg(
          `Uploaded ${r.count} engineers for ${ROTA_MONTH_NAMES[month - 1]} ${year}`,
        );
        loadMonths();
      } else {
        setMsg("Upload failed");
      }
    } catch (e) {
      setWholeYearMsg("Upload failed: " + e.message);
    }
    setLoading(false);
  }

  async function handleWholeYearUpload(file) {
    setWholeYearMsg("");
    setWholeYearLoading(true);
    try {
      const parsed = await parseWholeYearRotaXLSX(file);
      if (parsed.length === 0) {
        setWholeYearMsg("No month sheets found in file");
        setWholeYearLoading(false);
        return;
      }
      let uploaded = 0;
      for (const { year, month, dow, engineers } of parsed) {
        const r = await server.uploadRota(year, month, engineers, dow);
        if (r?.ok) uploaded++;
      }
      setWholeYearMsg(
        `Uploaded/replaced ${uploaded} month${uploaded !== 1 ? "s" : ""}`,
      );
      loadMonths();
    } catch (e) {
      setWholeYearMsg("Upload failed: " + e.message);
    }
    setWholeYearLoading(false);
  }

  async function handleDelete(year, month) {
    await server.deleteRotaMonth(year, month);
    setMonths((prev) =>
      prev.filter((m) => !(m.year === year && m.month === month)),
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.88)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 14,
          padding: 24,
          width: "100%",
          maxWidth: 520,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 14 }}>
            Rota Manager
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        {server?.serverStatus !== "online" && (
          <div
            style={{
              background: "#1a0505",
              border: "1px solid #7f1d1d",
              borderRadius: 8,
              padding: 12,
              color: "#f87171",
              fontSize: 11,
              marginBottom: 16,
            }}
          >
            Server offline - rota data unavailable
          </div>
        )}

        {months.length === 0 ? (
          <div style={{ color: "#4a6898", fontSize: 12, marginBottom: 16 }}>
            No rota months uploaded yet.
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            {months.map((m) => (
              <div
                key={`${m.year}-${m.month}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #1a2744",
                }}
              >
                <div>
                  <div
                    style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600 }}
                  >
                    {ROTA_MONTH_NAMES[m.month - 1]} {m.year}
                  </div>
                  <div style={{ color: "#4a6898", fontSize: 10 }}>
                    {m.engineer_count} engineers - uploaded{" "}
                    {new Date(m.uploaded_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(m.year, m.month)}
                  style={{
                    background: "#1a0a0a",
                    border: "1px solid #7f1d1d",
                    borderRadius: 6,
                    color: "#f87171",
                    cursor: "pointer",
                    fontSize: 11,
                    padding: "4px 10px",
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        <DropZone
          label="Upload Monthly Rota (.xlsx)"
          sub="One file per month. Uploading again overrides that month."
          onFile={handleUpload}
          accept=".xlsx"
          color="#a855f7"
        />
        {loading && (
          <div style={{ color: "#a855f7", fontSize: 11, marginTop: 8 }}>
            Parsing and uploading...
          </div>
        )}
        {msg && (
          <div
            style={{
              color: msg.startsWith("Uploaded") ? "#22c55e" : "#f87171",
              fontSize: 11,
              marginTop: 8,
            }}
          >
            {msg}
          </div>
        )}

        <div
          style={{
            borderTop: "1px solid #1a2744",
            marginTop: 18,
            paddingTop: 18,
          }}
        >
          <DropZone
            label="Upload Whole Year (.xlsx)"
            sub="Reads every month sheet - uploading again overrides those months."
            onFile={handleWholeYearUpload}
            accept=".xlsx"
            color="#38bdf8"
          />
          {wholeYearLoading && (
            <div style={{ color: "#38bdf8", fontSize: 11, marginTop: 8 }}>
              Reading all months and replacing uploaded rota data...
            </div>
          )}
          {wholeYearMsg && (
            <div
              style={{
                color: wholeYearMsg.startsWith("Uploaded")
                  ? "#22c55e"
                  : "#f87171",
                fontSize: 11,
                marginTop: 8,
              }}
            >
              {wholeYearMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Engineer Progress Tab
function EngineerManager({ onClose }) {
  const server = useServer();
  const [engineers, setEngineers] = useState([]);
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [msg, setMsg] = useState("");
  const [newEngineer, setNewEngineer] = useState({
    name: "",
    team: "",
    aliases: "",
    status: "active",
  });

  useEffect(() => {
    loadEngineers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // For starters the rota does not list yet. Once the rota has them, the rota
  // row becomes their identity and this entry simply merges into it.
  async function addNewEngineer() {
    const name = newEngineer.name.trim();
    if (!name) return;
    const key = normalizeEngineerName(name);
    const clash = engineers.find(
      (row) =>
        normalizeEngineerName(row.engineer) === key ||
        (row.aliases || []).some(
          (a) => normalizeEngineerName(a.alias || a) === key,
        ),
    );
    if (clash) {
      setMsg(`${name} already exists as ${clash.engineer}`);
      return;
    }
    setSaving("__new__");
    const added = await server.addEngineer?.(name, newEngineer.team, "manual");
    let ok = !!added?.ok;
    const aliases = newEngineer.aliases
      .split(/\r?\n|,/)
      .map((alias) => alias.trim())
      .filter(Boolean);
    if (ok && aliases.length) {
      ok = !!(await server.saveEngineerAliases(name, aliases))?.ok;
    }
    if (ok && newEngineer.status !== "active") {
      ok = !!(await server.saveEngineerStatus?.(name, newEngineer.status))?.ok;
    }
    setSaving("");
    if (ok) {
      setMsg(`Saved new engineer ${name}`);
      setNewEngineer({ name: "", team: "", aliases: "", status: "active" });
      await loadEngineers();
      setTimeout(() => setMsg(""), 2500);
    } else {
      setMsg(`Could not add ${name}`);
    }
  }

  async function removeAddedEngineer(engineer) {
    if (!window.confirm(`Remove ${engineer} from the roster? Saved History is kept.`))
      return;
    setSaving(engineer);
    const result = await server.removeEngineer?.(engineer);
    if (result?.ok) await server.saveEngineerAliases(engineer, []);
    setSaving("");
    if (result?.ok) {
      setMsg(`Saved: removed ${engineer}`);
      await loadEngineers();
      setTimeout(() => setMsg(""), 2500);
    } else {
      setMsg(`Could not remove ${engineer}`);
    }
  }

  async function loadEngineers() {
    if (server?.serverStatus !== "online") return;
    setLoading(true);
    const rows = await server.fetchEngineers();
    if (rows) {
      setEngineers(rows);
      const drafts = {};
      rows.forEach((row) => {
        drafts[row.engineer] = (row.aliases || [])
          .map((a) => a.alias || a)
          .join("\n");
      });
      setAliasDrafts(drafts);
    }
    setLoading(false);
  }

  async function saveAliases(engineer) {
    const aliases = String(aliasDrafts[engineer] || "")
      .split(/\r?\n|,/)
      .map((alias) => alias.trim())
      .filter(Boolean);
    setSaving(engineer);
    const result = await server.saveEngineerAliases(engineer, aliases);
    setSaving("");
    if (result?.ok) {
      setMsg(`Saved aliases for ${engineer}`);
      await loadEngineers();
      setTimeout(() => setMsg(""), 2500);
    } else {
      setMsg(`Could not save aliases for ${engineer}`);
    }
  }

  async function saveStatus(engineer, status) {
    setSaving(engineer);
    const result = await server.saveEngineerStatus?.(engineer, status);
    setSaving("");
    if (result?.ok) {
      setMsg(`Saved status for ${engineer}`);
      await loadEngineers();
      setTimeout(() => setMsg(""), 2500);
    } else {
      setMsg(`Could not save status for ${engineer}`);
    }
  }

  const normalizedFilter = filter.toLowerCase();
  const visible = engineers.filter((row) => {
    if (!normalizedFilter) return true;
    const text =
      `${row.engineer} ${row.section || ""} ${row.status || "active"} ${(row.aliases || []).map((a) => a.alias || a).join(" ")}`.toLowerCase();
    return text.includes(normalizedFilter);
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.88)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 14,
          padding: 24,
          width: "100%",
          maxWidth: 760,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div>
            <div style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 14 }}>
              Engineer Manager
            </div>
            <div style={{ color: "#4a6898", fontSize: 10, marginTop: 4 }}>
              Rota names are the canonical identity. Add AKA names from KPI
              exports here, or add a new starter who is not on the rota yet.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        {server?.serverStatus !== "online" && (
          <div
            style={{
              background: "#1a0505",
              border: "1px solid #7f1d1d",
              borderRadius: 8,
              padding: 12,
              color: "#f87171",
              fontSize: 11,
              marginBottom: 16,
            }}
          >
            Server offline - engineer aliases unavailable
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search engineer, team, or alias"
            style={{ flex: 1, minWidth: 220 }}
          />
          <Btn variant="secondary" onClick={loadEngineers} disabled={loading}>
            {loading ? "LOADING..." : "REFRESH"}
          </Btn>
        </div>

        <div
          style={{
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 10,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <Label>Add engineer</Label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 10,
              alignItems: "end",
            }}
          >
            <Input
              value={newEngineer.name}
              onChange={(e) =>
                setNewEngineer((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Name as on the rota, e.g. Lucas Payne"
            />
            <Select
              value={newEngineer.team}
              onChange={(e) =>
                setNewEngineer((prev) => ({ ...prev, team: e.target.value }))
              }
            >
              <option value="">Team…</option>
              {RC_TEAM_KEYS.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </Select>
            <Input
              value={newEngineer.aliases}
              onChange={(e) =>
                setNewEngineer((prev) => ({ ...prev, aliases: e.target.value }))
              }
              placeholder="KPI names, e.g. LUCASPAYNE"
            />
            <Select
              value={newEngineer.status}
              onChange={(e) =>
                setNewEngineer((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="active">Active</option>
              <option value="left">Left</option>
              <option value="admin">Admin - no commission</option>
            </Select>
            <Btn
              variant="success"
              onClick={addNewEngineer}
              disabled={
                !newEngineer.name.trim() ||
                saving === "__new__" ||
                server?.serverStatus !== "online"
              }
              style={{ fontSize: 10, padding: "7px 14px", whiteSpace: "nowrap" }}
            >
              {saving === "__new__" ? "ADDING..." : "ADD ENGINEER"}
            </Btn>
          </div>
        </div>

        {msg && (
          <div
            style={{
              color: msg.startsWith("Saved") ? "#22c55e" : "#f87171",
              fontSize: 11,
              marginBottom: 12,
            }}
          >
            {msg}
          </div>
        )}

        {visible.length === 0 ? (
          <div style={{ color: "#4a6898", fontSize: 12, padding: "24px 0" }}>
            {loading
              ? "Loading engineers..."
              : "No engineers found. Upload a rota first."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {visible.map((row) => {
              const team =
                normalizeSectionToTeam(row.section) || row.section || "Unknown";
              const aliasCount = (row.aliases || []).length;
              const status = row.status || "active";
              const muted = status !== "active";
              return (
                <div
                  key={row.engineer}
                  style={{
                    background: "#081224",
                    border: `1px solid ${muted ? "#3a2a12" : "#1e3056"}`,
                    borderRadius: 10,
                    padding: 14,
                    opacity: muted ? 0.72 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(210px, 1fr))",
                      gap: 12,
                      alignItems: "start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#e2e8f0",
                          fontSize: 13,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {row.engineer}
                        {muted && (
                          <span
                            style={{
                              background:
                                status === "admin" ? "#3a2a12" : "#2a1616",
                              color:
                                status === "admin" ? "#fbbf24" : "#f87171",
                              border: `1px solid ${
                                status === "admin" ? "#7c5a1a" : "#7f1d1d"
                              }`,
                              borderRadius: 6,
                              padding: "2px 7px",
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}
                          >
                            {status === "admin" ? "ADMIN" : "LEFT"}
                          </span>
                        )}
                        {row.addedSource && !row.year && (
                          <span
                            title="Not on the rota yet. Days worked fill in once a rota listing them is uploaded."
                            style={{
                              background: "#062a1c",
                              color: "#34d399",
                              border: "1px solid #14532d",
                              borderRadius: 6,
                              padding: "2px 7px",
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}
                          >
                            {row.addedSource === "upload"
                              ? "NEW · FROM UPLOAD"
                              : "NEW · NOT ON ROTA"}
                          </span>
                        )}
                      </div>
                      <div
                        style={{ color: "#4a6898", fontSize: 10, marginTop: 4 }}
                      >
                        {team}
                      </div>
                      {row.year && row.month && (
                        <div
                          style={{
                            color: "#2e4470",
                            fontSize: 9,
                            marginTop: 3,
                          }}
                        >
                          From {ROTA_MONTH_NAMES[row.month - 1]} {row.year}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>AKA / import names</Label>
                      <textarea
                        value={aliasDrafts[row.engineer] || ""}
                        onChange={(e) =>
                          setAliasDrafts((prev) => ({
                            ...prev,
                            [row.engineer]: e.target.value,
                          }))
                        }
                        rows={Math.max(2, Math.min(5, aliasCount + 1))}
                        style={{
                          width: "100%",
                          minHeight: 58,
                          resize: "vertical",
                          background: "#060e1e",
                          border: "1px solid #1e3056",
                          borderRadius: 8,
                          color: "#c8d8f0",
                          fontFamily: "inherit",
                          fontSize: 11,
                          padding: "8px 10px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select
                        value={status}
                        onChange={(e) =>
                          saveStatus(row.engineer, e.target.value)
                        }
                        disabled={saving === row.engineer}
                        style={{ width: "100%" }}
                      >
                        <option value="active">Active</option>
                        <option value="left">Left</option>
                        <option value="admin">Admin - no commission</option>
                      </Select>
                      <div
                        style={{ color: "#4a6898", fontSize: 9, marginTop: 5 }}
                      >
                        {status === "admin"
                          ? "Parts excluded from History in every month."
                          : status === "left"
                            ? "History kept; hidden from rotas and diagnostics."
                            : "Counts normally."}
                      </div>
                    </div>
                    <Btn
                      variant="success"
                      onClick={() => saveAliases(row.engineer)}
                      disabled={saving === row.engineer}
                      style={{
                        alignSelf: "end",
                        fontSize: 10,
                        padding: "7px 14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {saving === row.engineer ? "SAVING..." : "SAVE AKA"}
                    </Btn>
                    {row.addedSource && !row.year && (
                      <Btn
                        variant="secondary"
                        onClick={() => removeAddedEngineer(row.engineer)}
                        disabled={saving === row.engineer}
                        style={{
                          alignSelf: "end",
                          fontSize: 10,
                          padding: "7px 14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        REMOVE
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AllocationBlocklistManager({ onClose }) {
  const server = useServer();
  const [blockData, setBlockData] = useState({ global: [], engineers: {} });
  const [engineers, setEngineers] = useState([]);
  const [scope, setScope] = useState("global");
  const [engineer, setEngineer] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    loadBlocklist();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadBlocklist() {
    if (server?.serverStatus !== "online") return;
    setLoading(true);
    const [list, roster] = await Promise.all([
      server.fetchAllocationBlocklist(),
      server.fetchEngineers(),
    ]);
    if (list) setBlockData(list);
    if (roster) {
      setEngineers(roster);
      const selectable = roster.filter(
        (row) => (row.status || "active") === "active",
      );
      setEngineer((prev) => prev || selectable[0]?.engineer || "");
    }
    setLoading(false);
  }

  function splitPartIds(value) {
    return String(value || "")
      .split(/[\n,;]+/)
      .map((id) => id.trim().toUpperCase())
      .filter(Boolean);
  }

  async function addParts() {
    const partIds = splitPartIds(input);
    if (!partIds.length) return;
    if (scope === "engineer" && !engineer) {
      setMsg("Choose an engineer first.");
      return;
    }
    setSaving(true);
    const result = await server.addAllocationBlocklist(
      scope,
      engineer,
      partIds,
    );
    setSaving(false);
    if (result?.blocklist) {
      setBlockData(result.blocklist);
      setInput("");
      setMsg(`Saved ${partIds.length} part${partIds.length === 1 ? "" : "s"}.`);
      setTimeout(() => setMsg(""), 2500);
    } else {
      setMsg("Could not save blocklist entries.");
    }
  }

  async function removePart(removeScope, removeEngineer, partId) {
    const result = await server.deleteAllocationBlocklist(
      removeScope,
      removeEngineer,
      partId,
    );
    if (result?.blocklist) setBlockData(result.blocklist);
  }

  const globalParts = blockData.global || [];
  const engineerGroups = blockData.engineers || {};
  // Roster pickers stop offering leavers and admin staff. Anything already
  // blocked remains visible in the saved groups below.
  const selectableEngineers = engineers.filter(
    (row) => (row.status || "active") === "active",
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.88)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 14,
          padding: 24,
          width: "100%",
          maxWidth: 820,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div>
            <div style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 14 }}>
              Allocation Blocklist
            </div>
            <div style={{ color: "#4a6898", fontSize: 10, marginTop: 4 }}>
              Saved part exclusions used by Allocate monthly/daily views and
              shared task links.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        {server?.serverStatus !== "online" && (
          <div
            style={{
              background: "#1a0505",
              border: "1px solid #7f1d1d",
              borderRadius: 8,
              padding: 12,
              color: "#f87171",
              fontSize: 11,
              marginBottom: 16,
            }}
          >
            Server offline - saved allocation blocklists unavailable
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            alignItems: "end",
            marginBottom: 14,
          }}
        >
          <div>
            <Label>Apply To</Label>
            <Select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="global">All engineers</option>
              <option value="engineer">One engineer</option>
            </Select>
          </div>
          {scope === "engineer" && (
            <div>
              <Label>Engineer</Label>
              <Select
                value={engineer}
                onChange={(e) => setEngineer(e.target.value)}
              >
                {selectableEngineers.map((row) => (
                  <option key={row.engineer} value={row.engineer}>
                    {row.engineer}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <Label>Part ID(s)</Label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  addParts();
                }
              }}
              rows={2}
              placeholder="Comma, semicolon, or new line separated"
              style={{
                ...css.input,
                width: "100%",
                resize: "vertical",
                fontSize: 11,
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          <Btn
            onClick={addParts}
            disabled={saving || server?.serverStatus !== "online"}
          >
            {saving ? "SAVING..." : "ADD SAVED BLOCK"}
          </Btn>
          <Btn
            variant="secondary"
            onClick={loadBlocklist}
            disabled={loading || server?.serverStatus !== "online"}
          >
            {loading ? "LOADING..." : "REFRESH"}
          </Btn>
          {msg && (
            <span
              style={{
                color:
                  msg.startsWith("Could") || msg.startsWith("Choose")
                    ? "#f87171"
                    : "#22c55e",
                fontSize: 11,
              }}
            >
              {msg}
            </span>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          <div
            style={{
              background: "#081224",
              border: "1px solid #1e3056",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 10,
                letterSpacing: 1,
                marginBottom: 10,
              }}
            >
              ALL ENGINEERS ({globalParts.length})
            </div>
            {globalParts.length === 0 ? (
              <div style={{ color: "#3d5280", fontSize: 11 }}>
                No global blocks saved.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {globalParts.map((item) => {
                  const partId = item.partId || item.part_id || item;
                  return (
                    <div
                      key={partId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "#2a1020",
                        border: "1px solid #3d1830",
                        borderRadius: 6,
                        padding: "4px 10px",
                      }}
                    >
                      <span
                        style={{
                          color: "#f87171",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}
                      >
                        {partId}
                      </span>
                      <button
                        onClick={() => removePart("global", "", partId)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          cursor: "pointer",
                          fontSize: 12,
                          padding: 0,
                        }}
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div
            style={{
              background: "#081224",
              border: "1px solid #1e3056",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 10,
                letterSpacing: 1,
                marginBottom: 10,
              }}
            >
              ENGINEER-SPECIFIC
            </div>
            {Object.entries(engineerGroups).length === 0 ? (
              <div style={{ color: "#3d5280", fontSize: 11 }}>
                No engineer-specific blocks saved.
              </div>
            ) : (
              Object.entries(engineerGroups).map(([eng, items]) => (
                <div key={eng} style={{ marginBottom: 12 }}>
                  <div
                    style={{ color: "#7aa0d4", fontSize: 11, marginBottom: 5 }}
                  >
                    {eng}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(items || []).map((item) => {
                      const partId = item.partId || item.part_id || item;
                      return (
                        <div
                          key={`${eng}-${partId}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "#1c1917",
                            border: "1px solid #713f12",
                            borderRadius: 6,
                            padding: "4px 10px",
                          }}
                        >
                          <span
                            style={{
                              color: "#fbbf24",
                              fontFamily: "monospace",
                              fontSize: 11,
                            }}
                          >
                            {partId}
                          </span>
                          <button
                            onClick={() => removePart("engineer", eng, partId)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#64748b",
                              cursor: "pointer",
                              fontSize: 12,
                              padding: 0,
                            }}
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EngineerProgressTab({ state }) {
  const server = useServer();
  const todayISO = new Date().toISOString().split("T")[0];
  const [progressDate, setProgressDate] = useState(todayISO);
  const [progressData, setProgressData] = useState([]);
  const [flags, setFlags] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedEngineer, setExpandedEngineer] = useState(null);
  const [approvingFlag, setApprovingFlag] = useState(null);
  const [applyOverride, setApplyOverride] = useState(true);
  const [progressActionMsg, setProgressActionMsg] = useState("");
  const [resettingEngineer, setResettingEngineer] = useState("");

  const gradeValues = state?.gradeValues || DEFAULT_GRADE_VALUES;

  async function fetchProgress() {
    if (server?.serverStatus !== "online") return;
    setRefreshing(true);
    const [pd, fl] = await Promise.all([
      server.fetchEngineerProgress(progressDate),
      server.fetchProgressFlags("pending"),
    ]);
    if (pd) setProgressData(pd);
    if (fl) setFlags(fl);
    setLastRefreshed(Date.now());
    setSecondsAgo(0);
    setRefreshing(false);
  }

  useEffect(() => {
    fetchProgress();
  }, [progressDate, server?.serverStatus]);

  useEffect(() => {
    const interval = setInterval(fetchProgress, 30000);
    return () => clearInterval(interval);
  }, [progressDate, server?.serverStatus]);

  useEffect(() => {
    if (!lastRefreshed) return;
    const ticker = setInterval(
      () => setSecondsAgo(Math.floor((Date.now() - lastRefreshed) / 1000)),
      1000,
    );
    return () => clearInterval(ticker);
  }, [lastRefreshed]);

  function getEngineerStats(eng) {
    const completedSet = new Set(eng.completed || []);
    const parts = eng.parts || [];
    const gv = eng.gradeValues || gradeValues;
    const berVal = gv["BER"] || 0.1;
    const total = parts.length;
    const done = completedSet.size;
    const commission = parts.reduce(
      (s, p) =>
        completedSet.has(p.serial)
          ? s + (p.isBER ? berVal : gv[p.grade] || 0)
          : s,
      0,
    );
    const nonBerDone = parts.filter(
      (p) => completedSet.has(p.serial) && !p.isBER,
    ).length;
    const byPrio = {
      urgent: { done: 0, total: 0 },
      rep_req: { done: 0, total: 0 },
      stock: { done: 0, total: 0 },
    };
    parts.forEach((p) => {
      if (byPrio[p.priority]) {
        byPrio[p.priority].total++;
        if (completedSet.has(p.serial)) byPrio[p.priority].done++;
      }
    });
    const tiers = eng.tiers || [];
    const tiersHit = tiers.filter((t) => nonBerDone >= t.daily);
    return {
      total,
      done,
      commission,
      nonBerDone,
      byPrio,
      tiers,
      tiersHit,
      completedSet,
    };
  }

  function lastUpdatedLabel(ts) {
    if (!ts) return "";
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 5) return `${mins}m ago`;
    if (mins < 15) return `${mins}m ago`;
    return `${mins}m ago`;
  }

  function lastUpdatedColor(ts) {
    if (!ts) return "#3d5280";
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return "#22c55e";
    if (mins < 5) return "#4a6898";
    if (mins < 15) return "#f59e0b";
    return "#ef4444";
  }

  const sorted = [...progressData].sort((a, b) => {
    const aS = getEngineerStats(a);
    const bS = getEngineerStats(b);
    if (aS.total === 0 && bS.total > 0) return 1;
    if (bS.total === 0 && aS.total > 0) return -1;
    return bS.done / (bS.total || 1) - aS.done / (aS.total || 1);
  });

  const teamStats = progressData.reduce(
    (acc, eng) => {
      const s = getEngineerStats(eng);
      acc.totalDone += s.done;
      acc.totalParts += s.total;
      acc.totalCommission += s.commission;
      const tiers = eng.tiers || [];
      tiers.forEach((t, i) => {
        if (s.nonBerDone >= t.daily)
          acc.tiersHit[i] = (acc.tiersHit[i] || 0) + 1;
      });
      return acc;
    },
    { totalDone: 0, totalParts: 0, totalCommission: 0, tiersHit: {} },
  );

  const PRIO_LABELS = { urgent: "Call", rep_req: "Priority", stock: "Stock" };
  const liveRescueBoard = buildLiveRescueBoard(progressData, flags);
  const rescueTone = {
    good: { accent: "#22c55e", bg: "rgba(34,197,94,0.08)" },
    warning: { accent: "#fbbf24", bg: "rgba(251,191,36,0.08)" },
    risk: { accent: "#f87171", bg: "rgba(248,113,113,0.08)" },
  };

  function buildProgressLink(eng) {
    const [year, month, day] = String(progressDate || todayISO)
      .split("-")
      .map((n) => parseInt(n, 10));
    const labelDate = new Date(year, month - 1, day);
    const parts = (eng.parts || []).map((p) => ({
      serial: p.serial,
      partId: p.partId || p.ref,
      grade: p.grade,
      priority: p.priority,
      source: p.source,
      isBER: p.isBER || p.grade === "BER",
    }));
    const payload = {
      __sharedDay: true,
      engineerName: eng.engineer,
      rotaEngineerName: eng.rotaEngineerName || eng.engineer,
      label: labelDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      dayNumber: eng.dayNumber || 1,
      gradeValues: eng.gradeValues || gradeValues,
      tiers: eng.tiers || [],
      parts,
      serverUrl: SHARE_ORIGIN,
      shareDate: { year, month, day },
    };

    try {
      const encoded = btoa(
        unescape(encodeURIComponent(JSON.stringify(payload))),
      );
      return `${window.location.origin}${window.location.pathname}#day=${encoded}`;
    } catch {
      return null;
    }
  }

  function copyProgressLink(eng) {
    const url = buildProgressLink(eng);
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setProgressActionMsg(`Fresh link copied for ${eng.engineer}`);
        setTimeout(() => setProgressActionMsg(""), 2500);
      })
      .catch(() => prompt("Copy this link:", url));
  }

  async function resetEngineerProgress(eng) {
    if (
      !window.confirm(
        `Delete saved progress for ${eng.engineer} on ${progressDate} and copy a fresh link?`,
      )
    )
      return;
    const url = buildProgressLink(eng);
    setResettingEngineer(eng.engineer);
    const result = await server.deleteEngineerProgress(
      progressDate,
      eng.engineer,
    );
    setResettingEngineer("");
    if (!result) {
      setProgressActionMsg(`Could not reset ${eng.engineer}`);
      setTimeout(() => setProgressActionMsg(""), 3000);
      return;
    }
    setProgressData((prev) =>
      prev.filter((item) => item.engineer !== eng.engineer),
    );
    if (expandedEngineer === eng.engineer) setExpandedEngineer(null);
    if (url) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setProgressActionMsg(
            `Progress reset and fresh link copied for ${eng.engineer}`,
          );
          setTimeout(() => setProgressActionMsg(""), 3000);
        })
        .catch(() => prompt("Progress reset. Copy this fresh link:", url));
    } else {
      setProgressActionMsg(`Progress reset for ${eng.engineer}`);
      setTimeout(() => setProgressActionMsg(""), 3000);
    }
  }

  async function handleFlagAction(flagId, status) {
    await server.updateProgressFlag(
      flagId,
      status,
      status === "approved" ? applyOverride : false,
    );
    setFlags((prev) => prev.filter((f) => f.id !== flagId));
    setApprovingFlag(null);
  }

  if (server?.serverStatus !== "online") {
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: "#1a0505",
            border: "1px solid #7f1d1d",
            borderRadius: 10,
            padding: 16,
            color: "#f87171",
            fontSize: 12,
          }}
        >
          Server offline engineer progress unavailable
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 40px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 20,
          padding: "16px 0 0",
        }}
      >
        <input
          type="date"
          value={progressDate}
          onChange={(e) => {
            setProgressDate(e.target.value);
            setProgressData([]);
            setFlags([]);
          }}
          style={{
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 8,
            color: "#c8d8f0",
            fontSize: 12,
            padding: "6px 10px",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={fetchProgress}
          disabled={refreshing}
          style={{
            background: "#0d1b35",
            border: "1px solid #1e3056",
            borderRadius: 8,
            color: "#7ab4f7",
            cursor: "pointer",
            fontSize: 11,
            padding: "6px 14px",
            fontFamily: "inherit",
          }}
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
        {lastRefreshed && (
          <span style={{ color: "#3d5280", fontSize: 10 }}>
            Updated {secondsAgo}s ago
          </span>
        )}
        {progressActionMsg && (
          <span
            style={{
              color: progressActionMsg.startsWith("Could not")
                ? "#f87171"
                : "#10b981",
              fontSize: 10,
            }}
          >
            {progressActionMsg}
          </span>
        )}
        {flags.length > 0 && (
          <span
            style={{
              marginLeft: "auto",
              background: "#ef4444",
              color: "#fff",
              borderRadius: 12,
              fontSize: 10,
              padding: "2px 8px",
              fontWeight: 700,
            }}
          >
            {flags.length} flag{flags.length !== 1 ? "s" : ""} pending
          </span>
        )}
      </div>

      {(progressData.length > 0 || flags.length > 0) && (
        <div
          style={{
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 12,
            padding: 14,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  color: "#5de7ff",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Live Rescue Board
              </div>
              <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 3 }}>
                {liveRescueBoard.summary.openParts} open parts /{" "}
                {liveRescueBoard.summary.openCalls} calls /{" "}
                {liveRescueBoard.summary.openPriority} priority
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {[
                ["Stale", liveRescueBoard.summary.staleEngineers, "#fbbf24"],
                ["Flags", liveRescueBoard.summary.pendingFlags, "#f87171"],
                ["Active", liveRescueBoard.summary.activeEngineers, "#7ab4f7"],
              ].map(([label, value, color]) => (
                <span
                  key={label}
                  style={{
                    border: `1px solid ${color}55`,
                    color,
                    borderRadius: 10,
                    padding: "3px 8px",
                    fontSize: 10,
                    fontFamily: "monospace",
                  }}
                >
                  {label} {value}
                </span>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {liveRescueBoard.actions.map((item) => {
              const tone = rescueTone[item.tone] || rescueTone.warning;
              return (
                <div
                  key={item.id}
                  style={{
                    border: `1px solid ${tone.accent}33`,
                    background: tone.bg,
                    borderRadius: 8,
                    padding: 12,
                    minHeight: 132,
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      alignItems: "baseline",
                    }}
                  >
                    <div
                      style={{
                        color: tone.accent,
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        color: "#dbeafe",
                        fontSize: 12,
                        fontWeight: 800,
                        textAlign: "right",
                      }}
                    >
                      {item.value}
                    </div>
                  </div>
                  <div style={{ color: "#8aa9d6", fontSize: 11, lineHeight: 1.45 }}>
                    {item.detail}
                  </div>
                  <div
                    style={{
                      color: "#c8d8f0",
                      fontSize: 11,
                      lineHeight: 1.45,
                      marginTop: "auto",
                    }}
                  >
                    {item.action}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team stats row */}
      {progressData.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          {[
            ["Engineers Active", progressData.length, "#4f8ef7"],
            [
              "Parts Done",
              `${teamStats.totalDone} / ${teamStats.totalParts}`,
              "#10b981",
            ],
            [
              "Commission Earned",
              `GBP ${teamStats.totalCommission.toFixed(2)}`,
              "#f59e0b",
            ],
            ...Object.entries(teamStats.tiersHit).map(([i, count]) => [
              `Tier ${parseInt(i) + 1} Hit`,
              count,
              "#a855f7",
            ]),
          ].map(([label, value, color]) => (
            <div
              key={label}
              style={{
                background: "#081224",
                border: "1px solid #1e3056",
                borderRadius: 10,
                padding: "10px 16px",
                flex: 1,
                minWidth: 120,
              }}
            >
              <div
                style={{
                  color: "#4f6fa0",
                  fontSize: 9,
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                {label.toUpperCase()}
              </div>
              <div style={{ color, fontSize: 18, fontWeight: 800 }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Engineer cards */}
      {progressData.length === 0 ? (
        <div
          style={{
            color: "#3d5280",
            fontSize: 12,
            padding: "40px 0",
            textAlign: "center",
          }}
        >
          No engineers have opened their task links yet for this date.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 24,
          }}
        >
          {sorted.map((eng) => {
            const s = getEngineerStats(eng);
            const pct = s.total > 0 ? (s.done / s.total) * 100 : 0;
            const barColor =
              pct >= 75 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#ef4444";
            const isExpanded = expandedEngineer === eng.engineer;
            return (
              <div
                key={eng.engineer}
                style={{
                  background: "#0d1b35",
                  border: "1px solid #1e3056",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <div
                  onClick={() =>
                    setExpandedEngineer(isExpanded ? null : eng.engineer)
                  }
                  style={{ padding: "14px 18px", cursor: "pointer" }}
                >
                  {/* Name + last updated */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        color: "#e2e8f0",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {eng.engineer}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyProgressLink(eng);
                        }}
                        style={{
                          background: "#081224",
                          border: "1px solid #1e3056",
                          borderRadius: 7,
                          color: "#7ab4f7",
                          cursor: "pointer",
                          fontSize: 10,
                          padding: "4px 9px",
                          fontFamily: "inherit",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Copy link
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          resetEngineerProgress(eng);
                        }}
                        disabled={resettingEngineer === eng.engineer}
                        style={{
                          background: "#1a0505",
                          border: "1px solid #7f1d1d66",
                          borderRadius: 7,
                          color: "#f87171",
                          cursor:
                            resettingEngineer === eng.engineer
                              ? "default"
                              : "pointer",
                          opacity:
                            resettingEngineer === eng.engineer ? 0.65 : 1,
                          fontSize: 10,
                          padding: "4px 9px",
                          fontFamily: "inherit",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {resettingEngineer === eng.engineer
                          ? "Resetting..."
                          : "Reset + link"}
                      </button>
                      <span
                        style={{
                          color: lastUpdatedColor(eng.lastUpdated),
                          fontSize: 10,
                        }}
                      >
                        {lastUpdatedLabel(eng.lastUpdated)}
                      </span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div
                    style={{
                      background: "#1a2744",
                      borderRadius: 4,
                      height: 6,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: barColor,
                        borderRadius: 4,
                        transition: "width 0.4s",
                      }}
                    />
                  </div>
                  {/* Stats row */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px 18px",
                      fontSize: 11,
                    }}
                  >
                    <span style={{ color: barColor, fontWeight: 700 }}>
                      {s.done} / {s.total} parts
                    </span>
                    <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                      GBP {s.commission.toFixed(2)}
                    </span>
                    {/* Tier chips */}
                    {s.tiers.map((t, i) => {
                      const hit = s.nonBerDone >= t.daily;
                      return (
                        <span
                          key={i}
                          style={{
                            background: hit ? "#16a34a22" : "#1a2744",
                            border: `1px solid ${hit ? "#16a34a" : "#1e3056"}`,
                            borderRadius: 10,
                            color: hit ? "#4ade80" : "#3d5280",
                            fontSize: 9,
                            padding: "1px 7px",
                          }}
                        >
                          T{i + 1}
                          {hit ? " hit" : ` ${s.nonBerDone}/${t.daily}`}
                        </span>
                      );
                    })}
                    {/* Priority breakdown */}
                    {Object.entries(s.byPrio).map(
                      ([key, v]) =>
                        v.total > 0 && (
                          <span
                            key={key}
                            style={{ color: "#4a6898", fontSize: 10 }}
                          >
                            {PRIO_LABELS[key]} {v.done}/{v.total}
                          </span>
                        ),
                    )}
                  </div>
                </div>
                {/* Expanded parts list */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid #1a2744" }}>
                    <div
                      style={{
                        padding: "8px 18px 4px",
                        color: "#4f6fa0",
                        fontSize: 9,
                        letterSpacing: 1,
                      }}
                    >
                      ALL ASSIGNED PARTS
                    </div>
                    {ungradedFirst(eng.parts).map((p, i) => {
                      const done = s.completedSet.has(p.serial);
                      const val = p.isBER
                        ? eng.gradeValues?.BER || 0.1
                        : eng.gradeValues?.[p.grade] ||
                          gradeValues[p.grade] ||
                          0;
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "8px 18px",
                            borderBottom: "1px solid #0a1428",
                            opacity: done ? 1 : 0.6,
                          }}
                        >
                          <span
                            style={{
                              color: done ? "#10b981" : "#3d5280",
                              fontSize: 10,
                              width: 44,
                              flexShrink: 0,
                            }}
                          >
                            {done ? "Done" : "Open"}
                          </span>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 11,
                              flex: 1,
                              color: done ? "#10b981" : "#7aa0d4",
                              textDecoration: done ? "line-through" : "none",
                            }}
                          >
                            {p.serial}
                          </span>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 10,
                              color: "#4a6898",
                              flex: 1,
                            }}
                          >
                            {p.partId}
                          </span>
                          <GradeTag grade={p.grade} />
                          <span
                            style={{
                              color: done ? "#10b981" : "#4f6fa0",
                              fontFamily: "monospace",
                              fontSize: 10,
                              flexShrink: 0,
                            }}
                          >
                            GBP {val.toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Grade flags panel */}
      {flags.length > 0 && (
        <div
          style={{
            background: "#0d1b35",
            border: "1px solid #7f1d1d44",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 18px",
              background: "#100a0a",
              color: "#f87171",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            Grade Flags {flags.length} pending
          </div>
          {flags.map((flag) => (
            <div
              key={flag.id}
              style={{
                padding: "12px 18px",
                borderBottom: "1px solid #1a1010",
                display: "flex",
                alignItems: "flex-start",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    color: "#e2e8f0",
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  {flag.engineer}
                </div>
                <div style={{ color: "#4a6898", fontSize: 10 }}>
                  <span style={{ fontFamily: "monospace" }}>
                    {flag.part_serial}
                  </span>
                  {flag.part_id && <span> {flag.part_id}</span>}{" "}
                  <span style={{ color: "#ef4444" }}>{flag.current_grade}</span>
                  <span> to </span>
                  <span style={{ color: "#22c55e" }}>
                    {flag.suggested_grade}
                  </span>
                  {flag.notes && (
                    <span style={{ marginLeft: 8, color: "#3d5280" }}>
                      "{flag.notes}"
                    </span>
                  )}
                </div>
                <div style={{ color: "#2a3a5a", fontSize: 9, marginTop: 2 }}>
                  {new Date(flag.created_at).toLocaleString("en-GB")}
                </div>
              </div>
              {approvingFlag === flag.id ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: "#c8d8f0",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={applyOverride}
                      onChange={(e) => setApplyOverride(e.target.checked)}
                    />
                    Apply grade override to grading reference
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleFlagAction(flag.id, "approved")}
                      style={{
                        background: "#14532d",
                        border: "1px solid #16a34a",
                        borderRadius: 6,
                        color: "#4ade80",
                        cursor: "pointer",
                        fontSize: 11,
                        padding: "4px 12px",
                      }}
                    >
                      Confirm Approve
                    </button>
                    <button
                      onClick={() => setApprovingFlag(null)}
                      style={{
                        background: "none",
                        border: "1px solid #1e3056",
                        borderRadius: 6,
                        color: "#4a6898",
                        cursor: "pointer",
                        fontSize: 11,
                        padding: "4px 10px",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => setApprovingFlag(flag.id)}
                    style={{
                      background: "#14532d22",
                      border: "1px solid #16a34a44",
                      borderRadius: 6,
                      color: "#4ade80",
                      cursor: "pointer",
                      fontSize: 11,
                      padding: "4px 12px",
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleFlagAction(flag.id, "rejected")}
                    style={{
                      background: "#1a0505",
                      border: "1px solid #7f1d1d44",
                      borderRadius: 6,
                      color: "#f87171",
                      cursor: "pointer",
                      fontSize: 11,
                      padding: "4px 10px",
                    }}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Bank Holiday Manager
function BankHolidayManager({ onClose }) {
  const server = useServer();
  const [holidays, setHolidays] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");
  const [, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (server?.serverStatus === "online") {
      server.fetchBankHolidays().then((all) => {
        if (all) setHolidays(all);
      });
    }
  }, []);

  async function toggle(id, active) {
    await server.toggleBankHoliday(id, active);
    setHolidays((prev) =>
      prev.map((h) => (h.id === id ? { ...h, active: active ? 1 : 0 } : h)),
    );
  }

  async function addCustom() {
    if (!newDate || !newName) return;
    setLoading(true);
    await server.addBankHoliday(newDate, newName);
    const all = await server.fetchBankHolidays();
    if (all) setHolidays(all);
    setNewDate("");
    setNewName("");
    setLoading(false);
    setMsg("Added ");
    setTimeout(() => setMsg(""), 2000);
  }

  async function remove(id) {
    await server.deleteBankHoliday(id);
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  }

  const years = [...new Set(holidays.map((h) => h.year))].sort();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.88)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 14,
          padding: 24,
          width: "100%",
          maxWidth: 560,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 14 }}>
            {" "}
            UK Bank Holidays
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
            }}
          ></button>
        </div>

        {server?.serverStatus !== "online" && (
          <div
            style={{
              background: "#1a0505",
              border: "1px solid #7f1d1d",
              borderRadius: 8,
              padding: 12,
              color: "#f87171",
              fontSize: 11,
              marginBottom: 16,
            }}
          >
            Server offline bank holidays are read-only in this session
          </div>
        )}

        <p
          style={{
            color: "#4f6fa0",
            fontSize: 11,
            marginBottom: 16,
            lineHeight: 1.6,
          }}
        >
          Toggle holidays on/off to match your office. Disabled holidays are
          counted as working days. Add custom dates for office closures.
        </p>

        {years.map((year) => (
          <div key={year} style={{ marginBottom: 16 }}>
            <div
              style={{
                color: "#4f8ef7",
                fontSize: 10,
                letterSpacing: 1.5,
                marginBottom: 8,
              }}
            >
              {year}
            </div>
            {holidays
              .filter((h) => h.year === year)
              .map((h) => (
                <div
                  key={h.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 10px",
                    borderRadius: 7,
                    background: h.active ? "#060e1e" : "#0a0a14",
                    marginBottom: 4,
                  }}
                >
                  <button
                    onClick={() => toggle(h.id, !h.active)}
                    style={{
                      width: 32,
                      height: 18,
                      borderRadius: 9,
                      border: "none",
                      cursor: "pointer",
                      background: h.active ? "#1d4ed8" : "#1a2744",
                      position: "relative",
                      flexShrink: 0,
                      transition: "all 0.2s",
                    }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: h.active ? "#7ab4f7" : "#3d5280",
                        position: "absolute",
                        top: 3,
                        left: h.active ? 17 : 3,
                        transition: "all 0.2s",
                      }}
                    />
                  </button>
                  <span
                    style={{
                      color: "#4f6fa0",
                      fontSize: 11,
                      width: 90,
                      flexShrink: 0,
                    }}
                  >
                    {h.holiday_date}
                  </span>
                  <span
                    style={{
                      color: h.active ? "#c8d8f0" : "#3d5280",
                      fontSize: 11,
                      flex: 1,
                    }}
                  >
                    {h.name}
                  </span>
                  {h.is_custom ? (
                    <button
                      onClick={() => remove(h.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#f87171",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    ></button>
                  ) : (
                    <span style={{ color: "#1e3056", fontSize: 9 }}>UK</span>
                  )}
                </div>
              ))}
          </div>
        ))}

        {/* Add custom */}
        {server?.serverStatus === "online" && (
          <div
            style={{
              marginTop: 16,
              background: "#081224",
              border: "1px solid #1e3056",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 10,
                letterSpacing: 1,
                marginBottom: 10,
              }}
            >
              ADD CUSTOM DATE
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                style={{
                  background: "#060e1e",
                  border: "1px solid #1e3056",
                  borderRadius: 7,
                  color: "#c8d8f0",
                  padding: "7px 10px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  flex: "0 0 auto",
                }}
              />
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Office Closure"
                style={{
                  background: "#060e1e",
                  border: "1px solid #1e3056",
                  borderRadius: 7,
                  color: "#c8d8f0",
                  padding: "7px 10px",
                  fontSize: 12,
                  fontFamily: "inherit",
                  flex: 1,
                  minWidth: 150,
                }}
              />
              <Btn
                onClick={addCustom}
                style={{ fontSize: 10, padding: "7px 14px" }}
              >
                + ADD
              </Btn>
            </div>
            {msg && (
              <div style={{ color: "#4ade80", fontSize: 11, marginTop: 8 }}>
                {msg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Grading Reference Manager
function GradingManager({ state, onClose }) {
  const server = useServer();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState("");
  const [overrides, setOverrides] = useState([]);
  const [editPartId, setEditPartId] = useState("");
  const [editGrade, setEditGrade] = useState("");
  const [editMsg, setEditMsg] = useState("");

  useEffect(() => {
    if (server?.serverStatus === "online") {
      server.apiCall?.("GET", "/api/grading/overrides").then((r) => {
        if (r) setOverrides(r);
      });
    }
  }, []);

  async function handleUpload(file) {
    if (!server || server.serverStatus !== "online") {
      setMsg(" Server must be online to upload grading reference");
      return;
    }
    setUploading(true);
    setProgress(0);
    setMsg(" Parsing file...");
    try {
      // Both branches normalise to a rows-of-cells array, then go through the
      // same column resolution, so the ERP export and the Power BI export are
      // handled identically without the caller knowing which it has.
      let sheetRows = [];
      if (file.name.toLowerCase().endsWith(".csv")) {
        const { headers, rows: rawRows } = parseCSV(await file.text());
        sheetRows = [headers, ...rawRows.map((r) => headers.map((h) => r[h]))];
      } else {
        const sheets = await parseXLSX(file);
        sheetRows = sheets[Object.keys(sheets)[0]] || [];
      }

      const { records, headerRowIdx, columns, skipped } =
        buildGradingRecords(sheetRows);

      if (headerRowIdx === -1 || !columns || columns.partId === -1) {
        throw new Error(
          "Could not find a Part ID column. Expected a header row " +
            "containing Part ID / part_id.",
        );
      }
      if (records.length === 0) {
        throw new Error("Found the header row but no data rows with a Part ID.");
      }

      // A grade-less row still matters: in the Power BI export the Repair Team
      // column carries "PROJECTS ONLY" and friends, which is what marks a part
      // silently excluded. So they are uploaded, not dropped — the server
      // keeps any existing grade rather than blanking it.
      const withGrade = records.filter((r) => r.grade).length;
      const rows = records;

      if (columns.grade === -1) {
        setMsg(
          `No grade column found — uploading ${rows.length} parts for team info only...`,
        );
      } else {
        setMsg(
          `Uploading ${rows.length} parts (${withGrade} with grades)` +
            `${skipped ? `, ${skipped} rows skipped` : ""}...`,
        );
      }
      const result = await server.uploadGradingReference(rows);
      setMsg(
        `Done ${result.inserted} new, ${result.updated} updated (${result.total} total)`,
      );
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setUploading(false);
    setProgress(100);
  }

  async function saveOverride() {
    if (!editPartId || !editGrade) return;
    const r = await server.saveGradeOverride(
      editPartId.toUpperCase(),
      editGrade.toUpperCase(),
    );
    if (r?.ok) {
      setEditMsg("Saved for all users");
      setOverrides((prev) => {
        const existing = prev.findIndex(
          (o) => o.part_id === editPartId.toUpperCase(),
        );
        const entry = {
          part_id: editPartId.toUpperCase(),
          grade: editGrade.toUpperCase(),
          updated_at: new Date().toISOString(),
        };
        if (existing >= 0) {
          const n = [...prev];
          n[existing] = entry;
          return n;
        }
        return [entry, ...prev];
      });
      setEditPartId("");
      setEditGrade("");
      setTimeout(() => setEditMsg(""), 3000);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.88)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 14,
          padding: 24,
          width: "100%",
          maxWidth: 600,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 14 }}>
            Grading Reference Database
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: "2px 6px",
            }}
          >
            &#10005;
          </button>
        </div>

        {/* Status */}
        <div
          style={{
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 10,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#4f6fa0", fontSize: 9, letterSpacing: 1 }}>
                STATUS
              </div>
              <div
                style={{
                  color: server?.gradingLoaded ? "#4ade80" : "#f59e0b",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {server?.gradingLoaded
                  ? "Loaded from database"
                  : "Not yet uploaded"}
              </div>
            </div>
            {server?.gradingCount > 0 && (
              <div>
                <div
                  style={{ color: "#4f6fa0", fontSize: 9, letterSpacing: 1 }}
                >
                  PARTS IN DB
                </div>
                <div
                  style={{ color: "#f0f6ff", fontSize: 13, fontWeight: 700 }}
                >
                  {server.gradingCount.toLocaleString()}
                </div>
              </div>
            )}
            <div>
              <div style={{ color: "#4f6fa0", fontSize: 9, letterSpacing: 1 }}>
                MANUAL OVERRIDES
              </div>
              <div style={{ color: "#a78bfa", fontSize: 13, fontWeight: 700 }}>
                {overrides.length}
              </div>
            </div>
          </div>
        </div>

        {/* Upload */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              color: "#4f6fa0",
              fontSize: 10,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            UPLOAD GRADING REFERENCE
          </div>
          <p
            style={{
              color: "#4f6fa0",
              fontSize: 11,
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            Upload your part grading spreadsheet once. It will be stored in the
            database and loaded automatically for all users. Consumables,
            project-only, and logistics parts are silently excluded only genuine
            repair parts appear in allocations.
          </p>
          <DropZone
            label="Drop Part Grading Reference (.xlsx or .csv)"
            sub="Needs: part_id, part_class, user_def16 columns same format as always"
            onFile={handleUpload}
            accept=".xlsx,.csv"
            color="#4f8ef7"
          />
          {msg && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  color: msg.startsWith("OK:")
                    ? "#4ade80"
                    : msg.startsWith("OK:")
                      ? "#f87171"
                      : "#f59e0b",
                  fontSize: 12,
                }}
              >
                {msg}
              </div>
              {uploading && (
                <div
                  style={{
                    marginTop: 8,
                    background: "#1a2744",
                    borderRadius: 4,
                    height: 6,
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: "100%",
                      background: "#4f8ef7",
                      borderRadius: 4,
                      transition: "width 0.3s",
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manual grade entry */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              color: "#4f6fa0",
              fontSize: 10,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            MANUALLY SET A GRADE
          </div>
          <p
            style={{
              color: "#4f6fa0",
              fontSize: 11,
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            Set or correct a grade for any part ID. This saves for all users and
            takes priority over the reference file.
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: 2, minWidth: 180 }}>
              <div
                style={{
                  color: "#4f6fa0",
                  fontSize: 9,
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                PART ID
              </div>
              <Input
                value={editPartId}
                onChange={(e) => setEditPartId(e.target.value.toUpperCase())}
                placeholder="e.g. OST-HHT-ZEB-TC51"
              />
            </div>
            <div style={{ flex: 1, minWidth: 100 }}>
              <div
                style={{
                  color: "#4f6fa0",
                  fontSize: 9,
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                GRADE
              </div>
              <select
                value={editGrade}
                onChange={(e) => setEditGrade(e.target.value)}
                style={{
                  background: "#060e1e",
                  border: "1px solid #1e3056",
                  borderRadius: 9,
                  color: "#c8d8f0",
                  padding: "10px 12px",
                  fontSize: 13,
                  fontFamily: "inherit",
                  width: "100%",
                }}
              >
                <option value="">-- Select --</option>
                {["M", "A", "B", "C", "S", "X", "BER"].map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <Btn
              onClick={saveOverride}
              variant="success"
              style={{ fontSize: 10, padding: "10px 16px" }}
            >
              SAVE FOR ALL
            </Btn>
          </div>
          {editMsg && (
            <div style={{ color: "#4ade80", fontSize: 11, marginTop: 8 }}>
              {editMsg}
            </div>
          )}
        </div>

        {/* Recent overrides */}
        {overrides.length > 0 && (
          <div>
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 10,
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              RECENT OVERRIDES ({overrides.length})
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {overrides.slice(0, 30).map((o, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "6px 0",
                    borderBottom: "1px solid #0d1b35",
                  }}
                >
                  <span
                    style={{
                      color: "#c8d8f0",
                      fontFamily: "monospace",
                      fontSize: 11,
                      flex: 1,
                    }}
                  >
                    {o.part_id}
                  </span>
                  <GradeTag grade={o.grade} />
                  <span style={{ color: "#3d5280", fontSize: 10 }}>
                    {o.updated_at?.slice(0, 10)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function findSavedGradingRow(rows, partId, grade, teams) {
  const id = String(partId || "").trim().toUpperCase();
  const expectedGrade = String(grade || "").trim().toUpperCase();
  const expectedTeams = normalizeGradingTeams(teams);
  if (!id || !expectedGrade || !Array.isArray(rows)) return null;
  return (
    rows.find(
      (row) => {
        if (
          String(row.part_id || "").trim().toUpperCase() !== id ||
          String(row.grade || "").trim().toUpperCase() !== expectedGrade
        ) {
          return false;
        }
        if (!expectedTeams.length) return true;
        const actualTeams = getEffectiveGradingTeams(row).sort();
        return (
          actualTeams.length === expectedTeams.length &&
          actualTeams.every((team, index) => team === [...expectedTeams].sort()[index])
        );
      },
    ) || null
  );
}

/**
 * Fold a just-saved Parts Grading team assignment into the reference list this
 * session is holding.
 *
 * The save wrote the same row to team_part_references on the server. The
 * selected team's list was fetched when the team was chosen, so without this
 * the part would not allocate until the page was reloaded — and that reference
 * list is the only thing allocation and the call list consult.
 *
 * Returns null when nothing changed: a part with no client prefix, a team that
 * is not the one being planned, or a part the list already carries.
 */
export function addPartToTeamReferences({
  clientPartIds,
  clients,
  partId,
  teams,
  selectedTeam,
}) {
  const key = String(partId || "").trim().toUpperCase();
  const team = String(selectedTeam || "").trim().toUpperCase();
  if (!key || !team || team === "ALL") return null;
  if (!normalizeGradingTeams(teams).includes(team)) return null;

  const client = (clients || []).find((c) => {
    const prefix = String(c?.prefix || "").trim().toUpperCase();
    return prefix && key.startsWith(prefix);
  });
  if (!client) return null;

  const current = (clientPartIds || {})[client.id];
  const next = new Set(current instanceof Set ? current : []);
  if (next.has(key)) return null;
  next.add(key);
  return { ...(clientPartIds || {}), [client.id]: next };
}

// ---------------------------------------------------------------------------
// Team Access — which parts each team is allowed to fix, and why.
//
// Three stores answer that question and they disagree often enough to be worth
// showing side by side: an explicit Parts Grading tick, a client reference-list
// upload, and the legacy single team on the grading reference. See
// partTeamAccess.js. A part whose only label is a pseudo-team such as
// PROJECTS ONLY belongs to nobody and is listed under Unassigned.
// ---------------------------------------------------------------------------
const ACCESS_SOURCE_STYLE = {
  [PART_TEAM_SOURCE.MANUAL]: {
    label: "Parts Grading",
    color: "#4ade80",
    border: "#14532d",
    background: "#082014",
  },
  [PART_TEAM_SOURCE.CLIENT_REF]: {
    label: "Client reference",
    color: "#7ab4f7",
    border: "#1e3a6e",
    background: "#0a1a33",
  },
  [PART_TEAM_SOURCE.REFERENCE]: {
    label: "Grading reference",
    color: "#c4b5fd",
    border: "#3b2f6e",
    background: "#150f2b",
  },
};

function SourceBadge({ source }) {
  const tone = ACCESS_SOURCE_STYLE[source];
  if (!tone) return null;
  return (
    <span
      style={{
        border: `1px solid ${tone.border}`,
        background: tone.background,
        color: tone.color,
        borderRadius: 4,
        padding: "1px 6px",
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {tone.label}
    </span>
  );
}

export function TeamAccessTab({ access, loading, error, onReload }) {
  const [team, setTeam] = useState(GRADING_TEAM_OPTIONS[0]);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("ALL");

  const isUnassigned = team === "UNASSIGNED";
  const listForTeam = isUnassigned
    ? access?.unowned || []
    : access?.byTeam?.[team] || [];

  const query = search.trim().toUpperCase();
  const rows = listForTeam.filter((entry) => {
    if (query && !entry.partId.includes(query)) return false;
    if (isUnassigned || sourceFilter === "ALL") return true;
    return (entry.sources[team] || []).includes(sourceFilter);
  });

  function exportCsv() {
    const headers = [
      "team",
      "part_id",
      "grade",
      "granted_by",
      "all_allowed_teams",
      "grading_reference_team",
    ];
    const body = rows.map((entry) => [
      isUnassigned ? "UNASSIGNED" : team,
      entry.partId,
      entry.grade,
      isUnassigned ? "" : (entry.sources[team] || []).join(" + "),
      entry.teams.join(", "),
      entry.legacyLabel,
    ]);
    const csv = [headers, ...body]
      .map((row) =>
        row
          .map((cell) => {
            const s = cell === null || cell === undefined ? "" : String(cell);
            return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(","),
      )
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `razergrid-team-access-${(isUnassigned ? "unassigned" : team)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ color: "#4f6fa0", fontSize: 11, lineHeight: 1.6 }}>
        Every part the selected team is allowed to fix, and which setting grants
        it. A tick in Browse &amp; Edit grants access on its own, so the team
        does not have to wait for a new client reference file.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Select
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          style={{ minWidth: 210 }}
        >
          {GRADING_TEAM_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} ({(access?.byTeam?.[option] || []).length})
            </option>
          ))}
          <option value="UNASSIGNED">
            Unassigned ({(access?.unowned || []).length})
          </option>
        </Select>
        <Select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={{ minWidth: 170 }}
          disabled={isUnassigned}
        >
          <option value="ALL">Granted by anything</option>
          <option value={PART_TEAM_SOURCE.MANUAL}>Parts Grading only</option>
          <option value={PART_TEAM_SOURCE.CLIENT_REF}>
            Client reference only
          </option>
          <option value={PART_TEAM_SOURCE.REFERENCE}>
            Grading reference only
          </option>
        </Select>
        <Input
          placeholder="Search part ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 150 }}
        />
        <Btn variant="secondary" onClick={onReload} disabled={loading}>
          {loading ? "LOADING" : "REFRESH"}
        </Btn>
        <Btn
          variant="secondary"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          EXPORT
        </Btn>
      </div>

      {error && <div style={{ color: "#f87171", fontSize: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11 }}>
        <span style={{ color: "#7ab4f7" }}>
          {rows.length.toLocaleString()} shown
        </span>
        <span style={{ color: "#4a6898" }}>
          {(access?.ownedCount || 0).toLocaleString()} parts have an owning team
        </span>
        <span style={{ color: "#4a6898" }}>
          {(access?.unowned || []).length.toLocaleString()} unassigned
        </span>
      </div>

      {isUnassigned && (
        <div style={{ color: "#fbbf24", fontSize: 11, lineHeight: 1.6 }}>
          These parts carry no repair team. Most are labelled PROJECTS ONLY,
          CONSUMABLES or LOGISTICS MANAGED on the grading reference. Nobody is
          allocated them, and work booked against them is never counted as
          another team&apos;s.
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ color: "#4a6898", borderBottom: "1px solid #1e3a6e" }}>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>Part ID</th>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>Grade</th>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>
              {isUnassigned ? "Reference label" : "Granted by"}
            </th>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>
              {isUnassigned ? "" : "Also allowed"}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={4}
                style={{ padding: "14px 8px", color: "#4a6898", fontSize: 12 }}
              >
                {loading
                  ? "Loading team access..."
                  : "No parts match this filter."}
              </td>
            </tr>
          )}
          {rows.slice(0, 500).map((entry) => (
            <tr
              key={entry.partId}
              style={{ borderBottom: "1px solid #0d1b35", color: "#c8d8f0" }}
            >
              <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>
                {entry.partId}
              </td>
              <td style={{ padding: "6px 8px" }}>{entry.grade || "-"}</td>
              <td style={{ padding: "6px 8px" }}>
                {isUnassigned ? (
                  <span style={{ color: "#4a6898" }}>
                    {entry.legacyLabel || "none"}
                  </span>
                ) : (
                  <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {(entry.sources[team] || []).map((source) => (
                      <SourceBadge key={source} source={source} />
                    ))}
                  </span>
                )}
              </td>
              <td style={{ padding: "6px 8px", color: "#4a6898" }}>
                {isUnassigned
                  ? ""
                  : entry.teams.filter((other) => other !== team).join(", ") ||
                    "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 500 && (
        <div style={{ color: "#4a6898", fontSize: 11 }}>
          Showing the first 500 of {rows.length.toLocaleString()}. Narrow the
          search, or export for the full list.
        </div>
      )}
    </div>
  );
}

function PartsGradingModal({ state, setState, onClose }) {
  const server = useServer();
  const [tab, setTab] = useState("browse");
  const [parts, setParts] = useState([]);
  const [excludedParts, setExcludedParts] = useState([]);
  const [flaggedParts, setFlaggedParts] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editGrade, setEditGrade] = useState("");
  const [editTeams, setEditTeams] = useState([]);
  const [addId, setAddId] = useState("");
  const [addGrade, setAddGrade] = useState("B");
  const [addTeams, setAddTeams] = useState([]);
  const [status, setStatus] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [approvingFlag, setApprovingFlag] = useState(null);
  const [applyOverride, setApplyOverride] = useState(true);
  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState("");
  const GRADES = ["X", "S", "C", "B", "A", "M", "BER"];

  useEffect(() => {
    loadParts();
    loadExcluded();
    loadFlags();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "browse" && parts.length === 0) loadParts();
    if (tab === "excluded" && excludedParts.length === 0) loadExcluded();
    if (tab === "flagged" && flaggedParts.length === 0) loadFlags();
    if (tab === "access" && !access) loadAccess();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadParts() {
    setStatus("Loading...");
    const raw = await server.fetchGrading(undefined, 0);
    if (Array.isArray(raw)) {
      setParts(raw);
      setStatus("");
      return raw;
    } else {
      setStatus(" Could not load parts");
      return null;
    }
  }

  async function loadExcluded() {
    setStatus("Loading...");
    const raw = await server.fetchGrading(undefined, 1);
    if (Array.isArray(raw)) {
      setExcludedParts(raw);
      setStatus("");
    } else {
      setStatus(" Could not load excluded parts");
    }
  }

  async function loadFlags() {
    if (!server?.fetchProgressFlags) return;
    setStatus("Loading...");
    const raw = await server.fetchProgressFlags("pending");
    if (Array.isArray(raw)) {
      setFlaggedParts(raw);
      setStatus("");
    } else {
      setStatus("Could not load flagged parts");
    }
  }

  // The unfiltered grading read plus every team client allowlist. Browse
  // deliberately hides excluded rows; access is about ownership rather than
  // allocation eligibility, so an excluded part still has an owning team.
  async function loadAccess() {
    setAccessLoading(true);
    setAccessError("");
    try {
      const [gradingRows, clientRefRows] = await Promise.all([
        server.fetchGrading(undefined),
        server.fetchAllPartReferences?.(),
      ]);
      if (!Array.isArray(gradingRows)) {
        setAccessError("Could not load the grading reference");
        return;
      }
      if (!Array.isArray(clientRefRows)) {
        setAccessError(
          "Loaded grades, but the client reference lists were unavailable",
        );
      }
      setAccess(
        buildPartTeamAccess({
          gradingRows,
          clientRefRows: Array.isArray(clientRefRows) ? clientRefRows : [],
        }),
      );
    } catch (e) {
      setAccessError("Could not load team access: " + (e?.message || e));
    } finally {
      setAccessLoading(false);
    }
  }

  async function handleSaveEdit(partId) {
    const teams = normalizeGradingTeams(editTeams);
    if (!teams.length) {
      setStatus(" Select at least one team");
      return;
    }
    const result = await server.saveGradeOverrideWithTeams(
      partId,
      editGrade,
      teams,
    );
    if (!result?.ok) {
      setStatus("Failed to save grade and teams");
      return;
    }
    const allParts = await server.fetchGrading(undefined);
    const savedRow = findSavedGradingRow(allParts, partId, editGrade, teams);
    if (!savedRow) {
      setStatus("Failed to verify the grade and teams after saving");
      return;
    }
    const savedTeams = getEffectiveGradingTeams(savedRow);
    // The save replaced grading_part_teams outright, so the teams that came
    // back are the manual assignment.
    const savedManualTeams = normalizeGradingTeams(
      savedRow.manual_teams || savedTeams,
    );
    // Client-prefixed stock was also written onto each ticked team's part
    // reference list. Patch the copy this session already holds so the part
    // can be allocated now rather than after a reload.
    const patchedRefs = addPartToTeamReferences({
      clientPartIds: state.clientPartIds,
      clients: state.clients,
      partId,
      teams: savedManualTeams,
      selectedTeam:
        state.teamFilter === "CUSTOM" ? state.customTeamFilter : state.teamFilter,
    });
    setState({
      ...(patchedRefs ? { clientPartIds: patchedRefs } : {}),
      gradingLookup: {
        ...(state.gradingLookup || {}),
        [partId.toUpperCase()]: {
          ...((state.gradingLookup || {})[partId.toUpperCase()] || {}),
          grade: editGrade.toUpperCase(),
          team: formatGradingTeams(savedTeams),
          teams: savedTeams,
        },
      },
    });
    setEditingId(null);
    loadParts();
  }

  async function handleDelete(partId) {
    if (!window.confirm(`Delete ${partId} from grading reference?`)) return;
    await server.deleteGradingPart(partId);
    loadParts();
  }

  async function handleAdd() {
    if (!addId.trim()) return;
    const id = addId.trim().toUpperCase();
    const grade = String(addGrade || "").trim().toUpperCase();
    const teams = normalizeGradingTeams(addTeams);
    if (!teams.length) {
      setStatus(" Select at least one team");
      return;
    }
    const result = await server.saveGradeOverrideWithTeams(id, grade, teams);
    if (result?.ok !== true) {
      setStatus(" Failed to add part and teams");
      return;
    }

    // Confirm the row through the unfiltered grading read. Browse intentionally
    // hides excluded/silent-excluded rows, so checking its filtered list can
    // report a false failure even though Supabase saved the grade correctly.
    const allParts = await server.fetchGrading(undefined);
    const savedRow = findSavedGradingRow(allParts, id, grade, teams);
    if (!savedRow) {
      setStatus(" Failed to verify the new part and teams after saving");
      return;
    }
    const savedTeams = getEffectiveGradingTeams(savedRow);
    // The save replaced grading_part_teams outright, so the teams that came
    // back are the manual assignment.
    const savedManualTeams = normalizeGradingTeams(
      savedRow.manual_teams || savedTeams,
    );

    // Refresh Browse separately so its existing exclusion rules remain intact.
    const browseParts = await loadParts();

    setAddId("");
    setStatus(
      Array.isArray(browseParts)
        ? `OK: ${id} added`
        : `OK: ${id} saved; Browse could not refresh`,
    );
    // Client-prefixed stock was also written onto each ticked team's part
    // reference list. Patch the copy this session already holds so the part
    // can be allocated now rather than after a reload.
    const patchedRefs = addPartToTeamReferences({
      clientPartIds: state.clientPartIds,
      clients: state.clients,
      partId: id,
      teams: savedManualTeams,
      selectedTeam:
        state.teamFilter === "CUSTOM" ? state.customTeamFilter : state.teamFilter,
    });
    setState({
      ...(patchedRefs ? { clientPartIds: patchedRefs } : {}),
      ungradedPartIds: (state.ungradedPartIds || []).filter((u) => u !== id),
      gradingLookup: {
        ...(state.gradingLookup || {}),
        [id]: {
          ...((state.gradingLookup || {})[id] || {}),
          grade,
          team: formatGradingTeams(savedTeams),
          teams: savedTeams,
        },
      },
    });
  }

  function renderTeamChecks(values, setValues) {
    return (
      <div>
        <Label>Teams (select all that apply)</Label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 8,
            marginTop: 6,
          }}
        >
          {GRADING_TEAM_OPTIONS.map((team) => (
            <label
              key={team}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                color: "#c8d8f0",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={values.includes(team)}
                onChange={() =>
                  setValues((previous) =>
                    previous.includes(team)
                      ? previous.filter((value) => value !== team)
                      : [...previous, team],
                  )
                }
              />
              {team}
            </label>
          ))}
        </div>
      </div>
    );
  }

  async function handleExclude(partId) {
    const result = await server.excludeGradingPart(partId);
    if (!result) {
      setStatus(" Failed to exclude part");
      return;
    }
    const excluded = parts.find((p) => p.part_id === partId) || {
      part_id: partId,
      grade: "",
    };
    setParts((prev) => prev.filter((p) => p.part_id !== partId));
    setExcludedParts((prev) => [
      { ...excluded, excluded: 1 },
      ...prev.filter((p) => p.part_id !== partId),
    ]);
  }

  async function handleExcludeUngraded(partId) {
    const result = await server.excludeGradingPart(partId);
    if (!result) {
      setStatus(" Failed to exclude part");
      return;
    }
    setState({
      ungradedPartIds: (state.ungradedPartIds || []).filter(
        (u) => u !== partId,
      ),
    });
    setExcludedParts((prev) => [
      { part_id: partId, grade: "", excluded: 1 },
      ...prev.filter((p) => p.part_id !== partId),
    ]);
  }

  async function handleRestore(partId, hasGrade) {
    const result = await server.restoreGradingPart(partId);
    if (!result) {
      setStatus(" Failed to restore part");
      return;
    }
    const restored = excludedParts.find((p) => p.part_id === partId);
    setExcludedParts((prev) => prev.filter((p) => p.part_id !== partId));
    if (hasGrade && restored) {
      setParts((prev) => [{ ...restored, excluded: 0 }, ...prev]);
    }
  }

  async function handleFlagAction(flagId, nextStatus) {
    const result = await server.updateProgressFlag(
      flagId,
      nextStatus,
      nextStatus === "approved" ? applyOverride : false,
    );
    if (!result) {
      setStatus("Failed to update flagged part");
      return;
    }
    setFlaggedParts((prev) => prev.filter((flag) => flag.id !== flagId));
    setApprovingFlag(null);
    if (nextStatus === "approved" && applyOverride) loadParts();
  }

  async function handlePullGrades(format) {
    if (!server || server.serverStatus !== "online") {
      setExportStatus("Server must be online to pull grades");
      return;
    }
    setExportStatus(`Preparing ${format.toUpperCase()} export...`);
    try {
      const result = await server.downloadGradingExport(format);
      if (!result?.ok) {
        setExportStatus("Could not pull grades from server");
        return;
      }
      setExportStatus(`Downloaded ${result.filename}`);
    } catch (e) {
      setExportStatus("Export failed: " + e.message);
    }
  }

  const filtered = parts.filter(
    (p) => !search || p.part_id?.toLowerCase().includes(search.toLowerCase()),
  );
  const ungraded = state.ungradedPartIds || [];

  const tabStyle = (t) => ({
    padding: "6px 16px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    background: tab === t ? "#1e3a6e" : "transparent",
    color: tab === t ? "#7ab4f7" : "#4a6898",
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#0a1628",
          border: "1px solid #1e3a6e",
          borderRadius: 16,
          padding: 24,
          width: tab === "access" ? "min(1000px, 96vw)" : "min(700px, 95vw)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <span style={{ color: "#7ab4f7", fontWeight: 700, fontSize: 14 }}>
            {" "}
            PARTS GRADING
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          <button style={tabStyle("browse")} onClick={() => setTab("browse")}>
            Browse & Edit
          </button>
          <button style={tabStyle("access")} onClick={() => setTab("access")}>
            Team Access
          </button>
          <button style={tabStyle("add")} onClick={() => setTab("add")}>
            Add Part
          </button>
          <button style={tabStyle("alerts")} onClick={() => setTab("alerts")}>
            Ungraded Alerts{" "}
            {ungraded.length > 0 && (
              <span
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "1px 6px",
                  fontSize: 10,
                  marginLeft: 4,
                }}
              >
                {ungraded.length}
              </span>
            )}
          </button>
          <button
            style={tabStyle("excluded")}
            onClick={() => setTab("excluded")}
          >
            Excluded{" "}
            {excludedParts.length > 0 && (
              <span
                style={{
                  background: "#854d0e",
                  color: "#fde68a",
                  borderRadius: 10,
                  padding: "1px 6px",
                  fontSize: 10,
                  marginLeft: 4,
                }}
              >
                {excludedParts.length}
              </span>
            )}
          </button>
          <button style={tabStyle("flagged")} onClick={() => setTab("flagged")}>
            Flagged{" "}
            {flaggedParts.length > 0 && (
              <span
                style={{
                  background: "#7f1d1d",
                  color: "#fecaca",
                  borderRadius: 10,
                  padding: "1px 6px",
                  fontSize: 10,
                  marginLeft: 4,
                }}
              >
                {flaggedParts.length}
              </span>
            )}
          </button>
          <button style={tabStyle("pull")} onClick={() => setTab("pull")}>
            Pull Grades
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {tab === "browse" && (
            <>
              <Input
                placeholder="Search part ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              {status && (
                <div
                  style={{ color: "#fbbf24", fontSize: 12, marginBottom: 8 }}
                >
                  {status}
                </div>
              )}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#4a6898",
                      borderBottom: "1px solid #1e3a6e",
                    }}
                  >
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>
                      Part ID
                    </th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>
                      Grade
                    </th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>
                      Team
                    </th>
                    <th style={{ padding: "6px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.part_id}
                      style={{
                        borderBottom: "1px solid #0d1b35",
                        color: "#c8d8f0",
                      }}
                    >
                      <td
                        style={{ padding: "6px 8px", fontFamily: "monospace" }}
                      >
                        {p.part_id}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {editingId === p.part_id ? (
                          <select
                            value={editGrade}
                            onChange={(e) => setEditGrade(e.target.value)}
                            style={{
                              background: "#060e1e",
                              border: "1px solid #1a2f56",
                              color: "#dce9f8",
                              borderRadius: 6,
                              padding: "2px 6px",
                            }}
                          >
                            {GRADES.map((g) => (
                              <option key={g} value={g}>
                                {g}
                              </option>
                            ))}
                          </select>
                        ) : (
                          p.grade
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", color: "#4a6898" }}>
                        {editingId === p.part_id
                          ? renderTeamChecks(editTeams, setEditTeams)
                          : formatGradingTeams(getEffectiveGradingTeams(p))}
                      </td>
                      <td
                        style={{ padding: "6px 8px", display: "flex", gap: 6 }}
                      >
                        {editingId === p.part_id ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(p.part_id)}
                              style={{
                                background: "#1e5c3a",
                                border: "none",
                                color: "#4ade80",
                                borderRadius: 4,
                                padding: "2px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              style={{
                                background: "none",
                                border: "1px solid #1e3a6e",
                                color: "#4a6898",
                                borderRadius: 4,
                                padding: "2px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingId(p.part_id);
                                setEditGrade(p.grade);
                                setEditTeams(getEffectiveGradingTeams(p));
                              }}
                              style={{
                                background: "none",
                                border: "1px solid #1e3a6e",
                                color: "#4a6898",
                                borderRadius: 4,
                                padding: "2px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleExclude(p.part_id)}
                              style={{
                                background: "none",
                                border: "1px solid #713f12",
                                color: "#d97706",
                                borderRadius: 4,
                                padding: "2px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Exclude
                            </button>
                            <button
                              onClick={() => handleDelete(p.part_id)}
                              style={{
                                background: "none",
                                border: "1px solid #3d1010",
                                color: "#ef4444",
                                borderRadius: 4,
                                padding: "2px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {tab === "access" && (
            <TeamAccessTab
              access={access}
              loading={accessLoading}
              error={accessError}
              onReload={loadAccess}
            />
          )}

          {tab === "add" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <Label>Part ID</Label>
                <Input
                  value={addId}
                  onChange={(e) => setAddId(e.target.value.toUpperCase())}
                  placeholder="e.g. ABC-123"
                />
              </div>
              <div>
                <Label>Grade</Label>
                <Select
                  value={addGrade}
                  onChange={(e) => setAddGrade(e.target.value)}
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </Select>
              </div>
              {renderTeamChecks(addTeams, setAddTeams)}
              <Btn onClick={handleAdd} disabled={!addId.trim() || !addTeams.length}>
                Add Part
              </Btn>
              {status && (
                <div
                  style={{
                    color: status.startsWith("OK:") ? "#4ade80" : "#f87171",
                    fontSize: 12,
                  }}
                >
                  {status}
                </div>
              )}
            </div>
          )}

          {tab === "alerts" && (
            <div>
              {ungraded.length === 0 ? (
                <div style={{ color: "#4ade80", fontSize: 13 }}>
                  {" "}
                  No ungraded parts all parts in the last upload have grades.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <div
                    style={{ color: "#fbbf24", fontSize: 12, marginBottom: 8 }}
                  >
                    {ungraded.length} part{ungraded.length !== 1 ? "s" : ""} in
                    the last Power BI upload have no grade:
                  </div>
                  {ungraded.map((id) => (
                    <div
                      key={id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#0d1b35",
                        borderRadius: 6,
                        padding: "8px 12px",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "monospace",
                          color: "#c8d8f0",
                          fontSize: 12,
                        }}
                      >
                        {id}
                      </span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => {
                            setTab("add");
                            setAddId(id);
                          }}
                          style={{
                            background: "#1e3a6e",
                            border: "none",
                            color: "#7ab4f7",
                            borderRadius: 4,
                            padding: "3px 10px",
                            cursor: "pointer",
                            fontSize: 11,
                          }}
                        >
                          Quick Add
                        </button>
                        <button
                          onClick={() => handleExcludeUngraded(id)}
                          style={{
                            background: "none",
                            border: "1px solid #713f12",
                            color: "#d97706",
                            borderRadius: 4,
                            padding: "3px 10px",
                            cursor: "pointer",
                            fontSize: 11,
                          }}
                        >
                          Exclude
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "excluded" && (
            <>
              {status && (
                <div
                  style={{ color: "#fbbf24", fontSize: 12, marginBottom: 8 }}
                >
                  {status}
                </div>
              )}
              {excludedParts.length === 0 ? (
                <div style={{ color: "#4a6898", fontSize: 13 }}>
                  No excluded parts.
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        color: "#4a6898",
                        borderBottom: "1px solid #1e3a6e",
                      }}
                    >
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Part ID
                      </th>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Grade
                      </th>
                      <th style={{ padding: "6px 8px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {excludedParts.map((p) => (
                      <tr
                        key={p.part_id}
                        style={{
                          borderBottom: "1px solid #0d1b35",
                          color: "#c8d8f0",
                        }}
                      >
                        <td
                          style={{
                            padding: "6px 8px",
                            fontFamily: "monospace",
                          }}
                        >
                          {p.part_id}
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            color: p.grade ? "#c8d8f0" : "#4a6898",
                          }}
                        >
                          {p.grade || ""}
                        </td>
                        <td style={{ padding: "6px 8px" }}>
                          <button
                            onClick={() => handleRestore(p.part_id, !!p.grade)}
                            style={{
                              background: "none",
                              border: "1px solid #1e3a6e",
                              color: "#4ade80",
                              borderRadius: 4,
                              padding: "2px 8px",
                              cursor: "pointer",
                              fontSize: 11,
                            }}
                          >
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {tab === "flagged" && (
            <>
              {status && (
                <div
                  style={{ color: "#fbbf24", fontSize: 12, marginBottom: 8 }}
                >
                  {status}
                </div>
              )}
              {flaggedParts.length === 0 ? (
                <div style={{ color: "#4a6898", fontSize: 13 }}>
                  No flagged parts.
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        color: "#4a6898",
                        borderBottom: "1px solid #1e3a6e",
                      }}
                    >
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Part ID
                      </th>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Engineer
                      </th>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Grade
                      </th>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>
                        Status
                      </th>
                      <th style={{ padding: "6px 8px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {flaggedParts.map((flag) => (
                      <tr
                        key={flag.id}
                        style={{
                          borderBottom: "1px solid #0d1b35",
                          color: "#c8d8f0",
                        }}
                      >
                        <td
                          style={{
                            padding: "6px 8px",
                            fontFamily: "monospace",
                          }}
                        >
                          {flag.part_id || flag.part_serial}
                          {flag.part_id &&
                            flag.part_serial &&
                            flag.part_id !== flag.part_serial && (
                              <div style={{ color: "#3d5280", fontSize: 10 }}>
                                {flag.part_serial}
                              </div>
                            )}
                        </td>
                        <td style={{ padding: "6px 8px", color: "#7aa0d4" }}>
                          {flag.engineer}
                        </td>
                        <td style={{ padding: "6px 8px" }}>
                          <span style={{ color: "#ef4444" }}>
                            {flag.current_grade || "?"}
                          </span>
                          <span style={{ color: "#4a6898", margin: "0 6px" }}>
                            to
                          </span>
                          <span style={{ color: "#22c55e" }}>
                            {flag.suggested_grade}
                          </span>
                          {flag.notes && (
                            <div
                              style={{
                                color: "#3d5280",
                                fontSize: 10,
                                marginTop: 2,
                              }}
                            >
                              {flag.notes}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            color:
                              flag.status === "pending"
                                ? "#fbbf24"
                                : flag.status === "approved"
                                  ? "#4ade80"
                                  : "#f87171",
                          }}
                        >
                          {flag.status || "pending"}
                          {flag.created_at && (
                            <div style={{ color: "#2a3a5a", fontSize: 9 }}>
                              {new Date(flag.created_at).toLocaleString(
                                "en-GB",
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "6px 8px" }}>
                          {flag.status === "pending" &&
                            (approvingFlag === flag.id ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 6,
                                }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    color: "#c8d8f0",
                                    fontSize: 11,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={applyOverride}
                                    onChange={(e) =>
                                      setApplyOverride(e.target.checked)
                                    }
                                  />
                                  Apply override
                                </label>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    onClick={() =>
                                      handleFlagAction(flag.id, "approved")
                                    }
                                    style={{
                                      background: "#14532d",
                                      border: "1px solid #16a34a",
                                      color: "#4ade80",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      cursor: "pointer",
                                      fontSize: 11,
                                    }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setApprovingFlag(null)}
                                    style={{
                                      background: "none",
                                      border: "1px solid #1e3a6e",
                                      color: "#4a6898",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      cursor: "pointer",
                                      fontSize: 11,
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: "flex", gap: 6 }}>
                                <button
                                  onClick={() => setApprovingFlag(flag.id)}
                                  style={{
                                    background: "none",
                                    border: "1px solid #14532d",
                                    color: "#4ade80",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    cursor: "pointer",
                                    fontSize: 11,
                                  }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() =>
                                    handleFlagAction(flag.id, "rejected")
                                  }
                                  style={{
                                    background: "none",
                                    border: "1px solid #7f1d1d",
                                    color: "#f87171",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    cursor: "pointer",
                                    fontSize: 11,
                                  }}
                                >
                                  Reject
                                </button>
                              </div>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {tab === "pull" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  background: "#081224",
                  border: "1px solid #1e3056",
                  borderRadius: 10,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    color: "#7ab4f7",
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  Pull updated grading reference
                </div>
                <div
                  style={{ color: "#4f6fa0", fontSize: 11, lineHeight: 1.6 }}
                >
                  Download the current server grades with any approved overrides
                  applied. The export includes the live grade, reference grade,
                  override grade, team, description, exclusion flags, and
                  updated timestamp.
                </div>
              </div>
              {server?.serverStatus !== "online" && (
                <div
                  style={{
                    background: "#1a0505",
                    border: "1px solid #7f1d1d",
                    borderRadius: 8,
                    padding: 12,
                    color: "#f87171",
                    fontSize: 12,
                  }}
                >
                  Server offline. Connect to the RazerGrid server before pulling
                  grades.
                </div>
              )}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={() => handlePullGrades("xlsx")}
                  disabled={server?.serverStatus !== "online"}
                  style={{
                    background: "#1e3a6e",
                    border: "1px solid #2c5aa0",
                    color: "#dce9f8",
                    borderRadius: 7,
                    padding: "8px 14px",
                    cursor:
                      server?.serverStatus === "online" ? "pointer" : "default",
                    opacity: server?.serverStatus === "online" ? 1 : 0.55,
                    fontSize: 12,
                    fontFamily: "inherit",
                  }}
                >
                  Download Excel .xlsx
                </button>
                <button
                  onClick={() => handlePullGrades("csv")}
                  disabled={server?.serverStatus !== "online"}
                  style={{
                    background: "#0f2f26",
                    border: "1px solid #166534",
                    color: "#bbf7d0",
                    borderRadius: 7,
                    padding: "8px 14px",
                    cursor:
                      server?.serverStatus === "online" ? "pointer" : "default",
                    opacity: server?.serverStatus === "online" ? 1 : 0.55,
                    fontSize: 12,
                    fontFamily: "inherit",
                  }}
                >
                  Download CSV
                </button>
              </div>
              {exportStatus && (
                <div
                  style={{
                    color: exportStatus.startsWith("Downloaded")
                      ? "#4ade80"
                      : "#fbbf24",
                    fontSize: 12,
                  }}
                >
                  {exportStatus}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ClientsModal({ state, setState, onClose }) {
  const server = useServer();
  const TEAMS = [
    "RC DCD",
    "RC MCD",
    "RC PCB",
    "RC PC",
    "RC PRINTERS & PERIPHERALS",
    "RC PED",
  ];
  const [refCounts, setRefCounts] = useState({});
  const [newName, setNewName] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [newColor, setNewColor] = useState("#7ab4f7");
  const [uploadStatus, setUploadStatus] = useState({});
  // Which client/team list is open, and the parts it holds. Until this existed
  // the tab showed only a count, so a wrong entry on a list was invisible — it
  // could only be seen in the allocation it had already polluted.
  const [openList, setOpenList] = useState("");
  const [listParts, setListParts] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [newPartId, setNewPartId] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [listError, setListError] = useState("");
  const [listSaving, setListSaving] = useState(false);
  const listRequest = useRef(0);
  const openListRef = useRef("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    loadRefCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRefCounts() {
    const counts = {};
    for (const client of state.clients || []) {
      counts[client.id] = {};
      for (const team of TEAMS) {
        const r = await server.fetchPartRefs(team, client.id);
        counts[client.id][team] = Array.isArray(r?.partIds) ? r.partIds.length : null;
      }
    }
    setRefCounts(counts);
  }

  async function handleAddClient() {
    if (!newName.trim() || !newPrefix.trim()) return;
    const id = newName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const client = {
      id,
      name: newName.trim().toUpperCase(),
      prefix: newPrefix.trim().toUpperCase(),
      color: newColor,
    };
    await server.addOrUpdateStockClient(client);
    setState({
      clients: [...(state.clients || []).filter((c) => c.id !== id), client],
    });
    setNewName("");
    setNewPrefix("");
    setStatus(`OK: ${client.name} added`);
    loadRefCounts();
  }

  async function handleDeleteClient(client) {
    const hasRefs = Object.values(refCounts[client.id] || {}).some(
      (c) => c > 0,
    );
    if (hasRefs) {
      setStatus(`Warning: Delete all Part References for ${client.name} first`);
      return;
    }
    if (!window.confirm(`Remove client ${client.name}?`)) return;
    await server.deleteStockClient(client.id);
    setState({
      clients: (state.clients || []).filter((c) => c.id !== client.id),
    });
    loadRefCounts();
  }

  async function handleUploadRef(team, clientId, file) {
    const key = `${clientId}-${team}`;
    setUploadStatus((s) => ({ ...s, [key]: "Uploading..." }));
    try {
      let partIds = [];
      if (file.name.endsWith(".csv")) {
        const text = await file.text();
        const { headers, rows } = parseCSV(text);
        const col = detectCol(headers, [
          "part id",
          "part_id",
          "partid",
          "part",
          "id",
        ]);
        partIds = rows
          .map((r) => (r[col] || "").trim().toUpperCase())
          .filter(Boolean);
      } else {
        const sheets = await parseXLSX(file);
        const sheetRows = sheets[Object.keys(sheets)[0]];
        const startRow =
          sheetRows.length > 0 &&
          String(sheetRows[0][0]).toLowerCase().includes("part")
            ? 1
            : 0;
        partIds = sheetRows
          .slice(startRow)
          .map((r) =>
            String(r[0] || "")
              .trim()
              .toUpperCase(),
          )
          .filter(Boolean);
      }
      const result = await server.uploadPartRefs(team, clientId, partIds);
      if (!result?.ok) throw new Error(result?.error || "Reference upload failed");
      await refreshPartList(team, clientId);
      setUploadStatus((s) => ({ ...s, [key]: `OK: ${result.inserted} parts` }));
    } catch (e) {
      setUploadStatus((s) => ({ ...s, [key]: `Error: ${e.message}` }));
    }
  }

  async function openPartList(team, clientId) {
    const key = `${clientId}-${team}`;
    const request = ++listRequest.current;
    if (openListRef.current === key) {
      openListRef.current = "";
      setOpenList("");
      return;
    }
    openListRef.current = key;
    setOpenList(key);
    setListParts([]);
    setNewPartId("");
    setListFilter("");
    setListLoading(true);
    setListError("");
    try {
      const result = await server.fetchPartRefs(team, clientId);
      if (request !== listRequest.current) return;
      if (!Array.isArray(result?.partIds)) throw new Error("Server unavailable");
      setListParts([...result.partIds].sort());
    } catch (e) {
      if (request === listRequest.current) setListError(`Could not load ${team}: ${e.message}`);
    } finally {
      if (request === listRequest.current) setListLoading(false);
    }
  }

  async function refreshPartList(team, clientId) {
    const result = await server.fetchPartRefs(team, clientId);
    const activeTeam = state.teamFilter === "CUSTOM" ? state.customTeamFilter : state.teamFilter;
    if (!Array.isArray(result?.partIds)) {
      // The write succeeded but readback failed. Never use the old allowlist.
      if (activeTeam === team && state.clientPartIdsTeam === team) {
        setState({
          clientPartIdsTeam: "",
          mergedParts: (state.mergedParts || []).filter(p => p.source === "team"),
          callList: (state.callList || []).filter(p => p.source === "team"),
        });
      }
      throw new Error("Saved, but could not reload references. Reload Setup before parsing.");
    }
    const parts = [...new Set(result.partIds)].sort();
    setRefCounts(previous => ({ ...previous, [clientId]: { ...previous[clientId], [team]: parts.length } }));
    if (openListRef.current === `${clientId}-${team}`) setListParts(parts);
    // Keep the active plan on the same list the editor just read from the server.
    // Do not tag another team's cache as current or change existing allocations.
    if (activeTeam === team && state.clientPartIdsTeam === team) {
      const clientPartIds = { ...state.clientPartIds, [clientId]: new Set(parts) };
      const client = (state.clients || []).find(c => c.id === clientId);
      const prefix = String(client?.prefix || "").toUpperCase();
      setState({
        clientPartIds,
        mergedParts: mergePartsByClient(state.powerBIRows || [], state.clients || [], clientPartIds, state.gradingLookup || {}, team),
        callList: (state.callList || []).filter(p => {
          const partId = String(p.partId || p.ref || "").trim().toUpperCase();
          return !(prefix && partId.startsWith(prefix)) || clientPartIds[clientId].has(partId);
        }),
      });
    }
    return parts;
  }

  async function handleAddPartRef(team, clientId) {
    const partId = newPartId.trim().toUpperCase();
    if (!partId) return;
    const client = (state.clients || []).find((c) => c.id === clientId);
    const prefix = String(client?.prefix || "").trim().toUpperCase();
    if (prefix && !partId.startsWith(prefix)) {
      setStatus(`Warning: ${partId} does not start with ${prefix}`);
      return;
    }
    setListSaving(true);
    try {
      const result = await server.addPartRef(team, clientId, partId);
      if (!result?.ok) throw new Error(result?.error || "Save failed");
      const savedParts = await refreshPartList(team, clientId);
      if (!savedParts.includes(partId)) throw new Error("Server readback did not confirm the addition");
      setNewPartId("");
      setStatus(`OK: ${partId} added to ${team}. Rebuild the call list to include new references.`);
    } catch (e) {
      setStatus(`Warning: could not add ${partId}: ${e.message}`);
    } finally {
      setListSaving(false);
    }
  }

  async function handleRemovePartRef(team, clientId, partId) {
    setListSaving(true);
    try {
      const result = await server.removePartRef(team, clientId, partId);
      if (!result?.ok) throw new Error(result?.error || "Save failed");
      const savedParts = await refreshPartList(team, clientId);
      if (savedParts.includes(partId)) throw new Error("Server readback did not confirm the removal");
      setStatus(`OK: ${partId} removed from ${team}`);
    } catch (e) {
      setStatus(`Warning: could not remove ${partId}: ${e.message}`);
    } finally {
      setListSaving(false);
    }
  }

  async function handleDeleteRef(team, clientId) {
    if (
      !window.confirm(
        `Delete Part Reference for ${clientId.toUpperCase()} / ${team}?`,
      )
    )
      return;
    setListSaving(true);
    try {
      const result = await server.deletePartRefs(team, clientId);
      if (!result?.ok) throw new Error(result?.error || "Delete failed");
      await refreshPartList(team, clientId);
    } catch (e) {
      setStatus(`Warning: ${e.message}`);
    } finally {
      setListSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#0a1628",
          border: "1px solid #1e3a6e",
          borderRadius: 16,
          padding: 24,
          width: "min(780px, 95vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: "#7ab4f7", fontWeight: 700, fontSize: 14 }}>
            CLIENTS
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#4a6898",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        {status && (
          <div
            style={{
              color: status.startsWith("OK:") ? "#4ade80" : "#fbbf24",
              fontSize: 12,
            }}
          >
            {status}
          </div>
        )}

        <div>
          <Label>Configured Clients</Label>
          {(state.clients || []).map((client) => (
            <div
              key={client.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
                background: "#0d1b35",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: client.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ color: "#c8d8f0", fontWeight: 600, flex: 1 }}>
                {client.name}
              </span>
              <span
                style={{
                  color: "#4a6898",
                  fontSize: 11,
                  fontFamily: "monospace",
                }}
              >
                prefix: {client.prefix}
              </span>
              <button
                onClick={() => handleDeleteClient(client)}
                style={{
                  background: "none",
                  border: "1px solid #3d1010",
                  color: "#ef4444",
                  borderRadius: 4,
                  padding: "2px 8px",
                  cursor: "pointer",
                  fontSize: 11,
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div
          style={{
            background: "#0d1b35",
            border: "1px solid #1e3a6e",
            borderRadius: 10,
            padding: 14,
          }}
        >
          <Label>Add Client</Label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div>
              <Label>Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="KFC"
              />
            </div>
            <div>
              <Label>Prefix</Label>
              <Input
                value={newPrefix}
                onChange={(e) => setNewPrefix(e.target.value)}
                placeholder="KFC-"
              />
            </div>
            <Btn
              onClick={handleAddClient}
              disabled={!newName.trim() || !newPrefix.trim()}
            >
              Add
            </Btn>
          </div>
        </div>

        <div>
          <Label>Part Reference Files</Label>
          <p style={{ color: "#7ab4f7", fontSize: 12 }}>
            Client-prefixed parts are included only when listed for the selected team.
            Lists include uploaded parts and team assignments saved in Parts Grading.
          </p>
          {(state.clients || []).map((client) => (
            <div key={client.id} style={{ marginBottom: 16 }}>
              <div
                style={{
                  color: client.color || "#7ab4f7",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1,
                  marginBottom: 8,
                }}
              >
                {client.name}
              </div>
              {TEAMS.map((team) => {
                const count = refCounts[client.id]?.[team] ?? null;
                const key = `${client.id}-${team}`;
                return (
                  <div
                    key={team}
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 10px",
                      background: "#060e1e",
                      borderRadius: 6,
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ flex: 1, color: "#c8d8f0", fontSize: 12 }}>
                      {team}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: count > 0 ? "#4ade80" : "#4a6898",
                      }}
                    >
                      {count === null
                        ? "Loading"
                        : count > 0
                          ? `${count} parts`
                          : "No file"}
                    </span>
                    {uploadStatus[key] && (
                      <span
                        style={{
                          fontSize: 10,
                          color: uploadStatus[key].startsWith("OK:")
                            ? "#4ade80"
                            : "#fbbf24",
                        }}
                      >
                        {uploadStatus[key]}
                      </span>
                    )}
                    <button
                      aria-label={`View parts ${client.name} ${team}`}
                      aria-expanded={openList === key}
                      disabled={listSaving}
                      onClick={() => openPartList(team, client.id)}
                      style={{ background: "#1e3a6e", color: "#c8d8f0", border: 0, borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}
                    >
                      {openList === key ? "Close" : "Edit"}
                    </button>
                    <label
                      style={{
                        background: "#1e3a6e",
                        color: "#7ab4f7",
                        borderRadius: 4,
                        padding: "3px 10px",
                        cursor: "pointer",
                        fontSize: 11,
                      }}
                    >
                      {count > 0 ? "Replace" : "Upload"}
                      <input
                        type="file"
                        accept=".csv,.xlsx"
                        disabled={listSaving}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          if (e.target.files[0])
                            handleUploadRef(team, client.id, e.target.files[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {count > 0 && (
                      <button
                        onClick={() => handleDeleteRef(team, client.id)}
                        disabled={listSaving}
                        style={{
                          background: "none",
                          border: "1px solid #3d1010",
                          color: "#ef4444",
                          borderRadius: 4,
                          padding: "2px 8px",
                          cursor: "pointer",
                          fontSize: 11,
                        }}
                      >
                        Delete
                      </button>
                    )}
                    {openList === key && (
                      <div style={{ flexBasis: "100%", padding: "12px 0", color: "#c8d8f0" }}>
                        {listLoading ? <p>Loading parts...</p> : listError ? <p role="alert">{listError}</p> : (
                          <>
                            <Input placeholder="Filter part IDs..." value={listFilter} onChange={e => setListFilter(e.target.value)} />
                            <div style={{ maxHeight: 260, overflowY: "auto", margin: "10px 0" }}>
                              {listParts.filter(p => p.toUpperCase().includes(listFilter.trim().toUpperCase())).map(partId => (
                                <div key={partId} style={{ display: "flex", gap: 10, alignItems: "center", padding: "5px 0", fontSize: 12 }}>
                                  <span style={{ flex: 1, overflowWrap: "anywhere" }}>{partId}</span>
                                  <button aria-label={`Remove ${partId}`} disabled={listSaving} onClick={() => handleRemovePartRef(team, client.id, partId)}>Remove</button>
                                </div>
                              ))}
                              {!listParts.length && <p>No parts configured for {team}.</p>}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <Input placeholder="Part ID to add..." value={newPartId} disabled={listSaving} onChange={e => setNewPartId(e.target.value)} />
                              <button disabled={listSaving || !newPartId.trim()} onClick={() => handleAddPartRef(team, client.id)}>Add part</button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamTargetsModal({ state, setState, server, onClose }) {
  const configuredTargets = sanitizeTeamTargets(state.teamTargets);
  const configuredCount = Object.keys(configuredTargets).length;
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  function activeSetupTeam() {
    return state.teamFilter === "CUSTOM"
      ? state.customTeamFilter
      : state.teamFilter;
  }

  async function applyTargets(nextTargets) {
    const clean = sanitizeTeamTargets(nextTargets);
    const setupTeam = activeSetupTeam();
    const patch = { teamTargets: clean };
    if (setupTeam && setupTeam !== "ALL") {
      patch.tiers = getTeamTargetTiers(setupTeam, clean);
    }
    setState(patch);
    if (server?.serverStatus !== "online" || !server.saveTeamTargets) {
      setMessage("Saved locally. Supabase is offline.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const result = await server.saveTeamTargets(clean);
      if (result?.teamTargets) {
        setState({ ...patch, teamTargets: result.teamTargets });
        setMessage("Saved to Supabase.");
      } else {
        setMessage("Saved locally. Supabase save failed.");
      }
    } catch {
      setMessage("Saved locally. Supabase save failed.");
    } finally {
      setSaving(false);
    }
  }

  function getRows(team) {
    return getTeamTargetTiers(team, configuredTargets);
  }

  function updateTier(team, index, field, value) {
    const rows = getRows(team).map((tier, idx) =>
      idx === index ? { ...tier, [field]: parseFloat(value) || 0 } : tier,
    );
    applyTargets({ ...configuredTargets, [team]: rows });
  }

  function addTier(team) {
    const rows = getRows(team);
    const last = rows[rows.length - 1] || { daily: 11, bonus: 25 };
    applyTargets({
      ...configuredTargets,
      [team]: [
        ...rows,
        {
          daily: Math.max(1, Number(last.daily || 0) + 3),
          bonus: Number(last.bonus || 0) + 25,
        },
      ],
    });
  }

  function removeTier(team, index) {
    const rows = getRows(team).filter((_, idx) => idx !== index);
    if (!rows.length) return;
    applyTargets({ ...configuredTargets, [team]: rows });
  }

  function resetTeam(team) {
    const next = { ...configuredTargets };
    delete next[team];
    applyTargets(next);
  }

  function resetAll() {
    if (!window.confirm("Reset every team target and bonus tier to defaults?"))
      return;
    applyTargets({});
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#0a1628",
          border: "1px solid #1e3a6e",
          borderRadius: 16,
          padding: 24,
          width: "min(940px, 96vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          overflowY: "auto",
          boxShadow: "0 24px 80px rgba(0,0,0,0.42)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div>
            <span style={{ color: "#7ab4f7", fontWeight: 700, fontSize: 14 }}>
              TEAM TARGETS
            </span>
            <div style={{ color: "#4f6fa0", fontSize: 11, marginTop: 6 }}>
              Saved targets are used by Setup, Repair Centre, and History.
              Repair Centre file-specific overrides still take priority.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#4a6898",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            x
          </button>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            background: "#081224",
            border: "1px solid #12325c",
            borderRadius: 10,
            padding: 12,
          }}
        >
          <div style={{ color: "#8fb8e8", fontSize: 12 }}>
            {configuredCount > 0
              ? `${configuredCount} team target set${configuredCount === 1 ? "" : "s"} customised`
              : "All teams are using default target tiers"}
            {message ? (
              <span style={{ color: "#22c55e", marginLeft: 10 }}>
                {message}
              </span>
            ) : null}
          </div>
          <button
            onClick={resetAll}
            disabled={configuredCount === 0 || saving}
            style={{
              background: "none",
              border: "1px solid #3d5280",
              color:
                configuredCount === 0 || saving ? "#2a3d5f" : "#7aa0d4",
              borderRadius: 6,
              padding: "6px 10px",
              cursor: configuredCount === 0 || saving ? "default" : "pointer",
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            {saving ? "SAVING..." : "RESET ALL DEFAULTS"}
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {TEAM_TARGET_KEYS.map((team) => {
            const rows = getRows(team);
            const hasOverride = !!configuredTargets[team];
            return (
              <div
                key={team}
                style={{
                  background: "#0d1b35",
                  border: `1px solid ${hasOverride ? "#22c55e66" : "#1e3056"}`,
                  borderRadius: 10,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <span
                      style={{
                        color: "#c8d8f0",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      {team === "ALL" ? "ALL / DEFAULT" : team}
                    </span>
                    <span
                      style={{
                        color: hasOverride ? "#22c55e" : "#4f6fa0",
                        fontSize: 9,
                        border: `1px solid ${hasOverride ? "#22c55e55" : "#274166"}`,
                        borderRadius: 999,
                        padding: "2px 8px",
                      }}
                    >
                      {hasOverride ? "CUSTOM" : "DEFAULT"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {hasOverride && (
                      <button
                        onClick={() => resetTeam(team)}
                        style={{
                          background: "none",
                          border: "1px solid #3d5280",
                          color: "#7aa0d4",
                          borderRadius: 6,
                          padding: "5px 9px",
                          cursor: "pointer",
                          fontSize: 10,
                          fontWeight: 700,
                          fontFamily: "inherit",
                        }}
                      >
                        RESET TEAM
                      </button>
                    )}
                    <button
                      onClick={() => addTier(team)}
                      style={{
                        background: "#102b4c",
                        border: "1px solid #275d9e",
                        color: "#9dccff",
                        borderRadius: 6,
                        padding: "5px 9px",
                        cursor: "pointer",
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: "inherit",
                      }}
                    >
                      + ADD TIER
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 10,
                  }}
                >
                  {rows.map((tier, idx) => (
                    <div
                      key={`${team}-${idx}`}
                      style={{
                        background: "#060e1e",
                        border: "1px solid #172a4c",
                        borderRadius: 8,
                        padding: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: 8,
                        alignItems: "end",
                      }}
                    >
                      <div>
                        <Label>Daily</Label>
                        <Input
                          type="number"
                          min={0}
                          step={0.5}
                          value={tier.daily}
                          onChange={(e) =>
                            updateTier(team, idx, "daily", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <Label>Bonus</Label>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          value={tier.bonus}
                          onChange={(e) =>
                            updateTier(team, idx, "bonus", e.target.value)
                          }
                        />
                      </div>
                      <button
                        onClick={() => removeTier(team, idx)}
                        disabled={rows.length <= 1}
                        title="Remove tier"
                        style={{
                          height: 32,
                          minWidth: 32,
                          background: "none",
                          border: "1px solid #3d1010",
                          color: rows.length <= 1 ? "#3d5280" : "#ef4444",
                          borderRadius: 6,
                          cursor: rows.length <= 1 ? "default" : "pointer",
                          fontSize: 13,
                          fontFamily: "inherit",
                        }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepSetup({ state, setState, onNext }) {
  const server = useServer();
  const [partRefStatus, setPartRefStatus] = useState({});
  const [rotaEngineers, setRotaEngineers] = useState([]);
  const [rotaEngineerStatus, setRotaEngineerStatus] = useState("");

  function selectedTeamKey(team = state.teamFilter) {
    return team === "CUSTOM" ? state.customTeamFilter : team;
  }
  const selectedTargetTeam = selectedTeamKey();
  const selectedTeamTargets = getTeamTargetTiers(
    selectedTargetTeam,
    state.teamTargets,
  );

  function sectionMatchesTeam(section, team) {
    const resolved = selectedTeamKey(team);
    if (!resolved || resolved === "ALL") return true;
    return teamMatchesText(resolved, section);
  }

  async function fetchPartRefsForTeam(team) {
    const resolvedTeam = team === "CUSTOM" ? state.customTeamFilter : team;
    if (!resolvedTeam || !server || server.serverStatus !== "online") return;
    const clients = state.clients || [];
    const newClientPartIds = {};
    const newStatus = {};
    let complete = true;
    for (const client of clients) {
      const result = await server.fetchPartRefs(resolvedTeam, client.id);
      if (result && Array.isArray(result.partIds)) {
        newClientPartIds[client.id] = new Set(result.partIds);
        newStatus[client.id] =
          result.count > 0
            ? `Loaded: ${client.name} - ${result.count} part IDs loaded`
            : `Warning: ${client.name} - no reference file on server`;
      } else {
        complete = false;
        newStatus[client.id] = `Warning: ${client.name} - server unreachable`;
      }
    }
    // The tag is only set once every configured client answered. A part-loaded
    // set would gate the parse on an allowlist that is missing entries, which
    // is indistinguishable from the team genuinely not being allowed the part.
    setState({
      clientPartIds: newClientPartIds,
      clientPartIdsTeam: complete ? resolvedTeam : "",
    });
    setPartRefStatus(newStatus);
  }

  useEffect(() => {
    if (state.teamFilter && server?.serverStatus === "online") {
      fetchPartRefsForTeam(state.teamFilter);
    }
  }, [state.teamFilter, server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (server?.serverStatus !== "online") {
      setRotaEngineers([]);
      setRotaEngineerStatus("");
      return;
    }
    const now = new Date();
    server
      .fetchRotaForMonth(now.getFullYear(), now.getMonth() + 1)
      .then((entries) => {
        if (!Array.isArray(entries) || entries.length === 0) {
          setRotaEngineers([]);
          setRotaEngineerStatus(
            "Upload this month's rota to select engineers from it.",
          );
          return;
        }
        const todayDay = now.getDate();
        const options = entries
          .map((entry) => {
            let worked = null;
            try {
              worked = calculateRotaWorkedDays(
                JSON.parse(entry.days_json),
                JSON.parse(entry.dow_json),
                todayDay,
              );
            } catch {}
            return {
              name: entry.engineer,
              section: entry.section || "",
              worked,
            };
          })
          .filter((entry) => entry.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setRotaEngineers(options);
        setRotaEngineerStatus(
          `Loaded ${options.length} engineers from this month's rota.`,
        );
      });
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTech = () =>
    setState({
      techNames: [...state.techNames, `Tech ${state.techNames.length + 1}`],
    });
  const removeTech = (i) => {
    const name = state.techNames[i];
    const engineerRotaNames = { ...(state.engineerRotaNames || {}) };
    delete engineerRotaNames[name];
    setState({
      techNames: state.techNames.filter((_, idx) => idx !== i),
      engineerRotaNames,
    });
  };
  const updateTech = (i, v) => {
    const oldName = state.techNames[i];
    const nextNames = state.techNames.map((n, idx) => (idx === i ? v : n));
    const engineerRotaNames = { ...(state.engineerRotaNames || {}) };
    if (oldName && oldName !== v && engineerRotaNames[oldName]) {
      engineerRotaNames[v] = engineerRotaNames[oldName];
      delete engineerRotaNames[oldName];
    }
    setState({ techNames: nextNames, engineerRotaNames });
  };
  const selectRotaEngineer = (i, rotaName) => {
    const oldName = state.techNames[i];
    const nextNames = [...state.techNames];
    const engineerRotaNames = { ...(state.engineerRotaNames || {}) };
    if (oldName) delete engineerRotaNames[oldName];
    if (rotaName) {
      nextNames[i] = rotaName;
      engineerRotaNames[rotaName] = rotaName;
    }
    setState({ techNames: nextNames, engineerRotaNames });
  };
  const updateTier = (i, field, v) => {
    const tiers = state.tiers.map((t, idx) =>
      idx === i ? { ...t, [field]: parseFloat(v) || 0 } : t,
    );
    setState({ tiers });
  };
  const addTier = () =>
    setState({ tiers: [...state.tiers, { daily: 27, bonus: 100 }] });
  const removeTier = (i) =>
    setState({ tiers: state.tiers.filter((_, idx) => idx !== i) });
  const filteredRotaEngineers = rotaEngineers.filter((entry) =>
    sectionMatchesTeam(entry.section),
  );
  const rotaEngineerOptions =
    filteredRotaEngineers.length > 0 ? filteredRotaEngineers : rotaEngineers;
  const fillEngineersFromSelectedTeam = (
    team = state.teamFilter,
    patch = {},
  ) => {
    const matches = rotaEngineers.filter((entry) =>
      sectionMatchesTeam(entry.section, team),
    );
    if (!matches.length) return;
    const names = matches.map((entry) => entry.name);
    const engineerRotaNames = {};
    names.forEach((name) => {
      engineerRotaNames[name] = name;
    });
    setState({
      techNames: names,
      engineerRotaNames,
      ...patch,
    });
  };
  const hasPlaceholderEngineers = state.techNames.every((name) =>
    /^Engineer \d+$|^Tech \d+$/i.test(String(name || "")),
  );

  useEffect(() => {
    if (state.teamFilter === "ALL" || state.teamFilter === "CUSTOM") return;
    if (!filteredRotaEngineers.length || !hasPlaceholderEngineers) return;
    fillEngineersFromSelectedTeam();
  }, [rotaEngineers.length, state.teamFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <Label>Team Configuration</Label>
        <div
          className="grid-mobile"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        >
          <div>
            <Label>Working Days This Month</Label>
            <Input
              type="number"
              value={state.workingDays}
              min={1}
              max={31}
              onChange={(e) =>
                setState({ workingDays: parseInt(e.target.value) || 22 })
              }
            />
          </div>
          <div>
            <Label>DOA Deductions per Engineer (GBP)</Label>
            <Input
              type="number"
              value={state.doaCount}
              min={0}
              step={1}
              onChange={(e) =>
                setState({ doaCount: parseInt(e.target.value) || 0 })
              }
            />
            <div style={{ color: "#3d5280", fontSize: 10, marginTop: 4 }}>
              Each DOA = GBP 1 deducted from commission
            </div>
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <Label>Team Section / Filter</Label>
          <Select
            value={state.teamFilter}
            onChange={(e) => {
              const newTeam = e.target.value;
              const presetTiers = getTeamTargetTiers(
                newTeam,
                state.teamTargets,
              );
              const patch = {
                teamFilter: newTeam,
                tiers: presetTiers.map((t) => ({ ...t })),
              };
              const matchingRotaEngineers = rotaEngineers.filter((entry) =>
                sectionMatchesTeam(entry.section, newTeam),
              );
              if (matchingRotaEngineers.length) {
                const names = matchingRotaEngineers.map((entry) => entry.name);
                const engineerRotaNames = {};
                names.forEach((name) => {
                  engineerRotaNames[name] = name;
                });
                setState({ ...patch, techNames: names, engineerRotaNames });
              } else {
                setState(patch);
              }
              fetchPartRefsForTeam(newTeam);
            }}
          >
            <option value="RC DCD">RC DCD</option>
            <option value="RC PCB">RC PCB</option>
            <option value="RC PC">RC PC</option>
            <option value="RC PRINTERS & PERIPHERALS">
              RC PRINTERS &amp; PERIPHERALS
            </option>
            <option value="RC MCD">RC MCD</option>
            <option value="RC PED">RC PED</option>
            <option value="ALL">All Teams</option>
            <option value="CUSTOM">Custom...</option>
          </Select>
          {Object.values(partRefStatus).length > 0 && (
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {Object.values(partRefStatus).map((msg, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 11,
                    color: msg.startsWith("Loaded:") ? "#4ade80" : "#fbbf24",
                  }}
                >
                  {msg}
                </div>
              ))}
            </div>
          )}
          {state.teamFilter === "CUSTOM" && (
            <Input
              style={{ marginTop: 8 }}
              placeholder="Enter team name e.g. RC MYTEAM"
              value={state.customTeamFilter}
              onChange={(e) => setState({ customTeamFilter: e.target.value })}
            />
          )}
          {selectedTeamTargets.length > 0 && (
            <div style={{ marginTop: 8, color: "#3d5280", fontSize: 10 }}>
              Saved targets for{" "}
              <strong style={{ color: "#7aa0d4" }}>
                {selectedTargetTeam || "ALL"}
              </strong>:
              {selectedTeamTargets.map((t, i) => (
                <span key={i} style={{ marginLeft: 8, color: "#4f6fa0" }}>
                  T{i + 1}: {t.daily}/day - GBP {t.bonus}
                </span>
              ))}
              <button
                onClick={() =>
                  setState({
                    tiers: selectedTeamTargets.map((t) => ({
                      ...t,
                    })),
                  })
                }
                style={{
                  marginLeft: 10,
                  background: "none",
                  border: "1px solid #1e3056",
                  color: "#4f6fa0",
                  borderRadius: 5,
                  fontSize: 9,
                  padding: "2px 8px",
                  cursor: "pointer",
                }}
              >
                Apply saved targets
              </button>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <Label style={{ marginBottom: 0 }}>Engineers</Label>
          <Btn
            variant="secondary"
            style={{ padding: "6px 14px", fontSize: 11 }}
            onClick={addTech}
          >
            + ADD ENGINEER
          </Btn>
        </div>
        {rotaEngineerStatus && (
          <div
            style={{
              color: rotaEngineers.length ? "#4ade80" : "#fbbf24",
              fontSize: 11,
              marginBottom: 10,
            }}
          >
            {filteredRotaEngineers.length && state.teamFilter !== "ALL"
              ? `${filteredRotaEngineers.length} engineer${filteredRotaEngineers.length !== 1 ? "s" : ""} available for ${selectedTeamKey()}.`
              : rotaEngineerStatus}
          </div>
        )}
        {filteredRotaEngineers.length > 0 && state.teamFilter !== "ALL" && (
          <button
            onClick={() => fillEngineersFromSelectedTeam()}
            style={{
              background: "none",
              border: "1px solid #1e3056",
              color: "#4f6fa0",
              borderRadius: 6,
              fontSize: 10,
              padding: "4px 10px",
              cursor: "pointer",
              marginBottom: 10,
            }}
          >
            Fill engineers from selected team
          </button>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 10,
          }}
        >
          {state.techNames.map((name, i) => {
            const linkedRotaName =
              (state.engineerRotaNames || {})[name] ||
              (rotaEngineers.some((entry) => entry.name === name) ? name : "");
            const selectedRotaNames = new Set(
              Object.values(state.engineerRotaNames || {}),
            );
            const linked = rotaEngineers.find(
              (entry) => entry.name === linkedRotaName,
            );
            return (
              <div
                key={i}
                style={{ display: "flex", gap: 6, alignItems: "center" }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: TECH_COLORS[i % TECH_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {rotaEngineerOptions.length > 0 ? (
                    <>
                      <Select
                        value={linkedRotaName}
                        onChange={(e) => selectRotaEngineer(i, e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <option value="">Select engineer from rota...</option>
                        {rotaEngineerOptions.map((entry) => (
                          <option
                            key={entry.name}
                            value={entry.name}
                            disabled={
                              selectedRotaNames.has(entry.name) &&
                              entry.name !== linkedRotaName
                            }
                          >
                            {entry.name}
                            {entry.section ? ` - ${entry.section}` : ""}
                          </option>
                        ))}
                      </Select>
                      {linked && (
                        <div
                          style={{
                            color: "#4f6fa0",
                            fontSize: 9,
                            marginTop: 3,
                          }}
                        >
                          Rota-linked for share links
                          {linked.worked !== null
                            ? ` - ${linked.worked} days worked so far`
                            : ""}
                        </div>
                      )}
                    </>
                  ) : (
                    <Input
                      value={name}
                      onChange={(e) => updateTech(i, e.target.value)}
                      placeholder={`Engineer ${i + 1}`}
                      style={{ width: "100%" }}
                    />
                  )}
                </div>
                {state.techNames.length > 1 && (
                  <button
                    onClick={() => removeTech(i)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#f87171",
                      cursor: "pointer",
                      fontSize: 16,
                    }}
                  >
                    x
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <Label>Daily Targets & Bonus Tiers</Label>
          <Btn
            variant="secondary"
            style={{ padding: "6px 14px", fontSize: 11 }}
            onClick={addTier}
          >
            + ADD TIER
          </Btn>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {state.tiers.map((tier, i) => (
            <div
              key={i}
              style={{
                background: "#081224",
                border: "1px solid #1e3056",
                borderRadius: 10,
                padding: 16,
                minWidth: 180,
                position: "relative",
              }}
            >
              {state.tiers.length > 1 && (
                <button
                  onClick={() => removeTier(i)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 10,
                    background: "none",
                    border: "none",
                    color: "#f87171",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  x
                </button>
              )}
              <div
                style={{
                  color: "#4f8ef7",
                  fontSize: 10,
                  letterSpacing: 1,
                  marginBottom: 10,
                }}
              >
                TIER {i + 1}
              </div>
              <div style={{ marginBottom: 10 }}>
                <Label>Units/Day Target</Label>
                <Input
                  type="number"
                  value={tier.daily}
                  min={1}
                  onChange={(e) => updateTier(i, "daily", e.target.value)}
                />
              </div>
              <div>
                <Label>Monthly Bonus (GBP)</Label>
                <Input
                  type="number"
                  value={tier.bonus}
                  min={0}
                  onChange={(e) => updateTier(i, "bonus", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <Label>Grade Values (GBP per repair)</Label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 10,
          }}
        >
          {GRADE_ORDER.map((g) => (
            <div
              key={g}
              style={{
                background: "#081224",
                border: `1px solid ${GRADE_COLORS[g]}33`,
                borderRadius: 9,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <GradeTag grade={g} />
                <div style={{ color: "#3d5280", fontSize: 10 }}>
                  {GRADE_LABELS[g]}
                </div>
              </div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={state.gradeValues[g]}
                onChange={(e) =>
                  setState({
                    gradeValues: {
                      ...state.gradeValues,
                      [g]: parseFloat(e.target.value) || 0,
                    },
                  })
                }
                style={{ textAlign: "right" }}
              />
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn onClick={onNext}>NEXT: LOAD DATA</Btn>
      </div>
    </div>
  );
}

// Step 1: Load Power BI + Grading Data
export async function parseStockPowerBIFile(file) {
  if (file.name.endsWith(".csv")) {
    const text = await file.text();
    const { headers, rows: csvRows } = parseCSV(text);
    const partIdCol = detectCol(headers, ["part id", "part_id", "partid"]);
    const badCol = detectCol(headers, ["total bad", "bad"]);
    const repReqCol = detectCol(headers, [
      "rep req",
      "repair req",
      "requirement",
    ]);
    const goodCol = detectCol(headers, ["total good", "good"]);
    const customerCol = detectCol(headers, ["customer"]);
    const teamCol = detectCol(headers, ["team", "repair team"]);
    return csvRows
      .map((r) => ({
        partId: r[partIdCol] || "",
        bad: parseInt(r[badCol]) || 0,
        repReq: parseInt(r[repReqCol]) || 0,
        good: parseInt(r[goodCol]) || 0,
        customer: r[customerCol] || "",
        team: r[teamCol] || "",
      }))
      .filter((r) => r.partId);
  } else {
    const sheets = await parseXLSX(file);
    const sheetName = Object.keys(sheets)[0];
    return parsePowerBIData(sheets[sheetName]);
  }
}

export async function readStockCallsFile(file) {
  let h = [],
    r = [];
  if (file.name.endsWith(".csv")) {
    const parsed = parseCSV(await file.text());
    h = parsed.headers;
    r = parsed.rows;
  } else {
    const sheets = await parseXLSX(file);
    const sheetName = Object.keys(sheets)[0];
    const sheetRows = sheets[sheetName];
    if (sheetRows.length > 0) {
      h = sheetRows[0].map((v) => String(v).trim());
      r = sheetRows.slice(1).map((row) => {
        const obj = {};
        h.forEach((col, i) => {
          obj[col] = String(row[i] || "").trim();
        });
        return obj;
      });
    }
  }
  return { h, r };
}

export async function parseStockArchiveFile(file, kind, context) {
  if (kind === "powerbi") {
    const rows = await parseStockPowerBIFile(file);
    if (!rows.length) throw new Error("No Power BI part rows were found; the file was not imported.");
    return { rows, raw: {}, mapping: {}, mergedParts: mergePartsByClient(rows, context.clients, context.clientPartIds, context.gradingLookup, stockTeam(context)) };
  }
  const { h, r } = await readStockCallsFile(file);
  const mapping = {
    partIdCol: detectCol(h, ["part id", "part_id", "part requested", "serial", "ref", "job", "ticket"]),
    gradeCol: detectCol(h, ["grade", "class", "part_class"]),
    notesCol: detectCol(h, ["notes", "comment", "fault", "description"]),
    customerCol: detectCol(h, ["customer", "client", "site"]),
    departmentCol: detectCol(h, ["department", "team", "section"]),
    qtyCol: detectCol(h, ["quantity available", "qty available", "qty avail", "available", "quantity"]),
    rcInfoCol: detectCol(h, ["repair centre information", "repair center information", "rc information", "rc info"]),
  };
  if (!mapping.partIdCol) throw new Error("No call part column was found; the file was not imported.");
  const built = buildCallListItems({ ...mapping, rows:r, gradingLookup:context.gradingLookup, gradeValues:context.gradeValues, clients:context.clients, clientPartIds:context.clientPartIds, selectedTeam:stockTeam(context) });
  return { rows:built.list, raw:{headers:h,rows:r}, mapping, stats:built.stats };
}

function StepData({ state, setState, onNext, onBack }) {
  const server = useServer();
  const [stockReportTime, setStockReportTime] = useState("");
  const [partRefWarning, setPartRefWarning] = useState("");
  const [powerBIStatus, setPowerBIStatus] = useState(
    state.powerBIRows?.length > 0
      ? ` ${state.powerBIRows.length} parts loaded`
      : "",
  );
  const [serverGradingStatus, setServerGradingStatus] = useState(
    state.gradingLookup && Object.keys(state.gradingLookup).length > 0
      ? ` ${Object.keys(state.gradingLookup).length} grades loaded from server`
      : "",
  );

  const [loading, setLoading] = useState(false);
  const [previewParts, setPreviewParts] = useState(state.mergedParts || []);
  useEffect(() => {
    setPreviewParts(state.mergedParts || []);
  }, [state.mergedParts]);

  // parse a Power BI file (xlsx or csv) into raw rows
  // recompute combined merged parts and save to state
  function recompute(pbiRows, lookup, partIds) {
    const activeTeam =
      state.teamFilter === "CUSTOM" ? state.customTeamFilter : state.teamFilter;
    const { refs, stale } = partReferencesForTeam({
      ...(partIds !== undefined
        ? partIds
        : {
            clientPartIds: state.clientPartIds,
            clientPartIdsTeam: state.clientPartIdsTeam,
          }),
      team: activeTeam,
    });
    setPartRefWarning(
      stale
        ? `Client part references are not loaded for ${activeTeam || "the selected team"}. ` +
          "Client stock has been left out of this parse. Go back to Setup to reload them."
        : "",
    );
    const combined = mergePartsByClient(
      pbiRows,
      state.clients || [],
      refs,
      lookup,
      activeTeam,
    );
    const ungradedIds = pbiRows
      .filter((row) => row.bad > 0 && !lookup[row.partId?.toUpperCase()])
      .map((row) => row.partId);
    setPreviewParts(combined);
    setState({ mergedParts: combined, ungradedPartIds: ungradedIds });
  }

  // Returns the reference lists together with the team they were fetched for,
  // so the merge can refuse a list that belongs to somebody else.
  async function fetchFreshPartIds() {
    const team =
      state.teamFilter === "CUSTOM" ? state.customTeamFilter : state.teamFilter;
    if (
      !team ||
      !server ||
      server.serverStatus !== "online" ||
      !(state.clients || []).length
    ) {
      return {
        clientPartIds: state.clientPartIds || {},
        clientPartIdsTeam: state.clientPartIdsTeam || "",
      };
    }
    const newIds = {};
    let complete = true;
    for (const client of state.clients) {
      const result = await server.fetchPartRefs(team, client.id);
      if (result && Array.isArray(result.partIds)) {
        newIds[client.id] = new Set(result.partIds);
      } else {
        complete = false;
      }
    }
    const refs = {
      clientPartIds: newIds,
      clientPartIdsTeam: complete ? team : "",
    };
    setState(refs);
    return refs;
  }

  async function handlePowerBI(file) {
    const archiveTeam = stockTeam(state);
    const observedAt = stockReportTime ? new Date(stockReportTime).toISOString() : new Date().toISOString();
    setLoading(true);
    setPowerBIStatus("Parsing...");
    try {
      const rows = await parseStockPowerBIFile(file);
      setState({ powerBIRows: rows, stockPowerBI: null });
      setPowerBIStatus(`${rows.length} rows loaded`);
      const partIds = await fetchFreshPartIds();
      recompute(rows, state.gradingLookup || {}, partIds);
      setState({ stockPowerBI: enqueueStockSource({kind:"powerbi",file,rows,team:archiveTeam,observedAt,configuration:stockConfiguration({...state,...partIds})}) });
    } catch (e) {
      setPowerBIStatus("Error: " + e.message);
    }
    setLoading(false);
  }

  // Auto-fetch grading from server on mount
  useEffect(() => {
    if (!server || server.serverStatus !== "online") {
      if (server?.serverStatus && server.serverStatus !== "unknown") {
        setServerGradingStatus(
          " Server offline connect server to load grading",
        );
      }
      return;
    }
    setServerGradingStatus("Loading grading reference from server...");
    (async () => {
      try {
        const raw = await server.fetchGrading(undefined, 0);
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.records)
            ? raw.records
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        if (records.length === 0) {
          if (raw === null) {
            setServerGradingStatus(
              " Server fetch failed check tunnel URL and server logs",
            );
          } else {
            setServerGradingStatus(
              " Database is empty upload grading reference via Grading Manager",
            );
          }
          return;
        }
        const lookup = {};
        records.forEach((r) => {
          const partId = (r.part_id || "").toUpperCase().trim();
          const grade = (r.grade || "").toUpperCase().trim();
          if (partId && DEFAULT_GRADE_VALUES[grade] !== undefined) {
            const teams = getEffectiveGradingTeams(r);
            lookup[partId] = { grade, team: formatGradingTeams(teams), teams };
          }
        });
        const matchedCount = Object.keys(lookup).length;
        if (matchedCount === 0) {
          setServerGradingStatus(
            ` ${records.length} parts in DB but no valid grades re-upload grading reference`,
          );
          return;
        }
        setState({ gradingLookup: lookup });
        server.markGradingLoaded(matchedCount);
        setServerGradingStatus(` ${matchedCount} grades loaded from server`);
        if (state.powerBIRows?.length > 0) {
          const freshIds = await fetchFreshPartIds();
          recompute(state.powerBIRows, lookup, freshIds);
        }
      } catch (e) {
        setServerGradingStatus(" Error loading grading: " + e.message);
      }
    })();
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamParts = previewParts.filter((p) => p.source === "team");
  const clientParts = previewParts.filter((p) => p.source !== "team");
  const priorityCount = previewParts.filter((p) => p.repReq > 0).length;
  const gradeCounts = {};
  GRADE_ORDER.forEach((g) => {
    gradeCounts[g] = previewParts.filter((p) => p.grade === g).length;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Server Grading Status */}
      {serverGradingStatus && (
        <div
          style={{
            background: serverGradingStatus.startsWith("OK:")
              ? "#052e16"
              : "#1c1917",
            border: `1px solid ${serverGradingStatus.startsWith("OK:") ? "#166534" : "#44403c"}`,
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 12,
            color: serverGradingStatus.startsWith("OK:")
              ? "#4ade80"
              : serverGradingStatus.startsWith("OK:")
                ? "#fbbf24"
                : "#94a3b8",
          }}
        >
          Grading Reference {serverGradingStatus}
        </div>
      )}
      {/* DCD Section */}
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 12,
          padding: 20,
        }}
      >
        <div
          style={{
            color: "#4f8ef7",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2,
            marginBottom: 16,
          }}
        >
          POWER BI STOCK EXPORT
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <Label>Combined Power BI Export</Label>
            <p
              style={{
                color: "#4f6fa0",
                fontSize: 12,
                marginBottom: 16,
                lineHeight: 1.6,
              }}
            >
              Upload your combined Power BI export (.xlsx or .csv). Parts
              prefixed with a client code (e.g.{" "}
              <strong style={{ color: "#34d399" }}>MCD-</strong>) are
              automatically routed to the correct client pool using the Part
              Reference list fetched on the Setup page.
            </p>
            <DropZone
              label="Drop Combined Power BI Export (.xlsx or .csv)"
              sub="Columns needed: Part ID, Total Bad, Rep Req"
              onFile={handlePowerBI}
              accept=".xlsx,.csv"
              color="#4f8ef7"
            />
            <StockReportTime value={stockReportTime} onChange={setStockReportTime} />
            {powerBIStatus && (
              <div
                style={{
                  color: powerBIStatus.startsWith("OK:")
                    ? "#4ade80"
                    : "#f87171",
                  fontSize: 12,
                  marginTop: 10,
                }}
              >
                {powerBIStatus}
              </div>
            )}
            {partRefWarning && (
              <div
                style={{
                  background: "#2a1207",
                  border: "1px solid #9a3412",
                  borderRadius: 8,
                  color: "#fdba74",
                  fontSize: 12,
                  marginTop: 10,
                  padding: "10px 12px",
                }}
              >
                {partRefWarning}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Combined Preview */}
      {previewParts.length > 0 && (
        <Card>
          <Label>
            Combined Stock Preview ({previewParts.length} total repairable
            units)
          </Label>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <StatBox
              label="Total Bad Stock"
              value={previewParts.length}
              color="#ef4444"
            />
            <StatBox
              label="Priority (Rep Req)"
              value={priorityCount}
              color="#f59e0b"
            />
            <StatBox
              label="Team Parts"
              value={teamParts.length}
              color="#4f8ef7"
            />
            <StatBox
              label="Client Parts"
              value={clientParts.length}
              color="#34d399"
            />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {GRADE_ORDER.filter((g) => gradeCounts[g] > 0).map((g) => (
              <div
                key={g}
                style={{
                  background: "#081224",
                  border: `1px solid ${GRADE_COLORS[g]}44`,
                  borderRadius: 9,
                  padding: "10px 16px",
                  textAlign: "center",
                }}
              >
                <GradeTag grade={g} />
                <div
                  style={{
                    color: "#f0f6ff",
                    fontSize: 20,
                    fontWeight: 800,
                    margin: "6px 0 2px",
                  }}
                >
                  {gradeCounts[g]}
                </div>
                <div style={{ color: "#3d5280", fontSize: 10 }}>
                  {(gradeCounts[g] * state.gradeValues[g]).toFixed(2)} total
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading && (
        <div style={{ color: "#4f8ef7", fontSize: 12, textAlign: "center" }}>
          Processing file...
        </div>
      )}

      {/* Ungraded parts alert */}
      {(state.ungradedPartIds || []).length > 0 && (
        <Card style={{ background: "#1a0e00", border: "1px solid #78350f" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                color: "#f59e0b",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              UNGRADED PARTS ALERT {state.ungradedPartIds.length} part
              {state.ungradedPartIds.length !== 1 ? "s" : ""} in Power BI with
              no grading match
            </div>
          </div>
          <p
            style={{
              color: "#92400e",
              fontSize: 11,
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            These Part IDs appear in your Power BI export but are not in the
            grading reference. They have been excluded from the allocation.
            Check your grading spreadsheet is up to date.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {state.ungradedPartIds.slice(0, 50).map((id) => (
              <span
                key={id}
                style={{
                  background: "#78350f33",
                  border: "1px solid #78350f66",
                  borderRadius: 5,
                  padding: "2px 8px",
                  color: "#f59e0b",
                  fontFamily: "monospace",
                  fontSize: 11,
                }}
              >
                {id}
              </span>
            ))}
            {state.ungradedPartIds.length > 50 && (
              <span style={{ color: "#92400e", fontSize: 11 }}>
                ...and {state.ungradedPartIds.length - 50} more
              </span>
            )}
          </div>
        </Card>
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Btn variant="secondary" onClick={onBack}>
          BACK
        </Btn>
        <Btn onClick={onNext} disabled={previewParts.length === 0}>
          NEXT: CALL LIST
        </Btn>
      </div>
    </div>
  );
}

function hasPartReference(allowed, key) {
  if (!allowed) return false;
  if (allowed instanceof Set) return allowed.has(key);
  if (Array.isArray(allowed))
    return allowed.map((v) => String(v).toUpperCase()).includes(key);
  if (allowed.__set && Array.isArray(allowed.__set))
    return allowed.__set.map((v) => String(v).toUpperCase()).includes(key);
  return false;
}

function callRowText(row) {
  return Object.values(row || {})
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
}

function isCallListSectionBreak(row, partIdCol) {
  const text = callRowText(row);
  if (text.includes("PART REQUESTS FOR CALLS FROM VRS UP")) return true;

  const partValue = String(row?.[partIdCol] || "")
    .trim()
    .toUpperCase();
  return (
    partValue === "PART REQUESTED" &&
    text.includes("CALL ID") &&
    text.includes("REPAIR CENTRE INFORMATION")
  );
}

// RC Information is free text with no standard wording, so match loosely:
// "no stock", "No Stock at deployment", "bad stock", "out of stock",
// "nil stock", "zero stock", "stock: none", "no stk", "NOSTOCK" etc.
export function isStockShortageNote(text) {
  const t = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!t) return false;
  const stock = "(?:stock|stk|stocks)";
  const patterns = [
    new RegExp(`\\b(?:no|bad|nil|zero|low|insufficient|faulty|out of|without)\\s*${stock}\\b`),
    new RegExp(`\\bnostock\\b|\\bbadstock\\b|\\boutofstock\\b`),
    new RegExp(`\\b${stock}\\s+(?:none|nil|zero|bad|faulty|unavailable|not available|short|0)\\b`),
    new RegExp(`\\b(?:not|none)\\s+(?:in|on)\\s+${stock}\\b`),
    new RegExp(`\\b${stock}\\s+(?:issue|shortage)\\b`),
  ];
  return patterns.some((re) => re.test(t));
}

export function buildCallListItems({
  rows,
  partIdCol,
  gradeCol,
  notesCol,
  customerCol,
  qtyCol,
  rcInfoCol,
  departmentCol,
  gradingLookup,
  gradeValues,
  clients,
  clientPartIds,
  selectedTeam,
}) {
  const list = [];
  const stats = {
    rcInfoFiltered: 0,
    rcStockShortageKept: 0,
    departmentFiltered: 0,
    clientFiltered: 0,
    sectionFiltered: 0,
    noPart: 0,
    noQty: 0,
  };
  let ignoreRemainingSections = false;

  for (const row of rows || []) {
    if (ignoreRemainingSections) {
      stats.sectionFiltered++;
      continue;
    }
    if (isCallListSectionBreak(row, partIdCol)) {
      ignoreRemainingSections = true;
      stats.sectionFiltered++;
      continue;
    }

    const rawPartId = String(row[partIdCol] || "")
      .trim()
      .toUpperCase();
    if (!rawPartId || rawPartId === String(partIdCol || "").toUpperCase()) {
      stats.noPart++;
      continue;
    }

    // Any RC Information text excludes the call, EXCEPT stock-shortage notes
    // ("no stock", "bad stock", "no stock at deployment", "out of stock"...):
    // those are still live calls the team can fix, so they are kept.
    let rcStockShortage = false;
    if (rcInfoCol && row[rcInfoCol] !== undefined) {
      const rcInfo = String(row[rcInfoCol] || "").trim();
      if (
        rcInfo &&
        rcInfo.toLowerCase() !== "null" &&
        rcInfo.toLowerCase() !== "undefined"
      ) {
        if (isStockShortageNote(rcInfo)) {
          rcStockShortage = true;
          stats.rcStockShortageKept++;
        } else {
          stats.rcInfoFiltered++;
          continue;
        }
      }
    }

    // A stock-shortage call naturally shows zero quantity available, so the
    // zero-quantity filter must not drop it.
    if (!rcStockShortage && qtyCol && row[qtyCol] !== undefined) {
      const qty = parseFloat(row[qtyCol]);
      if (!isNaN(qty) && qty <= 0) {
        stats.noQty++;
        continue;
      }
    }

    const matchedClient = (clients || []).find((c) =>
      rawPartId.startsWith(String(c.prefix || "").toUpperCase()),
    );
    const source = matchedClient ? matchedClient.id : "team";
    if (matchedClient) {
      if (!hasPartReference(clientPartIds?.[matchedClient.id], rawPartId)) {
        stats.clientFiltered++;
        continue;
      }
    } else if (departmentCol && selectedTeam && selectedTeam !== "ALL") {
      const department = String(row[departmentCol] || "").trim();
      if (department && !teamMatchesText(selectedTeam, department)) {
        stats.departmentFiltered++;
        continue;
      }
    }

    let grade = String(row[gradeCol] || "")
      .toUpperCase()
      .trim();
    if (!grade || gradeValues[grade] === undefined) {
      const found = (gradingLookup || {})[rawPartId];
      if (found) grade = found.grade;
    }

    list.push({
      ref: rawPartId,
      grade: grade || "C",
      value: gradeValues[grade] || gradeValues["C"] || 0.75,
      notes: row[notesCol] || "",
      customer: row[customerCol] || "",
      department: row[departmentCol] || "",
      serial: `${rawPartId}-CALL-${list.length + 1}`,
      partId: rawPartId,
      repReq: 1,
      source,
    });
  }

  return { list, stats };
}

function formatCallListStatus(count, stats, selectedTeam) {
  const notes = [
    stats.rcInfoFiltered > 0 ? `${stats.rcInfoFiltered} RC Info excluded` : "",
    stats.rcStockShortageKept > 0
      ? `${stats.rcStockShortageKept} no/bad stock kept`
      : "",
    stats.departmentFiltered > 0
      ? `${stats.departmentFiltered} other-team rows excluded`
      : "",
    stats.clientFiltered > 0
      ? `${stats.clientFiltered} client rows not in ${selectedTeam || "team"} refs`
      : "",
    stats.sectionFiltered > 0
      ? `${stats.sectionFiltered} lower-section rows ignored`
      : "",
  ].filter(Boolean);
  return ` ${count} calls ready${notes.length ? ` (${notes.join(" ")})` : ""}`;
}

// Reusable Call List Builder
function CallListBuilder({
  label,
  color,
  gradingLookup,
  gradeValues,
  existingList,
  onListBuilt,
  fileState,
  onFileState,
  clients, // NEW
  clientPartIds, // NEW
  selectedTeam,
  onStockUpload,
  onStockRebuild,
}) {
  const [stockReportTime, setStockReportTime] = useState("");
  const fs = fileState || {};
  const [status, setStatus] = useState(
    fs.status ||
      (existingList?.length > 0 ? ` ${existingList.length} items loaded` : ""),
  );
  const [headers, setHeaders] = useState(fs.headers || []);
  const [rows, setRows] = useState(fs.rows || []);
  const [partIdCol, setPartIdCol] = useState(fs.partIdCol || "");
  const [gradeCol, setGradeCol] = useState(fs.gradeCol || "");
  const [notesCol, setNotesCol] = useState(fs.notesCol || "");
  const [customerCol, setCustomerCol] = useState(fs.customerCol || "");
  const [qtyCol, setQtyCol] = useState(fs.qtyCol || "");
  const [rcInfoCol, setRcInfoCol] = useState(fs.rcInfoCol || "");
  const [departmentCol, setDepartmentCol] = useState(fs.departmentCol || "");

  function persist(patch) {
    if (onFileState)
      onFileState({
        status,
        headers,
        rows,
        partIdCol,
        gradeCol,
        notesCol,
        customerCol,
        qtyCol,
        rcInfoCol,
        departmentCol,
        ...patch,
      });
  }

  async function handleFile(file) {
    const observedAt = stockReportTime ? new Date(stockReportTime).toISOString() : new Date().toISOString();
    try {
      const { h, r } = await readStockCallsFile(file);
      const pId = detectCol(h, [
        "part id",
        "part_id",
        "part requested",
        "serial",
        "ref",
        "job",
        "ticket",
      ]);
      const gCol = detectCol(h, ["grade", "class", "part_class"]);
      const nCol = detectCol(h, ["notes", "comment", "fault", "description"]);
      const cCol = detectCol(h, ["customer", "client", "site"]);
      const dCol = detectCol(h, ["department", "team", "section"]);
      const qCol = detectCol(h, [
        "quantity available",
        "qty available",
        "qty avail",
        "available",
        "quantity",
      ]);
      const rCol = detectCol(h, [
        "repair centre information",
        "repair center information",
        "rc information",
        "rc info",
      ]);
      setHeaders(h);
      setRows(r);
      setPartIdCol(pId);
      setGradeCol(gCol);
      setNotesCol(nCol);
      setCustomerCol(cCol);
      setQtyCol(qCol);
      setRcInfoCol(rCol);
      setDepartmentCol(dCol);
      const built = buildCallListItems({
        rows: r,
        partIdCol: pId,
        gradeCol: gCol,
        notesCol: nCol,
        customerCol: cCol,
        qtyCol: qCol,
        rcInfoCol: rCol,
        departmentCol: dCol,
        gradingLookup,
        gradeValues,
        clients,
        clientPartIds,
        selectedTeam,
      });
      onListBuilt(built.list);
      onStockUpload?.({file,rows:built.list,observedAt,raw:{headers:h,rows:r},mapping:{partIdCol:pId,gradeCol:gCol,notesCol:nCol,customerCol:cCol,qtyCol:qCol,rcInfoCol:rCol,departmentCol:dCol}});
      const readyStatus = formatCallListStatus(
        built.list.length,
        built.stats,
        selectedTeam,
      );
      setStatus(readyStatus);
      if (onFileState)
        onFileState({
          headers: h,
          rows: r,
          partIdCol: pId,
          gradeCol: gCol,
          notesCol: nCol,
          customerCol: cCol,
          qtyCol: qCol,
          rcInfoCol: rCol,
          departmentCol: dCol,
          status: readyStatus,
        });
    } catch (e) {
      const st = "Error: " + e.message;
      setStatus(st);
      persist({ status: st });
    }
  }

  function buildList() {
    const { list: taggedList, stats } = buildCallListItems({
      rows,
      partIdCol,
      gradeCol,
      notesCol,
      customerCol,
      qtyCol,
      rcInfoCol,
      departmentCol,
      gradingLookup,
      gradeValues,
      clients,
      clientPartIds,
      selectedTeam,
    });
    onListBuilt(taggedList);
    onStockRebuild?.({rows:taggedList,raw:{headers,rows},mapping:{partIdCol,gradeCol,notesCol,customerCol,qtyCol,rcInfoCol,departmentCol}});
    const st = formatCallListStatus(taggedList.length, stats, selectedTeam);
    setStatus(st);
    persist({ status: st });
  }

  const list = existingList || [];

  return (
    <Card>
      <Label>{label}</Label>
      <p
        style={{
          color: "#4f6fa0",
          fontSize: 12,
          marginBottom: 16,
          lineHeight: 1.6,
        }}
      >
        Upload call list (.csv or .xlsx). Only rows where{" "}
        <strong style={{ color: "#f59e0b" }}>Quantity Available &gt; 0</strong>{" "}
        will be included. Rows with any text in the{" "}
        <strong style={{ color: "#f87171" }}>Repair Centre Information</strong>{" "}
        column are automatically excluded these parts are already being handled
        internally, <strong style={{ color: "#34d399" }}>except</strong> stock
        notes such as "no stock", "bad stock" or "no stock at deployment",
        which are kept as calls. Grades are auto-looked-up from the grading reference if not
        in the file.
      </p>
      <DropZone
        label={`Drop ${label} (.csv or .xlsx)`}
        sub="Needs: Part ID / Serial, Quantity Available. Grade optional."
        onFile={handleFile}
        accept=".csv,.xlsx"
        color={color}
      />
      {onStockUpload && <StockReportTime value={stockReportTime} onChange={setStockReportTime} />}
      {status && (
        <div
          style={{
            color: status.startsWith("OK:") ? "#4ade80" : "#f87171",
            fontSize: 12,
            marginTop: 10,
          }}
        >
          {status}
        </div>
      )}

      {headers.length > 0 && (
        <div
          style={{
            marginTop: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
            gap: 12,
          }}
        >
          {[
            ["Part ID / Ref Column", partIdCol, setPartIdCol],
            ["Department Column", departmentCol, setDepartmentCol],
            ["Qty Available Column", qtyCol, setQtyCol],
            ["RC Info Column (exclude if set)", rcInfoCol, setRcInfoCol],
            ["Grade Column (opt)", gradeCol, setGradeCol],
            ["Notes Column (opt)", notesCol, setNotesCol],
            ["Customer Column (opt)", customerCol, setCustomerCol],
          ].map(([lbl, val, setter]) => (
            <div key={lbl}>
              <Label>{lbl}</Label>
              <Select
                value={val}
                onChange={(e) => {
                  setter(e.target.value);
                  persist({ [lbl]: e.target.value });
                }}
              >
                <option value="">-- None --</option>
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </Select>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Btn onClick={buildList} style={{ width: "100%" }}>
              BUILD LIST
            </Btn>
          </div>
        </div>
      )}

      {list.length > 0 && (
        <div style={{ marginTop: 16, overflowX: "auto", maxHeight: 240 }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
          >
            <thead>
              <tr style={{ color: "#3d5280", fontSize: 10 }}>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>REF</th>
                <th style={{ textAlign: "center", padding: "6px 10px" }}>
                  GRADE
                </th>
                <th style={{ textAlign: "right", padding: "6px 10px" }}>
                  VALUE
                </th>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>
                  CUSTOMER
                </th>
              </tr>
            </thead>
            <tbody>
              {ungradedFirst(list).slice(0, 40).map((p, i) => (
                <tr key={i} style={{ borderTop: "1px solid #1a2744" }}>
                  <td
                    style={{
                      padding: "6px 10px",
                      color: "#c8d8f0",
                      fontFamily: "monospace",
                      fontSize: 11,
                    }}
                  >
                    {p.ref}
                  </td>
                  <td style={{ padding: "6px 10px", textAlign: "center" }}>
                    <GradeTag grade={p.grade} />
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      color: "#10b981",
                      fontFamily: "monospace",
                    }}
                  >
                    {(gradeValues[p.grade] || 0).toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      color: "#7aa0d4",
                      fontSize: 11,
                    }}
                  >
                    {p.customer}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length > 40 && (
            <div
              style={{ color: "#3d5280", fontSize: 11, padding: "6px 10px" }}
            >
              ...and {list.length - 40} more
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
function StepCallList({ state, setState, onNext, onBack }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3a6e",
          borderRadius: 12,
          padding: 20,
        }}
      >
        <div
          style={{
            color: "#4f8ef7",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2,
            marginBottom: 16,
          }}
        >
          CALL LIST
        </div>
        <CallListBuilder
          label="Call List"
          color="#ef4444"
          gradingLookup={state.gradingLookup}
          gradeValues={state.gradeValues}
          existingList={state.callList}
          onListBuilt={(list) => setState({ callList: list })}
          onStockUpload={({mapping,...capture}) => setState({stockCalls:enqueueStockSource({...capture,kind:"calls",team:stockTeam(state),configuration:stockConfiguration(state,mapping)})})}
          onStockRebuild={({mapping,...capture}) => setState({stockCalls:enqueueStockCallsRevision({...capture,source:state.stockCalls,team:stockTeam(state),configuration:stockConfiguration(state,mapping)})})}
          fileState={state.callListFile}
          onFileState={(s) => setState({ callListFile: s })}
          clients={state.clients}
          // The call list gates client stock on the same reference lists the
          // Power BI merge uses, so it has to refuse another team's lists in
          // exactly the same way.
          clientPartIds={
            partReferencesForTeam({
              clientPartIds: state.clientPartIds,
              clientPartIdsTeam: state.clientPartIdsTeam,
              team:
                state.teamFilter === "CUSTOM"
                  ? state.customTeamFilter
                  : state.teamFilter,
            }).refs
          }
          selectedTeam={
            state.teamFilter === "CUSTOM"
              ? state.customTeamFilter
              : state.teamFilter
          }
        />
      </div>
      <Card style={{ background: "#0a2010", border: "1px solid #14532d" }}>
        <div style={{ color: "#4ade80", fontSize: 12 }}>
          No call list? That's fine skip this step. Stock parts will still be
          allocated fairly.
        </div>
      </Card>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Btn variant="secondary" onClick={onBack}>
          {" "}
          BACK
        </Btn>
        <Btn onClick={onNext}>NEXT: ALLOCATE </Btn>
      </div>
    </div>
  );
}

// Daily Allocation Helper
// Shared EngineerPartsPanel
function EngineerPartsPanel({
  tech,
  col,
  parts,
  gradeValues,
  emptyMsg = "No parts assigned",
}) {
  const [open, setOpen] = useState(false);
  const urgentParts = ungradedFirst(
    parts.filter((p) => p.priority === "urgent"),
  );
  const repReqParts = ungradedFirst(
    parts.filter((p) => p.priority === "rep_req"),
  );
  const stockParts = ungradedFirst(
    parts.filter((p) => p.priority === "stock"),
  );

  if (parts.length === 0) {
    return (
      <div style={{ padding: "12px 18px", color: "#3d5280", fontSize: 12 }}>
        {emptyMsg}
      </div>
    );
  }

  return (
    <div style={{ padding: "0 18px" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: "none",
          border: "none",
          color: "#3d5280",
          cursor: "pointer",
          fontSize: 11,
          padding: "10px 0",
          width: "100%",
          textAlign: "left",
        }}
      >
        {open ? " Hide parts list" : ` Show all ${parts.length} parts`}
      </button>
      {open && (
        <div style={{ maxHeight: 320, overflowY: "auto", paddingBottom: 12 }}>
          {urgentParts.length > 0 && (
            <>
              <div
                style={{
                  color: "#ef4444",
                  fontSize: 10,
                  letterSpacing: 1,
                  marginBottom: 6,
                  marginTop: 4,
                }}
              >
                CALL LIST (URGENT)
              </div>
              {urgentParts.map((p, j) => (
                <div
                  key={j}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 0",
                    borderBottom: "1px solid #0d1b35",
                  }}
                >
                  <span
                    style={{
                      color: "#ef4444",
                      fontFamily: "monospace",
                      fontSize: 11,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.ref || p.partId}
                  </span>
                  <span style={{ marginLeft: 8, flexShrink: 0 }}>
                    <GradeTag grade={p.grade} />
                  </span>
                  <span
                    style={{
                      color: "#4f6fa0",
                      fontFamily: "monospace",
                      fontSize: 11,
                      marginLeft: 8,
                      flexShrink: 0,
                    }}
                  >
                    {(gradeValues[p.grade] || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </>
          )}
          {repReqParts.length > 0 && (
            <>
              <div
                style={{
                  color: "#f59e0b",
                  fontSize: 10,
                  letterSpacing: 1,
                  marginBottom: 6,
                  marginTop: 8,
                }}
              >
                REPAIR REQUIRED
              </div>
              {repReqParts.map((p, j) => (
                <div
                  key={j}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 0",
                    borderBottom: "1px solid #0d1b35",
                  }}
                >
                  <span
                    style={{
                      color: "#c8d8f0",
                      fontFamily: "monospace",
                      fontSize: 11,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.partId}
                  </span>
                  <span style={{ marginLeft: 8, flexShrink: 0 }}>
                    <GradeTag grade={p.grade} />
                  </span>
                  <span
                    style={{
                      color: "#4f6fa0",
                      fontFamily: "monospace",
                      fontSize: 11,
                      marginLeft: 8,
                      flexShrink: 0,
                    }}
                  >
                    {(gradeValues[p.grade] || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </>
          )}
          {stockParts.length > 0 && (
            <>
              <div
                style={{
                  color: "#4f8ef7",
                  fontSize: 10,
                  letterSpacing: 1,
                  marginBottom: 6,
                  marginTop: 8,
                }}
              >
                STOCK
              </div>
              {stockParts.map((p, j) => (
                <div
                  key={j}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "5px 0",
                    borderBottom: "1px solid #0d1b35",
                  }}
                >
                  <span
                    style={{
                      color: "#7aa0d4",
                      fontFamily: "monospace",
                      fontSize: 11,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.partId}
                  </span>
                  <span style={{ marginLeft: 8, flexShrink: 0 }}>
                    <GradeTag grade={p.grade} />
                  </span>
                  <span
                    style={{
                      color: "#4f6fa0",
                      fontFamily: "monospace",
                      fontSize: 11,
                      marginLeft: 8,
                      flexShrink: 0,
                    }}
                  >
                    {(gradeValues[p.grade] || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Shared EngineerCard
function EngineerCard({
  tech,
  col,
  parts,
  gradeValues,
  headline,
  subline,
  badges = [],
}) {
  const gradeCounts = {};
  GRADE_ORDER.forEach((g) => {
    gradeCounts[g] = parts.filter((p) => p.grade === g).length;
  });
  return (
    <div
      style={{
        background: "#081224",
        border: `1px solid ${col}33`,
        borderRadius: 11,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: `linear-gradient(135deg, ${col}18 0%, transparent 80%)`,
          padding: "16px 18px",
          borderBottom: `1px solid ${col}22`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <div style={{ color: col, fontWeight: 800, fontSize: 15 }}>
              {tech.name}
            </div>
            <div style={{ color: "#3d5280", fontSize: 11, marginTop: 2 }}>
              {subline}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>{headline}</div>
        </div>
        {badges.length > 0 && (
          <div
            style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}
          >
            {badges}
          </div>
        )}
        <div
          style={{ display: "flex", gap: 4, marginTop: 12, flexWrap: "wrap" }}
        >
          {GRADE_ORDER.filter((g) => gradeCounts[g] > 0).map((g) => (
            <div key={g} style={{ textAlign: "center", flex: 1, minWidth: 36 }}>
              <GradeTag grade={g} />
              <div
                style={{
                  color: "#f0f6ff",
                  fontSize: 13,
                  fontWeight: 700,
                  marginTop: 3,
                }}
              >
                {gradeCounts[g]}
              </div>
              <div style={{ color: "#3d5280", fontSize: 9 }}>
                {(gradeCounts[g] * (gradeValues[g] || 0)).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <EngineerPartsPanel
        tech={tech}
        col={col}
        parts={parts}
        gradeValues={gradeValues}
      />
    </div>
  );
}

// Reusable Blocklist Panel
function BlocklistPanel({
  blocklist,
  blocklistInput,
  setBlocklistInput,
  addToBlocklist,
  removeFromBlocklist,
  setBlocklist,
  engineers = [],
  blocklistScope = "global",
  setBlocklistScope,
  blocklistEngineer = "",
  setBlocklistEngineer,
  engineerBlocklists = {},
  removeFromEngineerBlocklist,
  blocklistServerMsg = "",
  onSaveBlocklist,
  onReloadBlocklist,
  blocklistServerBusy = false,
}) {
  const selectedEngineer = blocklistEngineer || engineers[0]?.name || "";
  const [collapsedEngineerBlocks, setCollapsedEngineerBlocks] = useState({});
  const engineerBlockEntries = Object.entries(engineerBlocklists).filter(
    ([, ids]) => ids && ids.size > 0,
  );
  const allEngineerBlocksCollapsed =
    engineerBlockEntries.length > 0 &&
    engineerBlockEntries.every(
      ([engineer]) => collapsedEngineerBlocks[engineer],
    );
  function toggleEngineerBlock(engineer) {
    setCollapsedEngineerBlocks((prev) => ({
      ...prev,
      [engineer]: !prev[engineer],
    }));
  }
  function setAllEngineerBlocksCollapsed(collapsed) {
    const next = {};
    engineerBlockEntries.forEach(([engineer]) => {
      next[engineer] = collapsed;
    });
    setCollapsedEngineerBlocks(next);
  }
  return (
    <Card>
      <Label>Part ID Blocklist Remove Parts From All Engineers</Label>
      <p
        style={{
          color: "#4f6fa0",
          fontSize: 12,
          marginBottom: 14,
          lineHeight: 1.6,
        }}
      >
        Add Part IDs here to remove them from every engineer's list on both the
        Monthly and Daily views. Useful for parts that can't be worked due to
        missing tooling, awaiting spares, etc. Replacements are pulled in
        automatically from the queue.
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {onSaveBlocklist && (
          <Btn
            variant="success"
            onClick={onSaveBlocklist}
            disabled={blocklistServerBusy}
            style={{ fontSize: 10, padding: "7px 14px" }}
          >
            {blocklistServerBusy ? "SAVING..." : "SAVE BLOCKLIST TO SERVER"}
          </Btn>
        )}
        {onReloadBlocklist && (
          <Btn
            variant="secondary"
            onClick={onReloadBlocklist}
            disabled={blocklistServerBusy}
            style={{ fontSize: 10, padding: "7px 14px" }}
          >
            RELOAD SAVED
          </Btn>
        )}
        {blocklistServerMsg && (
          <span
            style={{
              color:
                blocklistServerMsg.startsWith("Could") ||
                blocklistServerMsg.startsWith("Server")
                  ? "#f87171"
                  : "#22c55e",
              fontSize: 11,
            }}
          >
            {blocklistServerMsg}
          </span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        {setBlocklistScope && (
          <div style={{ minWidth: 160 }}>
            <Label>Apply To</Label>
            <Select
              value={blocklistScope}
              onChange={(e) => setBlocklistScope(e.target.value)}
            >
              <option value="global">All engineers</option>
              <option value="engineer">One engineer</option>
            </Select>
          </div>
        )}
        {blocklistScope === "engineer" && setBlocklistEngineer && (
          <div style={{ minWidth: 180 }}>
            <Label>Engineer</Label>
            <Select
              value={selectedEngineer}
              onChange={(e) => setBlocklistEngineer(e.target.value)}
            >
              {engineers.map((eng) => (
                <option key={eng.name} value={eng.name}>
                  {eng.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 240 }}>
          <Label>Add Part ID(s) comma, semicolon, or new line separated</Label>
          <textarea
            value={blocklistInput}
            onChange={(e) => setBlocklistInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                addToBlocklist();
              }
            }}
            placeholder="e.g. COS-TAB-MIC-COMPLIANCE, SEL-SCN-DAT-PM9500"
            rows={2}
            style={{
              ...css.input,
              width: "100%",
              resize: "vertical",
              fontSize: 11,
            }}
          />
        </div>
        <Btn onClick={addToBlocklist} style={{ padding: "9px 18px" }}>
          + ADD TO BLOCKLIST
        </Btn>
      </div>
      {blocklist.size > 0 && (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              color: "#4f6fa0",
              fontSize: 10,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            BLOCKED PARTS ({blocklist.size}) REMOVED FROM ALL LISTS
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[...blocklist].map((id) => (
              <div
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  background: "#2a1020",
                  border: "1px solid #3d1830",
                  borderRadius: 6,
                  padding: "4px 10px",
                }}
              >
                <span
                  style={{
                    color: "#f87171",
                    fontFamily: "monospace",
                    fontSize: 11,
                  }}
                >
                  {id}
                </span>
                <button
                  onClick={() => removeFromBlocklist(id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#64748b",
                    cursor: "pointer",
                    fontSize: 12,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  x
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setBlocklist(new Set())}
            style={{
              marginTop: 8,
              background: "none",
              border: "none",
              color: "#3d5280",
              fontSize: 10,
              cursor: "pointer",
              padding: 0,
            }}
          >
            Clear all blocked parts
          </button>
        </div>
      )}
      {engineerBlockEntries.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ color: "#4f6fa0", fontSize: 10, letterSpacing: 1 }}>
              ENGINEER-SPECIFIC BLOCKS
            </div>
            <button
              onClick={() =>
                setAllEngineerBlocksCollapsed(!allEngineerBlocksCollapsed)
              }
              style={{
                background: "none",
                border: "1px solid #1e3056",
                borderRadius: 6,
                color: "#7aa0d4",
                cursor: "pointer",
                fontSize: 10,
                padding: "3px 8px",
              }}
            >
              {allEngineerBlocksCollapsed ? "Expand all" : "Collapse all"}
            </button>
          </div>
          {engineerBlockEntries.map(([engineer, ids]) => (
            <div
              key={engineer}
              style={{
                marginBottom: 8,
                background: "#071020",
                border: "1px solid #132746",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => toggleEngineerBlock(engineer)}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  color: "#7aa0d4",
                  cursor: "pointer",
                  fontSize: 11,
                  padding: "7px 9px",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
              >
                <span>
                  {collapsedEngineerBlocks[engineer] ? "+" : "-"} {engineer}
                </span>
                <span style={{ color: "#fbbf24", fontSize: 10 }}>
                  {ids.size} part{ids.size === 1 ? "" : "s"}
                </span>
              </button>
              {!collapsedEngineerBlocks[engineer] && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    padding: "0 9px 9px",
                  }}
                >
                  {[...ids].map((id) => (
                    <div
                      key={`${engineer}-${id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        background: "#1c1917",
                        border: "1px solid #713f12",
                        borderRadius: 6,
                        padding: "4px 10px",
                      }}
                    >
                      <span
                        style={{
                          color: "#fbbf24",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}
                      >
                        {id}
                      </span>
                      <button
                        onClick={() =>
                          removeFromEngineerBlocklist?.(engineer, id)
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          cursor: "pointer",
                          fontSize: 12,
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Monthly View
function MonthlyView({
  results,
  state,
  onExportCSV,
  blocklist = new Set(),
  blocklistInput,
  setBlocklistInput,
  addToBlocklist,
  removeFromBlocklist,
  setBlocklist,
  blocklistScope,
  setBlocklistScope,
  blocklistEngineer,
  setBlocklistEngineer,
  engineerBlocklists = {},
  removeFromEngineerBlocklist,
  blocklistServerMsg = "",
  onSaveBlocklist,
  onReloadBlocklist,
  blocklistServerBusy = false,
  sharedDailyTarget = 24,
  setSharedDailyTarget,
}) {
  const rawDailyAvg =
    results.length > 0 && state.workingDays > 0
      ? results.reduce((s, t) => s + t.totalUnits, 0) /
        results.length /
        state.workingDays
      : 18;

  // Use shared target no local state needed
  const monthlyDailyTarget = sharedDailyTarget;
  const setMonthlyDailyTarget = (v) =>
    setSharedDailyTarget(Math.max(1, parseInt(v) || 1));

  // Per-engineer capped parts = blocklist filtered, then min(dailyTarget workingDays, full allocation)
  const cappedResults = results.map((tech) => {
    const cap = monthlyDailyTarget * state.workingDays;
    const engineerBlocked = engineerBlocklists[tech.name] || new Set();
    const allowed = tech.parts.filter((p) => {
      const id = (p.partId || "").toUpperCase();
      return !blocklist.has(id) && !engineerBlocked.has(id);
    });
    const cappedParts = allowed.slice(0, cap);
    const cappedCommission = cappedParts.reduce(
      (s, p) => s + (state.gradeValues[p.grade] || 0),
      0,
    );
    const cappedUnits = cappedParts.length;
    const cappedDailyAvg =
      state.workingDays > 0 ? cappedUnits / state.workingDays : 0;
    const bonusTier =
      [...DEFAULT_TIERS]
        .sort((a, b) => b.daily - a.daily)
        .find((ti) => cappedDailyAvg >= ti.daily) || null;
    const netCommission = Math.max(0, cappedCommission - tech.doaDeduction);
    const totalEarnings = netCommission + (bonusTier?.bonus || 0);
    return {
      ...tech,
      cappedParts,
      cappedCommission,
      cappedUnits,
      cappedDailyAvg,
      bonusTier,
      totalEarnings,
    };
  });

  // Capped totals (what engineers actually earn at this daily target)
  const cappedTotalUnits = cappedResults.reduce((s, t) => s + t.cappedUnits, 0);
  const cappedTotalComm = cappedResults.reduce(
    (s, t) => s + t.cappedCommission,
    0,
  );
  const cappedTotalEarn = cappedResults.reduce(
    (s, t) => s + t.totalEarnings,
    0,
  );
  const cappedAvgDaily =
    results.length > 0 && state.workingDays > 0
      ? cappedTotalUnits / state.workingDays / results.length
      : 0;

  // Full backstock totals (the ceiling kept as reference)
  const backstockUnits = results.reduce((s, t) => s + t.totalUnits, 0);
  const backstockComm = results.reduce((s, t) => s + t.commission, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Daily target control */}
      <Card>
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div>
            <Label>Global Parts Per Day (Monthly Cap)</Label>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Input
                type="number"
                min={1}
                max={60}
                value={monthlyDailyTarget}
                onChange={(e) => setMonthlyDailyTarget(e.target.value)}
                style={{
                  width: 80,
                  textAlign: "center",
                  fontSize: 18,
                  fontWeight: 800,
                }}
              />
              <div style={{ color: "#3d5280", fontSize: 10, lineHeight: 1.6 }}>
                parts/day cap
                <br />
                <span style={{ color: "#f59e0b" }}>
                  Backstock avg: {rawDailyAvg.toFixed(1)}/day
                </span>
              </div>
            </div>
          </div>
          {/* Projected monthly earnings */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: 1 }}>
            <StatBox
              label="Projected Units"
              value={cappedTotalUnits}
              color="#4f8ef7"
              sub={`${monthlyDailyTarget}/day ${state.workingDays} days ${results.length} eng`}
            />
            <StatBox
              label="Projected Commission"
              value={`${cappedTotalComm.toFixed(2)}`}
              color="#a78bfa"
            />
            <StatBox
              label="Projected Earnings (inc bonus)"
              value={`${cappedTotalEarn.toFixed(2)}`}
              color="#10b981"
            />
          </div>
        </div>

        {/* Full backstock reference */}
        <div
          style={{
            marginTop: 14,
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 8,
            padding: "10px 16px",
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ color: "#3d5280", fontSize: 10, letterSpacing: 1 }}>
            FULL BACKSTOCK (if all parts were worked)
          </div>
          <div style={{ color: "#4f6fa0", fontSize: 12 }}>
            {backstockUnits} units{" "}
            <span style={{ color: "#a78bfa", fontFamily: "monospace" }}>
              {backstockComm.toFixed(2)}
            </span>{" "}
            commission potential{" "}
            <span style={{ color: "#3d5280" }}>
              {(
                backstockUnits /
                (state.workingDays || 1) /
                (results.length || 1)
              ).toFixed(1)}
              /day avg
            </span>
          </div>
        </div>
      </Card>

      {/* Bonus tier status based on capped daily avg */}
      <Card>
        <Label>Bonus Tier Status at {monthlyDailyTarget} parts/day</Label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[...state.tiers]
            .sort((a, b) => a.daily - b.daily)
            .map((tier) => {
              const allHit = cappedResults.every(
                (r) => r.cappedDailyAvg >= tier.daily,
              );
              const someHit = cappedResults.some(
                (r) => r.cappedDailyAvg >= tier.daily,
              );
              const c = allHit ? "#10b981" : someHit ? "#f59e0b" : "#3d5280";
              return (
                <div
                  key={tier.bonus}
                  style={{
                    background: c + "11",
                    border: `1px solid ${c}44`,
                    borderRadius: 10,
                    padding: "12px 18px",
                    flex: 1,
                    minWidth: 140,
                  }}
                >
                  <div style={{ color: c, fontSize: 10, letterSpacing: 1 }}>
                    {allHit
                      ? " ALL ENGINEERS"
                      : someHit
                        ? " SOME ENGINEERS"
                        : " NOT REACHED"}
                  </div>
                  <div
                    style={{
                      color: "#f0f6ff",
                      fontSize: 22,
                      fontWeight: 800,
                      marginTop: 4,
                    }}
                  >
                    {tier.bonus}
                  </div>
                  <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                    {tier.daily} units/day needed
                  </div>
                </div>
              );
            })}
        </div>
      </Card>

      {/* Engineer cards showing capped allocation */}
      <Card>
        <Label>
          Engineer Allocation {monthlyDailyTarget} parts/day cap over{" "}
          {state.workingDays} days
        </Label>
        <div
          className="engineer-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 14,
          }}
        >
          {cappedResults.map((tech, i) => {
            const col = TECH_COLORS[i % TECH_COLORS.length];
            const urgentCount = tech.cappedParts.filter(
              (p) => p.priority === "urgent",
            ).length;
            const repReqCount = tech.cappedParts.filter(
              (p) => p.priority === "rep_req",
            ).length;
            const mcdCount = tech.cappedParts.filter(
              (p) => p.source === "mcd",
            ).length;
            return (
              <EngineerCard
                key={tech.id}
                tech={tech}
                col={col}
                parts={tech.cappedParts}
                gradeValues={state.gradeValues}
                subline={`${tech.cappedUnits} units ${tech.cappedDailyAvg.toFixed(1)}/day${mcdCount > 0 ? ` ${mcdCount} MCD` : ""}`}
                headline={
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        color: "#f0f6ff",
                        fontSize: 20,
                        fontWeight: 800,
                      }}
                    >
                      {tech.totalEarnings.toFixed(2)}
                    </div>
                    <div style={{ color: "#3d5280", fontSize: 10 }}>
                      {tech.cappedCommission.toFixed(2)} commission
                    </div>
                    {tech.bonusTier && (
                      <div style={{ color: "#10b981", fontSize: 10 }}>
                        +{tech.bonusTier.bonus} bonus
                      </div>
                    )}
                    <div
                      style={{ color: "#3d5280", fontSize: 9, marginTop: 2 }}
                    >
                      backstock: {tech.totalUnits}
                    </div>
                  </div>
                }
                badges={[
                  tech.bonusTier ? (
                    <Badge key="bonus" color="#10b981">
                      {tech.bonusTier.bonus} BONUS TIER
                    </Badge>
                  ) : (
                    <Badge key="nobns" color="#ef4444">
                      No tier reached
                    </Badge>
                  ),
                  urgentCount > 0 && (
                    <Badge key="urg" color="#ef4444">
                      {urgentCount} call list
                    </Badge>
                  ),
                  repReqCount > 0 && (
                    <Badge key="rep" color="#f59e0b">
                      {repReqCount} rep req
                    </Badge>
                  ),
                  mcdCount > 0 && (
                    <Badge key="mcd" color="#34d399">
                      {mcdCount} MCD
                    </Badge>
                  ),
                ].filter(Boolean)}
              />
            );
          })}
        </div>
      </Card>

      <BlocklistPanel
        blocklist={blocklist}
        blocklistInput={blocklistInput}
        setBlocklistInput={setBlocklistInput}
        addToBlocklist={addToBlocklist}
        removeFromBlocklist={removeFromBlocklist}
        setBlocklist={setBlocklist}
        engineers={results}
        blocklistScope={blocklistScope}
        setBlocklistScope={setBlocklistScope}
        blocklistEngineer={blocklistEngineer}
        setBlocklistEngineer={setBlocklistEngineer}
        engineerBlocklists={engineerBlocklists}
        removeFromEngineerBlocklist={removeFromEngineerBlocklist}
        blocklistServerMsg={blocklistServerMsg}
        onSaveBlocklist={onSaveBlocklist}
        onReloadBlocklist={onReloadBlocklist}
        blocklistServerBusy={blocklistServerBusy}
      />

      {/* Distribution table capped */}
      <Card>
        <Label>
          Commission Distribution Table {monthlyDailyTarget} parts/day
        </Label>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
          >
            <thead>
              <tr
                style={{
                  color: "#3d5280",
                  fontSize: 10,
                  borderBottom: "1px solid #1a2744",
                }}
              >
                <th style={{ textAlign: "left", padding: "8px 10px" }}>
                  ENGINEER
                </th>
                {GRADE_ORDER.map((g) => (
                  <th
                    key={g}
                    style={{ textAlign: "center", padding: "8px 6px" }}
                  >
                    <GradeTag grade={g} />
                  </th>
                ))}
                <th style={{ textAlign: "right", padding: "8px 10px" }}>
                  UNITS
                </th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>
                  AVG/DAY
                </th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>MCD</th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>
                  COMMISSION
                </th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>
                  BONUS
                </th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {cappedResults.map((tech, i) => {
                const col = TECH_COLORS[i % TECH_COLORS.length];
                const gc = {};
                GRADE_ORDER.forEach((g) => {
                  gc[g] = tech.cappedParts.filter((p) => p.grade === g).length;
                });
                const mcdCount = tech.cappedParts.filter(
                  (p) => p.source === "mcd",
                ).length;
                return (
                  <tr key={tech.id} style={{ borderTop: "1px solid #1a2744" }}>
                    <td
                      style={{
                        padding: "9px 10px",
                        color: col,
                        fontWeight: 700,
                      }}
                    >
                      {tech.name}
                    </td>
                    {GRADE_ORDER.map((g) => (
                      <td
                        key={g}
                        style={{
                          padding: "9px 6px",
                          textAlign: "center",
                          color: gc[g] ? "#c8d8f0" : "#1e3056",
                        }}
                      >
                        {gc[g] || ""}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color: "#7aa0d4",
                      }}
                    >
                      {tech.cappedUnits}
                    </td>
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color:
                          tech.cappedDailyAvg >= (state.tiers[0]?.daily || 18)
                            ? "#10b981"
                            : "#f59e0b",
                      }}
                    >
                      {tech.cappedDailyAvg.toFixed(1)}
                    </td>
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color: "#34d399",
                      }}
                    >
                      {mcdCount || ""}
                    </td>
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color: "#a78bfa",
                        fontFamily: "monospace",
                      }}
                    >
                      {tech.cappedCommission.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color: "#10b981",
                        fontFamily: "monospace",
                      }}
                    >
                      {tech.bonusTier ? `+${tech.bonusTier.bonus}` : ""}
                    </td>
                    <td
                      style={{
                        padding: "9px 10px",
                        textAlign: "right",
                        color: "#f0f6ff",
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    >
                      {tech.totalEarnings.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              <tr
                style={{
                  borderTop: "2px solid #1e3056",
                  background: "#0d1b35",
                }}
              >
                <td
                  style={{
                    padding: "9px 10px",
                    color: "#4f6fa0",
                    fontWeight: 700,
                  }}
                >
                  TOTAL
                </td>
                {GRADE_ORDER.map((g) => {
                  const total = cappedResults.reduce(
                    (s, t) =>
                      s + t.cappedParts.filter((p) => p.grade === g).length,
                    0,
                  );
                  return (
                    <td
                      key={g}
                      style={{
                        padding: "9px 6px",
                        textAlign: "center",
                        color: total ? "#7aa0d4" : "#1e3056",
                      }}
                    >
                      {total || ""}
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#7aa0d4",
                    fontWeight: 700,
                  }}
                >
                  {cappedTotalUnits}
                </td>
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#7aa0d4",
                  }}
                >
                  {cappedAvgDaily.toFixed(1)}
                </td>
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#34d399",
                    fontWeight: 700,
                  }}
                >
                  {cappedResults.reduce(
                    (s, t) =>
                      s +
                      t.cappedParts.filter((p) => p.source === "mcd").length,
                    0,
                  ) || ""}
                </td>
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#a78bfa",
                    fontWeight: 700,
                    fontFamily: "monospace",
                  }}
                >
                  {cappedTotalComm.toFixed(2)}
                </td>
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#10b981",
                    fontWeight: 700,
                    fontFamily: "monospace",
                  }}
                >
                  {cappedResults.reduce(
                    (s, t) => s + (t.bonusTier?.bonus || 0),
                    0,
                  )}
                </td>
                <td
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "#f0f6ff",
                    fontWeight: 800,
                    fontFamily: "monospace",
                  }}
                >
                  {cappedTotalEarn.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Btn variant="secondary" onClick={onExportCSV}>
          EXPORT CSV
        </Btn>
        <Btn variant="secondary" onClick={() => window.print()}>
          PRINT
        </Btn>
      </div>
    </div>
  );
}

// Daily View
function DailyView({
  results,
  state,
  blocklist = new Set(),
  blocklistInput,
  setBlocklistInput,
  addToBlocklist,
  removeFromBlocklist,
  setBlocklist,
  blocklistScope,
  setBlocklistScope,
  blocklistEngineer,
  setBlocklistEngineer,
  engineerBlocklists = {},
  removeFromEngineerBlocklist,
  blocklistServerMsg = "",
  onSaveBlocklist,
  onReloadBlocklist,
  blocklistServerBusy = false,
  sharedDailyTarget = 24,
  setSharedDailyTarget,
  deletedSerials = {},
  setDeletedSerials = () => {},
  manualReplacements = {},
  setManualReplacements = () => {},
}) {
  const monthlyAvgPerTech =
    results.length > 0
      ? results.reduce((s, t) => s + t.totalUnits, 0) / results.length
      : 0;
  const rawDailyTarget =
    state.workingDays > 0 ? monthlyAvgPerTech / state.workingDays : 18;

  // Use the shared target no local state, changes propagate to Monthly view
  const dailyTarget = sharedDailyTarget;
  const setDailyTarget = (v) =>
    setSharedDailyTarget(Math.max(1, parseInt(v) || 1));

  const [dayNumber, setDayNumber] = useState(1);
  // Per-engineer overrides: { [techId]: number } null means use global
  const [perEngTarget, setPerEngTarget] = useState({});

  // MCD weight: 0 = all DCD, 100 = all MCD, 50 = balanced
  // Controls how many of each day's N slots come from MCD vs DCD parts
  const [mcdWeight, setMcdWeight] = useState(50);

  function getTargetForTech(techId) {
    return perEngTarget[techId] !== undefined
      ? perEngTarget[techId]
      : dailyTarget;
  }

  function setEngTarget(techId, val) {
    setPerEngTarget((prev) => ({
      ...prev,
      [techId]: Math.max(1, parseInt(val) || 1),
    }));
  }

  function resetEngTarget(techId) {
    setPerEngTarget((prev) => {
      const n = { ...prev };
      delete n[techId];
      return n;
    });
  }

  // deletedSerials: { [techId_day]: Set<serial> }
  // manualReplacements: { [techId_day_serial]: { partId, grade, value } }
  // which serial is currently showing the replace input
  const [replacingSerial, setReplacingSerial] = useState(null);
  const [replaceInput, setReplaceInput] = useState("");
  const [replaceError, setReplaceError] = useState("");
  const [copyChooser, setCopyChooser] = useState(null);
  const [draggedReplacement, setDraggedReplacement] = useState(null);
  const draggedReplacementRef = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [undoStack, setUndoStack] = useState([]);

  // Combined grading lookup (DCD + MCD) for manual part lookup
  const combinedLookup = { ...(state.gradingLookup || {}) };

  function cloneDeletedMap(map) {
    const copy = {};
    Object.entries(map || {}).forEach(([key, value]) => {
      copy[key] = new Set(value || []);
    });
    return copy;
  }

  function rememberListState(label) {
    setUndoStack((prev) => [
      ...prev.slice(-11),
      {
        label,
        deletedSerials: cloneDeletedMap(deletedSerials),
        manualReplacements: { ...(manualReplacements || {}) },
      },
    ]);
  }

  function undoLastListChange() {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setDeletedSerials(cloneDeletedMap(last.deletedSerials));
    setManualReplacements({ ...(last.manualReplacements || {}) });
    setCopyChooser(null);
    setDraggedReplacement(null);
    draggedReplacementRef.current = null;
    setDropTarget(null);
    setReplacingSerial(null);
    setReplaceInput("");
    setReplaceError("");
    setUndoStack((prev) => prev.slice(0, -1));
  }

  function getDisplayPart(techId, part, dayNum = dayNumber) {
    const manKey = `${techId}_${dayNum}_${part.serial}`;
    const manRep = manualReplacements[manKey];
    return {
      partId: manRep ? manRep.partId : part.ref || part.partId,
      grade: manRep ? manRep.grade : part.grade,
      value: manRep ? manRep.value : state.gradeValues[part.grade] || 0,
      isManual: !!manRep,
    };
  }

  function submitManualReplace(techId, serial) {
    const pid = replaceInput.trim().toUpperCase();
    if (!pid) {
      setReplaceError("Enter a Part ID");
      return;
    }
    const found = combinedLookup[pid];
    const grade = found ? found.grade : null;
    if (!grade) {
      setReplaceError(`"${pid}" not found in grading reference`);
      return;
    }
    const key = `${techId}_${dayNumber}_${serial}`;
    rememberListState("manual replacement");
    setManualReplacements((prev) => ({
      ...prev,
      [key]: { partId: pid, grade, value: state.gradeValues[grade] || 0 },
    }));
    setReplacingSerial(null);
    setReplaceInput("");
    setReplaceError("");
  }

  function clearManualReplace(techId, serial) {
    const key = `${techId}_${dayNumber}_${serial}`;
    if (!manualReplacements[key]) return;
    rememberListState("clear replacement");
    setManualReplacements((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  }

  function buildReplacementFromPart(techId, sourcePart) {
    const sourceDisplay = getDisplayPart(techId, sourcePart);
    return {
      partId: sourceDisplay.partId,
      grade: sourceDisplay.grade,
      value: sourceDisplay.value,
      duplicateOf: sourcePart.serial,
    };
  }

  function applyReplacementToRow(targetTechId, targetSerial, replacement) {
    if (!replacement || !targetTechId || !targetSerial) return;
    rememberListState("drag replacement");
    setManualReplacements((prev) => ({
      ...prev,
      [`${targetTechId}_${dayNumber}_${targetSerial}`]: replacement,
    }));
  }

  function readDraggedReplacement(event) {
    if (draggedReplacementRef.current) return draggedReplacementRef.current;
    try {
      const raw = event?.dataTransfer?.getData("application/json");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function completeDropReplacement(targetTechId, targetSerial, activeDrag) {
    if (!activeDrag || !targetTechId || !targetSerial) return false;
    if (
      activeDrag.sourceTechId === targetTechId &&
      activeDrag.sourceSerial === targetSerial
    ) {
      draggedReplacementRef.current = null;
      setDraggedReplacement(null);
      setDropTarget(null);
      return false;
    }
    applyReplacementToRow(targetTechId, targetSerial, activeDrag.replacement);
    draggedReplacementRef.current = null;
    setDraggedReplacement(null);
    setDropTarget(null);
    return true;
  }

  function completeDropFromPoint(x, y) {
    const activeDrag = draggedReplacementRef.current;
    if (!activeDrag || !Number.isFinite(x) || !Number.isFinite(y)) return false;
    const element = document.elementFromPoint(x, y);
    const target = element?.closest?.("[data-task-drop-target='true']");
    if (!target) return false;
    return completeDropReplacement(
      target.dataset.techId,
      target.dataset.serial,
      activeDrag,
    );
  }

  function openSameUnitChooser(techId, sourcePart) {
    const currentTech = daySchedule.find((t) => t.id === techId);
    if (!currentTech) return;
    const sourceIndex = currentTech.dayParts.findIndex(
      (p) => p.serial === sourcePart.serial,
    );
    const candidates = [
      ...currentTech.dayParts.slice(Math.max(0, sourceIndex + 1)),
      ...currentTech.dayParts.slice(0, Math.max(0, sourceIndex)),
    ].filter((p) => p.serial !== sourcePart.serial);

    if (candidates.length === 0) {
      alert("There are no other rows in this list to replace.");
      return;
    }
    setCopyChooser({
      techId,
      sourcePart,
      candidates,
      selected: new Set(),
    });
  }

  function toggleCopyCandidate(serial) {
    setCopyChooser((prev) => {
      if (!prev) return prev;
      const selected = new Set(prev.selected || []);
      selected.has(serial) ? selected.delete(serial) : selected.add(serial);
      return { ...prev, selected };
    });
  }

  function applySelectedCopies() {
    if (!copyChooser || !(copyChooser.selected?.size > 0)) return;
    const replacement = buildReplacementFromPart(
      copyChooser.techId,
      copyChooser.sourcePart,
    );
    rememberListState("selected copies");
    setManualReplacements((prev) => {
      const next = { ...prev };
      copyChooser.candidates.forEach((candidate) => {
        if (copyChooser.selected.has(candidate.serial)) {
          next[`${copyChooser.techId}_${dayNumber}_${candidate.serial}`] =
            replacement;
        }
      });
      return next;
    });
    setCopyChooser(null);
  }

  // Excel export for a single engineer's day
  function exportEngineerXLSX(tech, dayParts, completed, dayNum) {
    if (!window.XLSX) {
      alert("SheetJS not loaded yet please wait a moment and try again.");
      return;
    }
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const gradeValueMap = state.gradeValues;
    const berValue = gradeValueMap["BER"] || 0.1;

    // Cols: A=Done, B=BER, C=Part ID, D=Grade, E=Value, F=Section, G=Source, H=Comm
    const headerRow = [
      "Done (1=yes)",
      "BER (1=yes)",
      "Part ID",
      "Grade",
      "Value ()",
      "Section",
      "Source",
      "Comm (auto)",
    ];
    const dataStartRow = 3;
    const dataRows = dayParts.map((p) => {
      const manKey = `${tech.id}_${dayNum}_${p.serial}`;
      const manRep = manualReplacements[manKey];
      const displayId = manRep ? manRep.partId : p.ref || p.partId;
      const displayGrade = manRep ? manRep.grade : p.grade;
      const displayVal = manRep ? manRep.value : gradeValueMap[p.grade] || 0;
      const section =
        p.priority === "urgent"
          ? "CALL LIST"
          : p.priority === "rep_req"
            ? "REPAIR REQUIRED"
            : "STOCK";
      return [
        completed.has(p.serial) ? 1 : 0,
        0,
        displayId,
        displayGrade,
        displayVal,
        section,
        p.source || "DCD",
        "",
      ];
    });
    const dataEndRow = dataStartRow + dataRows.length - 1;

    const wsData = [
      [`${tech.name} Day ${dayNum} Task List`, "", "", "", "", "", "", ""],
      headerRow,
      ...dataRows,
      [],
      ["Commission Earned", "", "", "", "", "", "", ""],
      ["Parts Completed", "", "", "", "", "", "", ""],
      ["BER Units", "", "", "", "", "", "", ""],
      ["Parts Remaining", "", "", "", "", "", "", ""],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Write H-column formulas directly to each cell most reliable way with SheetJS
    for (let r = 0; r < dataRows.length; r++) {
      const rowNum = dataStartRow + r;
      const cellRef = `H${rowNum}`;
      ws[cellRef] = {
        t: "n",
        f: `IF(A${rowNum}=1,IF(B${rowNum}=1,${berValue},E${rowNum}),0)`,
      };
    }
    if (dataRows.length > 0) {
      ws[`H${dataEndRow + 2}`] = {
        t: "n",
        f: `SUM(H${dataStartRow}:H${dataEndRow})`,
      };
      ws[`H${dataEndRow + 3}`] = {
        t: "n",
        f: `SUM(A${dataStartRow}:A${dataEndRow})`,
      };
      ws[`H${dataEndRow + 4}`] = {
        t: "n",
        f: `SUM(B${dataStartRow}:B${dataEndRow})`,
      };
      ws[`H${dataEndRow + 5}`] = {
        t: "n",
        f: `${dataRows.length}-SUM(A${dataStartRow}:A${dataEndRow})`,
      };
    }

    ws["!cols"] = [
      { wch: 13 },
      { wch: 10 },
      { wch: 30 },
      { wch: 8 },
      { wch: 12 },
      { wch: 18 },
      { wch: 8 },
      { wch: 14 },
    ];

    if (dataRows.length > 0) {
      ws["!dataValidation"] = [
        {
          sqref: `A${dataStartRow}:A${dataEndRow}`,
          type: "whole",
          operator: "between",
          formula1: "0",
          formula2: "1",
          showInputMessage: true,
          promptTitle: "Completed?",
          prompt: "1 = done, 0 = not yet",
        },
        {
          sqref: `B${dataStartRow}:B${dataEndRow}`,
          type: "whole",
          operator: "between",
          formula1: "0",
          formula2: "1",
          showInputMessage: true,
          promptTitle: "BER?",
          prompt: "1 = BER (" + berValue.toFixed(2) + "), 0 = normal grade",
        },
      ];
    }

    XLSX.utils.book_append_sheet(wb, ws, tech.name.slice(0, 31));

    const summaryData = [
      ["RazerGrid"],
      [`Day ${dayNum} Summary ${tech.name}`],
      [],
      ["Total Parts", dataRows.length],
      ["Target Commission", `${tech.dayCommission.toFixed(2)}`],
      [],
      ["Grade Values Used", ""],
      ...GRADE_ORDER.map((g) => [
        ` ${g}${g === "BER" ? " (BER override)" : ""}`,
        `${(gradeValueMap[g] || 0).toFixed(2)}`,
      ]),
      [],
      ["Instructions", ""],
      ["", "Col A: Enter 1 when a part is completed, 0 if not done."],
      [
        "",
        "Col B: Enter 1 if the unit turned out to be BER commission uses " +
          berValue.toFixed(2) +
          " instead.",
      ],
      ["", "Col H (Comm): Calculated automatically do not edit."],
      ["", "Commission Earned (below list) = SUM of Col H."],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary["!cols"] = [{ wch: 24 }, { wch: 52 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    XLSX.writeFile(
      wb,
      `${tech.name.replace(/\s+/g, "_")}_Day${dayNum}_Tasks.xlsx`,
    );
  }

  // Export ALL engineers for the day
  function exportAllEngineersXLSX() {
    if (!window.XLSX) {
      alert("SheetJS not loaded yet please wait a moment and try again.");
      return;
    }
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const gradeValueMap = state.gradeValues;
    const berValue = gradeValueMap["BER"] || 0.1;

    daySchedule.forEach((tech) => {
      const completed = new Set();
      const dataStartRow = 3;

      const headerRow = [
        "Done (1=yes)",
        "BER (1=yes)",
        "Part ID",
        "Grade",
        "Value ()",
        "Section",
        "Source",
        "Comm (auto)",
      ];
      const dataRows = tech.dayParts.map((p) => {
        const manKey = `${tech.id}_${dayNumber}_${p.serial}`;
        const manRep = manualReplacements[manKey];
        const displayId = manRep ? manRep.partId : p.ref || p.partId;
        const displayGrade = manRep ? manRep.grade : p.grade;
        const displayVal = manRep ? manRep.value : gradeValueMap[p.grade] || 0;
        const section =
          p.priority === "urgent"
            ? "CALL LIST"
            : p.priority === "rep_req"
              ? "REPAIR REQUIRED"
              : "STOCK";
        return [
          completed.has(p.serial) ? 1 : 0,
          0,
          displayId,
          displayGrade,
          displayVal,
          section,
          p.source || "DCD",
          "",
        ];
      });
      const dataEndRow = dataStartRow + dataRows.length - 1;

      const wsData = [
        [`${tech.name} Day ${dayNumber} Task List`, "", "", "", "", "", "", ""],
        headerRow,
        ...dataRows,
        [],
        ["Commission Earned", "", "", "", "", "", "", ""],
        ["Parts Completed", "", "", "", "", "", "", ""],
        ["BER Units", "", "", "", "", "", "", ""],
        ["Parts Remaining", "", "", "", "", "", "", ""],
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Write H-column formulas directly bypasses aoa_to_sheet formula handling issue
      for (let r = 0; r < dataRows.length; r++) {
        const rowNum = dataStartRow + r;
        ws[`H${rowNum}`] = {
          t: "n",
          f: `IF(A${rowNum}=1,IF(B${rowNum}=1,${berValue},E${rowNum}),0)`,
        };
      }
      if (dataRows.length > 0) {
        ws[`H${dataEndRow + 2}`] = {
          t: "n",
          f: `SUM(H${dataStartRow}:H${dataEndRow})`,
        };
        ws[`H${dataEndRow + 3}`] = {
          t: "n",
          f: `SUM(A${dataStartRow}:A${dataEndRow})`,
        };
        ws[`H${dataEndRow + 4}`] = {
          t: "n",
          f: `SUM(B${dataStartRow}:B${dataEndRow})`,
        };
        ws[`H${dataEndRow + 5}`] = {
          t: "n",
          f: `${tech.dayParts.length}-SUM(A${dataStartRow}:A${dataEndRow})`,
        };
      }

      ws["!cols"] = [
        { wch: 13 },
        { wch: 10 },
        { wch: 30 },
        { wch: 8 },
        { wch: 12 },
        { wch: 18 },
        { wch: 8 },
        { wch: 14 },
      ];
      if (dataRows.length > 0) {
        ws["!dataValidation"] = [
          {
            sqref: `A${dataStartRow}:A${dataEndRow}`,
            type: "whole",
            operator: "between",
            formula1: "0",
            formula2: "1",
            showInputMessage: true,
            promptTitle: "Completed?",
            prompt: "1 = done, 0 = not yet",
          },
          {
            sqref: `B${dataStartRow}:B${dataEndRow}`,
            type: "whole",
            operator: "between",
            formula1: "0",
            formula2: "1",
            showInputMessage: true,
            promptTitle: "BER?",
            prompt:
              "1 = BER unit (" + berValue.toFixed(2) + "), 0 = normal grade",
          },
        ];
      }
      XLSX.utils.book_append_sheet(wb, ws, tech.name.slice(0, 31));
    });

    XLSX.writeFile(wb, `Day${dayNumber}_All_Engineers_Tasks.xlsx`);
  }

  const totalDays = state.workingDays;

  // Build the day schedule applying deletions, blocklist, MCD weight, and pulling in replacements
  function buildScheduleWithDeletions(dayNum) {
    return results.map((tech) => {
      const target = getTargetForTech(tech.id);
      const key = `${tech.id}_${dayNum}`;
      const deleted = deletedSerials[key] || new Set();

      // Filter blocklist
      const engineerBlocked = engineerBlocklists[tech.name] || new Set();
      const allowedParts = tech.parts.filter((p) => {
        const id = (p.partId || "").toUpperCase();
        return !blocklist.has(id) && !engineerBlocked.has(id);
      });

      // Separate DCD and MCD parts (maintaining priority order from allocator)
      const dcdPool = allowedParts.filter((p) => p.source !== "mcd");
      const mcdPool = allowedParts.filter((p) => p.source === "mcd");

      // Calls always come through in full slider only controls priority/stock split
      const dcdUrgent = dcdPool.filter((p) => p.priority === "urgent");
      const mcdUrgent = mcdPool.filter((p) => p.priority === "urgent");
      const dcdNonUrgent = dcdPool.filter((p) => p.priority !== "urgent");
      const mcdNonUrgent = mcdPool.filter((p) => p.priority !== "urgent");

      // Remaining capacity after calls
      const urgentCount = dcdUrgent.length + mcdUrgent.length;
      const remaining = Math.max(0, target - urgentCount);
      const mcdRemaining = Math.round((mcdWeight / 100) * remaining);
      const dcdRemaining = remaining - mcdRemaining;

      const dcdNonStart = (dayNum - 1) * dcdRemaining;
      const mcdNonStart = (dayNum - 1) * mcdRemaining;
      const dcdWindow = dcdNonUrgent.slice(
        dcdNonStart,
        dcdNonStart + dcdRemaining,
      );
      const mcdWindow = mcdNonUrgent.slice(
        mcdNonStart,
        mcdNonStart + mcdRemaining,
      );

      // Calls first (both DCD and MCD), then priority stock, then stock
      const interleaved = [
        ...dcdUrgent,
        ...mcdUrgent,
        ...dcdWindow.filter((p) => p.priority === "rep_req"),
        ...mcdWindow.filter((p) => p.priority === "rep_req"),
        ...dcdWindow.filter((p) => p.priority === "stock"),
        ...mcdWindow.filter((p) => p.priority === "stock"),
      ];

      const activeParts = interleaved.filter((p) => !deleted.has(p.serial));
      const needed = target - activeParts.length;

      // Pull replacements from overflow of both non-urgent pools
      const usedSerials = new Set([
        ...interleaved.map((p) => p.serial),
        ...deleted,
      ]);
      const replacements = [];
      if (needed > 0) {
        const overflow = [
          ...dcdNonUrgent.slice(dcdNonStart + dcdRemaining),
          ...mcdNonUrgent.slice(mcdNonStart + mcdRemaining),
        ];
        for (const p of overflow) {
          if (replacements.length >= needed) break;
          if (!usedSerials.has(p.serial) && !deleted.has(p.serial)) {
            replacements.push({ ...p, isReplacement: true });
            usedSerials.add(p.serial);
          }
        }
      }

      const dayParts = [...activeParts, ...replacements];
      const dayCommission = dayParts.reduce(
        (s, p) => s + getDisplayPart(tech.id, p, dayNum).value,
        0,
      );
      return {
        ...tech,
        dayParts,
        dayCommission,
        deletedCount: deleted.size,
        target,
      };
    });
  }

  function deletePart(techId, serial) {
    const key = `${techId}_${dayNumber}`;
    rememberListState("remove row");
    setDeletedSerials((prev) => {
      const next = { ...prev };
      next[key] = new Set(prev[key] || []);
      next[key].add(serial);
      return next;
    });
  }

  function undoDeletes(techId) {
    const key = `${techId}_${dayNumber}`;
    if (!(deletedSerials[key]?.size > 0)) return;
    rememberListState("restore removed rows");
    setDeletedSerials((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  const daySchedule = buildScheduleWithDeletions(dayNumber);
  const totalDayUnits = daySchedule.reduce((s, t) => s + t.dayParts.length, 0);
  const totalDayCommission = daySchedule.reduce(
    (s, t) => s + t.dayCommission,
    0,
  );
  const avgDayCommission =
    daySchedule.length > 0 ? totalDayCommission / daySchedule.length : 0;
  const commissions = daySchedule.map((t) => t.dayCommission);
  const spread =
    commissions.length > 0
      ? Math.max(...commissions) - Math.min(...commissions)
      : 0;

  const [openCards, setOpenCards] = useState({});
  function toggleCard(techId) {
    setOpenCards((prev) => ({ ...prev, [techId]: !prev[techId] }));
  }

  const prevDay = () => setDayNumber((d) => Math.max(1, d - 1));
  const nextDay = () => setDayNumber((d) => Math.min(totalDays, d + 1));

  // Keyboard shortcuts
  // / : prev/next day
  // Shift+ / Shift+ : also works
  useEffect(() => {
    function handleKey(e) {
      // Don't fire if focus is in an input/textarea
      if (
        ["INPUT", "TEXTAREA", "SELECT"].includes(
          document.activeElement?.tagName,
        )
      )
        return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevDay();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        nextDay();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [dayNumber, totalDays]);

  useEffect(() => {
    setCopyChooser(null);
    setDraggedReplacement(null);
    draggedReplacementRef.current = null;
    setDropTarget(null);
  }, [dayNumber]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {copyChooser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(3,10,22,0.82)",
            zIndex: 8500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#0d1b35",
              border: "1px solid #1e3a6e",
              borderRadius: 12,
              padding: 20,
              width: "100%",
              maxWidth: 720,
              maxHeight: "82vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{ color: "#f0f6ff", fontWeight: 800, fontSize: 13 }}
                >
                  Choose rows to replace
                </div>
                <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 4 }}>
                  Copy{" "}
                  {
                    getDisplayPart(copyChooser.techId, copyChooser.sourcePart)
                      .partId
                  }{" "}
                  into selected rows.
                </div>
              </div>
              <button
                onClick={() => setCopyChooser(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#f87171",
                  cursor: "pointer",
                  fontSize: 18,
                }}
              >
                x
              </button>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                marginBottom: 14,
              }}
            >
              {copyChooser.candidates.map((candidate) => {
                const display = getDisplayPart(copyChooser.techId, candidate);
                const checked = copyChooser.selected?.has(candidate.serial);
                return (
                  <label
                    key={candidate.serial}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: checked ? "#10243a" : "#081224",
                      border: `1px solid ${checked ? "#4f8ef766" : "#1e3056"}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!checked}
                      onChange={() => toggleCopyCandidate(candidate.serial)}
                    />
                    <span
                      style={{
                        color: checked ? "#7ab4f7" : "#7aa0d4",
                        fontFamily: "monospace",
                        fontSize: 11,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {display.partId}
                    </span>
                    <GradeTag grade={display.grade} />
                    <span
                      style={{
                        color: "#4f6fa0",
                        fontFamily: "monospace",
                        fontSize: 11,
                      }}
                    >
                      {display.value.toFixed(2)}
                    </span>
                  </label>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{ color: "#4f6fa0", fontSize: 11, marginRight: "auto" }}
              >
                {copyChooser.selected?.size || 0} row
                {copyChooser.selected?.size === 1 ? "" : "s"} selected
              </span>
              <Btn variant="secondary" onClick={() => setCopyChooser(null)}>
                Cancel
              </Btn>
              <Btn
                onClick={applySelectedCopies}
                disabled={!copyChooser.selected?.size}
              >
                Apply Copies
              </Btn>
            </div>
          </div>
        </div>
      )}
      {/* Controls */}
      <Card>
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <Label>Day Selector</Label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onClick={prevDay}
                disabled={dayNumber <= 1}
                style={{
                  background: "#1a2744",
                  border: "1px solid #1e3056",
                  color: dayNumber <= 1 ? "#1e3056" : "#7aa0d4",
                  borderRadius: 7,
                  width: 34,
                  height: 34,
                  cursor: dayNumber <= 1 ? "default" : "pointer",
                  fontSize: 16,
                  fontFamily: "inherit",
                }}
              ></button>
              <div
                style={{
                  background: "#081224",
                  border: "1px solid #1e3056",
                  borderRadius: 8,
                  padding: "8px 16px",
                  color: "#f0f6ff",
                  fontSize: 16,
                  fontWeight: 800,
                  minWidth: 100,
                  textAlign: "center",
                }}
              >
                Day {dayNumber}
                <div
                  style={{
                    color: "#3d5280",
                    fontSize: 9,
                    fontWeight: 400,
                    letterSpacing: 1,
                  }}
                >
                  of {totalDays} working days
                </div>
              </div>
              <button
                onClick={nextDay}
                disabled={dayNumber >= totalDays}
                style={{
                  background: "#1a2744",
                  border: "1px solid #1e3056",
                  color: dayNumber >= totalDays ? "#1e3056" : "#7aa0d4",
                  borderRadius: 7,
                  width: 34,
                  height: 34,
                  cursor: dayNumber >= totalDays ? "default" : "pointer",
                  fontSize: 16,
                  fontFamily: "inherit",
                }}
              ></button>
            </div>
          </div>
          <div style={{ minWidth: 180 }}>
            <Label>Global Parts Per Day (Default)</Label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input
                type="number"
                min={1}
                max={50}
                value={dailyTarget}
                onChange={(e) => setDailyTarget(e.target.value)}
                style={{
                  width: 80,
                  textAlign: "center",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              />
              <div style={{ color: "#3d5280", fontSize: 10, lineHeight: 1.4 }}>
                Monthly avg:
                <br />
                <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                  {rawDailyTarget.toFixed(1)}/day
                </span>
              </div>
            </div>
            <div style={{ color: "#3d5280", fontSize: 9, marginTop: 4 }}>
              Override per-engineer below
            </div>
          </div>

          {/* MCD weight slider */}
          <div style={{ minWidth: 220 }}>
            <Label>DCD MCD Daily Mix</Label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{ color: "#4f8ef7", fontSize: 9, whiteSpace: "nowrap" }}
              >
                DCD
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={mcdWeight}
                onChange={(e) => setMcdWeight(parseInt(e.target.value))}
                style={{ flex: 1, accentColor: "#34d399", cursor: "pointer" }}
              />
              <span
                style={{ color: "#34d399", fontSize: 9, whiteSpace: "nowrap" }}
              >
                MCD
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              <span style={{ color: "#4f8ef7", fontSize: 10 }}>
                {100 - mcdWeight}% DCD
              </span>
              <span
                style={{
                  color: mcdWeight === 50 ? "#f0f6ff" : "#34d399",
                  fontSize: 10,
                  fontWeight: mcdWeight !== 50 ? 700 : 400,
                }}
              >
                {mcdWeight === 50 ? "Balanced" : `${mcdWeight}% MCD`}
              </span>
              <span style={{ color: "#34d399", fontSize: 10 }}>
                {mcdWeight}% MCD
              </span>
            </div>
            <div style={{ color: "#3d5280", fontSize: 9, marginTop: 3 }}>
              {dailyTarget} parts/day{" "}
              {Math.round((mcdWeight / 100) * dailyTarget)} MCD +{" "}
              {dailyTarget - Math.round((mcdWeight / 100) * dailyTarget)} DCD
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              flex: 2,
              minWidth: 300,
            }}
          >
            <StatBox
              label="Units Today"
              value={totalDayUnits}
              color="#4f8ef7"
              sub={`${dailyTarget} per engineer`}
            />
            <StatBox
              label="Total Commission"
              value={`${totalDayCommission.toFixed(2)}`}
              color="#a78bfa"
              sub={`${avgDayCommission.toFixed(2)} avg/eng`}
            />
            <StatBox
              label="Fairness Spread"
              value={`${spread.toFixed(2)}`}
              color={
                spread <= 0.5
                  ? "#10b981"
                  : spread <= 1.5
                    ? "#f59e0b"
                    : "#ef4444"
              }
              sub={
                spread <= 0.5 ? "Excellent" : spread <= 1.5 ? "Good" : "Uneven"
              }
            />
          </div>
        </div>
        <div
          style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {[...state.tiers]
            .sort((a, b) => a.daily - b.daily)
            .map((tier) => {
              const allHit = daySchedule.every(
                (t) =>
                  t.dayParts.filter((p) => p.grade !== "BER").length >=
                  tier.daily,
              );
              const c = allHit ? "#10b981" : "#3d5280";
              const nonBerTarget =
                daySchedule.length > 0
                  ? daySchedule.reduce(
                      (s, t) =>
                        s + t.dayParts.filter((p) => p.grade !== "BER").length,
                      0,
                    ) / daySchedule.length
                  : 0;
              const pct = Math.min(100, (nonBerTarget / tier.daily) * 100);
              return (
                <div
                  key={tier.bonus}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    background: "#081224",
                    border: `1px solid ${c}44`,
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ color: c, fontSize: 10, letterSpacing: 1 }}>
                      {allHit ? "" : ""} {tier.bonus} tier
                    </span>
                    <span style={{ color: "#4f6fa0", fontSize: 10 }}>
                      {tier.daily}/day needed
                    </span>
                  </div>
                  <div
                    style={{
                      background: "#1a2744",
                      borderRadius: 3,
                      height: 4,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: allHit ? "#10b981" : "#3d5280",
                        borderRadius: 3,
                        transition: "width 0.3s",
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </Card>

      {/* Per-engineer daily cards with deletable parts */}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <Label>Day {dayNumber} Engineer Task List</Label>
          <Btn
            variant="secondary"
            style={{ fontSize: 10, padding: "7px 16px" }}
            onClick={undoLastListChange}
            disabled={undoStack.length === 0}
            title={
              undoStack.length
                ? `Undo ${undoStack[undoStack.length - 1].label}`
                : "No list changes to undo"
            }
          >
            UNDO
          </Btn>
          <Btn
            variant="success"
            style={{ fontSize: 10, padding: "7px 16px" }}
            onClick={exportAllEngineersXLSX}
          >
            EXPORT ALL TO EXCEL
          </Btn>
        </div>
        <div style={{ color: "#3d5280", fontSize: 11, marginBottom: 14 }}>
          <strong style={{ color: "#ef4444" }}></strong> removes a part and
          pulls in the next from queue. &nbsp;
          <strong style={{ color: "#f59e0b" }}></strong> lets you manually
          replace a part with one you actually worked. &nbsp;
          <strong style={{ color: "#7ab4f7" }}>x</strong> lets you choose rows
          to replace with the same unit. &nbsp;
          <span style={{ color: "#3d5280" }}>
            Drag a row onto another row to replace it. &nbsp;
          </span>
          <span style={{ color: "#3d5280" }}> arrow keys navigate days </span>
          <span style={{ color: "#3d5280" }}>Del = remove focused part</span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 14,
          }}
        >
          {daySchedule.map((tech, i) => {
            const col = TECH_COLORS[i % TECH_COLORS.length];
            const urgentToday = tech.dayParts.filter(
              (p) => p.priority === "urgent",
            ).length;
            const repReqToday = tech.dayParts.filter(
              (p) => p.priority === "rep_req",
            ).length;
            const replacements = tech.dayParts.filter(
              (p) => p.isReplacement,
            ).length;
            const gradeCounts = {};
            GRADE_ORDER.forEach((g) => {
              gradeCounts[g] = tech.dayParts.filter(
                (p) => getDisplayPart(tech.id, p).grade === g,
              ).length;
            });
            const open = !!openCards[tech.id];
            const hasOverride = perEngTarget[tech.id] !== undefined;
            const engTarget = tech.target;

            return (
              <div
                key={tech.id}
                style={{
                  background: "#081224",
                  border: `1px solid ${col}33`,
                  borderRadius: 11,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${col}18 0%, transparent 80%)`,
                    padding: "16px 18px",
                    borderBottom: `1px solid ${col}22`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{ color: col, fontWeight: 800, fontSize: 15 }}
                      >
                        {tech.name}
                      </div>
                      <div
                        style={{ color: "#3d5280", fontSize: 11, marginTop: 2 }}
                      >
                        {tech.dayParts.length} parts today{" "}
                        {tech.dayCommission.toFixed(2)}
                      </div>
                      {/* Per-engineer target override */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 6,
                        }}
                      >
                        <span
                          style={{
                            color: "#3d5280",
                            fontSize: 9,
                            letterSpacing: 1,
                          }}
                        >
                          PARTS/DAY:
                        </span>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={engTarget}
                          onChange={(e) =>
                            setEngTarget(tech.id, e.target.value)
                          }
                          style={{
                            width: 48,
                            textAlign: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            background: "#060e1e",
                            border: `1px solid ${hasOverride ? col + "88" : "#1e3056"}`,
                            borderRadius: 5,
                            color: hasOverride ? col : "#c8d8f0",
                            padding: "2px 4px",
                            fontFamily: "inherit",
                          }}
                        />
                        {hasOverride && (
                          <button
                            onClick={() => resetEngTarget(tech.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#3d5280",
                              fontSize: 9,
                              cursor: "pointer",
                              padding: 0,
                            }}
                            title="Reset to global default"
                          >
                            global
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          color: "#f0f6ff",
                          fontSize: 20,
                          fontWeight: 800,
                        }}
                      >
                        {tech.dayCommission.toFixed(2)}
                      </div>
                      <div style={{ color: "#3d5280", fontSize: 10 }}>
                        today's target
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginTop: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    {urgentToday > 0 && (
                      <Badge color="#ef4444"> {urgentToday} urgent</Badge>
                    )}
                    {repReqToday > 0 && (
                      <Badge color="#f59e0b"> {repReqToday} rep req</Badge>
                    )}
                    {tech.deletedCount > 0 && (
                      <Badge color="#64748b">
                        {" "}
                        {tech.deletedCount} removed
                      </Badge>
                    )}
                    {replacements > 0 && (
                      <Badge color="#10b981"> {replacements} replaced</Badge>
                    )}
                    {tech.dayParts.length < dailyTarget && (
                      <Badge color="#3d5280">
                        {" "}
                        Only {tech.dayParts.length} available
                      </Badge>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      marginTop: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    {GRADE_ORDER.filter((g) => gradeCounts[g] > 0).map((g) => (
                      <div
                        key={g}
                        style={{ textAlign: "center", flex: 1, minWidth: 36 }}
                      >
                        <GradeTag grade={g} />
                        <div
                          style={{
                            color: "#f0f6ff",
                            fontSize: 13,
                            fontWeight: 700,
                            marginTop: 3,
                          }}
                        >
                          {gradeCounts[g]}
                        </div>
                        <div style={{ color: "#3d5280", fontSize: 9 }}>
                          {(
                            gradeCounts[g] * (state.gradeValues[g] || 0)
                          ).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Parts list with delete + tick + manual replace buttons */}
                <div style={{ padding: "0 18px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    <button
                      onClick={() => toggleCard(tech.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#3d5280",
                        cursor: "pointer",
                        fontSize: 11,
                        padding: "10px 0",
                        textAlign: "left",
                      }}
                    >
                      {open
                        ? " Hide parts list"
                        : ` Show all ${tech.dayParts.length} parts`}
                    </button>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      {tech.deletedCount > 0 && (
                        <button
                          onClick={() => undoDeletes(tech.id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#f59e0b",
                            cursor: "pointer",
                            fontSize: 10,
                            padding: "4px 0",
                          }}
                        >
                          Restore removed
                        </button>
                      )}
                      <button
                        onClick={() => {
                          exportEngineerXLSX(
                            tech,
                            tech.dayParts,
                            new Set(),
                            dayNumber,
                          );
                        }}
                        style={{
                          background: "#0a2010",
                          border: "1px solid #14532d",
                          color: "#4ade80",
                          borderRadius: 6,
                          fontSize: 9,
                          padding: "3px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Excel
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div
                      style={{
                        maxHeight: 400,
                        overflowY: "auto",
                        paddingBottom: 12,
                      }}
                    >
                      {["urgent", "rep_req", "stock"].map((prio) => {
                        const sectionParts = ungradedFirst(
                          tech.dayParts.filter((p) => p.priority === prio),
                          (p) => {
                            const rep =
                              manualReplacements[
                                `${tech.id}_${dayNumber}_${p.serial}`
                              ];
                            return rep ? rep.grade : p.grade;
                          },
                        );
                        if (sectionParts.length === 0) return null;
                        const sectionLabel =
                          prio === "urgent"
                            ? " CALL LIST (URGENT)"
                            : prio === "rep_req"
                              ? " REPAIR REQUIRED"
                              : " STOCK";
                        const sectionColor =
                          prio === "urgent"
                            ? "#ef4444"
                            : prio === "rep_req"
                              ? "#f59e0b"
                              : "#4f8ef7";
                        return (
                          <div key={prio}>
                            <div
                              style={{
                                color: sectionColor,
                                fontSize: 10,
                                letterSpacing: 1,
                                margin: "8px 0 6px",
                              }}
                            >
                              {sectionLabel}
                            </div>
                            {sectionParts.map((p, j) => {
                              const manKey = `${tech.id}_${dayNumber}_${p.serial}`;
                              const manRep = manualReplacements[manKey];
                              const isReplacing = replacingSerial === manKey;
                              const displayId = manRep
                                ? manRep.partId
                                : p.ref || p.partId;
                              const displayGrade = manRep
                                ? manRep.grade
                                : p.grade;
                              const displayVal = manRep
                                ? manRep.value
                                : state.gradeValues[p.grade] || 0;

                              return (
                                <div key={j}>
                                  <div
                                    tabIndex={0}
                                    draggable
                                    data-task-drop-target="true"
                                    data-tech-id={tech.id}
                                    data-serial={p.serial}
                                    onDragStart={(e) => {
                                      const replacement =
                                        buildReplacementFromPart(tech.id, p);
                                      const payload = {
                                        sourceTechId: tech.id,
                                        sourceSerial: p.serial,
                                        replacement,
                                      };
                                      draggedReplacementRef.current = payload;
                                      setDraggedReplacement(payload);
                                      e.dataTransfer.effectAllowed = "copy";
                                      e.dataTransfer.setData(
                                        "application/json",
                                        JSON.stringify(payload),
                                      );
                                      e.dataTransfer.setData(
                                        "text/plain",
                                        replacement.partId,
                                      );
                                    }}
                                    onDragOver={(e) => {
                                      if (
                                        !draggedReplacementRef.current &&
                                        !draggedReplacement
                                      )
                                        return;
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = "copy";
                                      setDropTarget(manKey);
                                    }}
                                    onDragEnter={(e) => {
                                      if (
                                        !draggedReplacementRef.current &&
                                        !draggedReplacement
                                      )
                                        return;
                                      e.preventDefault();
                                      setDropTarget(manKey);
                                    }}
                                    onDragLeave={() => {
                                      setDropTarget((current) =>
                                        current === manKey ? null : current,
                                      );
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const activeDrag =
                                        readDraggedReplacement(e);
                                      completeDropReplacement(
                                        tech.id,
                                        p.serial,
                                        activeDrag,
                                      );
                                    }}
                                    onMouseUp={() => {
                                      if (!draggedReplacementRef.current)
                                        return;
                                      completeDropReplacement(
                                        tech.id,
                                        p.serial,
                                        draggedReplacementRef.current,
                                      );
                                    }}
                                    onPointerUp={() => {
                                      if (!draggedReplacementRef.current)
                                        return;
                                      completeDropReplacement(
                                        tech.id,
                                        p.serial,
                                        draggedReplacementRef.current,
                                      );
                                    }}
                                    onDragEnd={(e) => {
                                      if (
                                        completeDropFromPoint(
                                          e.clientX,
                                          e.clientY,
                                        )
                                      )
                                        return;
                                      window.setTimeout(() => {
                                        draggedReplacementRef.current = null;
                                        setDraggedReplacement(null);
                                        setDropTarget(null);
                                      }, 80);
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Delete" ||
                                        e.key === "Backspace"
                                      ) {
                                        e.preventDefault();
                                        deletePart(tech.id, p.serial);
                                      }
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "5px 0",
                                      borderBottom: isReplacing
                                        ? "none"
                                        : dropTarget === manKey
                                          ? "1px solid #4f8ef7"
                                          : "1px solid #0d1b35",
                                      background:
                                        dropTarget === manKey
                                          ? "#4f8ef71a"
                                          : manRep
                                            ? "#f59e0b08"
                                            : p.isReplacement
                                              ? "#4f8ef708"
                                              : "transparent",
                                      outline: "none",
                                      cursor: "grab",
                                    }}
                                    title="Drag this row onto another row to replace it"
                                  >
                                    {/* Delete button */}
                                    <button
                                      onClick={() =>
                                        deletePart(tech.id, p.serial)
                                      }
                                      title="Remove next in queue replaces it"
                                      style={{
                                        background: "#2a1020",
                                        border: "1px solid #3d1830",
                                        color: "#f87171",
                                        borderRadius: 4,
                                        width: 18,
                                        height: 18,
                                        cursor: "pointer",
                                        fontSize: 10,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    ></button>
                                    {/* Manual replace button */}
                                    <button
                                      onClick={() => {
                                        if (isReplacing) {
                                          setReplacingSerial(null);
                                          setReplaceInput("");
                                          setReplaceError("");
                                        } else {
                                          setReplacingSerial(manKey);
                                          setReplaceInput("");
                                          setReplaceError("");
                                        }
                                      }}
                                      title="Manually replace with a different part"
                                      style={{
                                        background: manRep
                                          ? "#78350f"
                                          : "#1a2010",
                                        border: `1px solid ${manRep ? "#d9770633" : "#1e3a1e"}`,
                                        color: manRep ? "#fbbf24" : "#4ade80",
                                        borderRadius: 4,
                                        width: 18,
                                        height: 18,
                                        cursor: "pointer",
                                        fontSize: 10,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    ></button>
                                    <button
                                      onClick={() =>
                                        openSameUnitChooser(tech.id, p)
                                      }
                                      title="Add more of this same unit and choose which rows it replaces"
                                      style={{
                                        background: "#10243a",
                                        border: "1px solid #1e3a6e",
                                        color: "#7ab4f7",
                                        borderRadius: 4,
                                        width: 24,
                                        height: 18,
                                        cursor: "pointer",
                                        fontSize: 9,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                      }}
                                    >
                                      x
                                    </button>
                                    {manRep && (
                                      <span
                                        style={{
                                          color: "#f59e0b",
                                          fontSize: 9,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {manRep.duplicateOf ? "COPY" : "SUB"}
                                      </span>
                                    )}
                                    {!manRep && p.isReplacement && (
                                      <span
                                        style={{
                                          color: "#10b981",
                                          fontSize: 9,
                                          flexShrink: 0,
                                        }}
                                      >
                                        NEW
                                      </span>
                                    )}
                                    <span
                                      style={{
                                        color: manRep
                                          ? "#f59e0b"
                                          : prio === "urgent"
                                            ? "#ef4444"
                                            : prio === "rep_req"
                                              ? "#c8d8f0"
                                              : "#7aa0d4",
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {displayId}
                                    </span>
                                    {p.source === "mcd" && (
                                      <span
                                        style={{
                                          color: "#34d399",
                                          fontSize: 9,
                                          flexShrink: 0,
                                        }}
                                      >
                                        MCD
                                      </span>
                                    )}
                                    <span style={{ flexShrink: 0 }}>
                                      <GradeTag grade={displayGrade} />
                                    </span>
                                    <span
                                      style={{
                                        color: "#4f6fa0",
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {displayVal.toFixed(2)}
                                    </span>
                                  </div>
                                  {/* Inline manual replace input */}
                                  {isReplacing && (
                                    <div
                                      style={{
                                        background: "#0d1b2a",
                                        border: "1px solid #1e3a6e",
                                        borderRadius: 7,
                                        padding: "10px 12px",
                                        marginBottom: 6,
                                      }}
                                    >
                                      <div
                                        style={{
                                          color: "#4f8ef7",
                                          fontSize: 10,
                                          marginBottom: 6,
                                        }}
                                      >
                                        Enter the Part ID you actually worked:
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: 8,
                                          alignItems: "center",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <input
                                          autoFocus
                                          value={replaceInput}
                                          onChange={(e) => {
                                            setReplaceInput(
                                              e.target.value.toUpperCase(),
                                            );
                                            setReplaceError("");
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter")
                                              submitManualReplace(
                                                tech.id,
                                                p.serial,
                                              );
                                            if (e.key === "Escape") {
                                              setReplacingSerial(null);
                                              setReplaceInput("");
                                              setReplaceError("");
                                            }
                                          }}
                                          placeholder="e.g. PAD-NV200-NOTE-READER-BPS"
                                          style={{
                                            ...css.input,
                                            flex: 1,
                                            minWidth: 180,
                                            fontSize: 11,
                                            textTransform: "uppercase",
                                          }}
                                        />
                                        <Btn
                                          style={{
                                            padding: "6px 14px",
                                            fontSize: 10,
                                          }}
                                          onClick={() =>
                                            submitManualReplace(
                                              tech.id,
                                              p.serial,
                                            )
                                          }
                                        >
                                          CONFIRM
                                        </Btn>
                                        {manRep && (
                                          <button
                                            onClick={() =>
                                              clearManualReplace(
                                                tech.id,
                                                p.serial,
                                              )
                                            }
                                            style={{
                                              background: "none",
                                              border: "1px solid #3d5280",
                                              borderRadius: 6,
                                              color: "#64748b",
                                              fontSize: 10,
                                              padding: "5px 10px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            Clear sub
                                          </button>
                                        )}
                                      </div>
                                      {replaceError && (
                                        <div
                                          style={{
                                            color: "#f87171",
                                            fontSize: 10,
                                            marginTop: 6,
                                          }}
                                        >
                                          {replaceError}
                                        </div>
                                      )}
                                      {replaceInput &&
                                        combinedLookup[
                                          replaceInput.toUpperCase()
                                        ] && (
                                          <div
                                            style={{
                                              color: "#4ade80",
                                              fontSize: 10,
                                              marginTop: 6,
                                            }}
                                          >
                                            Found:{" "}
                                            <strong>
                                              {replaceInput.toUpperCase()}
                                            </strong>
                                            Grade{" "}
                                            <strong>
                                              {
                                                combinedLookup[
                                                  replaceInput.toUpperCase()
                                                ].grade
                                              }
                                            </strong>{" "}
                                            {(
                                              state.gradeValues[
                                                combinedLookup[
                                                  replaceInput.toUpperCase()
                                                ].grade
                                              ] || 0
                                            ).toFixed(2)}
                                          </div>
                                        )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <BlocklistPanel
        blocklist={blocklist}
        blocklistInput={blocklistInput}
        setBlocklistInput={setBlocklistInput}
        addToBlocklist={addToBlocklist}
        removeFromBlocklist={removeFromBlocklist}
        setBlocklist={setBlocklist}
        engineers={results}
        blocklistScope={blocklistScope}
        setBlocklistScope={setBlocklistScope}
        blocklistEngineer={blocklistEngineer}
        setBlocklistEngineer={setBlocklistEngineer}
        engineerBlocklists={engineerBlocklists}
        removeFromEngineerBlocklist={removeFromEngineerBlocklist}
        blocklistServerMsg={blocklistServerMsg}
        onSaveBlocklist={onSaveBlocklist}
        onReloadBlocklist={onReloadBlocklist}
        blocklistServerBusy={blocklistServerBusy}
      />

      <Card>
        <Label>Monthly Schedule Overview All {totalDays} Days</Label>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}
          >
            <thead>
              <tr
                style={{
                  color: "#3d5280",
                  fontSize: 10,
                  borderBottom: "1px solid #1a2744",
                }}
              >
                <th
                  style={{
                    textAlign: "left",
                    padding: "7px 10px",
                    position: "sticky",
                    left: 0,
                    background: "#0d1b35",
                  }}
                >
                  ENGINEER
                </th>
                {Array.from({ length: totalDays }, (_, d) => (
                  <th
                    key={d}
                    onClick={() => setDayNumber(d + 1)}
                    style={{
                      textAlign: "center",
                      padding: "7px 6px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      color: dayNumber === d + 1 ? "#4f8ef7" : "#3d5280",
                      borderBottom:
                        dayNumber === d + 1 ? "2px solid #4f8ef7" : "none",
                      fontWeight: dayNumber === d + 1 ? 700 : 400,
                      minWidth: 32,
                    }}
                  >
                    D{d + 1}
                  </th>
                ))}
                <th style={{ textAlign: "right", padding: "7px 10px" }}>
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map((tech, i) => {
                const col = TECH_COLORS[i % TECH_COLORS.length];
                return (
                  <tr key={tech.id} style={{ borderTop: "1px solid #1a2744" }}>
                    <td
                      style={{
                        padding: "7px 10px",
                        color: col,
                        fontWeight: 700,
                        position: "sticky",
                        left: 0,
                        background: "#0d1b35",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tech.name}
                    </td>
                    {Array.from({ length: totalDays }, (_, d) => {
                      const target = getTargetForTech(tech.id);
                      const mcdSlots = Math.round((mcdWeight / 100) * target);
                      const dcdSlots = target - mcdSlots;
                      const engineerBlocked =
                        engineerBlocklists[tech.name] || new Set();
                      const allowed = tech.parts.filter((p) => {
                        const id = (p.partId || "").toUpperCase();
                        return !blocklist.has(id) && !engineerBlocked.has(id);
                      });
                      const dcdPool = allowed.filter((p) => p.source !== "mcd");
                      const mcdPool = allowed.filter((p) => p.source === "mcd");
                      const dcdW = dcdPool.slice(
                        d * dcdSlots,
                        (d + 1) * dcdSlots,
                      );
                      const mcdW = mcdPool.slice(
                        d * mcdSlots,
                        (d + 1) * mcdSlots,
                      );
                      const dayParts = [...dcdW, ...mcdW];
                      const dayCom = dayParts.reduce(
                        (s, p) => s + (state.gradeValues[p.grade] || 0),
                        0,
                      );
                      const isActive = dayNumber === d + 1;
                      const hasWork = dayParts.length > 0;
                      const hasDeletes =
                        (deletedSerials[`${tech.id}_${d + 1}`] || new Set())
                          .size > 0;
                      return (
                        <td
                          key={d}
                          onClick={() => setDayNumber(d + 1)}
                          style={{
                            padding: "7px 4px",
                            textAlign: "center",
                            cursor: "pointer",
                            background: isActive ? "#4f8ef711" : "transparent",
                            borderRadius: 4,
                          }}
                        >
                          {hasWork ? (
                            <div>
                              <div
                                style={{
                                  color: isActive ? "#4f8ef7" : "#c8d8f0",
                                  fontWeight: isActive ? 700 : 400,
                                }}
                              >
                                {dayParts.length}
                                {hasDeletes ? "*" : ""}
                              </div>
                              <div style={{ color: "#3d5280", fontSize: 9 }}>
                                {dayCom.toFixed(0)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "#1e3056" }}></span>
                          )}
                        </td>
                      );
                    })}
                    <td
                      style={{
                        padding: "7px 10px",
                        textAlign: "right",
                        color: "#a78bfa",
                        fontFamily: "monospace",
                        fontWeight: 700,
                      }}
                    >
                      {tech.totalUnits} {tech.commission.toFixed(0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ color: "#3d5280", fontSize: 10, marginTop: 10 }}>
          Click any day column to jump to it. * = parts removed on that day.
        </div>
      </Card>
    </div>
  );
}

// Step 3: Results
function StepResults({ state, setState, onBack, onReset }) {
  const [results, setResults] = useState(null);
  const [view, setView] = useState("daily");
  const [rerunDays, setRerunDays] = useState(state.workingDays);
  const [rerunNames, setRerunNames] = useState(state.techNames);
  const [showRerun, setShowRerun] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [parsedReportOpen, setParsedReportOpen] = useState(false);
  const [parsedReportFilter, setParsedReportFilter] = useState("all");
  const [shareDay, setShareDay] = useState(1);
  const [shareTarget, setShareTarget] = useState(
    Math.ceil(state.workingDays > 0 ? state.tiers?.[0]?.daily || 18 : 18),
  );
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved", "error"
  const server = useServer();

  const [clientWeight] = useState(0);
  const parsedPartReport = useMemo(() => buildParsedPartReport(state), [state]);
  const filteredParsedPartReport = useMemo(
    () =>
      filterParsedPartReport(
        parsedPartReport,
        state.clients || [],
        parsedReportFilter,
      ),
    [parsedPartReport, state.clients, parsedReportFilter],
  );
  const parsedReportFilterLabel = useMemo(() => {
    if (parsedReportFilter === "clients-only") return "configured clients only";
    if (parsedReportFilter === "clients-excluded")
      return "configured clients excluded";
    if (parsedReportFilter.startsWith("client:")) {
      const clientId = parsedReportFilter.slice("client:".length);
      const client = (state.clients || []).find((c) => c.id === clientId);
      return `${client?.name || clientId.toUpperCase()} only`;
    }
    return "all parts";
  }, [parsedReportFilter, state.clients]);

  // Shared daily target persists across Monthly Daily view switches, default 24
  const [sharedDailyTarget, setSharedDailyTarget] = useState(24);

  useEffect(() => {
    setShareTarget(sharedDailyTarget);
  }, [sharedDailyTarget]);

  // Blocklist lives here so it persists across Monthly Daily view switches
  const [blocklist, setBlocklist] = useState(new Set());
  const [engineerBlocklists, setEngineerBlocklists] = useState({});
  const [blocklistScope, setBlocklistScope] = useState("global");
  const [blocklistEngineer, setBlocklistEngineer] = useState(
    state.techNames?.[0] || "",
  );
  const [blocklistInput, setBlocklistInput] = useState("");
  const [blocklistServerMsg, setBlocklistServerMsg] = useState("");
  const [blocklistServerBusy, setBlocklistServerBusy] = useState(false);
  const [deletedSerials, setDeletedSerials] = useState({});
  const [manualReplacements, setManualReplacements] = useState({});

  function applyServerBlocklist(data) {
    const globalIds = (data?.global || [])
      .map((item) =>
        String(item.partId || item.part_id || item || "")
          .trim()
          .toUpperCase(),
      )
      .filter(Boolean);
    const engineerSets = {};
    Object.entries(data?.engineers || {}).forEach(([engineer, items]) => {
      const ids = (items || [])
        .map((item) =>
          String(item.partId || item.part_id || item || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean);
      if (ids.length) engineerSets[engineer] = new Set(ids);
    });
    setBlocklist(new Set(globalIds));
    setEngineerBlocklists(engineerSets);
  }

  async function loadSavedBlocklist(showMessage = true) {
    if (server?.serverStatus !== "online") {
      if (showMessage)
        setBlocklistServerMsg("Server offline - saved blocklist unavailable");
      return;
    }
    setBlocklistServerBusy(true);
    const result = await server.fetchAllocationBlocklist();
    setBlocklistServerBusy(false);
    if (result) {
      applyServerBlocklist(result);
      if (showMessage) {
        setBlocklistServerMsg("Loaded saved blocklist");
        setTimeout(() => setBlocklistServerMsg(""), 2500);
      }
    } else if (showMessage) {
      setBlocklistServerMsg("Could not load saved blocklist");
    }
  }

  async function saveCurrentBlocklist() {
    if (server?.serverStatus !== "online") {
      setBlocklistServerMsg("Server offline - blocklist not saved");
      return;
    }
    const engineers = {};
    Object.entries(engineerBlocklists).forEach(([engineer, ids]) => {
      const clean = [...(ids || [])]
        .map((id) =>
          String(id || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean);
      if (clean.length) engineers[engineer] = clean;
    });
    setBlocklistServerBusy(true);
    const result = await server.replaceAllocationBlocklist(
      [...blocklist]
        .map((id) =>
          String(id || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
      engineers,
    );
    setBlocklistServerBusy(false);
    if (result?.blocklist) {
      applyServerBlocklist(result.blocklist);
      setBlocklistServerMsg("Saved blocklist to server");
      setTimeout(() => setBlocklistServerMsg(""), 2500);
    } else {
      setBlocklistServerMsg("Could not save blocklist");
    }
  }

  useEffect(() => {
    if (server?.serverStatus === "online") loadSavedBlocklist(false);
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  function addToBlocklist() {
    const ids = blocklistInput
      .split(/[\n,;]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (!ids.length) return;
    if (blocklistScope === "engineer") {
      const engineer = blocklistEngineer || state.techNames?.[0] || "";
      if (!engineer) return;
      setEngineerBlocklists((prev) => ({
        ...prev,
        [engineer]: new Set([...(prev[engineer] || []), ...ids]),
      }));
    } else {
      setBlocklist((prev) => new Set([...prev, ...ids]));
    }
    setBlocklistInput("");
  }
  function removeFromBlocklist(id) {
    setBlocklist((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }
  function removeFromEngineerBlocklist(engineer, id) {
    setEngineerBlocklists((prev) => {
      const n = { ...prev };
      n[engineer] = new Set(prev[engineer] || []);
      n[engineer].delete(id);
      if (n[engineer].size === 0) delete n[engineer];
      return n;
    });
  }

  function getSharedDisplayPart(techId, part, dayNum) {
    const manRep = manualReplacements[`${techId}_${dayNum}_${part.serial}`];
    const grade = manRep ? manRep.grade : part.grade;
    return {
      partId: manRep ? manRep.partId : part.partId || part.ref,
      grade,
      isManual: !!manRep,
    };
  }

  function buildSharedTaskParts(tech, dayNum, dailyTarget) {
    const target = Math.max(1, parseInt(dailyTarget, 10) || 1);
    const deleted = deletedSerials[`${tech.id}_${dayNum}`] || new Set();
    const engineerBlocked = engineerBlocklists[tech.name] || new Set();
    const allowedParts = (tech.parts || []).filter((p) => {
      const id = String(p.partId || p.ref || "").toUpperCase();
      return !blocklist.has(id) && !engineerBlocked.has(id);
    });
    const dcdPool = allowedParts.filter((p) => p.source !== "mcd");
    const mcdPool = allowedParts.filter((p) => p.source === "mcd");
    const dcdUrgent = dcdPool.filter((p) => p.priority === "urgent");
    const mcdUrgent = mcdPool.filter((p) => p.priority === "urgent");
    const dcdNonUrgent = dcdPool.filter((p) => p.priority !== "urgent");
    const mcdNonUrgent = mcdPool.filter((p) => p.priority !== "urgent");
    const urgentCount = dcdUrgent.length + mcdUrgent.length;
    const remaining = Math.max(0, target - urgentCount);
    const mcdRemaining = Math.round(0.5 * remaining);
    const dcdRemaining = remaining - mcdRemaining;
    const dcdNonStart = (dayNum - 1) * dcdRemaining;
    const mcdNonStart = (dayNum - 1) * mcdRemaining;
    const dcdWindow = dcdNonUrgent.slice(
      dcdNonStart,
      dcdNonStart + dcdRemaining,
    );
    const mcdWindow = mcdNonUrgent.slice(
      mcdNonStart,
      mcdNonStart + mcdRemaining,
    );
    const interleaved = [
      ...dcdUrgent,
      ...mcdUrgent,
      ...dcdWindow.filter((p) => p.priority === "rep_req"),
      ...mcdWindow.filter((p) => p.priority === "rep_req"),
      ...dcdWindow.filter((p) => p.priority === "stock"),
      ...mcdWindow.filter((p) => p.priority === "stock"),
    ];
    const activeParts = interleaved.filter((p) => !deleted.has(p.serial));
    const needed = target - activeParts.length;
    const usedSerials = new Set([
      ...interleaved.map((p) => p.serial),
      ...deleted,
    ]);
    const replacements = [];
    if (needed > 0) {
      const overflow = [
        ...dcdNonUrgent.slice(dcdNonStart + dcdRemaining),
        ...mcdNonUrgent.slice(mcdNonStart + mcdRemaining),
      ];
      for (const p of overflow) {
        if (replacements.length >= needed) break;
        if (!usedSerials.has(p.serial) && !deleted.has(p.serial)) {
          replacements.push({ ...p, isReplacement: true });
          usedSerials.add(p.serial);
        }
      }
    }
    return [...activeParts, ...replacements].map((p) => {
      const display = getSharedDisplayPart(tech.id, p, dayNum);
      return {
        serial: p.serial,
        partId: display.partId,
        grade: display.grade,
        priority: p.priority,
        source: p.source,
        isBER: display.grade === "BER",
        isManual: display.isManual,
        isReplacement: !!p.isReplacement,
      };
    });
  }

  function runAllocation(workingDays, techNames, weight, client) {
    const r = allocate({
      parts: state.mergedParts || [],
      callListParts: state.callList || [],
      techNames,
      targetDaily: state.tiers[0]?.daily || 18,
      tiers: state.tiers,
      workingDays,
      doaCount: state.doaCount,
      gradeValues: state.gradeValues,
      activeClient: client !== undefined ? client : state.activeClient,
      clientWeight: weight !== undefined ? weight : 0,
      engineerClients: state.engineerClients || {},
    });
    setResults(r);
  }

  useEffect(() => {
    runAllocation(state.workingDays, state.techNames, 0, null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!results)
    return (
      <div style={{ color: "#4f8ef7", textAlign: "center", padding: 40 }}>
        Calculating optimal allocation...
      </div>
    );

  function handleRerun() {
    setState({ workingDays: rerunDays, techNames: rerunNames });
    runAllocation(rerunDays, rerunNames, 0, null);
    setShowRerun(false);
  }

  // Build a shareable link for a single engineer on a specific day
  function buildEngineerLink(tech, dayNumber, dailyTarget) {
    const parts = buildSharedTaskParts(tech, dayNumber, dailyTarget);

    const now = new Date();
    const rotaEngineerName =
      (state.engineerRotaNames || {})[tech.name] || tech.name;
    const payload = {
      __sharedDay: true,
      engineerName: tech.name,
      rotaEngineerName,
      label: now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      dayNumber,
      gradeValues: state.gradeValues,
      tiers: state.tiers,
      parts,
      serverUrl: SHARE_ORIGIN,
      shareDate: {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
      },
    };

    try {
      const encoded = btoa(
        unescape(encodeURIComponent(JSON.stringify(payload))),
      );
      return `${window.location.origin}${window.location.pathname}#day=${encoded}`;
    } catch {
      return null;
    }
  }

  function copyLink(url) {
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setShareMsg(" Link copied!");
        setTimeout(() => setShareMsg(""), 2500);
      })
      .catch(() => prompt("Copy this link:", url));
  }

  function exportCSV() {
    const lines = [
      "Engineer,Total Units,Daily Avg,Commission,Bonus,Total Earnings",
    ];
    results.forEach((t) => {
      lines.push(
        `"${t.name}",${t.totalUnits},${t.dailyAvg.toFixed(2)},${t.commission.toFixed(2)},${t.bonusTier ? `${t.bonusTier.bonus}` : "0"},${t.totalEarnings.toFixed(2)}`,
      );
    });
    results.forEach((t) => {
      lines.push("");
      lines.push(`"=== ${t.name} PARTS ==="`);
      lines.push("Part ID,Grade,Value,Priority,Source,Customer");
      t.parts.forEach((p) => {
        const prio =
          p.priority === "urgent"
            ? "CALL LIST"
            : p.priority === "rep_req"
              ? "REP REQUIRED"
              : "STOCK";
        lines.push(
          `"${p.partId || p.serial || ""}","${p.grade}",${(state.gradeValues[p.grade] || 0).toFixed(2)},"${prio}","${p.source || "DCD"}","${p.customer || ""}"`,
        );
      });
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vista-allocation-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  }

  function exportParsedPartReport() {
    if (!window.XLSX) {
      alert("SheetJS not loaded");
      return;
    }
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const summaryRows = [
      [
        "Part ID",
        "Repair Requirement",
        "Priority Rank",
        "Backstock Count",
        "Call List Count",
        "Repair Required Count",
        "Stock Count",
        "Grades",
        "Highest Value",
        "Teams",
        "Sources",
        "Customers",
      ],
      ...filteredParsedPartReport.summary.map((r) => [
        r.partId,
        r.requirement,
        r.priorityRank,
        r.backstockCount,
        r.callListCount,
        r.repairRequiredCount,
        r.stockCount,
        r.gradeSummary,
        r.value,
        r.teamsText,
        r.sourcesText,
        r.customersText,
      ]),
    ];
    const detailRows = [
      [
        "Part ID",
        "Requirement",
        "Grade",
        "Value",
        "Source",
        "Team",
        "Customer",
        "Serial",
      ],
      ...filteredParsedPartReport.details.map((r) => [
        r.partId,
        r.requirement,
        r.grade,
        r.value,
        r.source,
        r.team,
        r.customer,
        r.serial,
      ]),
    ];
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
    summaryWs["!cols"] = [
      { wch: 24 },
      { wch: 18 },
      { wch: 12 },
      { wch: 15 },
      { wch: 14 },
      { wch: 20 },
      { wch: 12 },
      { wch: 24 },
      { wch: 12 },
      { wch: 20 },
      { wch: 14 },
      { wch: 24 },
    ];
    const detailWs = XLSX.utils.aoa_to_sheet(detailRows);
    detailWs["!cols"] = [
      { wch: 24 },
      { wch: 18 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 24 },
      { wch: 28 },
    ];
    XLSX.utils.book_append_sheet(wb, summaryWs, "Part Summary");
    XLSX.utils.book_append_sheet(wb, detailWs, "Parsed Parts");
    XLSX.writeFile(
      wb,
      `parsed-part-report-${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  }

  function printParsedPartReport() {
    const rows = filteredParsedPartReport.summary || [];
    const filterText = parsedReportFilterLabel;
    const generatedAt = new Date().toLocaleString("en-GB");
    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Parsed Part Report</title>
<style>
 body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 24px; }
 h1 { font-size: 20px; margin: 0 0 6px; }
 .meta { color: #4b5563; font-size: 12px; margin-bottom: 14px; }
 table { width: 100%; border-collapse: collapse; font-size: 11px; }
 th { text-align: left; background: #e5e7eb; border: 1px solid #9ca3af; padding: 6px; }
 td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; }
 .num { text-align: right; }
 @media print {
   body { margin: 12mm; }
   h1 { break-after: avoid; }
   thead { display: table-header-group; }
 }
</style>
</head>
<body>
<h1>Parsed Part Report</h1>
<div class="meta">Filter: ${escapeHtml(filterText)} | ${rows.length} unique parts | ${filteredParsedPartReport.totalLoaded} backstock rows | ${filteredParsedPartReport.totalCallList} call-list rows | Printed ${escapeHtml(generatedAt)}</div>
<table>
<thead>
<tr>
<th>Part</th>
<th>Need</th>
<th class="num">Backstock</th>
<th class="num">Calls</th>
<th class="num">Rep Req</th>
<th>Grades</th>
<th>Sources</th>
<th>Customers</th>
</tr>
</thead>
<tbody>
${
  rows
    .map(
      (row) => `<tr>
<td>${escapeHtml(row.partId)}</td>
<td>${escapeHtml(row.requirement)}</td>
<td class="num">${escapeHtml(row.backstockCount)}</td>
<td class="num">${escapeHtml(row.callListCount || "-")}</td>
<td class="num">${escapeHtml(row.repairRequiredCount || "-")}</td>
<td>${escapeHtml(row.gradeSummary)}</td>
<td>${escapeHtml(row.sourcesText)}</td>
<td>${escapeHtml(row.customersText)}</td>
</tr>`,
    )
    .join("") ||
  `<tr><td colspan="8">No parsed parts match this filter.</td></tr>`
}
</tbody>
</table>
</body>
</html>`;
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      alert(
        "Pop-up blocked. Allow pop-ups for RazerGrid to print the parsed part report.",
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* View switcher + re-run + share */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 0,
            background: "#081224",
            border: "1px solid #1e3056",
            borderRadius: 10,
            padding: 4,
          }}
        >
          {[
            ["daily", " DAILY VIEW"],
            ["monthly", " MONTHLY VIEW"],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                background:
                  view === v
                    ? "linear-gradient(135deg,#1d4ed8,#4f8ef7)"
                    : "none",
                border: "none",
                borderRadius: 7,
                padding: "9px 20px",
                color: view === v ? "#fff" : "#3d5280",
                fontSize: 11,
                fontFamily: "inherit",
                fontWeight: 700,
                letterSpacing: 1,
                cursor: "pointer",
                transition: "all 0.18s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <Btn
          variant="secondary"
          style={{ fontSize: 10, padding: "7px 14px" }}
          onClick={() => setShowRerun(!showRerun)}
        >
          RE-RUN
        </Btn>
        <Btn
          variant="secondary"
          style={{ fontSize: 10, padding: "7px 14px" }}
          onClick={() => setShowSharePanel(!showSharePanel)}
        >
          SHARE TASK LISTS
        </Btn>

        {/* Save to Server */}
        {server?.serverStatus === "online" && results && (
          <Btn
            variant="success"
            style={{ fontSize: 10, padding: "7px 14px" }}
            onClick={async () => {
              setSaveStatus("saving");
              const today = new Date().toISOString().split("T")[0];
              const month = today.slice(0, 7);
              let ok = true;
              for (const tech of results) {
                const r = await server.saveMonthlySummary(
                  tech.name,
                  state.teamFilter,
                  month,
                  tech.totalUnits,
                  tech.netCommission,
                  tech.bonusTier?.bonus || 0,
                  state.workingDays > 0
                    ? tech.totalUnits / state.workingDays
                    : 0,
                  state.workingDays,
                );
                if (!r) ok = false;
              }
              setSaveStatus(ok ? "saved" : "error");
              setTimeout(() => setSaveStatus(""), 3000);
            }}
          >
            {saveStatus === "saving"
              ? "SAVING..."
              : saveStatus === "saved"
                ? "SAVED"
                : saveStatus === "error"
                  ? "FAILED"
                  : "SAVE TO SERVER"}
          </Btn>
        )}

        {saveStatus === "" && server?.serverStatus === "offline" && (
          <span style={{ color: "#f87171", fontSize: 10 }}>
            Server offline data not saved
          </span>
        )}
        {shareMsg && (
          <span style={{ color: "#10b981", fontSize: 11 }}>{shareMsg}</span>
        )}
      </div>

      {/* Re-run panel */}
      {showRerun && (
        <Card style={{ background: "#0d1b35", border: "1px solid #1e3a6e" }}>
          <Label>Re-run Allocation Adjust Without Going Back</Label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <Label>Working Days</Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={rerunDays}
                onChange={(e) => setRerunDays(parseInt(e.target.value) || 22)}
              />
            </div>
            <div>
              <Label>Engineers (comma-separated)</Label>
              <Input
                value={rerunNames.join(", ")}
                onChange={(e) =>
                  setRerunNames(
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                placeholder="Luke, Kam, Huss"
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={handleRerun}> RECALCULATE</Btn>
            <Btn variant="secondary" onClick={() => setShowRerun(false)}>
              Cancel
            </Btn>
          </div>
        </Card>
      )}

      {/* Share task lists panel */}
      {showSharePanel && (
        <Card style={{ background: "#0d1b35", border: "1px solid #1e3a6e" }}>
          <Label>Share Individual Engineer Task Lists</Label>
          <p
            style={{
              color: "#4f6fa0",
              fontSize: 12,
              marginBottom: 14,
              lineHeight: 1.6,
            }}
          >
            Generate a personal link for each engineer. They open it on their
            phone or computer and see their own task list for that day with
            tap-to-tick checkboxes and a live running commission total. Each
            link is self-contained no login needed.
          </p>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 16,
              alignItems: "flex-end",
            }}
          >
            <div>
              <Label>Day Number</Label>
              <Input
                type="number"
                min={1}
                max={state.workingDays}
                value={shareDay}
                onChange={(e) =>
                  setShareDay(Math.max(1, parseInt(e.target.value) || 1))
                }
                style={{
                  width: 80,
                  textAlign: "center",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              />
            </div>
            <div>
              <Label>Parts Per Engineer</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={shareTarget}
                onChange={(e) =>
                  setSharedDailyTarget(
                    Math.max(1, parseInt(e.target.value) || 1),
                  )
                }
                style={{
                  width: 80,
                  textAlign: "center",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {results.map((tech, i) => {
              const col = TECH_COLORS[i % TECH_COLORS.length];
              const url = buildEngineerLink(tech, shareDay, shareTarget);
              return (
                <div
                  key={tech.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: "#081224",
                    border: `1px solid ${col}33`,
                    borderRadius: 9,
                    padding: "12px 16px",
                  }}
                >
                  <div
                    style={{
                      color: col,
                      fontWeight: 700,
                      fontSize: 13,
                      minWidth: 120,
                    }}
                  >
                    {tech.name}
                  </div>
                  <div style={{ color: "#3d5280", fontSize: 10, flex: 1 }}>
                    Day {shareDay} {shareTarget} parts
                  </div>
                  <button
                    onClick={() => copyLink(url)}
                    style={{
                      background: "linear-gradient(135deg,#1d4ed8,#4f8ef7)",
                      border: "none",
                      borderRadius: 7,
                      color: "#fff",
                      fontSize: 10,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, color: "#3d5280", fontSize: 10 }}>
            Engineers can tick off parts on their own device. Progress stays on
            their device it does not sync back here. For real-time sync, contact
            your manager about upgrading to the cloud version.
          </div>
        </Card>
      )}

      <Card
        style={{
          background: "#0d1b35",
          border: "1px solid #1e3056",
          padding: 0,
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => setParsedReportOpen((open) => !open)}
          style={{
            width: "100%",
            background: "none",
            border: "none",
            padding: "13px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: "pointer",
            color: "#c8d8f0",
            fontFamily: "inherit",
            textAlign: "left",
          }}
        >
          <span style={{ color: "#4f8ef7", fontSize: 13, width: 18 }}>
            {parsedReportOpen ? "v" : ">"}
          </span>
          <span style={{ flex: 1 }}>
            <span
              style={{
                color: "#6be8ff",
                fontSize: 10,
                letterSpacing: 1.6,
                fontWeight: 800,
              }}
            >
              Parsed Part Report
            </span>
            <span style={{ color: "#4f6fa0", fontSize: 11, marginLeft: 10 }}>
              {filteredParsedPartReport.summary.length} unique parts,{" "}
              {filteredParsedPartReport.totalLoaded} backstock rows,{" "}
              {filteredParsedPartReport.totalCallList} call-list rows
            </span>
          </span>
          <span style={{ color: "#3d5280", fontSize: 10 }}>
            Excel export available
          </span>
        </button>
        {parsedReportOpen && (
          <div style={{ borderTop: "1px solid #1e3056", padding: 16 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 10,
                marginBottom: 14,
              }}
            >
              {[
                [
                  "Unique Parts",
                  filteredParsedPartReport.summary.length,
                  "#4f8ef7",
                ],
                [
                  "Backstock Rows",
                  filteredParsedPartReport.totalLoaded,
                  "#10b981",
                ],
                [
                  "Call List Rows",
                  filteredParsedPartReport.totalCallList,
                  "#ef4444",
                ],
                [
                  "Repair Required",
                  filteredParsedPartReport.summary.reduce(
                    (s, p) => s + p.repairRequiredCount,
                    0,
                  ),
                  "#f59e0b",
                ],
              ].map(([label, value, color]) => (
                <div
                  key={label}
                  style={{
                    background: "#081224",
                    border: `1px solid ${color}33`,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      color: "#3d5280",
                      fontSize: 8,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      color,
                      fontSize: 17,
                      fontWeight: 800,
                      marginTop: 3,
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "flex-end",
                marginBottom: 12,
              }}
            >
              <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                Sorted by call-list parts first, then repair-required stock,
                then normal stock.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ minWidth: 220 }}>
                  <Label>Report Filter</Label>
                  <Select
                    value={parsedReportFilter}
                    onChange={(e) => setParsedReportFilter(e.target.value)}
                    style={{ fontSize: 11, minHeight: 34, padding: "7px 10px" }}
                  >
                    <option value="all">All parsed parts</option>
                    <option value="clients-excluded">
                      Filter out configured clients
                    </option>
                    <option value="clients-only">
                      Configured clients only
                    </option>
                    {(state.clients || []).length > 1 &&
                      (state.clients || []).map((client) => (
                        <option key={client.id} value={`client:${client.id}`}>
                          {client.name} only
                        </option>
                      ))}
                  </Select>
                </div>
                <Btn
                  variant="secondary"
                  style={{ fontSize: 10, padding: "7px 12px" }}
                  onClick={exportParsedPartReport}
                  disabled={filteredParsedPartReport.summary.length === 0}
                >
                  Export Parsed Parts
                </Btn>
                <Btn
                  variant="secondary"
                  style={{ fontSize: 10, padding: "7px 12px" }}
                  onClick={printParsedPartReport}
                  disabled={filteredParsedPartReport.summary.length === 0}
                >
                  Print Parsed Parts
                </Btn>
              </div>
            </div>
            <div
              style={{
                maxHeight: 220,
                overflow: "auto",
                border: "1px solid #1e3056",
                borderRadius: 8,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead
                  style={{ position: "sticky", top: 0, background: "#081224" }}
                >
                  <tr>
                    {[
                      "PART",
                      "NEED",
                      "BACKSTOCK",
                      "CALLS",
                      "REP REQ",
                      "GRADES",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          color: "#4f6fa0",
                          textAlign:
                            h === "PART" || h === "NEED" || h === "GRADES"
                              ? "left"
                              : "right",
                          padding: "8px 10px",
                          borderBottom: "1px solid #1e3056",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredParsedPartReport.summary.slice(0, 80).map((row) => (
                    <tr
                      key={row.partId}
                      style={{ borderBottom: "1px solid #10203e" }}
                    >
                      <td
                        style={{
                          padding: "8px 10px",
                          color: "#c8d8f0",
                          fontFamily: "monospace",
                        }}
                      >
                        {row.partId}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          color:
                            row.priorityRank === 1
                              ? "#ef4444"
                              : row.priorityRank === 2
                                ? "#f59e0b"
                                : "#4f8ef7",
                        }}
                      >
                        {row.requirement}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          color: "#10b981",
                          textAlign: "right",
                          fontWeight: 700,
                        }}
                      >
                        {row.noStockForCall ? (
                          <span style={{ color: "#ef4444" }}>0</span>
                        ) : (
                          row.backstockCount
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          color: "#ef4444",
                          textAlign: "right",
                        }}
                      >
                        {row.callListCount || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          color: "#f59e0b",
                          textAlign: "right",
                        }}
                      >
                        {row.repairRequiredCount || "-"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#4f6fa0" }}>
                        {row.gradeSummary}
                      </td>
                    </tr>
                  ))}
                  {filteredParsedPartReport.summary.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: 16,
                          color: "#4f6fa0",
                          textAlign: "center",
                        }}
                      >
                        {parsedPartReport.summary.length === 0
                          ? "Load data and a call list to build the parsed part report."
                          : "No parsed parts match this filter."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {view === "monthly" && (
        <MonthlyView
          results={results}
          state={{ ...state, workingDays: rerunDays }}
          onExportCSV={exportCSV}
          blocklist={blocklist}
          blocklistInput={blocklistInput}
          setBlocklistInput={setBlocklistInput}
          addToBlocklist={addToBlocklist}
          removeFromBlocklist={removeFromBlocklist}
          setBlocklist={setBlocklist}
          blocklistScope={blocklistScope}
          setBlocklistScope={setBlocklistScope}
          blocklistEngineer={blocklistEngineer}
          setBlocklistEngineer={setBlocklistEngineer}
          engineerBlocklists={engineerBlocklists}
          removeFromEngineerBlocklist={removeFromEngineerBlocklist}
          blocklistServerMsg={blocklistServerMsg}
          onSaveBlocklist={saveCurrentBlocklist}
          onReloadBlocklist={() => loadSavedBlocklist(true)}
          blocklistServerBusy={blocklistServerBusy}
          sharedDailyTarget={sharedDailyTarget}
          setSharedDailyTarget={setSharedDailyTarget}
          deletedSerials={deletedSerials}
          setDeletedSerials={setDeletedSerials}
          manualReplacements={manualReplacements}
          setManualReplacements={setManualReplacements}
        />
      )}
      {view === "daily" && (
        <DailyView
          results={results}
          state={{ ...state, workingDays: rerunDays }}
          blocklist={blocklist}
          blocklistInput={blocklistInput}
          setBlocklistInput={setBlocklistInput}
          addToBlocklist={addToBlocklist}
          removeFromBlocklist={removeFromBlocklist}
          setBlocklist={setBlocklist}
          blocklistScope={blocklistScope}
          setBlocklistScope={setBlocklistScope}
          blocklistEngineer={blocklistEngineer}
          setBlocklistEngineer={setBlocklistEngineer}
          engineerBlocklists={engineerBlocklists}
          removeFromEngineerBlocklist={removeFromEngineerBlocklist}
          blocklistServerMsg={blocklistServerMsg}
          onSaveBlocklist={saveCurrentBlocklist}
          onReloadBlocklist={() => loadSavedBlocklist(true)}
          blocklistServerBusy={blocklistServerBusy}
          sharedDailyTarget={sharedDailyTarget}
          setSharedDailyTarget={setSharedDailyTarget}
          deletedSerials={deletedSerials}
          setDeletedSerials={setDeletedSerials}
          manualReplacements={manualReplacements}
          setManualReplacements={setManualReplacements}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <Btn variant="secondary" onClick={onBack}>
          BACK
        </Btn>
        <Btn variant="danger" onClick={onReset}>
          START OVER
        </Btn>
      </div>
    </div>
  );
}

/**
 * Cross-Team Work — parts booked out by an engineer whose team is not
 * allowed to fix them. Presentation only: buildCrossTeamReport does the
 * judging, and the History tab owns the loading and the CSV export.
 */
export function CrossTeamPanel({
  report,
  access,
  expanded,
  onToggle,
  view,
  onViewChange,
  loading,
  error,
  onReload,
  onExport,
  summaryLabel,
  fxEnabled,
  n,
  money,
  pct,
}) {
  return (
    <Card>
      <div
        onClick={() => onToggle()}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              color: "#7aa0d4",
              fontSize: 13,
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s",
              display: "inline-block",
            }}
          >
            ▶
          </span>
          <Label>Cross-Team Work</Label>
          <span style={{ color: "#4f6fa0", fontSize: 11 }}>
            ({summaryLabel})
          </span>
        </div>
        <span style={{ color: "#4f6fa0", fontSize: 11 }}>
          {expanded ? "Hide" : "Show breakdown"}
        </span>
      </div>

      {expanded && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              margin: "12px 0",
            }}
          >
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 11,
                maxWidth: 620,
                lineHeight: 1.6,
              }}
            >
              Parts booked out by an engineer whose team is not allowed to
              fix them. A team is allowed by a Parts Grading tick, by its
              uploaded client reference list, or by owning the part on the
              grading reference. Parts labelled PROJECTS ONLY,
              CONSUMABLES or LOGISTICS MANAGED belong to nobody and are
              never counted here.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Select
                value={view}
                onChange={(e) => onViewChange(e.target.value)}
                style={{ minWidth: 150 }}
              >
                <option value="engineers">By engineer</option>
                <option value="pairs">By team pair</option>
                <option value="teams">By team balance</option>
                <option value="parts">By part</option>
                <option value="months">By month</option>
                <option value="rows">Every record</option>
              </Select>
              <Btn
                variant="secondary"
                onClick={onReload}
                disabled={loading}
              >
                {loading ? "LOADING" : "REFRESH"}
              </Btn>
              <Btn
                variant="secondary"
                onClick={onExport}
                disabled={report.rows.length === 0}
              >
                EXPORT
              </Btn>
            </div>
          </div>

          {error && (
            <div
              style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}
            >
              {error}
            </div>
          )}
          {!access && !loading && !error && (
            <div
              style={{ color: "#fbbf24", fontSize: 12, marginBottom: 10 }}
            >
              Loading the part ownership reference...
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {[
              {
                key: "cross",
                label: "Cross-team parts",
                value: n(report.totals.cross).toLocaleString(),
                accent: "#f87171",
                bg: "#1a0808",
              },
              {
                key: "rate",
                label: "Share of judged work",
                value: pct(report.totals.crossRate),
                accent: "#fbbf24",
                bg: "#1a1405",
              },
              {
                key: "commission",
                label: "Commission moved",
                value: money(report.totals.crossCommission),
                accent: "#4ade80",
                bg: "#08170f",
              },
              {
                key: "engineers",
                label: "Engineers involved",
                value: n(
                  report.totals.crossEngineers,
                ).toLocaleString(),
                accent: "#7ab4f7",
                bg: "#08152b",
              },
              {
                key: "parts",
                label: "Distinct parts",
                value: n(report.totals.crossParts).toLocaleString(),
                accent: "#c4b5fd",
                bg: "#130d26",
              },
              {
                key: "ber",
                label: "BER / NFF",
                value: `${n(report.totals.crossBer)} / ${n(
                  report.totals.crossNff,
                )}`,
                accent: "#fb923c",
                bg: "#1a0f05",
              },
            ].map((tile, index) => (
              <div
                key={tile.key}
                style={mergeStyles(
                  {
                    border: `1px solid ${tile.accent}33`,
                    background: tile.bg,
                    borderRadius: 8,
                    padding: "10px 12px",
                  },
                  historyStaggerStyle(index, fxEnabled, {
                    stepMs: 20,
                    maxDelayMs: 100,
                  }),
                )}
              >
                <div
                  style={{
                    color: tile.accent,
                    fontSize: 10,
                    textTransform: "uppercase",
                    fontWeight: 800,
                  }}
                >
                  {tile.label}
                </div>
                <div
                  style={{
                    color: "#dbeafe",
                    fontSize: 20,
                    fontWeight: 900,
                    lineHeight: 1.2,
                    marginTop: 4,
                  }}
                >
                  {tile.value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              color: "#4f6fa0",
              fontSize: 11,
              marginBottom: 10,
              lineHeight: 1.6,
            }}
          >
            {n(report.totals.judged).toLocaleString()} of{" "}
            {n(report.totals.records).toLocaleString()} records could
            be judged.{" "}
            {n(report.totals.unowned).toLocaleString()} are on parts
            nobody owns,{" "}
            {n(report.totals.unknownPart).toLocaleString()} are on
            parts missing from the grading reference, and{" "}
            {n(report.totals.noTeam).toLocaleString()} carry no
            engineer team.
          </div>

          <div style={{ overflowX: "auto" }}>
            {view === "engineers" && (
              <CrossTeamTable
                empty="No engineer booked out another team's part in this period."
                headers={[
                  "Engineer",
                  "Team",
                  "Cross parts",
                  "Share of own work",
                  "Commission",
                  "BER",
                  "NFF",
                  "Teams taken from",
                  "Most-taken part",
                ]}
                rows={report.byEngineer.map((row) => [
                  row.engineer,
                  row.team,
                  n(row.cross).toLocaleString(),
                  pct(row.crossRate),
                  money(row.commission),
                  n(row.ber).toLocaleString(),
                  n(row.nff).toLocaleString(),
                  row.owningTeams
                    .map((t) => `${t.team} (${t.count})`)
                    .join(", "),
                  row.parts[0]
                    ? `${row.parts[0].partId} (${row.parts[0].count})`
                    : "-",
                ])}
              />
            )}

            {view === "pairs" && (
              <CrossTeamTable
                empty="No team took work from another in this period."
                headers={[
                  "Team doing the work",
                  "Team that owns the part",
                  "Parts",
                  "Commission",
                  "Engineers",
                  "Distinct parts",
                ]}
                rows={report.byTeamPair.map((row) => [
                  row.fromTeam,
                  row.toTeam,
                  n(row.count).toLocaleString(),
                  money(row.commission),
                  n(row.engineers).toLocaleString(),
                  n(row.parts).toLocaleString(),
                ])}
              />
            )}

            {view === "teams" && (
              <CrossTeamTable
                empty="No cross-team work in this period."
                headers={[
                  "Team",
                  "Took in",
                  "Gave away",
                  "Net",
                  "Commission taken in",
                  "Commission given away",
                  "Share of own work taken in",
                ]}
                rows={report.byTeam.map((row) => [
                  row.team,
                  n(row.incoming).toLocaleString(),
                  n(row.outgoing).toLocaleString(),
                  `${row.net > 0 ? "+" : ""}${n(row.net).toLocaleString()}`,
                  money(row.incomingCommission),
                  money(row.outgoingCommission),
                  pct(row.incomingRate),
                ])}
              />
            )}

            {view === "parts" && (
              <CrossTeamTable
                empty="No part was worked by the wrong team in this period."
                headers={[
                  "Part ID",
                  "Grade",
                  "Owned by",
                  "Worked by",
                  "Times",
                  "Commission",
                  "BER",
                  "Engineers",
                ]}
                rows={report.byPart.slice(0, 300).map((row) => [
                  row.partId,
                  row.grade || "-",
                  row.allowedTeams.join(", "),
                  row.teams.join(", "),
                  n(row.count).toLocaleString(),
                  money(row.commission),
                  n(row.ber).toLocaleString(),
                  row.engineers.join(", "),
                ])}
              />
            )}

            {view === "months" && (
              <CrossTeamTable
                empty="No cross-team work in this period."
                headers={[
                  "Month",
                  "Cross parts",
                  "Judged parts",
                  "Share",
                  "Commission",
                ]}
                rows={report.byMonth.map((row) => [
                  row.month,
                  n(row.cross).toLocaleString(),
                  n(row.judged).toLocaleString(),
                  pct(row.crossRate),
                  money(row.commission),
                ])}
              />
            )}

            {view === "rows" && (
              <CrossTeamTable
                empty="No cross-team records in this period."
                headers={[
                  "Month",
                  "Engineer",
                  "Their team",
                  "Part ID",
                  "Grade",
                  "Owned by",
                  "Commission",
                  "Status",
                  "Completed",
                ]}
                rows={report.rows.slice(0, 300).map((row) => [
                  row.month,
                  row.engineer,
                  row.engineerTeam,
                  row.partId,
                  row.grade || "-",
                  row.owningTeams,
                  money(row.commission),
                  row.unitStatus || "-",
                  row.completedAt || "-",
                ])}
              />
            )}
          </div>

          {((view === "rows" && report.rows.length > 300) ||
            (view === "parts" &&
              report.byPart.length > 300)) && (
            <div
              style={{ color: "#4f6fa0", fontSize: 11, marginTop: 8 }}
            >
              Showing the first 300. Export for the full list.
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** Plain read-only table shared by every Cross-Team Work view. */
export function CrossTeamTable({ headers, rows, empty }) {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 11,
        minWidth: 640,
      }}
    >
      <thead>
        <tr style={{ color: "#4a6898", borderBottom: "1px solid #1e3a6e" }}>
          {headers.map((header) => (
            <th
              key={header}
              style={{
                textAlign: "left",
                padding: "6px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td
              colSpan={headers.length}
              style={{ padding: "14px 8px", color: "#4a6898" }}
            >
              {empty}
            </td>
          </tr>
        )}
        {rows.map((cells, index) => (
          <tr
            key={index}
            style={{ borderBottom: "1px solid #0d1b35", color: "#c8d8f0" }}
          >
            {cells.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                style={{
                  padding: "6px 8px",
                  fontFamily: cellIndex === 0 ? "monospace" : "inherit",
                  verticalAlign: "top",
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function HistoryTab({ teamTargets, aliasRefreshVersion = 0 }) {
  const server = useServer();
  const fxEnabled = useFX();
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [historyMode, setHistoryMode] = useState("month");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [compareMonth, setCompareMonth] = useState("");
  const [historySnapshot, setHistorySnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [historyTeam, setHistoryTeam] = useState("ALL");
  const [engineerSearch, setEngineerSearch] = useState("");
  const [selectedEngineer, setSelectedEngineer] = useState("ALL");
  const [consistencySeverity, setConsistencySeverity] = useState("ALL");
  const [consistencyCategory, setConsistencyCategory] = useState("ALL");
  // Collapsed by default, like the Completed Parts list — the panel is tall and
  // is a diagnostic, not something needed on every visit. The header still shows
  // the critical/warning tally so the state is visible without expanding.
  const [consistencyExpanded, setConsistencyExpanded] = useState(false);
  const [aliasApplyMsg, setAliasApplyMsg] = useState("");
  const [aliasApplying, setAliasApplying] = useState(false);
  const [engineerAliases, setEngineerAliases] = useState([]);
  const historyAliasIndex = useMemo(
    () => buildEngineerAliasIndex(engineerAliases),
    [engineerAliases],
  );
  const engineerStatusIndex = useMemo(
    () =>
      buildEngineerStatusIndex(
        (engineerAliases || []).map((row) => ({
          engineer: row.engineer,
          status: row.status,
        })),
        engineerAliases,
      ),
    [engineerAliases],
  );
  // --- Cross-team work ------------------------------------------------------
  // The part ownership reference is independent of the selected period, so it
  // loads once and is reused as the month or range changes.
  const [crossAccess, setCrossAccess] = useState(null);
  const [crossLoading, setCrossLoading] = useState(false);
  const [crossError, setCrossError] = useState("");
  const [crossExpanded, setCrossExpanded] = useState(false);
  const [crossView, setCrossView] = useState("engineers");
  const historyRequest = useRef(0);
  const crossRequest = useRef(0);
  const selectionKey = historyMode === "range"
    ? JSON.stringify(["range", rangeFrom, rangeTo])
    : JSON.stringify(["month", month, month === "ALL" ? "" : compareMonth]);
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;
  const data = historySnapshot?.key === selectionKey ? historySnapshot.data : null;

  useEffect(() => () => {
    historyRequest.current += 1;
    crossRequest.current += 1;
  }, []);

  const loadCrossAccess = useCallback(async () => {
    if (server?.serverStatus !== "online") return;
    const request = ++crossRequest.current;
    setCrossLoading(true);
    setCrossError("");
    try {
      const [gradingRows, clientRefRows] = await Promise.all([
        server.fetchGrading?.(undefined),
        server.fetchAllPartReferences?.(),
      ]);
      if (request !== crossRequest.current) return;
      if (!Array.isArray(gradingRows)) {
        setCrossError(
          "Could not load the grading reference, so no work can be judged.",
        );
        return;
      }
      if (!Array.isArray(clientRefRows)) {
        setCrossError(
          "Client reference lists were unavailable. The last complete ownership reference is unchanged; retry Refresh.",
        );
        return;
      }
      setCrossAccess(
        buildPartTeamAccess({
          gradingRows,
          clientRefRows,
        }),
      );
    } catch (e) {
      if (request === crossRequest.current) setCrossError("Could not load part ownership: " + (e?.message || e));
    } finally {
      if (request === crossRequest.current) setCrossLoading(false);
    }
  }, [server]);

  useEffect(() => {
    if (server?.serverStatus !== "online") return;
    loadCrossAccess();
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const GRADES = ["X", "S", "C", "B", "A", "M", "BER", "UNGRADED"];
  // Grade overrides overlaid on the completed-parts list. Keyed by UPPERCASE part_id.
  // Sourced from the same store as the Parts Grading Manager (grade_overrides) so a fix
  // made here is uniform across the app and inherited by future uploads.
  const [gradeOverrides, setGradeOverrides] = useState({});
  const [savingPart, setSavingPart] = useState("");
  const [gradeMsg, setGradeMsg] = useState("");
  // Collapsed by default — the list can be long. Pending grade picks are staged here and
  // only written to the server when the user clicks Save (so a stray click isn't destructive).
  const [partsExpanded, setPartsExpanded] = useState(false);
  const [pendingGrades, setPendingGrades] = useState({});
  useEffect(() => {
    if (server?.serverStatus !== "online") return;
    server
      .apiCall?.("GET", "/api/grading/overrides")
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        const map = {};
        rows.forEach((o) => {
          if (o.part_id)
            map[String(o.part_id).toUpperCase()] = String(
              o.grade || "",
            ).toUpperCase();
        });
        setGradeOverrides(map);
      })
      .catch(() => {});
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveGrade = (p) =>
    gradeOverrides[String(p.partId).toUpperCase()] || p.storedGrade;
  async function changePartGrade(partId, grade) {
    const pid = String(partId).toUpperCase();
    const g = String(grade).toUpperCase();
    if (!pid || !g) return;
    setSavingPart(pid);
    try {
      const r = await server.saveGradeOverride?.(pid, g);
      if (r?.ok) {
        setGradeOverrides((prev) => ({ ...prev, [pid]: g }));
        setPendingGrades((prev) => {
          const nextp = { ...prev };
          delete nextp[pid];
          return nextp;
        });
        setGradeMsg(`${pid} → ${g} saved for all users`);
        setTimeout(() => setGradeMsg(""), 3000);
      } else {
        setGradeMsg(`Failed to save ${pid}`);
      }
    } catch {
      setGradeMsg(`Failed to save ${pid}`);
    } finally {
      setSavingPart("");
    }
  }

  useEffect(() => {
    if (server?.serverStatus !== "online") return;
    server.fetchHistoryMonths?.().then((rows) => {
      if (!Array.isArray(rows)) return;
      setMonths(rows);
      if (!month && rows[0]?.month) {
        setMonth(rows[0].month);
        setCompareMonth(rows[1]?.month || "");
        setRangeTo(rows[0].month);
        setRangeFrom(rows[rows.length - 1]?.month || rows[0].month);
      }
    });
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  async function refreshEngineerAliases() {
    if (server?.serverStatus !== "online") return [];
    const rows = await server.fetchEngineers?.();
    if (Array.isArray(rows)) {
      setEngineerAliases(rows);
      return rows;
    }
    return [];
  }

  useEffect(() => {
    refreshEngineerAliases();
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!aliasRefreshVersion || server?.serverStatus !== "online") return;
    refreshEngineerAliases().then(() => {
      if (historyMode === "range") {
        if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return;
        loadHistory(month, "", { from: rangeFrom, to: rangeTo });
        return;
      }
      if (!month) return;
      loadHistory(month, month === "ALL" ? "" : compareMonth);
    });
  }, [aliasRefreshVersion, server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (server?.serverStatus !== "online") return;
    if (historyMode === "range") {
      if (!rangeFrom || !rangeTo) return;
      if (rangeFrom > rangeTo) {
        setHistorySnapshot(null);
        setLoading(false);
        setError("Choose a range where From is before To.");
        return;
      }
      loadHistory(month, "", { from: rangeFrom, to: rangeTo });
      return;
    }
    if (!month) return;
    const effectiveCompare = month === "ALL" ? "" : compareMonth;
    loadHistory(month, effectiveCompare);
  }, [
    server?.serverStatus,
    month,
    compareMonth,
    historyMode,
    rangeFrom,
    rangeTo,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadHistory(selected, compare, range = null) {
    const key = range
      ? JSON.stringify(["range", range.from, range.to])
      : JSON.stringify(["month", selected, selected === "ALL" ? "" : compare]);
    // Alias refreshes and earlier selections may still be awaiting a response.
    if (key !== selectionKeyRef.current) return;
    const request = ++historyRequest.current;
    setLoading(true);
    setError("");
    try {
      const result = await server.fetchRepairCentreHistory?.(selected, compare, range);
      if (request !== historyRequest.current || key !== selectionKeyRef.current) return;
      if (!result) throw new Error("Could not pull History data from the server.");
      setHistorySnapshot({ key, data: result });
    } catch (e) {
      if (request === historyRequest.current && key === selectionKeyRef.current) {
        setError(e?.message || "Could not pull History data from the server.");
      }
    } finally {
      if (request === historyRequest.current && key === selectionKeyRef.current) setLoading(false);
    }
  }

  function refreshHistory() {
    if (historyMode === "range") {
      if (!rangeFrom || !rangeTo) return;
      if (rangeFrom > rangeTo) {
        setHistorySnapshot(null);
        setLoading(false);
        setError("Choose a range where From is before To.");
        return;
      }
      loadHistory(month, "", { from: rangeFrom, to: rangeTo });
      return;
    }
    loadHistory(month, month === "ALL" ? "" : compareMonth);
  }

  function n(value) {
    return Number(value || 0) || 0;
  }
  function money(value) {
    return `GBP ${n(value).toFixed(2)}`;
  }
  function pct(value) {
    return `${n(value).toFixed(1)}%`;
  }
  function dateKey(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return londonDateKey(d);
  }
  function hourKey(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return londonHourKey(d);
  }
  function canonicalEngineerName(name) {
    const raw = String(name || "").trim() || "Unknown";
    return resolveEngineerIdentity(raw, historyAliasIndex) || raw;
  }
  function canonicalEngineerKey(name) {
    const canonical = canonicalEngineerName(name);
    return (
      normalizeEngineerName(canonical) ||
      normalizeEngineerName(name) ||
      "unknown"
    );
  }
  function ensureEngineer(map, rawName, team) {
    const key = canonicalEngineerKey(rawName);
    const displayName = canonicalEngineerName(rawName);
    if (!map[key])
      map[key] = {
        engineer: displayName,
        team: team || "",
        totalParts: 0,
        recordParts: 0,
        commission: 0,
        weekendCommission: 0,
        weekendParts: 0,
        bonus: 0,
        daysWorked: 0,
        avgPerDay: 0,
        ber: 0,
        nonBer: 0,
        grades: {},
        sourceNames: [],
      };
    map[key].engineer = chooseEngineerDisplayName(
      map[key].engineer,
      displayName,
    );
    if (rawName && !map[key].sourceNames.includes(rawName))
      map[key].sourceNames.push(rawName);
    map[key].team = map[key].team || team || "";
    return map[key];
  }
  function summaryDedupeScore(summary, detailStats) {
    const display = canonicalEngineerName(summary.engineer || "");
    let score = chooseEngineerDisplayName("", display) === display ? 4 : 0;
    if (detailStats) {
      score -= Math.abs(n(summary.total_parts) - detailStats.parts) * 100;
      score -= Math.abs(n(summary.total_commission) - detailStats.commission);
    }
    if (/[a-z]/.test(String(summary.engineer || ""))) score += 2;
    if (/\s/.test(String(summary.engineer || ""))) score += 1;
    return score;
  }
  // Weekend work is not eligible for commission. Each record still stores its
  // grade value, so the paid figure excludes weekend parts and their value is
  // tracked separately as weekendCommission (information only).
  function isWeekendRecord(r) {
    if (!r?.completed_at) return false;
    const d = new Date(r.completed_at);
    return !isNaN(d.getTime()) && isLondonWeekend(d);
  }
  function buildMetrics(pack) {
    const summaries = pack?.summaries || [];
    const records = pack?.records || [];
    const recordStatsBySummaryKey = {};
    records.forEach((r) => {
      const rm = r.month || pack?.month || "Unknown";
      const key = `${canonicalEngineerKey(r.engineer || "Unknown")}|${rm}`;
      if (!recordStatsBySummaryKey[key])
        recordStatsBySummaryKey[key] = { parts: 0, commission: 0 };
      recordStatsBySummaryKey[key].parts += 1;
      if (!isWeekendRecord(r))
        recordStatsBySummaryKey[key].commission += n(r.commission);
    });
    const summaryBest = {};
    summaries.forEach((s) => {
      const sm = s.month || pack?.month || "Unknown";
      const key = `${canonicalEngineerKey(s.engineer || "Unknown")}|${sm}`;
      const score = summaryDedupeScore(s, recordStatsBySummaryKey[key]);
      if (!summaryBest[key] || score > summaryBest[key].score)
        summaryBest[key] = { row: s, score };
    });
    const summariesToUse = Object.values(summaryBest).map((entry) => entry.row);
    const engineers = {};
    const summaryKeys = new Set();
    const grades = {};
    const teams = {};
    const rotaByEngineer = {};
    const daily = {};
    const hourly = {};
    const weekday = {};
    const monthly = {};
    const missing = { team: 0, grade: 0, timestamp: 0 };
    const parts = {};
    let partsNoId = 0;
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    GRADES.forEach((g) => {
      grades[g] = { parts: 0, commission: 0 };
    });
    dayNames.forEach((d) => {
      weekday[d] = 0;
    });
    const bankHolidaySet = new Set(
      (pack?.bankHolidays || []).map((h) => h.holiday_date),
    );
    (pack?.rota || []).forEach((row) => {
      try {
        const year = Number(row.year);
        const mo = Number(row.month);
        const keyMonth =
          row.month_key || `${year}-${String(mo).padStart(2, "0")}`;
        const days = JSON.parse(row.days_json || "[]");
        const dow = JSON.parse(row.dow_json || "[]");
        const summary = summarizeRotaMonth(
          days,
          dow,
          year,
          mo,
          bankHolidaySet,
        );
        const isCurrentRotaMonth = keyMonth === currentMonthKey();
        const elapsedSummary = isCurrentRotaMonth
          ? summarizeRotaMonth(
              days,
              dow,
              year,
              mo,
              bankHolidaySet,
              getElapsedRotaDayLimit(),
            )
          : summary;
        const name = canonicalEngineerName(row.engineer || "Unknown");
        const key = canonicalEngineerKey(name);
        if (!rotaByEngineer[key])
          rotaByEngineer[key] = {
            engineer: name,
            workedDays: 0,
            fullWorkingDays: 0,
            halfDays: 0,
            holidays: 0,
            sick: 0,
            other: 0,
            halfOther: 0,
            bankHolidays: 0,
            elapsedWorkedDays: 0,
            months: {},
          };
        rotaByEngineer[key].engineer = chooseEngineerDisplayName(
          rotaByEngineer[key].engineer,
          name,
        );
        Object.keys(summary).forEach((field) => {
          rotaByEngineer[key][field] += n(summary[field]);
        });
        rotaByEngineer[key].elapsedWorkedDays += n(
          elapsedSummary.workedDays,
        );
        rotaByEngineer[key].months[keyMonth] = summary;
      } catch {}
    });
    summariesToUse.forEach((s) => {
      const key = canonicalEngineerKey(s.engineer || "Unknown");
      const sm = s.month || pack?.month || "Unknown";
      summaryKeys.add(`${key}|${sm}`);
      const engineer = ensureEngineer(
        engineers,
        s.engineer || "Unknown",
        s.team || "",
      );
      engineer.totalParts += n(s.total_parts);
      engineer.commission += n(s.total_commission);
      engineer.bonus += n(s.bonus_earned);
      engineer.daysWorked += n(s.days_worked);
      engineer.avgPerDay =
        engineer.daysWorked > 0
          ? engineer.totalParts / engineer.daysWorked
          : n(s.avg_per_day);
      if (!monthly[sm])
        monthly[sm] = {
          month: sm,
          parts: 0,
          commission: 0,
          ber: 0,
          bonusEligible: 0,
          hasSummary: false,
        };
      monthly[sm].hasSummary = true;
      monthly[sm].parts += n(s.total_parts);
      monthly[sm].commission += n(s.total_commission);
    });
    records.forEach((r) => {
      const rawName = r.engineer || "Unknown";
      const key = canonicalEngineerKey(rawName);
      const rm = r.month || pack?.month || "Unknown";
      const grade = r.is_ber ? "BER" : r.grade || "UNGRADED";
      const isWeekend = isWeekendRecord(r);
      const paidCommission = isWeekend ? 0 : n(r.commission);
      const weekendValue = isWeekend ? n(r.commission) : 0;
      const pid = String(r.part_id || "").trim();
      // Records with no part id can't be graded/identified — count them for transparency
      // but keep them out of the parts list so a blank bucket never dominates the metrics.
      if (!pid) {
        partsNoId += 1;
      } else {
        if (!parts[pid])
          parts[pid] = {
            partId: pid,
            count: 0,
            commission: 0,
            ber: 0,
            grades: {},
          };
        const pAgg = parts[pid];
        pAgg.count += 1;
        pAgg.commission += paidCommission;
        if (r.is_ber) pAgg.ber += 1;
        pAgg.grades[grade] = (pAgg.grades[grade] || 0) + 1;
      }
      const engineer = ensureEngineer(engineers, rawName, r.team || "");
      engineer.recordParts += 1;
      engineer.grades[grade] = (engineer.grades[grade] || 0) + 1;
      const completedDate = r.completed_at ? new Date(r.completed_at) : null;
      const computedBonusEligible =
        !r.is_ber && isRepairBonusWindow(completedDate);
      const bonusEligible =
        r.bonus_eligible === 0 || r.bonus_eligible === 1
          ? !!r.bonus_eligible
          : computedBonusEligible;
      if (r.is_ber) engineer.ber += 1;
      else engineer.nonBer += 1;
      if (bonusEligible)
        engineer.bonusEligible = (engineer.bonusEligible || 0) + 1;
      if (!summaryKeys.has(`${key}|${rm}`))
        engineer.commission += paidCommission;
      if (isWeekend) {
        engineer.weekendParts += 1;
        engineer.weekendCommission += weekendValue;
      }
      if (!grades[grade]) grades[grade] = { parts: 0, commission: 0 };
      grades[grade].parts += 1;
      grades[grade].commission += paidCommission;
      const team = r.team || "Unknown";
      if (!r.team) missing.team += 1;
      if (!r.grade && !r.is_ber) missing.grade += 1;
      if (!r.completed_at) missing.timestamp += 1;
      if (!teams[team])
        teams[team] = {
          team,
          parts: 0,
          commission: 0,
          weekendCommission: 0,
          ber: 0,
        };
      teams[team].parts += 1;
      teams[team].commission += paidCommission;
      teams[team].weekendCommission += weekendValue;
      if (r.is_ber) teams[team].ber += 1;
      if (!monthly[rm])
        monthly[rm] = {
          month: rm,
          parts: 0,
          commission: 0,
          ber: 0,
          bonusEligible: 0,
          hasSummary: false,
        };
      if (!monthly[rm].hasSummary) {
        monthly[rm].parts += 1;
        monthly[rm].commission += paidCommission;
      }
      monthly[rm].weekendCommission =
        (monthly[rm].weekendCommission || 0) + weekendValue;
      if (r.is_ber) monthly[rm].ber += 1;
      if (bonusEligible) monthly[rm].bonusEligible += 1;
      const dk = dateKey(r.completed_at || r.upload_date);
      if (dk) daily[dk] = (daily[dk] || 0) + 1;
      if (r.completed_at) {
        const wd = dayNames[londonWeekdayIndex(new Date(r.completed_at))];
        weekday[wd] = (weekday[wd] || 0) + 1;
      }
      const hk = hourKey(r.completed_at);
      if (hk) hourly[hk] = (hourly[hk] || 0) + 1;
    });
    Object.values(engineers).forEach((e) => {
      if (!e.totalParts) e.totalParts = e.recordParts;
      if (!e.nonBer && e.recordParts) e.nonBer = e.recordParts - e.ber;
      const rota =
        findByLooseEngineerName(rotaByEngineer, e.engineer) ||
        rotaByEngineer[normalizeEngineerName(e.engineer)];
      e.rota = rota || null;
      e.rotaWorkedDays = rota ? rota.workedDays : 0;
      e.elapsedRotaWorkedDays = rota ? rota.elapsedWorkedDays : 0;
      // Rota is the source of truth for days worked. Saved days_worked can be stale (rows
      // written before rota auto-fill worked stored placeholder values), so prefer the live
      // rota figure whenever rota data exists for this engineer/month; fall back to the saved
      // value only for legacy rows with no rota.
      const dayMetrics = calculateHistoryEffectiveDays(e);
      e.effectiveDays = dayMetrics.effectiveDays;
      e.daySource = dayMetrics.daySource;
      e.usingElapsedRota = dayMetrics.usingElapsedRota;
      if (e.effectiveDays > 0) e.avgPerDay = e.totalParts / e.effectiveDays;
      e.extraDays = e.daysWorked && rota ? e.daysWorked - rota.workedDays : 0;
      e.earningsPerRotaDay =
        e.rotaWorkedDays > 0 ? e.commission / e.rotaWorkedDays : 0;
      e.earningsPerSavedDay =
        e.daysWorked > 0 ? e.commission / e.daysWorked : 0;
      e.bonusEligible = e.bonusEligible || 0;
      e.bonusPerRotaDay =
        e.rotaWorkedDays > 0 ? e.bonusEligible / e.rotaWorkedDays : 0;
      // bonusAvg = bonusEligibleParts / days worked, using rota days as the source of truth
      // and falling back to saved days only for legacy rows with no rota.
      const bonusDays =
        e.effectiveDays > 0 ? e.effectiveDays : e.rotaWorkedDays;
      const bonusAvg = bonusDays > 0 ? e.bonusEligible / bonusDays : 0;
      const achievedTier =
        [...getRepairHistoryTeamTiers(e.team, teamTargets)]
          .sort((a, b) => b.daily - a.daily)
          .find((tier) => bonusAvg >= tier.daily) || null;
      e.bonusDays = bonusDays;
      e.bonusAvg = bonusAvg;
      e.bonusTier = achievedTier;
      e.calculatedBonus = achievedTier ? n(achievedTier.bonus) : 0;
      // Trust the bonus saved by the Repair Centre Calculator (it knows the user's custom tier
      // overrides). Only fall back to the recalculation if nothing was saved.
      if (!(e.bonus > 0)) e.bonus = e.calculatedBonus;
      e.totalEarnings = e.commission + e.bonus;
      const gradeBest = Object.entries(e.grades || {})
        .filter(([g]) => !["BER", "UNGRADED", "M", "A"].includes(g))
        .sort((a, b) => b[1] - a[1])[0];
      e.focusSuggestion = gradeBest
        ? `Keep pushing ${gradeBest[0]} volume; it is already a strong earner for this engineer.`
        : "Increase X/S/C repairs where practical; low-grade volume needs more units for the same money.";
    });
    const partsList = Object.values(parts).map((p) => {
      // Representative stored grade = the grade most of this part's records carried at upload.
      const storedGrade =
        Object.entries(p.grades).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        "UNGRADED";
      return { ...p, storedGrade, isBer: p.ber > p.count / 2 };
    });
    const engineerList = Object.values(engineers).sort(
      (a, b) => b.commission - a.commission,
    );
    const totalParts = engineerList.reduce(
      (sum, e) => sum + n(e.totalParts),
      0,
    );
    const totalCommission = engineerList.reduce(
      (sum, e) => sum + n(e.commission),
      0,
    );
    const totalBonus = engineerList.reduce((sum, e) => sum + n(e.bonus), 0);
    const totalWeekendCommission = engineerList.reduce(
      (sum, e) => sum + n(e.weekendCommission),
      0,
    );
    const totalWeekendParts = engineerList.reduce(
      (sum, e) => sum + n(e.weekendParts),
      0,
    );
    const berParts = records.filter((r) => r.is_ber).length;
    const datedRecords = records.filter((r) => r.completed_at);
    const bestDay =
      Object.entries(daily).sort((a, b) => b[1] - a[1])[0] || null;
    const peakHour =
      Object.entries(hourly).sort((a, b) => b[1] - a[1])[0] || null;
    return {
      engineers: engineerList,
      grades,
      teams: Object.values(teams).sort((a, b) => b.parts - a.parts),
      monthly: Object.values(monthly).sort((a, b) =>
        a.month.localeCompare(b.month),
      ),
      daily,
      hourly,
      weekday,
      totalParts,
      totalCommission,
      totalWeekendCommission,
      totalWeekendParts,
      totalBonus,
      berParts,
      nonBerParts: Math.max(0, totalParts - berParts),
      avgPerEngineer: engineerList.length
        ? totalParts / engineerList.length
        : 0,
      datedRecords: datedRecords.length,
      bestDay,
      peakHour,
      missing,
      avgCommissionPerPart: totalParts > 0 ? totalCommission / totalParts : 0,
      rotaCoverage: Object.keys(rotaByEngineer).length,
      rotaByEngineer,
      parts: partsList,
      partsNoId,
    };
  }

  // Presentation-only changes (including the cross-team dropdown) must not
  // rescan every saved booking and rerun diagnostics.
  const historyToday = londonDateKey(new Date());
  const rawCurrent = useMemo(
    () => buildMetrics(data?.current),
    [data?.current, historyAliasIndex, teamTargets, historyToday], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Canonical engineer -> team map, derived from the unfiltered metrics. Records frequently
  // arrive with empty team values; we resolve a single team per engineer so the team filter
  // matches every row for that engineer instead of dropping rows with no team column.
  const teamByEngineerKey = useMemo(() => {
    const map = {};
    rawCurrent.engineers.forEach((e) => {
      const key = canonicalEngineerKey(e.engineer);
      if (!map[key] && e.team) map[key] = e.team;
    });
    return map;
  }, [rawCurrent]); // eslint-disable-line react-hooks/exhaustive-deps
  const engineerMatchesTeam = (rawName, fallbackTeam) => {
    if (historyTeam === "ALL") return true;
    const key = canonicalEngineerKey(rawName);
    const resolved = teamByEngineerKey[key] || fallbackTeam || "Unknown";
    return resolved === historyTeam;
  };

  function filterPack(pack) {
    const q = engineerSearch.trim().toLowerCase();
    const selectedKey = selectedEngineer === "ALL" ? "" : selectedEngineer;
    return {
      ...pack,
      summaries: (pack?.summaries || []).filter((s) => {
        const display = canonicalEngineerName(s.engineer || "Unknown");
        const key = canonicalEngineerKey(s.engineer || "Unknown");
        return (
          !isExcludedFromHistory(s.engineer, engineerStatusIndex) &&
          engineerMatchesTeam(s.engineer, s.team) &&
          (!selectedKey || key === selectedKey) &&
          (!q ||
            String(s.engineer || "")
              .toLowerCase()
              .includes(q) ||
            display.toLowerCase().includes(q))
        );
      }),
      records: (pack?.records || []).filter((r) => {
        const name = r.engineer || "Unknown";
        const display = canonicalEngineerName(name);
        const key = canonicalEngineerKey(name);
        return (
          !isExcludedFromHistory(name, engineerStatusIndex) &&
          engineerMatchesTeam(name, r.team) &&
          (!selectedKey || key === selectedKey) &&
          (!q ||
            String(name).toLowerCase().includes(q) ||
            display.toLowerCase().includes(q))
        );
      }),
    };
  }
  const engineerOptions = rawCurrent.engineers
    .filter(
      (e) =>
        historyTeam === "ALL" ||
        (teamByEngineerKey[canonicalEngineerKey(e.engineer)] ||
          e.team ||
          "Unknown") === historyTeam,
    )
    .map((e) => ({ key: canonicalEngineerKey(e.engineer), label: e.engineer }))
    .filter(
      (e, idx, arr) =>
        e.key && arr.findIndex((item) => item.key === e.key) === idx,
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  const allTeamsForFilter = rawCurrent.teams
    .map((t) => t.team)
    .filter(Boolean)
    .sort();
  // Reset selectedEngineer when team filter no longer contains them
  useEffect(() => {
    if (selectedEngineer === "ALL" || loading || !data?.current) return;
    if (!engineerOptions.some((o) => o.key === selectedEngineer))
      setSelectedEngineer("ALL");
  }, [historyTeam, engineerOptions, selectedEngineer, loading, data]);
  const visibleCurrentPack = useMemo(
    () => filterPack(data?.current),
    [data?.current, historyTeam, selectedEngineer, engineerSearch, historyAliasIndex, engineerStatusIndex, teamByEngineerKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visibleComparePack = useMemo(
    () => filterPack(data?.compare),
    [data?.compare, historyTeam, selectedEngineer, engineerSearch, historyAliasIndex, engineerStatusIndex, teamByEngineerKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const current = useMemo(
    () => buildMetrics(visibleCurrentPack),
    [visibleCurrentPack, historyAliasIndex, teamTargets, historyToday], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const compare = useMemo(
    () => buildMetrics(visibleComparePack),
    [visibleComparePack, historyAliasIndex, teamTargets, historyToday], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasData = !!data?.current?.exists;
  const selectedPeriodLabel =
    historyMode === "range"
      ? `${rangeFrom || "?"} to ${rangeTo || "?"}`
      : month;
  const compareDisabled = historyMode === "range" || month === "ALL";
  const comparisonActive = !compareDisabled && !!compareMonth;
  // London month, not the UTC one - just after midnight on the 1st during BST
  // these disagree, and the History tab would compare against the wrong month.
  const currentCalendarMonth = monthKeyFromDate(new Date());
  const isCurrentMonth = historyMode === "month" && month === currentCalendarMonth;
  const daysWorkedEngineers = current.engineers.filter(
    (e) => n(e.effectiveDays) > 0,
  );
  const daysWorkedAvg = daysWorkedEngineers.length
    ? daysWorkedEngineers.reduce((sum, e) => sum + n(e.effectiveDays), 0) /
      daysWorkedEngineers.length
    : 0;
  const remainingDays = isCurrentMonth ? n(server?.workingDaysLeft) : 0;
  const projectedParts =
    daysWorkedAvg > 0
      ? current.totalParts +
        (current.totalParts / daysWorkedAvg) * remainingDays
      : current.totalParts;
  const projectedCommission =
    daysWorkedAvg > 0
      ? current.totalCommission +
        (current.totalCommission / daysWorkedAvg) * remainingDays
      : current.totalCommission;

  const delta = {
    parts: current.totalParts - compare.totalParts,
    commission: current.totalCommission - compare.totalCommission,
    ber: current.berParts - compare.berParts,
    bonus: current.totalBonus - compare.totalBonus,
  };

  const compareByEngineer = {};
  compare.engineers.forEach((e) => {
    compareByEngineer[canonicalEngineerKey(e.engineer)] = e;
  });
  const engineerDeltas = current.engineers
    .map((e) => ({
      ...e,
      partsDelta:
        e.totalParts -
        n(compareByEngineer[canonicalEngineerKey(e.engineer)]?.totalParts),
      commissionDelta:
        e.commission -
        n(compareByEngineer[canonicalEngineerKey(e.engineer)]?.commission),
    }))
    .sort((a, b) => b.commissionDelta - a.commissionDelta);
  const topEngineer = current.engineers[0];
  const berLeader = [...current.engineers].sort((a, b) => b.ber - a.ber)[0];
  const topImprover = engineerDeltas[0];
  const biggestDrop = [...engineerDeltas].sort(
    (a, b) => a.commissionDelta - b.commissionDelta,
  )[0];
  const timestampCoverage = current.totalParts
    ? (current.datedRecords / current.totalParts) * 100
    : 0;
  const visibleRecordCount = (visibleCurrentPack?.records || []).length;
  const summaryRecordGap = Math.abs(current.totalParts - visibleRecordCount);
  // Judged against the same filtered records the rest of the tab shows, so the
  // team and engineer pickers narrow this panel too. Alias spellings fold into
  // one engineer, exactly as they do everywhere else in History.
  const crossReport = useMemo(
    () =>
      buildCrossTeamReport({
        records: visibleCurrentPack?.records || [],
        access: crossAccess,
        resolveEngineer: canonicalEngineerName,
      }),
    // canonicalEngineerName closes over the alias index, which is the part that
    // actually changes.
    [visibleCurrentPack, crossAccess, historyAliasIndex], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const crossSummaryLabel = !crossAccess
    ? "reference not loaded"
    : crossReport.totals.cross === 0
      ? "none found"
      : `${n(crossReport.totals.cross).toLocaleString()} parts, ` +
        `${pct(crossReport.totals.crossRate)} of judged work`;
  function exportCrossTeamCsv() {
    const headers = [
      "period",
      "month",
      "engineer",
      "engineer_team",
      "part_id",
      "grade",
      "owning_teams",
      "commission",
      "unit_status",
      "is_ber",
      "completed_at",
    ];
    const rows = crossReport.rows.map((row) => [
      selectedPeriodLabel,
      row.month,
      row.engineer,
      row.engineerTeam,
      row.partId,
      row.grade,
      row.owningTeams,
      row.commission.toFixed(2),
      row.unitStatus,
      row.ber,
      row.completedAt,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `razergrid-cross-team-${(selectedPeriodLabel || "history").replace(/[^a-z0-9-]+/gi, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  const consistencyReport = useMemo(() => buildHistoryConsistencyReport(
    current,
    visibleCurrentPack,
    {
      month: selectedPeriodLabel,
      aliasRows: engineerAliases,
      statusIndex: engineerStatusIndex,
    },
  ), [current, visibleCurrentPack, selectedPeriodLabel, engineerAliases, engineerStatusIndex]);
  // Shown next to the collapsed header so the panel's state is readable without
  // expanding it.
  const consistencyIssueSummary = (() => {
    const critical = n(consistencyReport.counts.critical);
    const warning = n(consistencyReport.counts.warning);
    const info = n(consistencyReport.counts.info);
    const parts = [];
    if (critical) parts.push(`${critical} critical`);
    if (warning) parts.push(`${warning} warning${warning === 1 ? "" : "s"}`);
    if (info) parts.push(`${info} info`);
    return parts.length ? parts.join(", ") : "no issues";
  })();
  const consistencyRows = consistencyReport.issues.filter(
    (issue) =>
      (consistencySeverity === "ALL" ||
        issue.severity === consistencySeverity) &&
      (consistencyCategory === "ALL" ||
        issue.category === consistencyCategory),
  );
  const aliasSuggestions = useMemo(() => buildEngineerAliasSuggestions(
    visibleCurrentPack,
    engineerAliases,
    { statusIndex: engineerStatusIndex },
  ), [visibleCurrentPack, engineerAliases, engineerStatusIndex]);
  const autoAliasSuggestions = aliasSuggestions.filter((s) => s.autoApply);
  const historyReconciliation = useMemo(() => {
    const historyReconciliationSourceNames = [
    ...(visibleCurrentPack?.summaries || []).map((row) => row.engineer),
    ...(visibleCurrentPack?.records || []).map((row) => row.engineer),
    ...(visibleCurrentPack?.uploadNames || []),
    ...(visibleCurrentPack?.uploadRows || []).map(
      (row) => row?.engineer || row?.repairer || row?.person || row?.name,
    ),
  ];
    return buildReconciliationReport({
    sourceNames: historyReconciliationSourceNames,
    targets: engineerAliases,
    rotaTargets: visibleCurrentPack?.rota || [],
    suggestions: aliasSuggestions,
    statusIndex: engineerStatusIndex,
    targetSource: visibleCurrentPack?.rota?.length
      ? "history rota"
      : engineerAliases.length
        ? "full roster"
        : "unavailable",
    });
  }, [visibleCurrentPack, engineerAliases, aliasSuggestions, engineerStatusIndex]);
  const insightCards = [
    topEngineer && {
      title: "Highest earner",
      value: topEngineer.engineer,
      detail: `${money(topEngineer.commission)} from ${topEngineer.totalParts} parts`,
      accent: "#22c55e",
    },
    topImprover &&
      comparisonActive && {
        title: "Biggest commission lift",
        value: topImprover.engineer,
        detail: `${topImprover.commissionDelta >= 0 ? "+" : ""}${money(topImprover.commissionDelta)} vs ${compareMonth}`,
        accent: "#8ab8fa",
      },
    biggestDrop &&
      comparisonActive &&
      biggestDrop.commissionDelta < 0 && {
        title: "Biggest commission drop",
        value: biggestDrop.engineer,
        detail: `${money(biggestDrop.commissionDelta)} vs ${compareMonth}`,
        accent: "#f87171",
      },
    berLeader &&
      berLeader.ber > 0 && {
        title: "BER hotspot",
        value: berLeader.engineer,
        detail: `${berLeader.ber} BER units (${pct(berLeader.totalParts ? (berLeader.ber / berLeader.totalParts) * 100 : 0)})`,
        accent: "#fbbf24",
      },
    current.bestDay && {
      title: "Peak workload day",
      value: current.bestDay[0],
      detail: `${current.bestDay[1]} completed parts`,
      accent: "#5de7ff",
    },
    current.peakHour && {
      title: "Peak completion hour",
      value: current.peakHour[0],
      detail: `${current.peakHour[1]} completed parts`,
      accent: "#fbbf24",
    },
    timestampCoverage < 60 && {
      title: "Timestamp coverage",
      value: pct(timestampCoverage),
      detail: "Daily/hourly charts are limited until newer files are saved",
      accent: "#f87171",
    },
    summaryRecordGap > 0 && {
      title: "Summary/detail gap",
      value: summaryRecordGap.toLocaleString(),
      detail: "Summary part total differs from saved part rows",
      accent: "#f59e0b",
    },
  ].filter(Boolean);

  function csvCell(value) {
    const s = value === null || value === undefined ? "" : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }
  function exportEngineerCsv() {
    const headers = [
      "month",
      "engineer",
      "team",
      "parts",
      "non_ber",
      "ber",
      "commission",
      "weekend_commission_not_paid",
      "bonus",
      "days_worked",
      "avg_per_day",
      "commission_delta",
      "parts_delta",
    ];
    const rows = current.engineers.map((e) => {
      const d = engineerDeltas.find((row) => row.engineer === e.engineer) || {};
      return [
        month,
        e.engineer,
        e.team || "",
        e.totalParts,
        e.nonBer,
        e.ber,
        e.commission.toFixed(2),
        n(e.weekendCommission).toFixed(2),
        e.bonus.toFixed(2),
        e.effectiveDays || "",
        e.avgPerDay ? e.avgPerDay.toFixed(2) : "",
        comparisonActive ? n(d.commissionDelta).toFixed(2) : "",
        comparisonActive ? n(d.partsDelta) : "",
      ];
    });
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `razergrid-history-${month || "month"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function exportConsistencyCsv() {
    const headers = [
      "period",
      "severity",
      "category",
      "month",
      "engineer",
      "team",
      "summary",
      "detail",
      "action",
      "evidence",
    ];
    const rows = consistencyRows.map((issue) => [
      selectedPeriodLabel,
      issue.severity,
      issue.category,
      issue.month,
      issue.engineer,
      issue.team,
      issue.summary,
      issue.detail,
      issue.action,
      issue.evidence,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `razergrid-consistency-${(selectedPeriodLabel || "history").replace(/[^a-z0-9-]+/gi, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function exportReconciliationCsv() {
    const headers = [
      "period",
      "name",
      "engineer",
      "classification",
      "occurrences",
      "source",
      "status",
      "reason",
      "evidence",
      "action",
    ];
    const rows = historyReconciliation.rows.map((row) => [
      selectedPeriodLabel,
      row.name,
      row.engineer,
      row.classification,
      row.occurrences,
      row.source,
      row.status,
      row.reason,
      row.evidence,
      row.action,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `razergrid-reconciliation-${(selectedPeriodLabel || "history").replace(/[^a-z0-9-]+/gi, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  async function applyHighConfidenceAliases() {
    if (!autoAliasSuggestions.length || server?.serverStatus !== "online")
      return;
    setAliasApplying(true);
    setAliasApplyMsg("");
    const currentRoster = engineerAliases || [];
    const grouped = {};
    autoAliasSuggestions.forEach((suggestion) => {
      if (!grouped[suggestion.engineer]) grouped[suggestion.engineer] = [];
      grouped[suggestion.engineer].push(suggestion.alias);
    });

    let saved = 0;
    let failed = 0;
    for (const [engineer, aliases] of Object.entries(grouped)) {
      const row = currentRoster.find((entry) => entry.engineer === engineer);
      const existing = (row?.aliases || [])
        .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
        .filter(Boolean);
      const merged = [...existing];
      aliases.forEach((alias) => {
        const key = normalizeEngineerName(alias);
        if (!key) return;
        if (!merged.some((item) => normalizeEngineerName(item) === key))
          merged.push(alias);
      });
      try {
        const result = await server.saveEngineerAliases?.(engineer, merged);
        if (result?.ok) saved += aliases.length;
        else failed += aliases.length;
      } catch {
        failed += aliases.length;
      }
    }

    await refreshEngineerAliases();
    setAliasApplying(false);
    setAliasApplyMsg(
      failed
        ? `Applied ${saved} aliases; ${failed} need manual review.`
        : `Applied ${saved} high-confidence aliases.`,
    );
    setTimeout(() => setAliasApplyMsg(""), 3500);
  }

  const panel = {
    background: "#071225",
    border: "1px solid #142849",
    borderRadius: 10,
    padding: 14,
  };
  const DeltaText = ({ value, kind = "number" }) => {
    const good = value >= 0;
    const text =
      kind === "money"
        ? `${good ? "+" : ""}${money(value)}`
        : `${good ? "+" : ""}${value.toLocaleString()}`;
    return (
      <span
        style={{
          color: good ? "#22c55e" : "#f87171",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {text}
      </span>
    );
  };
  const Metric = ({ label, value, sub, accent = "#8ab8fa", index = 0 }) => (
    <div
      style={mergeStyles(
        panel,
        historyStaggerStyle(index, fxEnabled, { stepMs: 35, maxDelayMs: 210 }),
      )}
    >
      <div
        style={{
          color: "#4f6fa0",
          fontSize: 9,
          letterSpacing: 1,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: accent,
          fontSize: 22,
          fontWeight: 800,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ color: "#3d5280", fontSize: 10, marginTop: 6 }}>
          {sub}
        </div>
      )}
    </div>
  );
  const BarList = ({
    rows,
    valueKey = "value",
    labelKey = "label",
    color = "#4f8ef7",
    suffix = "",
    displayKey = null,
  }) => {
    const max = Math.max(1, ...rows.map((r) => n(r[valueKey])));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {rows.map((r, index) => {
          const percent = (n(r[valueKey]) / max) * 100;
          return (
            <div
              key={r[labelKey]}
              style={mergeStyles(
                {
                  display: "grid",
                  gridTemplateColumns: "minmax(90px, 150px) 1fr 64px",
                  gap: 8,
                  alignItems: "center",
                },
                historyStaggerStyle(index, fxEnabled, {
                  stepMs: 18,
                  maxDelayMs: 180,
                }),
              )}
            >
              <div
                style={{
                  color: "#7aa0d4",
                  fontSize: 11,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r[labelKey]}
              </div>
              <div
                style={{
                  height: 8,
                  background: "#0a1830",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div style={historyBarFillStyle(percent, fxEnabled, color)} />
              </div>
              <div
                style={{
                  color: "#c8d8f0",
                  fontSize: 11,
                  textAlign: "right",
                  fontFamily: "monospace",
                }}
              >
                {n(
                  displayKey && r[displayKey] !== undefined
                    ? r[displayKey]
                    : r[valueKey],
                ).toLocaleString()}
                {suffix}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const dailyRows = Object.entries(current.daily)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, value]) => ({ label, value }));
  const hourlyRows = Object.entries(current.hourly)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, value]) => ({ label, value }));
  const weekdayRows = Object.entries(current.weekday)
    .map(([label, value]) => ({ label, value }))
    .filter((r) => r.value > 0);
  const monthlyRows = current.monthly.map((m) => ({
    label: m.month,
    parts: m.parts,
    commission: m.commission,
    ber: m.ber,
    bonusEligible: m.bonusEligible,
  }));
  const rotaRows = current.engineers
    .filter((e) => e.rota)
    .map((e) => ({
      label: e.engineer,
      value: e.rotaWorkedDays,
      extraDays: e.extraDays,
      earningsPerRotaDay: e.earningsPerRotaDay,
      bonusPerRotaDay: e.bonusPerRotaDay,
      sick: e.rota?.sick || 0,
      other: (e.rota?.other || 0) + (e.rota?.halfOther || 0) * 0.5,
      bankHolidays: e.rota?.bankHolidays || 0,
    }));
  const gradeRows = GRADES.map((grade) => ({
    label: grade,
    parts: current.grades[grade]?.parts || 0,
    commission: current.grades[grade]?.commission || 0,
  })).filter((r) => r.parts > 0);
  // Completed-parts list for the current selection, with grade overrides overlaid.
  // Ungraded (non-BER) parts are flagged to the top so they can be fixed first.
  const PART_GRADE_OPTIONS = ["X", "S", "C", "B", "A", "M", "BER"];
  const PART_ROW_CAP = 200;
  const partRowsAll = (current.parts || []).map((p) => {
    const effGrade = effectiveGrade(p);
    return { ...p, effGrade, isUngraded: effGrade === "UNGRADED" && !p.isBer };
  });
  const partRows = [...partRowsAll].sort((a, b) => {
    if (a.isUngraded !== b.isUngraded) return a.isUngraded ? -1 : 1;
    if (b.count !== a.count) return b.count - a.count;
    return b.commission - a.commission;
  });
  const partRowsShown = partRows.slice(0, PART_ROW_CAP);
  const mostFixedPart = partRowsAll.reduce(
    (best, p) => (!best || p.count > best.count ? p : best),
    null,
  );
  const topValuePart = partRowsAll.reduce(
    (best, p) => (!best || p.commission > best.commission ? p : best),
    null,
  );
  const ungradedCount = partRowsAll.filter((p) => p.isUngraded).length;
  const totalCompletions = partRowsAll.reduce((sum, p) => sum + p.count, 0);
  const historyBriefItems = buildHistoryOperationalBrief(current, {
    ungradedCount,
    summaryRecordGap,
    maxItems: 5,
  });
  const dailyAverageRows = buildEngineerDailyAverageRows(current.engineers, {
    limit: 8,
  });
  const performanceCoachRows = buildHistoryPerformanceCoach(current.engineers, {
    tiersByTeam: buildHistoryTierMap(teamTargets),
    limit: 8,
  });
  const briefTone = {
    good: { accent: "#22c55e", bg: "rgba(34,197,94,0.08)" },
    warning: { accent: "#fbbf24", bg: "rgba(251,191,36,0.08)" },
    risk: { accent: "#f87171", bg: "rgba(248,113,113,0.08)" },
  };
  const consistencyTone = {
    critical: { label: "Critical", accent: "#f87171", bg: "#2a1118" },
    warning: { label: "Warning", accent: "#fbbf24", bg: "#241b0a" },
    info: { label: "Info", accent: "#5de7ff", bg: "#071a25" },
    clean: { label: "Clean", accent: "#22c55e", bg: "#081f18" },
  };

  const filteredEmpty = hasData && current.engineers.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <Label>History</Label>
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 12,
                lineHeight: 1.6,
                maxWidth: 860,
              }}
            >
              Server-saved Repair Centre history. Compare months, inspect grade
              mix, track BER load, and spot workload peaks by date or hour where
              completed timestamps exist.
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div>
              <Label>Timespan</Label>
              <Select
                value={historyMode}
                onChange={(e) => setHistoryMode(e.target.value)}
              >
                <option value="month">Single / all</option>
                <option value="range">Month range</option>
              </Select>
            </div>
            {historyMode === "range" ? (
              <>
                <div>
                  <Label>From</Label>
                  <Select
                    value={rangeFrom}
                    onChange={(e) => setRangeFrom(e.target.value)}
                  >
                    {months
                      .slice()
                      .reverse()
                      .map((m) => (
                        <option key={m.month} value={m.month}>
                          {m.month}
                        </option>
                      ))}
                  </Select>
                </div>
                <div>
                  <Label>To</Label>
                  <Select
                    value={rangeTo}
                    onChange={(e) => setRangeTo(e.target.value)}
                  >
                    {months
                      .slice()
                      .reverse()
                      .map((m) => (
                        <option key={m.month} value={m.month}>
                          {m.month}
                        </option>
                      ))}
                  </Select>
                </div>
              </>
            ) : (
              <div>
                <Label>Month</Label>
                <Select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  <option value="ALL">All uploaded months</option>
                  {months.map((m) => (
                    <option key={m.month} value={m.month}>
                      {m.month}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div>
              <Label>Compare With</Label>
              <Select
                value={compareDisabled ? "" : compareMonth}
                onChange={(e) => setCompareMonth(e.target.value)}
                disabled={compareDisabled}
                title={
                  compareDisabled
                    ? "Comparison only applies to a single month"
                    : ""
                }
              >
                <option value="">
                  {compareDisabled ? "Not available" : "No comparison"}
                </option>
                {!compareDisabled &&
                  months
                    .filter((m) => m.month !== month)
                    .map((m) => (
                      <option key={m.month} value={m.month}>
                        {m.month}
                      </option>
                    ))}
              </Select>
            </div>
            <div>
              <Label>Team</Label>
              <Select
                value={historyTeam}
                onChange={(e) => setHistoryTeam(e.target.value)}
              >
                <option value="ALL">All teams</option>
                {allTeamsForFilter.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Engineer</Label>
              <Select
                value={selectedEngineer}
                onChange={(e) => setSelectedEngineer(e.target.value)}
              >
                <option value="ALL">All engineers</option>
                {engineerOptions.map((engineer) => (
                  <option key={engineer.key} value={engineer.key}>
                    {engineer.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <Input
                value={engineerSearch}
                onChange={(e) => setEngineerSearch(e.target.value)}
                placeholder="Engineer name..."
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ color: "#4f6fa0", fontSize: 11 }}>
              {hasData
                ? `${current.engineers.length} engineer${current.engineers.length === 1 ? "" : "s"} match the current filters`
                : ""}
              {(historyTeam !== "ALL" ||
                selectedEngineer !== "ALL" ||
                engineerSearch) && (
                <button
                  onClick={() => {
                    setHistoryTeam("ALL");
                    setSelectedEngineer("ALL");
                    setEngineerSearch("");
                  }}
                  style={{
                    marginLeft: 10,
                    background: "none",
                    border: "1px solid #1e3056",
                    color: "#7aa0d4",
                    borderRadius: 4,
                    fontSize: 10,
                    padding: "2px 8px",
                    cursor: "pointer",
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn
                variant="secondary"
                onClick={refreshHistory}
                disabled={loading || server?.serverStatus !== "online"}
              >
                {loading ? "LOADING..." : "REFRESH"}
              </Btn>
              {hasData && (
                <Btn variant="secondary" onClick={exportEngineerCsv}>
                  EXPORT CSV
                </Btn>
              )}
            </div>
          </div>
        </div>
      </Card>

      {server?.serverStatus !== "online" && (
        <Card>
          <div style={{ color: "#f87171", fontSize: 13 }}>
            Server offline. History needs the live server because it reads saved
            Repair Centre data.
          </div>
        </Card>
      )}
      {error && (
        <Card>
          <div style={{ color: "#f87171", fontSize: 13 }}>{error}</div>
        </Card>
      )}
      {!loading && server?.serverStatus === "online" && months.length === 0 && (
        <Card>
          <div style={{ color: "#fbbf24", fontSize: 13 }}>
            No Repair Centre history exists on the server yet. Upload a KPI file
            in Repair Centre Earnings and use Save To Server.
          </div>
        </Card>
      )}
      {!loading && month && !hasData && (
        <Card>
          <div style={{ color: "#fbbf24", fontSize: 13 }}>
            No saved data exists for {selectedPeriodLabel}. Upload that period
            in the Repair Centre Earnings calculator, calculate it, then use
            Save To Server.
          </div>
        </Card>
      )}
      {filteredEmpty && (
        <Card>
          <div style={{ color: "#fbbf24", fontSize: 13 }}>
            No engineers match the current filters. Clear or widen the team /
            engineer filter to see data.
          </div>
        </Card>
      )}

      {hasData && !filteredEmpty && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10,
            }}
          >
            <Metric
              index={0}
              label="Total Parts"
              value={current.totalParts.toLocaleString()}
              sub={
                comparisonActive ? (
                  <DeltaText value={delta.parts} />
                ) : (
                  `${current.engineers.length} engineers`
                )
              }
            />
            <Metric
              index={1}
              label="Commission (excl. weekend)"
              value={money(current.totalCommission)}
              sub={
                comparisonActive ? (
                  <DeltaText value={delta.commission} kind="money" />
                ) : (
                  `Bonus ${money(current.totalBonus)}`
                )
              }
              accent="#22c55e"
            />
            <Metric
              index={2}
              label="Total Earnings"
              value={money(current.totalCommission + current.totalBonus)}
              sub={`${money(current.totalBonus)} calculated bonus`}
              accent="#22c55e"
            />
            <Metric
              index={2}
              label="Weekend Commission"
              value={money(current.totalWeekendCommission || 0)}
              sub={`${(current.totalWeekendParts || 0).toLocaleString()} weekend parts - not paid, info only`}
              accent="#f59e0b"
            />
            <Metric
              index={3}
              label="BER Units"
              value={current.berParts.toLocaleString()}
              sub={`${pct(current.totalParts ? (current.berParts / current.totalParts) * 100 : 0)} of workload`}
              accent="#94a3b8"
            />
            <Metric
              index={4}
              label="Avg Parts / Engineer"
              value={current.avgPerEngineer.toFixed(1)}
              sub={
                current.bestDay
                  ? `Peak day ${current.bestDay[0]} (${current.bestDay[1]})`
                  : "No dated peak"
              }
              accent="#fbbf24"
            />
            <Metric
              index={5}
              label="Timestamp Coverage"
              value={pct(
                current.totalParts
                  ? (current.datedRecords / current.totalParts) * 100
                  : 0,
              )}
              sub={
                current.peakHour
                  ? `Peak hour ${current.peakHour[0]} (${current.peakHour[1]})`
                  : "Older uploads may not include times"
              }
              accent="#5de7ff"
            />
          </div>

          <CrossTeamPanel
            report={crossReport}
            access={crossAccess}
            expanded={crossExpanded}
            onToggle={() => setCrossExpanded((v) => !v)}
            view={crossView}
            onViewChange={setCrossView}
            loading={crossLoading}
            error={crossError}
            onReload={loadCrossAccess}
            onExport={exportCrossTeamCsv}
            summaryLabel={crossSummaryLabel}
            fxEnabled={fxEnabled}
            n={n}
            money={money}
            pct={pct}
          />

          <Card>
            <div
              onClick={() => setConsistencyExpanded((v) => !v)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    color: "#7aa0d4",
                    fontSize: 13,
                    transform: consistencyExpanded
                      ? "rotate(90deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.15s",
                    display: "inline-block",
                  }}
                >
                  ▶
                </span>
                <Label>Consistency Check</Label>
                <span style={{ color: "#4f6fa0", fontSize: 11 }}>
                  ({consistencyIssueSummary})
                </span>
              </div>
              <span style={{ color: "#4f6fa0", fontSize: 11 }}>
                {consistencyExpanded ? "Hide" : "Show list"}
              </span>
            </div>
            {consistencyExpanded && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    margin: "12px 0",
                  }}
                >
                  <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                    Read-only data health for rota, aliases, summaries, teams,
                    timestamps, and grades.
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Select
                      value={consistencySeverity}
                      onChange={(e) => setConsistencySeverity(e.target.value)}
                      style={{ minWidth: 128 }}
                    >
                      <option value="ALL">All severities</option>
                      <option value="critical">Critical</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </Select>
                    <Select
                      value={consistencyCategory}
                      onChange={(e) => setConsistencyCategory(e.target.value)}
                      style={{ minWidth: 150 }}
                    >
                      <option value="ALL">All categories</option>
                      {consistencyReport.categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Select>
                    <Btn
                      variant="secondary"
                      onClick={exportConsistencyCsv}
                      disabled={consistencyRows.length === 0}
                    >
                      EXPORT ISSUES
                    </Btn>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  {["critical", "warning", "info", "clean"].map((key, index) => {
                    const tone = consistencyTone[key];
                    return (
                      <div
                        key={key}
                        style={mergeStyles(
                          {
                            border: `1px solid ${tone.accent}33`,
                            background: tone.bg,
                            borderRadius: 8,
                            padding: "10px 12px",
                          },
                          historyStaggerStyle(index, fxEnabled, {
                            stepMs: 20,
                            maxDelayMs: 100,
                          }),
                        )}
                      >
                        <div
                          style={{
                            color: tone.accent,
                            fontSize: 10,
                            textTransform: "uppercase",
                            fontWeight: 800,
                          }}
                        >
                          {tone.label}
                        </div>
                        <div
                          style={{
                            color: "#dbeafe",
                            fontSize: 20,
                            fontWeight: 900,
                            lineHeight: 1.2,
                            marginTop: 4,
                          }}
                        >
                          {n(consistencyReport.counts[key]).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    border: "1px solid #142849",
                    background: "#071225",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                      marginBottom: aliasSuggestions.length ? 10 : 0,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#dbeafe",
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        Auto Alias Suggestions
                      </div>
                      <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 3 }}>
                        High-confidence matches are one-to-one only. Review matches
                        are shown but not applied.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {aliasApplyMsg && (
                        <div
                          style={{
                            color: aliasApplyMsg.startsWith("Applied")
                              ? "#22c55e"
                              : "#f87171",
                            fontSize: 10,
                          }}
                        >
                          {aliasApplyMsg}
                        </div>
                      )}
                      <Btn
                        variant="secondary"
                        onClick={applyHighConfidenceAliases}
                        disabled={
                          aliasApplying ||
                          autoAliasSuggestions.length === 0 ||
                          server?.serverStatus !== "online"
                        }
                      >
                        {aliasApplying
                          ? "APPLYING..."
                          : `APPLY HIGH CONFIDENCE (${autoAliasSuggestions.length})`}
                      </Btn>
                    </div>
                  </div>
                  {aliasSuggestions.length > 0 ? (
                    <div style={{ display: "grid", gap: 7 }}>
                      {aliasSuggestions.slice(0, 8).map((suggestion, index) => (
                        <div
                          key={suggestion.id}
                          style={mergeStyles(
                            {
                              display: "grid",
                              gridTemplateColumns:
                                "minmax(140px, 0.75fr) minmax(160px, 0.9fr) minmax(0, 1.3fr) 92px",
                              gap: 10,
                              alignItems: "center",
                              borderTop: index ? "1px solid #0d1b35" : "none",
                              paddingTop: index ? 7 : 0,
                            },
                            historyStaggerStyle(index, fxEnabled, {
                              stepMs: 12,
                              maxDelayMs: 120,
                            }),
                          )}
                        >
                          <div>
                            <div
                              style={{
                                color: "#c8d8f0",
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              {suggestion.alias}
                            </div>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontFamily: "monospace",
                                fontSize: 10,
                                marginTop: 2,
                              }}
                            >
                              KPI / History name
                            </div>
                          </div>
                          <div>
                            <div style={{ color: "#7aa0d4", fontSize: 11 }}>
                              {suggestion.engineer}
                            </div>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontFamily: "monospace",
                                fontSize: 10,
                                marginTop: 2,
                              }}
                            >
                              Rota engineer
                            </div>
                          </div>
                          <div style={{ color: "#8aa9d6", fontSize: 10 }}>
                            {suggestion.reason} / {suggestion.evidence}
                          </div>
                          <div
                            style={{
                              color: suggestion.autoApply ? "#22c55e" : "#fbbf24",
                              fontFamily: "monospace",
                              fontSize: 10,
                              fontWeight: 800,
                              textAlign: "right",
                            }}
                          >
                            {suggestion.autoApply ? "AUTO" : "REVIEW"}
                          </div>
                        </div>
                      ))}
                      {aliasSuggestions.length > 8 && (
                        <div style={{ color: "#4f6fa0", fontSize: 10 }}>
                          Showing 8 of {aliasSuggestions.length.toLocaleString()}
                          alias suggestions. Apply high-confidence matches, then
                          refresh to review the rest.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                      No new alias suggestions for the current History filters.
                    </div>
                  )}
                </div>
                <div
                  style={{
                    border: "1px solid #142849",
                    background: "#071225",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                      marginBottom: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#dbeafe",
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        Alias Diagnostics
                      </div>
                      <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 3 }}>
                        {historyReconciliation.message} Raw names are kept so
                        unresolved identities can be fixed without guessing.
                      </div>
                    </div>
                    <Btn
                      variant="secondary"
                      onClick={exportReconciliationCsv}
                      disabled={historyReconciliation.rows.length === 0}
                    >
                      EXPORT NAMES
                    </Btn>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                      gap: 7,
                      marginBottom: 10,
                    }}
                  >
                    {[
                      ["exact", "Exact", "#22c55e"],
                      ["alias", "Alias", "#5de7ff"],
                      ["suggestion", "Suggestion", "#8ab8fa"],
                      ["review", "Review", "#fbbf24"],
                      ["unresolved", "Unresolved", "#f87171"],
                      ["inactive", "Inactive", "#f97316"],
                      ["rota-only", "Rota only", "#a78bfa"],
                    ].map(([key, label, accent]) => (
                      <div
                        key={key}
                        style={{
                          border: `1px solid ${accent}33`,
                          background: `${accent}0d`,
                          borderRadius: 7,
                          padding: "8px 9px",
                        }}
                      >
                        <div
                          style={{
                            color: accent,
                            fontSize: 9,
                            textTransform: "uppercase",
                            fontWeight: 800,
                          }}
                        >
                          {label}
                        </div>
                        <div
                          style={{
                            color: "#dbeafe",
                            fontSize: 17,
                            fontWeight: 900,
                            marginTop: 3,
                          }}
                        >
                          {n(historyReconciliation.counts[key]).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                  {historyReconciliation.rows.length ? (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 10,
                        }}
                      >
                        <thead>
                          <tr style={{ color: "#4f6fa0" }}>
                            {[
                              "Raw name",
                              "Engineer",
                              "Result",
                              "Uses",
                              "Evidence",
                              "Action",
                            ].map((heading) => (
                              <th
                                key={heading}
                                style={{ textAlign: "left", padding: "6px" }}
                              >
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {historyReconciliation.rows.slice(0, 20).map((row) => {
                            const accent =
                              row.classification === "unresolved"
                                ? "#f87171"
                                : row.classification === "review"
                                  ? "#fbbf24"
                                  : row.classification === "inactive"
                                    ? "#f97316"
                                    : row.classification === "rota-only"
                                      ? "#a78bfa"
                                      : "#8ab8fa";
                            return (
                              <tr
                                key={row.id}
                                style={{ borderTop: "1px solid #0d1b35" }}
                              >
                                <td style={{ padding: "6px", color: "#c8d8f0", fontWeight: 700 }}>
                                  {row.name}
                                </td>
                                <td style={{ padding: "6px", color: "#7aa0d4" }}>
                                  {row.engineer || "—"}
                                </td>
                                <td
                                  style={{
                                    padding: "6px",
                                    color: accent,
                                    fontWeight: 800,
                                  }}
                                >
                                  {row.classification}
                                </td>
                                <td style={{ padding: "6px", color: "#c8d8f0" }}>
                                  {row.occurrences}
                                </td>
                                <td style={{ padding: "6px", color: "#8aa9d6" }}>
                                  {row.evidence}
                                </td>
                                <td style={{ padding: "6px", color: "#7aa0d4" }}>
                                  {row.action}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {historyReconciliation.rows.length > 20 && (
                        <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 8 }}>
                          Showing 20 of {historyReconciliation.rows.length.toLocaleString()} name diagnostics.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                      No engineer names were available for reconciliation in this selection.
                    </div>
                  )}
                </div>
                {consistencyRows.length ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ color: "#4f6fa0", fontSize: 10 }}>
                          <th style={{ textAlign: "left", padding: "8px 6px" }}>
                            Severity
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 6px" }}>
                            Category
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 6px" }}>
                            Engineer
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 6px" }}>
                            Issue
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 6px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {consistencyRows.slice(0, 40).map((issue, index) => {
                          const tone =
                            consistencyTone[issue.severity] ||
                            consistencyTone.warning;
                          return (
                            <tr
                              key={issue.id}
                              style={mergeStyles(
                                {
                                  borderTop: "1px solid #0d1b35",
                                  color: "#c8d8f0",
                                  fontSize: 11,
                                  verticalAlign: "top",
                                },
                                historyStaggerStyle(index, fxEnabled, {
                                  stepMs: 12,
                                  maxDelayMs: 180,
                                }),
                              )}
                            >
                              <td style={{ padding: "9px 6px" }}>
                                <span
                                  style={{
                                    color: tone.accent,
                                    fontFamily: "monospace",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {issue.severity}
                                </span>
                              </td>
                              <td style={{ padding: "9px 6px", color: "#7aa0d4" }}>
                                {issue.category}
                                <div
                                  style={{
                                    color: "#4f6fa0",
                                    fontFamily: "monospace",
                                    fontSize: 10,
                                    marginTop: 2,
                                  }}
                                >
                                  {issue.month}
                                </div>
                              </td>
                              <td style={{ padding: "9px 6px" }}>
                                <div style={{ fontWeight: 800 }}>
                                  {issue.engineer}
                                </div>
                                <div
                                  style={{
                                    color: "#4f6fa0",
                                    fontFamily: "monospace",
                                    fontSize: 10,
                                    marginTop: 2,
                                  }}
                                >
                                  {issue.team}
                                </div>
                              </td>
                              <td style={{ padding: "9px 6px", lineHeight: 1.45 }}>
                                <div style={{ color: "#dbeafe", fontWeight: 800 }}>
                                  {issue.summary}
                                </div>
                                <div style={{ color: "#8aa9d6", marginTop: 3 }}>
                                  {issue.detail}
                                </div>
                                <div style={{ color: "#4f6fa0", marginTop: 3 }}>
                                  {issue.evidence}
                                </div>
                              </td>
                              <td style={{ padding: "9px 6px", lineHeight: 1.45 }}>
                                {issue.action}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {consistencyRows.length > 40 && (
                      <div style={{ color: "#4f6fa0", fontSize: 11, marginTop: 8 }}>
                        Showing 40 of {consistencyRows.length.toLocaleString()}
                        filtered issues. Export CSV for the full list.
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1px solid #1f3f35",
                      background: "rgba(34,197,94,0.08)",
                      borderRadius: 8,
                      color: "#9be7bd",
                      fontSize: 12,
                      padding: 12,
                    }}
                  >
                    No consistency issues detected for the current filters.
                  </div>
                )}
              </>
            )}
          </Card>

          {historyBriefItems.length > 0 && (
            <Card>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <Label>Operational Brief</Label>
                <div
                  style={{
                    color: "#4f6fa0",
                    fontSize: 10,
                    fontFamily: "monospace",
                  }}
                >
                  {selectedPeriodLabel}
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 10,
                }}
              >
                {historyBriefItems.map((item, index) => {
                  const tone = briefTone[item.tone] || briefTone.warning;
                  return (
                    <div
                      key={item.id}
                      style={mergeStyles(
                        {
                          border: `1px solid ${tone.accent}33`,
                          background: tone.bg,
                          borderRadius: 8,
                          padding: 12,
                          minHeight: 142,
                          display: "flex",
                          flexDirection: "column",
                          gap: 7,
                        },
                        historyStaggerStyle(index, fxEnabled, {
                          stepMs: 28,
                          maxDelayMs: 180,
                        }),
                      )}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "baseline",
                        }}
                      >
                        <div
                          style={{
                            color: tone.accent,
                            fontSize: 10,
                            fontWeight: 800,
                            textTransform: "uppercase",
                          }}
                        >
                          {item.title}
                        </div>
                        <div
                          style={{
                            color: "#dbeafe",
                            fontSize: 13,
                            fontWeight: 800,
                            textAlign: "right",
                          }}
                        >
                          {item.value}
                        </div>
                      </div>
                      <div
                        style={{ color: "#8aa9d6", fontSize: 11, lineHeight: 1.45 }}
                      >
                        {item.detail}
                      </div>
                      <div
                        style={{
                          color: "#c8d8f0",
                          fontSize: 11,
                          lineHeight: 1.45,
                          marginTop: "auto",
                        }}
                      >
                        {item.action}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <section>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 0.95fr)",
                gap: 14,
              }}
            >
              <Card>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 12,
                  }}
                >
                  <Label>Repair Centre Daily Pace</Label>
                  <div
                    style={{
                      color: "#4f6fa0",
                      fontSize: 10,
                      fontFamily: "monospace",
                    }}
                  >
                    {selectedPeriodLabel}
                  </div>
                </div>
                {dailyAverageRows.length ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {dailyAverageRows.map((row, index) => (
                      <div
                        key={row.engineer}
                        style={mergeStyles(
                          {
                            border: `1px solid ${row.mismatch ? "#fbbf24" : "#1e3056"}`,
                            background: row.mismatch
                              ? "rgba(251, 191, 36, 0.07)"
                              : "#071225",
                            borderRadius: 8,
                            padding: 12,
                            minHeight: 116,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          },
                          historyStaggerStyle(index, fxEnabled, {
                            stepMs: 24,
                            maxDelayMs: 160,
                          }),
                        )}
                      >
                        <div
                          style={{
                            color: "#c8d8f0",
                            fontSize: 12,
                            fontWeight: 800,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.engineer}
                        </div>
                        <div
                          style={{
                            color: "#5de7ff",
                            fontSize: 22,
                            fontWeight: 900,
                            lineHeight: 1,
                          }}
                        >
                          {row.label}
                        </div>
                        <div
                          style={{
                            color: "#7aa0d4",
                            fontSize: 11,
                            lineHeight: 1.45,
                          }}
                        >
                          {row.detail}
                        </div>
                        <div
                          style={{
                            color: "#4f6fa0",
                            fontSize: 10,
                            lineHeight: 1.35,
                          }}
                        >
                          {row.totalPartsDetail}
                        </div>
                        <div
                          style={{
                            color: "#c8d8f0",
                            fontSize: 11,
                            lineHeight: 1.35,
                            fontWeight: 700,
                          }}
                        >
                          {row.actualAverageLabel}
                        </div>
                        <div
                          style={{
                            color: "#4f6fa0",
                            fontSize: 10,
                            lineHeight: 1.35,
                          }}
                        >
                          {row.actualAverageDetail}
                        </div>
                        <div
                          style={{
                            color: row.daySource === "rota" ? "#22c55e" : "#fbbf24",
                            fontSize: 10,
                            fontFamily: "monospace",
                            marginTop: "auto",
                          }}
                        >
                          {row.daySource === "rota"
                            ? "ROTA DAYS"
                            : "SAVED DAYS"}
                          {row.mismatch
                            ? ` / saved ${row.savedDays.toFixed(1)}`
                            : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                    No Repair Centre daily pace available for this selection.
                  </div>
                )}
              </Card>
              <Card>
                <Label>Performance Coach</Label>
                {performanceCoachRows.length ? (
                  <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                    {performanceCoachRows.map((row, index) => {
                      const toneColor =
                        row.tone === "good"
                          ? "#22c55e"
                          : row.tone === "risk"
                            ? "#f87171"
                            : "#fbbf24";
                      return (
                        <div
                          key={row.engineer}
                          style={mergeStyles(
                            {
                              display: "grid",
                              gridTemplateColumns:
                                "minmax(105px, 0.7fr) minmax(0, 1.3fr) 86px",
                              gap: 10,
                              alignItems: "center",
                              borderBottom: "1px solid #0d1b35",
                              paddingBottom: 8,
                            },
                            historyStaggerStyle(index, fxEnabled, {
                              stepMs: 24,
                              maxDelayMs: 160,
                            }),
                          )}
                        >
                          <div>
                            <div
                              style={{
                                color: "#c8d8f0",
                                fontSize: 12,
                                fontWeight: 800,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.engineer}
                            </div>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontSize: 10,
                                fontFamily: "monospace",
                              }}
                            >
                              {row.team}
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                color: toneColor,
                                fontSize: 11,
                                lineHeight: 1.45,
                                fontWeight: 700,
                              }}
                            >
                              {row.action}
                            </div>
                            {row.notes.length > 0 && (
                              <div
                                style={{
                                  color: "#7aa0d4",
                                  fontSize: 10,
                                  lineHeight: 1.45,
                                  marginTop: 3,
                                }}
                              >
                                {row.notes.join(" / ")}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              color: toneColor,
                              fontFamily: "monospace",
                              fontSize: 12,
                              fontWeight: 800,
                              textAlign: "right",
                            }}
                          >
                            {row.status === "gap"
                              ? `+${row.requiredMoreParts}`
                              : "TOP"}
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontSize: 9,
                                marginTop: 2,
                              }}
                            >
                              {row.status === "gap"
                                ? "parts needed"
                                : `${row.currentAvg.toFixed(1)}/day`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                    No coaching rows available for this selection.
                  </div>
                )}
              </Card>
            </div>
          </section>

          <div style={{ display: "grid", gap: 14 }}>
            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.4fr) minmax(260px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Top Engineers By Commission</Label>
                  <BarList
                    rows={current.engineers
                      .slice(0, 10)
                      .map((e) => ({ label: e.engineer, value: e.commission }))}
                    color="#22c55e"
                  />
                </Card>
                <Card>
                  <Label>Month Difference</Label>
                  {comparisonActive ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      <Metric
                        label="Parts Difference"
                        value={<DeltaText value={delta.parts} />}
                        sub={`${selectedPeriodLabel} vs ${compareMonth}`}
                      />
                      <Metric
                        label="Commission Difference"
                        value={
                          <DeltaText value={delta.commission} kind="money" />
                        }
                        sub={`${money(current.totalCommission)} now`}
                      />
                      <Metric
                        label="BER Difference"
                        value={<DeltaText value={delta.ber} />}
                        sub={`${current.berParts} current BER`}
                      />
                    </div>
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      {historyMode === "range"
                        ? "Comparison is not available for month ranges."
                        : "Choose a comparison month to see month-on-month movement."}
                    </div>
                  )}
                </Card>
              </div>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Parts Across Uploaded Months</Label>
                  <BarList
                    rows={monthlyRows}
                    valueKey="parts"
                    color="#8ab8fa"
                  />
                </Card>
                <Card>
                  <Label>Commission Across Uploaded Months</Label>
                  <BarList
                    rows={monthlyRows}
                    valueKey="commission"
                    color="#22c55e"
                  />
                </Card>
                <Card>
                  <Label>BER Across Uploaded Months</Label>
                  <BarList
                    rows={monthlyRows.filter((m) => m.ber > 0)}
                    valueKey="ber"
                    color="#fbbf24"
                  />
                </Card>
                <Card>
                  <Label>Bonus Eligible Jobs By Month</Label>
                  <BarList
                    rows={monthlyRows.filter((m) => m.bonusEligible > 0)}
                    valueKey="bonusEligible"
                    color="#5de7ff"
                  />
                </Card>
              </div>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 10,
                }}
              >
                {insightCards.map((card) => (
                  <div
                    key={card.title}
                    style={{ ...panel, borderColor: "#1e3056" }}
                  >
                    <div
                      style={{
                        color: "#4f6fa0",
                        fontSize: 9,
                        letterSpacing: 1,
                        marginBottom: 7,
                      }}
                    >
                      {card.title.toUpperCase()}
                    </div>
                    <div
                      style={{
                        color: card.accent,
                        fontSize: 16,
                        fontWeight: 800,
                        marginBottom: 6,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {card.value}
                    </div>
                    <div
                      style={{
                        color: "#7aa0d4",
                        fontSize: 11,
                        lineHeight: 1.5,
                      }}
                    >
                      {card.detail}
                    </div>
                  </div>
                ))}
                <div style={panel}>
                  <div
                    style={{
                      color: "#4f6fa0",
                      fontSize: 9,
                      letterSpacing: 1,
                      marginBottom: 7,
                    }}
                  >
                    DATA QUALITY
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gap: 6,
                      color: "#7aa0d4",
                      fontSize: 11,
                    }}
                  >
                    <div>
                      Missing teams:{" "}
                      <span
                        style={{
                          color: current.missing.team ? "#fbbf24" : "#22c55e",
                        }}
                      >
                        {current.missing.team}
                      </span>
                    </div>
                    <div>
                      Missing grades:{" "}
                      <span
                        style={{
                          color: current.missing.grade ? "#f87171" : "#22c55e",
                        }}
                      >
                        {current.missing.grade}
                      </span>
                    </div>
                    <div>
                      Missing timestamps:{" "}
                      <span
                        style={{
                          color: current.missing.timestamp
                            ? "#fbbf24"
                            : "#22c55e",
                        }}
                      >
                        {current.missing.timestamp}
                      </span>
                    </div>
                    <div>
                      Avg commission/part:{" "}
                      <span style={{ color: "#22c55e" }}>
                        {money(current.avgCommissionPerPart)}
                      </span>
                    </div>
                    <div>
                      Rota matches:{" "}
                      <span
                        style={{
                          color: current.rotaCoverage ? "#22c55e" : "#fbbf24",
                        }}
                      >
                        {current.rotaCoverage}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ ...panel, gridColumn: "1 / -1" }}>
                  <div
                    style={{
                      color: "#4f6fa0",
                      fontSize: 9,
                      letterSpacing: 1,
                      marginBottom: 7,
                    }}
                  >
                    WORK OPTIMISATION SUGGESTIONS
                  </div>
                  <div style={{ display: "grid", gap: 7 }}>
                    {current.engineers.slice(0, 8).map((e) => (
                      <div
                        key={e.engineer}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(130px, 0.6fr) minmax(0, 1.4fr) 110px",
                          gap: 10,
                          alignItems: "center",
                          borderBottom: "1px solid #0d1b35",
                          paddingBottom: 7,
                        }}
                      >
                        <div
                          style={{
                            color: "#c8d8f0",
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {e.engineer}
                        </div>
                        <div style={{ color: "#7aa0d4", fontSize: 11 }}>
                          {e.focusSuggestion}
                        </div>
                        <div
                          style={{
                            color: "#22c55e",
                            fontSize: 11,
                            fontFamily: "monospace",
                            textAlign: "right",
                          }}
                        >
                          {e.rota
                            ? `${money(e.earningsPerRotaDay)}/day`
                            : money(e.avgCommissionPerPart)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Rota Adjusted Working Days</Label>
                  {rotaRows.length ? (
                    <BarList rows={rotaRows} color="#8ab8fa" />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No uploaded rota rows match the visible engineers/months.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Extra / Less Days vs Rota</Label>
                  {rotaRows.length ? (
                    <BarList
                      rows={rotaRows.map((r) => ({
                        label: r.label,
                        value: Math.abs(r.extraDays),
                        display: r.extraDays,
                      }))}
                      displayKey="display"
                      color="#fbbf24"
                    />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No rota comparison available.
                    </div>
                  )}
                  <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 8 }}>
                    Positive means saved days worked is above rota-adjusted
                    days. Other and half-day Other reduce rota days; completed
                    work still earns commission.
                  </div>
                </Card>
                <Card>
                  <Label>Money Per Rota Day</Label>
                  {rotaRows.length ? (
                    <BarList
                      rows={rotaRows.map((r) => ({
                        label: r.label,
                        value: r.earningsPerRotaDay,
                      }))}
                      color="#22c55e"
                    />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No rota value data available.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Rota Exceptions</Label>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 11,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: "#4f6fa0",
                            borderBottom: "1px solid #1e3056",
                          }}
                        >
                          {[
                            "Engineer",
                            "Sick",
                            "Other",
                            "Bank Hols",
                            "GBP/Rota Day",
                          ].map((h) => (
                            <th
                              key={h}
                              style={{ textAlign: "left", padding: "6px 8px" }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rotaRows.map((r) => (
                          <tr
                            key={r.label}
                            style={{
                              color: "#c8d8f0",
                              borderBottom: "1px solid #0d1b35",
                            }}
                          >
                            <td style={{ padding: "6px 8px" }}>{r.label}</td>
                            <td
                              style={{
                                padding: "6px 8px",
                                fontFamily: "monospace",
                              }}
                            >
                              {r.sick}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                fontFamily: "monospace",
                              }}
                            >
                              {r.other}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                fontFamily: "monospace",
                              }}
                            >
                              {r.bankHolidays}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                color: "#22c55e",
                                fontFamily: "monospace",
                              }}
                            >
                              {money(r.earningsPerRotaDay)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </section>

            <section>
              <Card>
                <Label>Engineer Detail</Label>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          color: "#4f6fa0",
                          borderBottom: "1px solid #1e3056",
                        }}
                      >
                        {[
                          "Engineer",
                          "Team",
                          "Parts",
                          "Non-BER",
                          "BER",
                          "RC Daily Pace",
                          "Bonus Tier",
                          "Commission",
                          "Weekend (not paid)",
                          "Bonus",
                          "Total",
                          "Days",
                          "Rota Days",
                          "+/- Days",
                          "GBP/Rota Day",
                          "Total Parts/Day",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{ textAlign: "left", padding: "7px 8px" }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {current.engineers.map((e) => (
                        <tr
                          key={e.engineer}
                          style={{
                            color: "#c8d8f0",
                            borderBottom: "1px solid #0d1b35",
                          }}
                        >
                          <td style={{ padding: "7px 8px", fontWeight: 700 }}>
                            {e.engineer}
                          </td>
                          <td style={{ padding: "7px 8px", color: "#7aa0d4" }}>
                            {e.team || "Unknown"}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {e.totalParts}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {e.nonBer}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: e.ber ? "#fbbf24" : "#4f6fa0",
                            }}
                          >
                            {e.ber}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: e.bonusTier ? "#22c55e" : "#fbbf24",
                            }}
                          >
                            {e.bonusDays ? e.bonusAvg.toFixed(1) : ""}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: e.bonusTier ? "#22c55e" : "#4f6fa0",
                            }}
                          >
                            {e.bonusTier
                              ? `${e.bonusTier.daily}/day`
                              : "No tier"}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: "#22c55e",
                            }}
                          >
                            {money(e.commission)}
                          </td>
                          <td
                            title={
                              e.weekendParts
                                ? `${e.weekendParts} weekend parts - not included in commission or total`
                                : "No weekend parts"
                            }
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: e.weekendCommission ? "#f59e0b" : "#4f6fa0",
                            }}
                          >
                            {e.weekendCommission
                              ? `${money(e.weekendCommission)} (${e.weekendParts})`
                              : "-"}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {money(e.bonus)}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: "#22c55e",
                              fontWeight: 700,
                            }}
                          >
                            {money(e.totalEarnings)}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {e.effectiveDays || ""}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {e.rota ? e.rotaWorkedDays.toFixed(1) : ""}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color:
                                e.extraDays > 0
                                  ? "#fbbf24"
                                  : e.extraDays < 0
                                    ? "#5de7ff"
                                    : "#4f6fa0",
                            }}
                          >
                            {e.rota ? e.extraDays.toFixed(1) : ""}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                              color: e.earningsPerRotaDay
                                ? "#22c55e"
                                : "#4f6fa0",
                            }}
                          >
                            {e.rota ? money(e.earningsPerRotaDay) : ""}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              fontFamily: "monospace",
                            }}
                          >
                            {e.avgPerDay ? e.avgPerDay.toFixed(1) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <Metric
                  label="Most Fixed Part"
                  value={mostFixedPart ? mostFixedPart.partId : "—"}
                  sub={
                    mostFixedPart
                      ? `${mostFixedPart.count.toLocaleString()} completed`
                      : "No parts in selection"
                  }
                  accent="#8ab8fa"
                />
                <Metric
                  label="Ungraded Parts"
                  value={ungradedCount.toLocaleString()}
                  sub={
                    ungradedCount
                      ? "Need grading — flagged at top"
                      : "All parts graded"
                  }
                  accent={ungradedCount ? "#fbbf24" : "#22c55e"}
                />
                <Metric
                  label="Unique Parts"
                  value={partRowsAll.length.toLocaleString()}
                  sub={`${totalCompletions.toLocaleString()} total completions`}
                  accent="#5de7ff"
                />
                <Metric
                  label="Top Value Part"
                  value={topValuePart ? topValuePart.partId : "—"}
                  sub={
                    topValuePart
                      ? `${money(topValuePart.commission)} commission`
                      : "No parts in selection"
                  }
                  accent="#22c55e"
                />
              </div>
              <Card>
                <div
                  onClick={() => setPartsExpanded((v) => !v)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        color: "#7aa0d4",
                        fontSize: 13,
                        transform: partsExpanded
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s",
                        display: "inline-block",
                      }}
                    >
                      ▶
                    </span>
                    <Label>Completed Parts</Label>
                    <span style={{ color: "#4f6fa0", fontSize: 11 }}>
                      ({partRowsAll.length.toLocaleString()} parts
                      {ungradedCount ? `, ${ungradedCount} ungraded` : ""})
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    {gradeMsg && (
                      <span
                        style={{
                          color: gradeMsg.startsWith("Failed")
                            ? "#f87171"
                            : "#22c55e",
                          fontSize: 11,
                        }}
                      >
                        {gradeMsg}
                      </span>
                    )}
                    <span style={{ color: "#4f6fa0", fontSize: 11 }}>
                      {partsExpanded ? "Hide" : "Show list"}
                    </span>
                  </div>
                </div>
                {partsExpanded && (
                  <>
                    <div
                      style={{
                        color: "#4f6fa0",
                        fontSize: 11,
                        lineHeight: 1.6,
                        margin: "10px 0",
                        maxWidth: 860,
                      }}
                    >
                      Every distinct part completed in the current selection.
                      Ungraded parts are flagged to the top — pick a grade and
                      click Save to fix it on the shared database (same store as
                      the Parts Grading Manager, inherited by future uploads).
                      {current.partsNoId > 0 && (
                        <span style={{ color: "#fbbf24" }}>
                          {" "}
                          {current.partsNoId.toLocaleString()} completion
                          {current.partsNoId === 1 ? "" : "s"} had no part ID
                          and are not listed.
                        </span>
                      )}
                    </div>
                    {partRowsAll.length === 0 ? (
                      <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                        No completed parts in the current selection.
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 12,
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                color: "#4f6fa0",
                                borderBottom: "1px solid #1e3056",
                              }}
                            >
                              {[
                                "Part",
                                "Grade",
                                "",
                                "Count",
                                "Commission",
                                "Status",
                              ].map((h, i) => (
                                <th
                                  key={i}
                                  style={{
                                    textAlign: "left",
                                    padding: "7px 8px",
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {partRowsShown.map((p) => {
                              const pidUp = String(p.partId).toUpperCase();
                              const pending = pendingGrades[pidUp];
                              const selectValue =
                                pending !== undefined
                                  ? pending
                                  : p.effGrade === "UNGRADED"
                                    ? ""
                                    : p.effGrade;
                              const dirty =
                                pending !== undefined &&
                                pending !== "" &&
                                pending !==
                                  (p.effGrade === "UNGRADED" ? "" : p.effGrade);
                              const saving = savingPart === pidUp;
                              return (
                                <tr
                                  key={p.partId}
                                  style={{
                                    color: "#c8d8f0",
                                    borderBottom: "1px solid #0d1b35",
                                    background: p.isUngraded
                                      ? "rgba(251,191,36,0.07)"
                                      : "transparent",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "7px 8px",
                                      fontFamily: "monospace",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {p.partId}
                                  </td>
                                  <td style={{ padding: "7px 8px" }}>
                                    <Select
                                      value={selectValue}
                                      onChange={(e) =>
                                        setPendingGrades((prev) => ({
                                          ...prev,
                                          [pidUp]: e.target.value,
                                        }))
                                      }
                                      disabled={saving}
                                    >
                                      <option value="" disabled>
                                        — set grade —
                                      </option>
                                      {PART_GRADE_OPTIONS.map((g) => (
                                        <option key={g} value={g}>
                                          {g}
                                        </option>
                                      ))}
                                    </Select>
                                  </td>
                                  <td style={{ padding: "7px 8px" }}>
                                    <Btn
                                      variant="secondary"
                                      onClick={() =>
                                        changePartGrade(p.partId, pending)
                                      }
                                      disabled={!dirty || saving}
                                      style={{
                                        fontSize: 10,
                                        padding: "5px 12px",
                                        opacity: dirty && !saving ? 1 : 0.45,
                                      }}
                                    >
                                      {saving ? "SAVING…" : "SAVE"}
                                    </Btn>
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 8px",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {p.count.toLocaleString()}
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 8px",
                                      fontFamily: "monospace",
                                      color: "#22c55e",
                                    }}
                                  >
                                    {money(p.commission)}
                                  </td>
                                  <td
                                    style={{ padding: "7px 8px", fontSize: 11 }}
                                  >
                                    {saving ? (
                                      <span style={{ color: "#5de7ff" }}>
                                        Saving…
                                      </span>
                                    ) : p.isUngraded ? (
                                      <span
                                        style={{
                                          color: "#fbbf24",
                                          fontWeight: 700,
                                        }}
                                      >
                                        ⚑ Ungraded
                                      </span>
                                    ) : gradeOverrides[pidUp] ? (
                                      <span style={{ color: "#22c55e" }}>
                                        Overridden
                                      </span>
                                    ) : (
                                      <span style={{ color: "#4f6fa0" }}>
                                        Graded
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {partRows.length > PART_ROW_CAP && (
                          <div
                            style={{
                              color: "#4f6fa0",
                              fontSize: 11,
                              marginTop: 8,
                            }}
                          >
                            Showing top {PART_ROW_CAP} of{" "}
                            {partRows.length.toLocaleString()} parts (ungraded
                            always shown first). Narrow the filters to see more.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Team Workload</Label>
                  <BarList
                    rows={current.teams.map((t) => ({
                      label: t.team,
                      value: t.parts,
                    }))}
                    color="#8ab8fa"
                  />
                </Card>
                <Card>
                  <Label>Team Commission</Label>
                  <BarList
                    rows={current.teams.map((t) => ({
                      label: t.team,
                      value: t.commission,
                    }))}
                    color="#22c55e"
                  />
                </Card>
                <Card>
                  <Label>Team BER Load</Label>
                  {current.teams.some((t) => t.ber > 0) ? (
                    <BarList
                      rows={current.teams
                        .map((t) => ({ label: t.team, value: t.ber }))
                        .filter((t) => t.value > 0)}
                      color="#fbbf24"
                    />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No BER units in the current filter.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Team Efficiency</Label>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 7 }}
                  >
                    {current.teams.map((t) => (
                      <div
                        key={t.team}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          borderBottom: "1px solid #0d1b35",
                          paddingBottom: 6,
                        }}
                      >
                        <span style={{ color: "#7aa0d4", fontSize: 11 }}>
                          {t.team}
                        </span>
                        <span
                          style={{
                            color: "#22c55e",
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                        >
                          {money(t.parts ? t.commission / t.parts : 0)} / part
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Completed Parts By Grade</Label>
                  <BarList rows={gradeRows} valueKey="parts" color="#8ab8fa" />
                </Card>
                <Card>
                  <Label>Commission By Grade</Label>
                  <BarList
                    rows={gradeRows.map((r) => ({
                      ...r,
                      commissionLabel: r.commission,
                    }))}
                    valueKey="commission"
                    color="#22c55e"
                  />
                </Card>
              </div>
            </section>

            <section>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
                  gap: 14,
                }}
              >
                <Card>
                  <Label>Daily Workload Peaks</Label>
                  {dailyRows.length ? (
                    <BarList rows={dailyRows} color="#5de7ff" />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No completed date data saved for this month.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Hourly Completion Pattern</Label>
                  {hourlyRows.length ? (
                    <BarList rows={hourlyRows} color="#fbbf24" />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No completed time data saved for this month.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Weekday Distribution</Label>
                  {weekdayRows.length ? (
                    <BarList rows={weekdayRows} color="#8ab8fa" />
                  ) : (
                    <div style={{ color: "#4f6fa0", fontSize: 12 }}>
                      No weekday data saved for this month.
                    </div>
                  )}
                </Card>
                <Card>
                  <Label>Workload Notes</Label>
                  <div
                    style={{ color: "#7aa0d4", fontSize: 12, lineHeight: 1.7 }}
                  >
                    {current.bestDay ? (
                      <div>
                        Peak day is {current.bestDay[0]} with{" "}
                        {current.bestDay[1]} parts.
                      </div>
                    ) : (
                      <div>No date peaks available.</div>
                    )}
                    {current.peakHour ? (
                      <div>
                        Peak hour is {current.peakHour[0]} with{" "}
                        {current.peakHour[1]} parts.
                      </div>
                    ) : (
                      <div>No hour peaks available.</div>
                    )}
                    <div>Timestamp coverage is {pct(timestampCoverage)}.</div>
                  </div>
                </Card>
              </div>
            </section>

            <section>
              <Card>
                <Label>Projection Panel</Label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                    gap: 10,
                  }}
                >
                  <Metric
                    label="Working Days Left"
                    value={remainingDays.toLocaleString()}
                    sub={
                      isCurrentMonth
                        ? "From server calendar"
                        : "Only current month projects"
                    }
                  />
                  <Metric
                    label="Projected Parts"
                    value={Math.round(projectedParts).toLocaleString()}
                    sub={`Current ${current.totalParts.toLocaleString()}`}
                    accent="#8ab8fa"
                  />
                  <Metric
                    label="Projected Commission"
                    value={money(projectedCommission)}
                    sub={`Current ${money(current.totalCommission)}`}
                    accent="#22c55e"
                  />
                  <Metric
                    label="Run Rate"
                    value={
                      daysWorkedAvg > 0
                        ? `${(current.totalParts / daysWorkedAvg).toFixed(1)} / day`
                        : "No days"
                    }
                    sub="Uses rota-adjusted days average"
                    accent="#fbbf24"
                  />
                </div>
                <div
                  style={{
                    color: "#4f6fa0",
                    fontSize: 11,
                    marginTop: 12,
                    lineHeight: 1.6,
                  }}
                >
                  Projections use saved month-to-date totals and the average
                  rota-adjusted days worked. For older months this panel stays
                  factual unless the selected month is the current server month.
                </div>
              </Card>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// Repair Centre Earnings Tab
export function RepairCentreEarnings({
  state,
  fileState,
  onFileState,
  onEngineerAliasesChanged,
}) {
  const fs = fileState || {};
  const [rows, setRows] = useState(fs.rows || []);
  const [headers, setHeaders] = useState(fs.headers || []);
  const [partIdCol, setPartIdCol] = useState(fs.partIdCol || "");
  const [personCol, setPersonCol] = useState(fs.personCol || "");
  const [unitStatusCol, setUnitStatusCol] = useState(fs.unitStatusCol || "");
  const [teamCol, setTeamCol] = useState(fs.teamCol || "");
  const [closeDttmCol, setCloseDttmCol] = useState(fs.closeDttmCol || "");
  const [fileName, setFileName] = useState(fs.fileName || "");
  const [parsedPersons, setParsedPersons] = useState(fs.parsedPersons || null);
  const [msg, setMsg] = useState(fs.msg || "");
  const [daysWorked, setDaysWorked] = useState(fs.daysWorked || {});
  // Informational only: the same rota-derived count but counting today as
  // worked too. "Days worked" itself stays "as of yesterday" — commission
  // pace is deliberately computed against completed days — this is purely
  // so it's visible that today would add one more if it were included. Not
  // persisted: it's cheap to recompute and would otherwise go stale between
  // sessions.
  const [daysWorkedWithToday, setDaysWorkedWithToday] = useState({});
  const [daysLeft, setDaysLeft] = useState(fs.daysLeft || "");
  // Tracks which engineers' "days worked" the user typed by hand, plus the month
  // the saved data belongs to so a new month always refreshes from the rota.
  const [manualDays, setManualDays] = useState(
    () => new Set(Array.isArray(fs.manualDays) ? fs.manualDays : []),
  );
  const [daysWorkedMonth, setDaysWorkedMonth] = useState(
    fs.daysWorkedMonth || "",
  );
  const [daysLeftManual, setDaysLeftManual] = useState(!!fs.daysLeftManual);
  const [daysLeftMonth, setDaysLeftMonth] = useState(fs.daysLeftMonth || "");
  // Month the Save to Database button writes to. Seeded from the uploaded file,
  // still editable — see the sync effect below.
  const [saveMonth, setSaveMonth] = useState(fs.saveMonth || "");
  const appliedSaveMonth = useRef(null);
  // Bumped every time Calculate runs. "Working days left" is computed from
  // today's date, which keeps moving even when the detected month and the
  // roster size don't — without this, reloading a fresh KPI export for the
  // same month never re-reads the clock, so the figure freezes at whatever
  // day it was first shown.
  const [calcSeq, setCalcSeq] = useState(0);
  const server = useServer();

  // The month this upload belongs to, taken from the completion dates in the
  // file rather than from today's date — so July's export opened in August is
  // treated as July throughout. Files with no usable date column fall back to
  // the current month, which is the behaviour that existed before.
  const uploadDetectedMonth = useMemo(
    () => detectRepairUploadMonth(rows, closeDttmCol),
    [rows, closeDttmCol],
  );
  const detectedMonth = parsedPersons?.detectedMonth || uploadDetectedMonth || "";
  const activeMonth = detectedMonth || currentMonthKey();

  // Refresh "days left" for the month being worked on. A finished month has none
  // left; the current month counts from tomorrow. A manual override is kept only
  // if it was entered against this same month.
  useEffect(() => {
    const parsed = parseMonthKey(activeMonth);
    if (!parsed) return;
    if (daysLeftManual && daysLeftMonth === activeMonth) return;
    const next = String(
      calcWorkingDaysLeftForMonth(
        server?.bankHolidays,
        parsed.year,
        parsed.month,
      ),
    );
    if (next === daysLeft && daysLeftMonth === activeMonth) return;
    setDaysLeft(next);
    setDaysLeftManual(false);
    setDaysLeftMonth(activeMonth);
    persist({ daysLeft: next, daysLeftManual: false, daysLeftMonth: activeMonth });
  }, [activeMonth, server?.bankHolidays, calcSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed the Save to Database month from the file. Only fires when the detected
  // month actually changes, so a month picked by hand survives re-renders and is
  // only replaced when a different month's file is loaded.
  useEffect(() => {
    if (!detectedMonth) return;
    if (appliedSaveMonth.current === detectedMonth) return;
    appliedSaveMonth.current = detectedMonth;
    setSaveMonth(detectedMonth);
    persist({ saveMonth: detectedMonth });
  }, [detectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps
  const [teamFilter, setTeamFilter] = useState(fs.teamFilter || "ALL");
  // deletedParts stored as plain objects (Set array) for JSON serialisability
  const [deletedParts, setDeletedParts] = useState(() => {
    const raw = fs.deletedParts || {};
    const out = {};
    Object.entries(raw).forEach(([k, v]) => {
      out[k] = new Set(Array.isArray(v) ? v : []);
    });
    return out;
  });
  const [expandedPerson, setExpandedPerson] = useState(null);
  const [teamTiersConfig, setTeamTiersConfig] = useState(
    fs.teamTiersConfig || {},
  );
  const [rcSaveStatus, setRcSaveStatus] = useState("");
  const [rotaFilledNames, setRotaFilledNames] = useState(new Set());
  const [rotaSections, setRotaSections] = useState(fs.rotaSections || {});
  const [engineerAliases, setEngineerAliases] = useState(
    fs.engineerAliases || [],
  );
  const [repairRotaEntries, setRepairRotaEntries] = useState([]);
  const [repairRotaMonth, setRepairRotaMonth] = useState("");
  const [uploadAliasApplyMsg, setUploadAliasApplyMsg] = useState("");
  const [uploadAliasApplying, setUploadAliasApplying] = useState(false);
  const [reconciliationAcknowledged, setReconciliationAcknowledged] =
    useState(false);
  const [rosterMsg, setRosterMsg] = useState("");
  const engineerAliasIndex = useMemo(
    () => buildEngineerAliasIndex(engineerAliases),
    [engineerAliases],
  );
  const engineerStatusIndex = useMemo(
    () =>
      buildEngineerStatusIndex(
        (engineerAliases || []).map((row) => ({
          engineer: row.engineer,
          status: row.status,
        })),
        engineerAliases,
      ),
    [engineerAliases],
  );
  const repairRotaAliasTargets = useMemo(() => {
    return buildRepairUploadAliasTargets({
      activeMonth,
      repairRotaMonth,
      repairRotaEntries,
      engineerAliases,
      statusIndex: engineerStatusIndex,
    });
  }, [
    activeMonth,
    engineerAliases,
    engineerStatusIndex,
    repairRotaEntries,
    repairRotaMonth,
  ]);
  const uploadAliasSuggestions = useMemo(
    () =>
      buildEngineerAliasSuggestions(
        {
          uploadRows:
            personCol && rows.length
              ? rows.map((row) => ({ engineer: row[personCol] }))
              : [],
        },
        repairRotaAliasTargets,
        { statusIndex: engineerStatusIndex },
      ),
    [engineerStatusIndex, personCol, repairRotaAliasTargets, rows],
  );
  const autoUploadAliasSuggestions = uploadAliasSuggestions.filter(
    (suggestion) => suggestion.autoApply,
  );
  const uploadReconciliation = useMemo(
    () =>
      buildReconciliationReport({
        sourceNames:
          personCol && rows.length ? rows.map((row) => row[personCol]) : [],
        targets: engineerAliases,
        rotaTargets: repairRotaEntries.length ? repairRotaAliasTargets : [],
        suggestions: uploadAliasSuggestions,
        statusIndex: engineerStatusIndex,
        classifyNew: true,
        targetSource: repairRotaEntries.length
          ? "month rota"
          : engineerAliases.length
            ? "full roster"
            : "unavailable",
      }),
    [
      engineerAliases,
      engineerStatusIndex,
      personCol,
      repairRotaAliasTargets,
      repairRotaEntries,
      rows,
      uploadAliasSuggestions,
    ],
  );

  useEffect(() => {
    setReconciliationAcknowledged(false);
    setRcSaveStatus("");
    setRosterMsg("");
  }, [detectedMonth, fileName, personCol]);

  useEffect(() => {
    if (server?.serverStatus !== "online") return;
    server.fetchEngineers().then((rows) => {
      if (!rows) return;
      setEngineerAliases(rows);
      if (onFileState) persist({ engineerAliases: rows });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.serverStatus]);

  useEffect(() => {
    const parsedMonth = parseMonthKey(activeMonth);
    if (!parsedMonth || server?.serverStatus !== "online") {
      setRepairRotaEntries([]);
      setRepairRotaMonth("");
      return;
    }
    const { year, month } = parsedMonth;
    let cancelled = false;
    setRepairRotaEntries([]);
    setRepairRotaMonth(activeMonth);
    server.fetchRotaForMonth(year, month).then((entries) => {
      if (cancelled) return;
      setRepairRotaEntries(entries || []);
      setRepairRotaMonth(activeMonth);
    });
    return () => {
      cancelled = true;
    };
  }, [activeMonth, server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!parsedPersons?.persons?.length) return;
    if (server?.serverStatus !== "online") return;
    const parsedMonth = parseMonthKey(activeMonth);
    if (!parsedMonth) return;
    const { year, month } = parsedMonth;
    const now = new Date();
    const isCurrentMonth =
      year === now.getFullYear() && month === now.getMonth() + 1;
    // The current month can only be counted as far as today. A month that has
    // already been and gone is counted in full — otherwise July's figures get
    // truncated at today's date in August.
    const dayLimit = isCurrentMonth
      ? getElapsedRotaDayLimit(now)
      : new Date(year, month, 0).getDate();
    const monthKey = activeMonth;
    server.fetchRotaForMonth(year, month).then((entries) => {
      // A month with no rota entered yet (or a fetch that comes back empty)
      // must still fall through to the reset below — otherwise reloading a
      // KPI export for a month the rota hasn't been entered for leaves every
      // engineer's "days worked" frozen on whatever the last successful month
      // computed, with no visible indication why.
      const byName = {};
      const byNameWithToday = {};
      const sectionByName = {};
      (entries || []).forEach((e) => {
        try {
          const days = JSON.parse(e.days_json);
          const dow = JSON.parse(e.dow_json);
          const count = calculateRotaWorkedDays(days, dow, dayLimit);
          byName[normalizeEngineerName(e.engineer)] = count;
          // Informational "if today counted too" figure — only differs from
          // the real count in the current month, and only once today's own
          // rota cell is in range.
          if (isCurrentMonth)
            byNameWithToday[normalizeEngineerName(e.engineer)] =
              calculateRotaWorkedDays(days, dow, dayLimit + 1);
          if (e.section)
            sectionByName[normalizeEngineerName(e.engineer)] = e.section;
        } catch {}
      });
      const matchedCsvNames = new Set();
      const daysWorkedPatch = {};
      const daysWorkedWithTodayPatch = {};
      const rotaSectionsPatch = {};
      parsedPersons.persons.forEach((p) => {
        const canonicalName = resolveEngineerIdentity(
          p.name,
          engineerAliasIndex,
        );
        const key = normalizeEngineerName(canonicalName || p.name);
        const rotaDays = findByLooseEngineerName(
          byName,
          canonicalName || p.name,
        );
        const rotaDaysWithToday = findByLooseEngineerName(
          byNameWithToday,
          canonicalName || p.name,
        );
        const rotaSection = findByLooseEngineerName(
          sectionByName,
          canonicalName || p.name,
        );
        if (rotaDays !== undefined) {
          matchedCsvNames.add(p.name);
          daysWorkedPatch[p.name] = rotaDays;
        }
        if (rotaDaysWithToday !== undefined)
          daysWorkedWithTodayPatch[p.name] = rotaDaysWithToday;
        if (rotaSection) rotaSectionsPatch[key] = rotaSection;
      });
      setRotaFilledNames(matchedCsvNames);
      setDaysWorkedWithToday(daysWorkedWithTodayPatch);
      setRotaSections(rotaSectionsPatch);
      setParsedPersons((prev) => {
        if (!prev?.persons?.length) return prev;
        const nextPersons = prev.persons.map((person) => {
          const section = findByLooseEngineerName(
            rotaSectionsPatch,
            person.name,
          );
          const rotaTeam = normalizeSectionToTeam(section);
          return rotaTeam ? { ...person, team: rotaTeam } : person;
        });
        const allTeams = [
          ...new Set(
            nextPersons
              .map((p) => getTeamForPerson({ ...p, team: p.team }))
              .filter(Boolean),
          ),
        ].sort();
        return { ...prev, persons: nextPersons, allTeams };
      });
      // The rota is the single source of truth whenever it has a figure for an
      // engineer: a hand-typed value is a temporary override that only lasts
      // until the next KPI calculation re-syncs from the rota, not a permanent
      // correction. It only survives untouched for engineers the rota has no
      // entry for (unmatched names) — there's nothing to replace it with.
      // A new month wipes last month's saved figures outright.
      const monthChanged = daysWorkedMonth !== monthKey;
      const nextManualDays = new Set(monthChanged ? [] : manualDays);
      Object.keys(daysWorkedPatch).forEach((name) => nextManualDays.delete(name));
      setDaysWorked((prev) => {
        const next = monthChanged ? {} : { ...prev };
        Object.entries(daysWorkedPatch).forEach(([name, count]) => {
          next[name] = String(count);
        });
        if (onFileState)
          persist({
            daysWorked: next,
            rotaSections: rotaSectionsPatch,
            manualDays: [...nextManualDays],
            daysWorkedMonth: monthKey,
          });
        return next;
      });
      setManualDays(nextManualDays);
      if (monthChanged) {
        setDaysWorkedMonth(monthKey);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parsedPersons?.persons?.length,
    activeMonth,
    server?.serverStatus,
    engineerAliases.length,
  ]);

  // Persist all non-Set state back to parent (convert Sets to arrays for JSON)
  function persist(patch) {
    if (!onFileState) return;
    const dpRaw = {};
    Object.entries(deletedParts).forEach(([k, v]) => {
      dpRaw[k] = [...v];
    });
    onFileState({
      rows,
      headers,
      partIdCol,
      personCol,
      unitStatusCol,
      teamCol,
      closeDttmCol,
      fileName,
      parsedPersons,
      msg,
      daysWorked,
      daysLeft,
      manualDays: [...manualDays],
      daysWorkedMonth,
      daysLeftManual,
      daysLeftMonth,
      saveMonth,
      teamFilter,
      deletedParts: dpRaw,
      teamTiersConfig,
      rotaSections,
      engineerAliases,
      ...patch,
    });
  }

  const MONTH_NAMES = new Set([
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ]);
  const gradeValues = state.gradeValues;
  const berValue = gradeValues["BER"] || 0.1;

  // File-specific Repair Centre overrides win; otherwise use the app-wide Team Targets config.
  function getRepairTeamTiers(team) {
    if (teamTiersConfig[team]) return teamTiersConfig[team];
    const resolvedTeam = normalizeSectionToTeam(team) || team || "ALL";
    return getTeamTargetTiers(resolvedTeam, state.teamTargets);
  }

  // Map a rota section string (e.g. "RC DCD Engineers") to a TEAM_DEFAULT_TIERS key
  // Resolve the correct team for tier lookups prefers rota section over CSV team column
  function getTeamForPerson(person) {
    const rotaTeam = normalizeSectionToTeam(
      findByLooseEngineerName(rotaSections, person.name),
    );
    return rotaTeam || person.team;
  }

  function updateTeamTier(team, idx, field, val) {
    setTeamTiersConfig((prev) => {
      const current = prev[team] || getRepairTeamTiers(team);
      const updated = current.map((t, i) =>
        i === idx ? { ...t, [field]: parseFloat(val) || 0 } : t,
      );
      const next = { ...prev, [team]: updated };
      persist({ teamTiersConfig: next });
      return next;
    });
  }
  function addTeamTier(team) {
    setTeamTiersConfig((prev) => {
      const current = prev[team] || getRepairTeamTiers(team);
      const next = { ...prev, [team]: [...current, { daily: 27, bonus: 100 }] };
      persist({ teamTiersConfig: next });
      return next;
    });
  }
  function removeTeamTier(team, idx) {
    setTeamTiersConfig((prev) => {
      const current = prev[team] || getRepairTeamTiers(team);
      const next = { ...prev, [team]: current.filter((_, i) => i !== idx) };
      persist({ teamTiersConfig: next });
      return next;
    });
  }
  function resetTeamTiers(team) {
    setTeamTiersConfig((prev) => {
      const n = { ...prev };
      delete n[team];
      persist({ teamTiersConfig: n });
      return n;
    });
  }

  async function refreshRepairEngineerAliases() {
    if (server?.serverStatus !== "online" || !server.fetchEngineers) return [];
    const freshRows = await server.fetchEngineers();
    if (!freshRows) return [];
    setEngineerAliases(freshRows);
    persist({ engineerAliases: freshRows });
    return freshRows;
  }

  // A "new" upload name that is really an existing engineer under another
  // spelling: save it as their alias instead of registering a new person.
  async function linkUploadNameToEngineer(alias, engineer) {
    if (!alias || !engineer || !server?.saveEngineerAliases) return;
    const row = (engineerAliases || []).find(
      (entry) => entry.engineer === engineer,
    );
    const existing = (row?.aliases || [])
      .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
      .filter(Boolean);
    const result = await server.saveEngineerAliases(engineer, [
      ...existing,
      alias,
    ]);
    const freshRows = await refreshRepairEngineerAliases();
    if (result?.ok) onEngineerAliasesChanged?.(freshRows);
    setRosterMsg(
      result?.ok
        ? `Linked ${alias} to ${engineer}.`
        : `Could not link ${alias} to ${engineer}.`,
    );
  }

  async function applyUploadHighConfidenceAliases() {
    if (
      !autoUploadAliasSuggestions.length ||
      server?.serverStatus !== "online" ||
      !server.saveEngineerAliases
    )
      return;
    setUploadAliasApplying(true);
    setUploadAliasApplyMsg("");
    const grouped = {};
    autoUploadAliasSuggestions.forEach((suggestion) => {
      if (!grouped[suggestion.engineer]) grouped[suggestion.engineer] = [];
      grouped[suggestion.engineer].push(suggestion.alias);
    });

    let attempted = 0;
    let failed = 0;
    const attemptedGrouped = {};
    for (const [engineer, aliases] of Object.entries(grouped)) {
      const row = (engineerAliases || []).find(
        (entry) => entry.engineer === engineer,
      );
      const existing = (row?.aliases || [])
        .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
        .filter(Boolean);
      const merged = [...existing];
      aliases.forEach((alias) => {
        const key = normalizeEngineerName(alias);
        if (!key) return;
        if (!merged.some((item) => normalizeEngineerName(item) === key))
          merged.push(alias);
      });
      try {
        const result = await server.saveEngineerAliases(engineer, merged);
        if (result?.ok) {
          attempted += aliases.length;
          attemptedGrouped[engineer] = aliases;
        } else {
          failed += aliases.length;
        }
      } catch {
        failed += aliases.length;
      }
    }

    const freshRows = await refreshRepairEngineerAliases();
    const verified = countVerifiedEngineerAliases(freshRows, attemptedGrouped);
    if (verified > 0) {
      onEngineerAliasesChanged?.(freshRows);
    }
    failed += Math.max(0, attempted - verified);
    setUploadAliasApplying(false);
    setUploadAliasApplyMsg(
      failed
        ? `Applied ${verified} aliases; ${failed} were not confirmed by Supabase.`
        : `Applied ${verified} high-confidence aliases.`,
    );
    setTimeout(() => setUploadAliasApplyMsg(""), 3500);
  }

  function deletePart(personName, partIdx) {
    setDeletedParts((prev) => {
      const next = { ...prev };
      next[personName] = new Set(prev[personName] || []);
      next[personName].add(partIdx);
      const raw = {};
      Object.entries(next).forEach(([k, v]) => {
        raw[k] = [...v];
      });
      persist({ deletedParts: raw });
      return next;
    });
  }
  function restorePart(personName, partIdx) {
    setDeletedParts((prev) => {
      const next = { ...prev };
      if (next[personName]) {
        next[personName] = new Set(next[personName]);
        next[personName].delete(partIdx);
      }
      const raw = {};
      Object.entries(next).forEach(([k, v]) => {
        raw[k] = [...v];
      });
      persist({ deletedParts: raw });
      return next;
    });
  }
  function restoreAll(personName) {
    setDeletedParts((prev) => {
      const n = { ...prev };
      delete n[personName];
      const raw = {};
      Object.entries(n).forEach(([k, v]) => {
        raw[k] = [...v];
      });
      persist({ deletedParts: raw });
      return n;
    });
  }

  async function handleFile(file) {
    try {
      let h = [],
        r = [];
      if (file.name.endsWith(".csv")) {
        const { headers: hh, rows: rr } = parseCSV(await file.text());
        h = hh;
        r = rr;
      } else {
        const sheets = await parseXLSX(file);
        const sheetName = Object.keys(sheets)[0];
        const sheetRows = sheets[sheetName];
        if (sheetRows.length > 0) {
          // Find header row search first 10 rows for one containing a part ID keyword
          let headerRowIdx = 0;
          for (let i = 0; i < Math.min(sheetRows.length, 10); i++) {
            const rowStr = sheetRows[i]
              .map((v) => String(v).toLowerCase())
              .join(",");
            if (
              rowStr.includes("part_id") ||
              rowStr.includes("part id") ||
              rowStr.includes("part id1")
            ) {
              headerRowIdx = i;
              break;
            }
          }
          h = sheetRows[headerRowIdx].map((v) => String(v).trim());
          r = sheetRows.slice(headerRowIdx + 1).map((row) => {
            const obj = {};
            h.forEach((col, i) => {
              obj[col] = String(row[i] || "").trim();
            });
            return obj;
          });
        }
      }
      const pId = detectCol(h, ["part_id", "part id", "partid"]);
      const pCol = detectCol(h, [
        "person_id_asgnd_to",
        "person_id",
        "engineer",
        "repairer",
        "technician",
        "person",
      ]);
      const uCol = detectCol(h, ["unit_status", "status"]);
      const tCol = detectCol(h, ["team_id", "team id", "team", "section"]);
      const dttmCol = detectCol(h, [
        "close_dttm",
        "close_date",
        "repaired date",
        "reapired date",
        "close_time",
        "completed",
        "finish_time",
        "finish_date",
      ]);
      const st = `Loaded ${r.length} parts from ${file.name}`;
      setHeaders(h);
      setRows(r);
      setFileName(file.name);
      setParsedPersons(null);
      setPartIdCol(pId);
      setPersonCol(pCol);
      setUnitStatusCol(uCol);
      setTeamCol(tCol);
      setCloseDttmCol(dttmCol);
      setMsg(st);
      if (onFileState)
        onFileState({
          ...fs,
          headers: h,
          rows: r,
          fileName: file.name,
          parsedPersons: null,
          partIdCol: pId,
          personCol: pCol,
          unitStatusCol: uCol,
          teamCol: tCol,
          closeDttmCol: dttmCol,
          msg: st,
        });
    } catch (e) {
      const st = "Error: " + e.message;
      setMsg(st);
      persist({ msg: st });
    }
  }

  function calculate() {
    if (!rows.length) return;
    setCalcSeq((v) => v + 1);
    const lookup = { ...(state.gradingLookup || {}) };
    const personMap = {};
    let weekendCompleted = 0;
    // Tally of completion months across the file. The month holding the most
    // parts is the month this export belongs to, which then drives the rota
    // lookup, the working-days-left box and the Save to Database month.
    const monthTally = {};

    const noStatusCol = !unitStatusCol;
    for (const row of rows) {
      let statusRaw;
      if (noStatusCol) {
        statusRaw = "COMP";
      } else {
        statusRaw = (row[unitStatusCol] || "").trim();
        if (
          statusRaw.toLowerCase().startsWith("unit_status") &&
          statusRaw !== "unit_status"
        )
          continue;
        if (!statusRaw || statusRaw === "unit_status") continue;
      }
      const firstVal = (row[Object.keys(row)[0]] || "").trim().toLowerCase();
      if (MONTH_NAMES.has(firstVal)) continue;
      const partId = (row[partIdCol] || "").trim().toUpperCase();
      if (!partId || partId.toLowerCase().startsWith("part_id")) continue;
      const rawPerson = (row[personCol] || "").trim();
      if (!rawPerson || rawPerson.toLowerCase().startsWith("person_id"))
        continue;
      const person = resolveEngineerIdentity(rawPerson, engineerAliasIndex);
      // Admin staff book parts but are not repair engineers, so their rows must
      // not enter earnings figures or the summaries saved from this upload.
      if (isExcludedFromHistory(person, engineerStatusIndex)) continue;
      // Numeric status (e.g. "1" in old XLSX grouped-row format) = one completed repair
      const statusNum = parseFloat(statusRaw);
      const status =
        !isNaN(statusNum) && isFinite(statusNum) && statusNum > 0
          ? "COMP"
          : statusRaw.toUpperCase();
      const lookupInfo = lookup[partId] || {};
      const lookupGrade = lookupInfo.grade || null;
      const lookupTeam =
        lookupInfo.team || normalizeSectionToTeam(lookupInfo.section || "");
      const team = (row[teamCol] || "").trim() || lookupTeam || "";
      const isBER =
        looksLikeBER(
          statusRaw,
          status,
          partId,
          lookupGrade,
          row[unitStatusCol],
        ) ||
        Object.values(row).some(
          (value) => looksLikeBER(value) && String(value || "").length <= 40,
        );
      if (!noStatusCol && !["COMP", "BER", "NFF"].includes(status) && !isBER)
        continue;
      const closeDttmRaw = closeDttmCol ? (row[closeDttmCol] || "").trim() : "";
      let closeDttm = "";
      let closeDttmISO = "";
      let isWeekend = false;
      let inBonusWindow = true;
      if (closeDttmRaw) {
        const d = parseRepairCompletedDate(closeDttmRaw);
        if (d && !isNaN(d.getTime())) {
          const mk = monthKeyFromDate(d);
          if (mk) monthTally[mk] = (monthTally[mk] || 0) + 1;
          isWeekend = isLondonWeekend(d);
          if (isWeekend) weekendCompleted++;
          inBonusWindow = isRepairBonusWindow(d);
          closeDttmISO = d.toISOString();
          closeDttm = formatLondonShort(d);
        }
      }
      const grade = isBER ? "BER" : lookupGrade;
      const val = grade ? gradeValues[grade] || 0 : 0;
      if (!personMap[person])
        personMap[person] = { parts: [], team, importNames: new Set() };
      else if (!personMap[person].team && team) personMap[person].team = team;
      personMap[person].importNames.add(rawPerson);
      personMap[person].parts.push({
        partId,
        grade,
        val,
        isBER,
        status,
        closeDttm,
        closeDttmISO,
        closeDttmRaw,
        isWeekend,
        inBonusWindow,
        bonusEligible: !isBER && inBonusWindow,
        idx: personMap[person].parts.length,
      });
    }

    const persons = Object.entries(personMap)
      .map(([name, data]) => ({
        name,
        team: data.team,
        parts: data.parts,
        importNames: [...data.importNames],
      }))
      .sort((a, b) => b.parts.length - a.parts.length);

    const allTeams = [
      ...new Set(persons.map((p) => p.team).filter(Boolean)),
    ].sort();
    const detectedMonth = pickDominantMonth(monthTally);
    setParsedPersons({
      persons,
      allTeams,
      weekendExcluded: weekendCompleted,
      detectedMonth,
    });
    setDeletedParts({});
    if (onFileState)
      onFileState({
        ...fs,
        rows,
        headers,
        partIdCol,
        personCol,
        unitStatusCol,
        teamCol,
        closeDttmCol,
        fileName,
        msg,
        daysWorked,
        daysLeft,
        teamFilter,
        teamTiersConfig,
        rotaSections,
        engineerAliases,
        parsedPersons: {
          persons,
          allTeams,
          weekendExcluded: weekendCompleted,
          detectedMonth,
        },
        deletedParts: {},
      });
  }

  // Derive active persons with deletion applied
  function getActiveParts(person) {
    const deleted = deletedParts[person.name] || new Set();
    return person.parts.filter((p) => !deleted.has(p.idx));
  }

  function getPersonStats(person) {
    const active = getActiveParts(person);
    const gradeCounts = {};
    GRADE_ORDER.forEach((g) => {
      gradeCounts[g] = 0;
    });
    // Weekend parts are not eligible for commission: their value is kept
    // separately in weekendCommission (shown for information only) and is
    // never added to totalCommission.
    let totalCommission = 0,
      weekendCommission = 0,
      ungraded = 0,
      berCount = 0,
      weekendCount = 0,
      outsideBonusHours = 0;
    active.forEach((p) => {
      if (p.isWeekend) weekendCount++;
      if (p.closeDttmISO && !p.inBonusWindow) outsideBonusHours++;
      let partValue = 0;
      if (p.isBER) {
        berCount++;
        partValue = berValue;
        gradeCounts["BER"]++;
      } else if (p.grade && gradeValues[p.grade] !== undefined) {
        gradeCounts[p.grade]++;
        partValue = gradeValues[p.grade] || 0;
      } else {
        ungraded++;
      }
      if (p.isWeekend) weekendCommission += partValue;
      else totalCommission += partValue;
    });
    // BER and weekend parts do NOT count toward daily avg / tier checks
    const nonBerParts = active.length - berCount;
    const bonusEligibleParts = active.filter((p) =>
      p.bonusEligible !== undefined
        ? p.bonusEligible
        : !p.isBER && !p.isWeekend,
    ).length;
    return {
      active,
      gradeCounts,
      totalCommission,
      weekendCommission,
      ungraded,
      berCount,
      weekendCount,
      outsideBonusHours,
      totalParts: active.length,
      nonBerParts,
      bonusEligibleParts,
    };
  }

  function safeSheetName(name, fallback = "Engineer") {
    const cleaned = String(name || fallback)
      .replace(new RegExp("[\\[\\]*\\\\/?:]", "g"), " ")
      .trim()
      .slice(0, 31);
    return cleaned || fallback;
  }

  function getExportProjection(person, stats, days) {
    const remaining = parseInt(daysLeft || "0", 10) || 0;
    const avgCommissionPerDay = days > 0 ? stats.totalCommission / days : 0;
    const projectedExtraCommission =
      days > 0 ? avgCommissionPerDay * remaining : 0;
    const projectedMonthCommission =
      stats.totalCommission + projectedExtraCommission;
    const avgNonBerPerDay = days > 0 ? stats.bonusEligibleParts / days : 0;
    const tiers = getRepairTeamTiers(getTeamForPerson(person));
    const tierHit = [...tiers]
      .sort((a, b) => b.daily - a.daily)
      .find((t) => avgNonBerPerDay >= t.daily);
    const finalMonthEarnings = stats.totalCommission + (tierHit?.bonus || 0);
    return {
      remaining,
      avgCommissionPerDay,
      projectedExtraCommission,
      projectedMonthCommission,
      finalMonthEarnings,
      avgNonBerPerDay,
      tierHit,
    };
  }

  function buildRepairCentreExportSheet(person, stats) {
    const days = parseFloat(daysWorked[person.name] || "") || 0;
    const projection = getExportProjection(person, stats, days);
    const resolvedTeam = getTeamForPerson(person) || person.team || "";
    const hasTime = stats.active.some((p) => p.closeDttm);
    const summaryRows = [
      [
        `${person.name} - ${resolvedTeam || "Unknown Team"} Commission Report`,
        "",
        "",
        "",
        "",
        ...(hasTime ? [""] : []),
      ],
      [],
      ["Engineer", person.name],
      ["Team", resolvedTeam || "Unknown"],
      ["Total Parts", stats.totalParts],
      ["Non-BER Parts", stats.nonBerParts],
      ["Weekend Parts", stats.weekendCount],
      ["Outside Bonus Hours", stats.outsideBonusHours],
      ["Bonus-Eligible Parts", stats.bonusEligibleParts],
      ["BER Parts", stats.berCount],
      ["Ungraded Parts", stats.ungraded],
      ["Days Worked", days || ""],
      [
        "Avg Non-BER Parts/Day",
        days > 0 ? Number(projection.avgNonBerPerDay.toFixed(2)) : "",
      ],
      [
        "Total Commission Earned (excl. weekend)",
        Number(stats.totalCommission.toFixed(2)),
      ],
      [
        "Weekend Commission (not paid, info only)",
        Number((stats.weekendCommission || 0).toFixed(2)),
      ],
      [
        "Avg Commission/Day",
        days > 0 ? Number(projection.avgCommissionPerDay.toFixed(2)) : "",
      ],
      ["Working Days Left", projection.remaining || ""],
      [
        "Projected Extra Commission",
        projection.remaining > 0 && days > 0
          ? Number(projection.projectedExtraCommission.toFixed(2))
          : "",
      ],
      [
        projection.remaining > 0
          ? "Projected Month Commission"
          : "Final Month Earnings",
        projection.remaining > 0 && days > 0
          ? Number(projection.projectedMonthCommission.toFixed(2))
          : Number(projection.finalMonthEarnings.toFixed(2)),
      ],
      [
        "Bonus Tier Hit",
        projection.tierHit
          ? `${projection.tierHit.daily}/day - ${projection.tierHit.bonus}`
          : "",
      ],
      [],
      ["Grade Breakdown", "Count", "Value Each", "Commission"],
      ...GRADE_ORDER.map((grade) => [
        grade,
        stats.gradeCounts[grade] || 0,
        grade === "BER" ? berValue : gradeValues[grade] || 0,
        Number(
          (
            (stats.gradeCounts[grade] || 0) *
            (grade === "BER" ? berValue : gradeValues[grade] || 0)
          ).toFixed(2),
        ),
      ]),
      [],
    ];
    const headerRow = [
      "Part ID",
      "Grade",
      "Commission",
      "BER",
      "Weekend",
      "Bonus Eligible",
      "Status",
      "Source Row",
      ...(hasTime ? ["Completed"] : []),
    ];
    const dataRows = stats.active.map((p) => [
      p.partId,
      p.isBER ? "BER" : p.grade || "UNGRADED",
      p.isBER ? berValue : p.val || 0,
      p.isBER ? "YES" : "NO",
      p.isWeekend ? "YES" : "NO",
      p.bonusEligible ? "YES" : "NO",
      p.status,
      p.idx + 1,
      ...(hasTime ? [p.closeDttm || ""] : []),
    ]);
    return {
      rows: [...summaryRows, headerRow, ...dataRows],
      hasTime,
      partListStartRow: summaryRows.length + 1,
    };
  }

  // Excel export for one person
  function exportPersonXLSX(person, stats) {
    if (!window.XLSX) {
      alert("SheetJS not loaded");
      return;
    }
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const sheetData = buildRepairCentreExportSheet(person, stats);
    const ws = XLSX.utils.aoa_to_sheet(sheetData.rows);
    ws["!cols"] = [
      { wch: 34 },
      { wch: 18 },
      { wch: 14 },
      { wch: 8 },
      { wch: 10 },
      { wch: 14 },
      { wch: 12 },
      ...(sheetData.hasTime ? [{ wch: 18 }] : []),
    ];
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(person.name));
    XLSX.writeFile(wb, `${person.name}_Commission.xlsx`);
  }

  // Export ALL visible engineers
  function exportAllXLSX() {
    if (!window.XLSX) {
      alert("SheetJS not loaded");
      return;
    }
    if (!parsedPersons) return;
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const visiblePersons =
      teamFilter === "ALL"
        ? parsedPersons.persons
        : parsedPersons.persons.filter(
            (p) => getTeamForPerson(p) === teamFilter,
          );

    // Summary sheet
    const summaryHeader = [
      "Engineer",
      "Team",
      ...GRADE_ORDER,
      "Total Parts",
      "Bonus Parts",
      "Days Worked",
      "Avg/Day",
      "Commission",
    ];
    const summaryRows = visiblePersons.map((person) => {
      const stats = getPersonStats(person);
      const days = parseFloat(daysWorked[person.name] || "") || 0;
      return [
        person.name,
        getTeamForPerson(person) || "",
        ...GRADE_ORDER.map((g) => stats.gradeCounts[g] || 0),
        stats.totalParts,
        stats.bonusEligibleParts,
        days || "",
        days > 0
          ? parseFloat((stats.bonusEligibleParts / days).toFixed(1))
          : "",
        parseFloat(stats.totalCommission.toFixed(2)),
      ];
    });
    const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeader, ...summaryRows]);
    wsSummary["!cols"] = [
      { wch: 20 },
      { wch: 16 },
      ...GRADE_ORDER.map(() => ({ wch: 6 })),
      { wch: 10 },
      { wch: 11 },
      { wch: 12 },
      { wch: 10 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // Per-person sheets
    visiblePersons.forEach((person) => {
      const stats = getPersonStats(person);
      const sheetData = buildRepairCentreExportSheet(person, stats);
      const ws = XLSX.utils.aoa_to_sheet(sheetData.rows);
      ws["!cols"] = [
        { wch: 34 },
        { wch: 18 },
        { wch: 14 },
        { wch: 8 },
        { wch: 10 },
        { wch: 14 },
        { wch: 12 },
        ...(sheetData.hasTime ? [{ wch: 18 }] : []),
      ];
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(person.name));
    });

    XLSX.writeFile(
      wb,
      `RepairCentre_Commission${teamFilter !== "ALL" ? `_${teamFilter}` : ""}.xlsx`,
    );
  }

  const allTeams = parsedPersons?.allTeams || [];
  const visiblePersons = parsedPersons
    ? teamFilter === "ALL"
      ? parsedPersons.persons
      : parsedPersons.persons.filter((p) => getTeamForPerson(p) === teamFilter)
    : [];

  // Team totals (after deletions)
  const teamTotals = parsedPersons
    ? (() => {
        const t = {};
        parsedPersons.persons.forEach((p) => {
          const stats = getPersonStats(p);
          const tm = getTeamForPerson(p) || "Unknown";
          if (!t[tm]) t[tm] = { parts: 0, commission: 0, count: 0 };
          t[tm].parts += stats.totalParts;
          t[tm].commission += stats.totalCommission;
          t[tm].count++;
        });
        return t;
      })()
    : {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Upload + column mapping */}
      <Card>
        <Label>Repair Centre Earnings Calculator</Label>
        <p
          style={{
            color: "#4f6fa0",
            fontSize: 12,
            marginBottom: 14,
            lineHeight: 1.6,
          }}
        >
          Upload the repair centre KPI export (.csv or .xlsx). Each engineer's
          completed parts are cross-referenced against the loaded grading data.
          BER is detected automatically from{" "}
          <strong style={{ color: "#c8d8f0" }}>unit_status</strong>.
          {Object.keys(state.gradingLookup || {}).length > 0 ? (
            <span style={{ color: "#4ade80" }}>
              {" "}
              Grading reference loaded (
              {Object.keys(state.gradingLookup).length} parts)
            </span>
          ) : (
            <span style={{ color: "#f59e0b" }}>
              {" "}
              Load grading data in Planner first for best results
            </span>
          )}
        </p>
        <DropZone
          label={fileName || "Drop Repair Centre KPI Export (.csv or .xlsx)"}
          sub="Supports old format (part_id, unit_status) and new format (part_id1, close_dttm)"
          onFile={handleFile}
          accept=".csv,.xlsx"
          color="#f472b6"
        />
        {msg && (
          <div
            style={{
              color: msg.startsWith("OK:") ? "#4ade80" : "#f87171",
              fontSize: 12,
              marginTop: 10,
            }}
          >
            {msg}
          </div>
        )}
        {headers.length > 0 && (
          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
              gap: 12,
              alignItems: "flex-end",
            }}
          >
            {[
              ["Part ID Column", partIdCol, setPartIdCol],
              ["Person / Engineer", personCol, setPersonCol],
              ["Unit Status Col", unitStatusCol, setUnitStatusCol],
              ["Team Column (opt)", teamCol, setTeamCol],
              ["Close Datetime (opt)", closeDttmCol, setCloseDttmCol],
            ].map(([lbl, val, setter]) => (
              <div key={lbl}>
                <Label>{lbl}</Label>
                <Select value={val} onChange={(e) => setter(e.target.value)}>
                  <option value="">-- None --</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
            <Btn
              onClick={calculate}
              variant="success"
              style={{ alignSelf: "flex-end" }}
            >
              CALCULATE
            </Btn>
          </div>
        )}
        {personCol && rows.length > 0 && (
          <div
            style={{
              marginTop: 16,
              background: "#071225",
              border: `1px solid ${uploadReconciliation.requiresReview ? "#7f1d1d" : "#142849"}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 10,
              }}
            >
              <div>
                <Label>Pre-save Reconciliation</Label>
                <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                  {uploadReconciliation.message} Review names can split one
                  engineer across History identities. New names are added to
                  the engineer roster when you save — link them first if they
                  are an existing engineer under another spelling.
                </div>
              </div>
              <div
                style={{
                  color: uploadReconciliation.requiresReview ? "#f87171" : "#22c55e",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}
              >
                {uploadReconciliation.requiresReview
                  ? "Review required before save"
                  : uploadReconciliation.available
                    ? "Ready to save"
                    : "Verification unavailable"}
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))",
                gap: 7,
                marginBottom: 10,
              }}
            >
              {[
                ["exact", "Exact", "#22c55e"],
                ["alias", "Alias", "#5de7ff"],
                ["suggestion", "Suggest", "#8ab8fa"],
                ["review", "Review", "#fbbf24"],
                ["new", "New", "#34d399"],
                ["unresolved", "Unresolved", "#f87171"],
                ["inactive", "Inactive", "#f97316"],
                ["rota-only", "Rota only", "#a78bfa"],
              ].map(([key, label, accent]) => (
                <div
                  key={key}
                  style={{
                    border: `1px solid ${accent}33`,
                    background: `${accent}0d`,
                    borderRadius: 7,
                    padding: "8px 9px",
                  }}
                >
                  <div
                    style={{
                      color: accent,
                      fontSize: 9,
                      textTransform: "uppercase",
                      fontWeight: 800,
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      color: "#dbeafe",
                      fontSize: 17,
                      fontWeight: 900,
                      marginTop: 3,
                    }}
                  >
                    {Number(uploadReconciliation.counts[key] || 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            {uploadReconciliation.rows.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 10,
                  }}
                >
                  <thead>
                    <tr style={{ color: "#4f6fa0" }}>
                      {["Upload name", "Engineer", "Result", "Uses", "Evidence"].map(
                        (heading) => (
                          <th
                            key={heading}
                            style={{ textAlign: "left", padding: "6px" }}
                          >
                            {heading}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadReconciliation.rows.slice(0, 12).map((row) => {
                      const accent =
                        row.classification === "unresolved"
                          ? "#f87171"
                          : row.classification === "review"
                            ? "#fbbf24"
                            : row.classification === "new"
                              ? "#34d399"
                              : row.classification === "inactive"
                                ? "#f97316"
                                : "#8ab8fa";
                      return (
                        <tr key={row.id} style={{ borderTop: "1px solid #0d1b35" }}>
                          <td style={{ padding: "6px", color: "#c8d8f0", fontWeight: 700 }}>
                            {row.name}
                          </td>
                          <td style={{ padding: "6px", color: "#7aa0d4" }}>
                            {row.classification === "new" ? (
                              <select
                                value=""
                                onChange={(event) =>
                                  linkUploadNameToEngineer(
                                    row.name,
                                    event.target.value,
                                  )
                                }
                                title="Link this name to an existing engineer instead of adding a new one"
                                style={{
                                  background: "#060e1e",
                                  border: "1px solid #1e3056",
                                  borderRadius: 6,
                                  color: "#c8d8f0",
                                  fontSize: 10,
                                  padding: "3px 5px",
                                }}
                              >
                                <option value="">New engineer · link to…</option>
                                {(engineerAliases || [])
                                  .filter(
                                    (entry) =>
                                      (entry.status || "active") === "active",
                                  )
                                  .map((entry) => (
                                    <option
                                      key={entry.engineer}
                                      value={entry.engineer}
                                    >
                                      {entry.engineer}
                                    </option>
                                  ))}
                              </select>
                            ) : (
                              row.engineer || "—"
                            )}
                          </td>
                          <td style={{ padding: "6px", color: accent, fontWeight: 800 }}>
                            {row.classification}
                          </td>
                          <td style={{ padding: "6px", color: "#c8d8f0" }}>
                            {row.occurrences}
                          </td>
                          <td style={{ padding: "6px", color: "#8aa9d6" }}>
                            {row.evidence}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {uploadReconciliation.rows.length > 12 && (
                  <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 8 }}>
                    Showing 12 of {uploadReconciliation.rows.length.toLocaleString()} name diagnostics.
                  </div>
                )}
              </div>
            )}
            {rosterMsg && (
              <div
                style={{
                  color: /^(Added|Linked)/.test(rosterMsg) && !/ of /.test(rosterMsg)
                    ? "#22c55e"
                    : "#f87171",
                  fontSize: 11,
                  marginTop: 10,
                }}
              >
                {rosterMsg}
              </div>
            )}
            {uploadReconciliation.requiresReview && (
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  color: "#fbbf24",
                  fontSize: 11,
                  lineHeight: 1.5,
                  marginTop: 12,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={reconciliationAcknowledged}
                  onChange={(event) =>
                    setReconciliationAcknowledged(event.target.checked)
                  }
                  style={{ marginTop: 2 }}
                />
                <span>
                  I reviewed the unresolved or ambiguous names and want to save
                  anyway.
                </span>
              </label>
            )}
          </div>
        )}
        {uploadAliasSuggestions.length > 0 && (
          <div
            style={{
              marginTop: 16,
              background: "#071225",
              border: "1px solid #142849",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 10,
              }}
            >
              <div>
                <Label>Auto Alias Suggestions</Label>
                <div style={{ color: "#4f6fa0", fontSize: 11 }}>
                  Found from this Repair Centre upload before saving to History.
                  High-confidence matches are safe to apply; review matches stay
                  manual.
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {uploadAliasApplyMsg && (
                  <span
                    style={{
                      color: uploadAliasApplyMsg.startsWith("Applied")
                        ? "#22c55e"
                        : "#f87171",
                      fontSize: 11,
                    }}
                  >
                    {uploadAliasApplyMsg}
                  </span>
                )}
                <Btn
                  variant="success"
                  style={{ padding: "7px 12px", fontSize: 10 }}
                  disabled={
                    uploadAliasApplying ||
                    !autoUploadAliasSuggestions.length ||
                    server?.serverStatus !== "online"
                  }
                  onClick={applyUploadHighConfidenceAliases}
                >
                  {uploadAliasApplying
                    ? "APPLYING..."
                    : `APPLY HIGH CONFIDENCE (${autoUploadAliasSuggestions.length})`}
                </Btn>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#4f6fa0",
                      borderBottom: "1px solid #1e3056",
                    }}
                  >
                    {["Upload Name", "Engineer", "Confidence", "Reason"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{ textAlign: "left", padding: "6px 8px" }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {uploadAliasSuggestions.slice(0, 6).map((suggestion) => (
                    <tr
                      key={suggestion.id}
                      style={{
                        color: "#c8d8f0",
                        borderBottom: "1px solid #0d1b35",
                      }}
                    >
                      <td style={{ padding: "6px 8px", fontWeight: 700 }}>
                        {suggestion.alias}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {suggestion.engineer}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: suggestion.autoApply ? "#22c55e" : "#fbbf24",
                          fontWeight: 700,
                        }}
                      >
                        {suggestion.autoApply ? "AUTO" : "REVIEW"}
                      </td>
                      <td style={{ padding: "6px 8px", color: "#7aa0d4" }}>
                        {suggestion.reason} - {suggestion.evidence}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {uploadAliasSuggestions.length > 6 && (
              <div style={{ color: "#4f6fa0", fontSize: 10, marginTop: 8 }}>
                Showing 6 of {uploadAliasSuggestions.length.toLocaleString()}{" "}
                suggestions.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Save to server shown after calculate if server is online */}
      {parsedPersons && server?.serverStatus === "online" && (
        <Card style={{ background: "#051208", border: "1px solid #14532d44" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  color: "#4ade80",
                  fontSize: 11,
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                Save to Database
              </div>
              <div style={{ color: "#3d5280", fontSize: 10 }}>
                Saves all engineer earnings data to your Vista server for
                reporting and history tracking. Enter the month below before
                saving.
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <Label>Month (YYYY-MM)</Label>
                <Input
                  type="month"
                  value={saveMonth || activeMonth}
                  onChange={(e) => {
                    setSaveMonth(e.target.value);
                    appliedSaveMonth.current = detectedMonth;
                    persist({ saveMonth: e.target.value });
                  }}
                  id="rc-save-month"
                  style={{ width: 160 }}
                />
                {detectedMonth && (
                  <div
                    style={{ color: "#4ade80", fontSize: 9, marginTop: 4 }}
                    title="Taken from the completion dates in the uploaded file"
                  >
                    DETECTED FROM FILE: {formatMonthKey(detectedMonth)}
                  </div>
                )}
                {rcSaveStatus === "review-required" && (
                  <div style={{ color: "#f87171", fontSize: 10 }}>
                    Review the name diagnostics and tick the acknowledgement before saving.
                  </div>
                )}
              </div>
              <Btn
                variant="success"
                style={{
                  fontSize: 10,
                  padding: "7px 14px",
                  alignSelf: "flex-end",
                }}
                onClick={async () => {
                  if (
                    uploadReconciliation.requiresReview &&
                    !reconciliationAcknowledged
                  ) {
                    setRcSaveStatus("review-required");
                    return;
                  }
                  const month = saveMonth || activeMonth;
                  setRcSaveStatus("saving");
                  const records = [];
                  let ok = true;
                  for (const person of parsedPersons.persons) {
                    const stats = getPersonStats(person);
                    const resolvedTeam = getTeamForPerson(person);
                    const days = parseFloat(daysWorked[person.name] || "") || 0;
                    const avgPD =
                      days > 0 ? stats.bonusEligibleParts / days : 0;
                    const tiers = getRepairTeamTiers(resolvedTeam);
                    const projTier =
                      [...tiers]
                        .sort((a, b) => b.daily - a.daily)
                        .find((t) => avgPD >= t.daily) || null;
                    // Save monthly summary
                    const r = await server.saveMonthlySummary(
                      person.name,
                      resolvedTeam || person.team,
                      month,
                      stats.totalParts,
                      stats.totalCommission,
                      projTier?.bonus || 0,
                      avgPD,
                      days,
                    );
                    if (!r) ok = false;
                    // Save individual part records
                    for (const p of stats.active) {
                      records.push({
                        engineer: person.name,
                        team: resolvedTeam || person.team,
                        partId: p.partId,
                        grade: p.isBER ? "BER" : p.grade,
                        commission: p.isBER
                          ? state.gradeValues.BER || 0.1
                          : p.val || 0,
                        isBER: p.isBER,
                        status: p.status,
                        completedAt: p.closeDttmISO || null,
                        sourceCompletedAt: p.closeDttmRaw || p.closeDttm || "",
                        bonusEligible:
                          p.bonusEligible !== undefined
                            ? p.bonusEligible
                            : !p.isBER && !p.isWeekend,
                      });
                    }
                  }
                  const r2 = await server.saveRepairData(month, records);
                  if (!r2) ok = false;
                  // New starters join the roster automatically, under the
                  // team they booked for, so they are picked up from now on.
                  const newNames = uploadReconciliation.newEngineers || [];
                  let added = 0;
                  for (const name of newNames) {
                    const key = normalizeEngineerName(name);
                    const person = parsedPersons.persons.find(
                      (entry) => normalizeEngineerName(entry.name) === key,
                    );
                    const team = person
                      ? getTeamForPerson(person) || person.team || ""
                      : "";
                    const r3 = await server.addEngineer?.(name, team, "upload");
                    if (r3?.ok) added += 1;
                    else ok = false;
                  }
                  if (newNames.length) {
                    const freshRows = await refreshRepairEngineerAliases();
                    if (added) onEngineerAliasesChanged?.(freshRows);
                    setRosterMsg(
                      added === newNames.length
                        ? `Added ${added} new engineer${added === 1 ? "" : "s"} to the roster: ${newNames.join(", ")}.`
                        : `Added ${added} of ${newNames.length} new engineers; the rest were not confirmed by Supabase.`,
                    );
                  }
                  setRcSaveStatus(ok ? "saved" : "error");
                  setTimeout(() => setRcSaveStatus(""), 4000);
                }}
              >
                {rcSaveStatus === "review-required"
                  ? "REVIEW REQUIRED"
                  : rcSaveStatus === "saving"
                  ? "SAVING..."
                  : rcSaveStatus === "saved"
                    ? "SAVED TO DB"
                    : rcSaveStatus === "error"
                      ? "SAVE FAILED"
                      : "SAVE TO SERVER"}
              </Btn>
            </div>
          </div>
        </Card>
      )}
      {parsedPersons && allTeams.length > 0 && (
        <Card>
          <Label>Per-Team Bonus Tiers</Label>
          <p style={{ color: "#4f6fa0", fontSize: 11, marginBottom: 14 }}>
            Set custom daily targets and bonus amounts for each team. Leave as
            default to use Team Targets from the hamburger menu.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {allTeams.map((team) => {
              const tiers = getRepairTeamTiers(team);
              const hasOverride = !!teamTiersConfig[team];
              return (
                <div
                  key={team}
                  style={{
                    background: "#081224",
                    border: `1px solid ${hasOverride ? "#f472b644" : "#1e3056"}`,
                    borderRadius: 10,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        color: "#f472b6",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      {team}
                    </div>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      {hasOverride && (
                        <span style={{ color: "#f472b6", fontSize: 9 }}>
                          CUSTOM TIERS
                        </span>
                      )}
                      {hasOverride && (
                        <button
                          onClick={() => resetTeamTiers(team)}
                          style={{
                            background: "none",
                            border: "1px solid #3d5280",
                            color: "#3d5280",
                            borderRadius: 5,
                            fontSize: 9,
                            padding: "2px 8px",
                            cursor: "pointer",
                          }}
                        >
                          Reset to global
                        </button>
                      )}
                      <Btn
                        variant="secondary"
                        style={{ padding: "4px 12px", fontSize: 10 }}
                        onClick={() => addTeamTier(team)}
                      >
                        + ADD TIER
                      </Btn>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {tiers.map((tier, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: "#0d1b35",
                          border: "1px solid #1e3056",
                          borderRadius: 8,
                          padding: 12,
                          minWidth: 150,
                          position: "relative",
                        }}
                      >
                        <button
                          onClick={() => removeTeamTier(team, idx)}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 8,
                            background: "none",
                            border: "none",
                            color: "#f87171",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        ></button>
                        <div
                          style={{
                            color: "#4f8ef7",
                            fontSize: 9,
                            letterSpacing: 1,
                            marginBottom: 8,
                          }}
                        >
                          TIER {idx + 1}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Label>Units/Day</Label>
                          <Input
                            type="number"
                            value={tier.daily}
                            min={1}
                            onChange={(e) =>
                              updateTeamTier(team, idx, "daily", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Monthly Bonus (GBP)</Label>
                          <Input
                            type="number"
                            value={tier.bonus}
                            min={0}
                            onChange={(e) =>
                              updateTeamTier(team, idx, "bonus", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {parsedPersons && (
        <>
          {parsedPersons.weekendExcluded > 0 && (
            <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 8 }}>
              {parsedPersons.weekendExcluded} weekend part
              {parsedPersons.weekendExcluded !== 1 ? "s" : ""} shown but
              excluded from bonus pace
            </div>
          )}
          {/* Team overview + filter */}
          {Object.keys(teamTotals).length > 1 && (
            <Card>
              <Label>Team Overview</Label>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                {Object.entries(teamTotals)
                  .sort((a, b) => b[1].commission - a[1].commission)
                  .map(([team, t]) => (
                    <div
                      key={team}
                      style={{
                        background: "#081224",
                        border: "1px solid #1e3056",
                        borderRadius: 10,
                        padding: "12px 16px",
                        flex: 1,
                        minWidth: 160,
                      }}
                    >
                      <div
                        style={{
                          color: "#f472b6",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 1,
                          marginBottom: 6,
                        }}
                      >
                        {team}
                      </div>
                      <div
                        style={{
                          color: "#f0f6ff",
                          fontSize: 18,
                          fontWeight: 800,
                        }}
                      >
                        {t.commission.toFixed(2)}
                      </div>
                      <div
                        style={{ color: "#3d5280", fontSize: 10, marginTop: 2 }}
                      >
                        {t.parts} parts {t.count} engineers
                      </div>
                    </div>
                  ))}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#3d5280", fontSize: 10 }}>Filter:</span>
                {["ALL", ...allTeams].map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setTeamFilter(t);
                      persist({ teamFilter: t });
                    }}
                    style={{
                      background: teamFilter === t ? "#f472b611" : "none",
                      border: `1px solid ${teamFilter === t ? "#f472b6" : "#1e3056"}`,
                      color: teamFilter === t ? "#f472b6" : "#3d5280",
                      borderRadius: 6,
                      padding: "4px 12px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontFamily: "inherit",
                      fontWeight: teamFilter === t ? 700 : 400,
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {/* Global days-left input */}
              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      color: "#4f6fa0",
                      fontSize: 11,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Working days left in{" "}
                    {formatMonthKey(activeMonth) || "month"}:
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={daysLeft}
                    onChange={(e) => {
                      setDaysLeft(e.target.value);
                      setDaysLeftManual(true);
                      setDaysLeftMonth(activeMonth);
                      persist({
                        daysLeft: e.target.value,
                        daysLeftManual: true,
                        daysLeftMonth: activeMonth,
                      });
                    }}
                    placeholder=""
                    style={{
                      width: 52,
                      textAlign: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      background: "#060e1e",
                      border: `1px solid ${daysLeft ? "#f472b6" : "#1e3056"}`,
                      borderRadius: 6,
                      color: "#c8d8f0",
                      padding: "4px 6px",
                      fontFamily: "inherit",
                    }}
                  />
                </div>
                {daysLeft && parseInt(daysLeft) > 0 && (
                  <span style={{ color: "#3d5280", fontSize: 10 }}>
                    Projections will appear on each engineer card below
                  </span>
                )}
              </div>
            </Card>
          )}

          {/* Engineer cards */}
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: Object.keys(teamTotals).length <= 1 ? 14 : 6,
              }}
            >
              <Label>
                Engineer Breakdown {visiblePersons.length} engineers
                {teamFilter !== "ALL" ? ` ${teamFilter}` : ""}
              </Label>
              <Btn
                variant="success"
                style={{ fontSize: 10, padding: "7px 16px" }}
                onClick={exportAllXLSX}
              >
                EXPORT ALL TO EXCEL
              </Btn>
            </div>
            {/* Show daysLeft input here when team overview card isn't visible */}
            {Object.keys(teamTotals).length <= 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 14,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: "#4f6fa0",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                  }}
                >
                  Working days left in{" "}
                  {formatMonthKey(activeMonth) || "month"}:
                </span>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={daysLeft}
                  onChange={(e) => {
                    setDaysLeft(e.target.value);
                    setDaysLeftManual(true);
                    setDaysLeftMonth(activeMonth);
                    persist({
                      daysLeft: e.target.value,
                      daysLeftManual: true,
                      daysLeftMonth: activeMonth,
                    });
                  }}
                  placeholder=""
                  style={{
                    width: 52,
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    background: "#060e1e",
                    border: `1px solid ${daysLeft ? "#f472b6" : "#1e3056"}`,
                    borderRadius: 6,
                    color: "#c8d8f0",
                    padding: "4px 6px",
                    fontFamily: "inherit",
                  }}
                />
                {daysLeft && parseInt(daysLeft) > 0 && (
                  <span style={{ color: "#3d5280", fontSize: 10 }}>
                    Projections shown on each card below
                  </span>
                )}
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 14,
              }}
            >
              {visiblePersons.map((person, i) => {
                const col = TECH_COLORS[i % TECH_COLORS.length];
                const stats = getPersonStats(person);
                const days = parseFloat(daysWorked[person.name] || "") || 0;
                const avgPerDay =
                  days > 0 ? stats.bonusEligibleParts / days : null;
                const avgCommPerDay =
                  days > 0 ? stats.totalCommission / days : null;
                const tiers = getRepairTeamTiers(getTeamForPerson(person));
                const isExpanded = expandedPerson === person.name;
                const deleted = deletedParts[person.name] || new Set();
                const deletedCount = deleted.size;

                return (
                  <div
                    key={person.name}
                    style={{
                      background: "#081224",
                      border: `1px solid ${col}33`,
                      borderRadius: 11,
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        background: `linear-gradient(135deg,${col}18 0%,transparent 80%)`,
                        padding: "16px 18px",
                        borderBottom: `1px solid ${col}22`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              color: col,
                              fontWeight: 800,
                              fontSize: 14,
                            }}
                          >
                            {person.name}
                          </div>
                          {person.importNames?.some(
                            (n) =>
                              normalizeEngineerName(n) !==
                              normalizeEngineerName(person.name),
                          ) && (
                            <div
                              style={{
                                color: "#2e4470",
                                fontSize: 9,
                                marginTop: 2,
                              }}
                            >
                              Matched from {person.importNames.join(", ")}
                            </div>
                          )}
                          {person.team && (
                            <div
                              style={{
                                color: "#3d5280",
                                fontSize: 10,
                                marginTop: 2,
                              }}
                            >
                              {person.team}
                            </div>
                          )}
                          <div
                            style={{
                              color: "#4f6fa0",
                              fontSize: 11,
                              marginTop: 4,
                            }}
                          >
                            {stats.totalParts} total parts
                            {deletedCount > 0 && (
                              <span style={{ color: "#64748b", marginLeft: 6 }}>
                                ({deletedCount} removed)
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              color: "#3d5280",
                              fontSize: 10,
                              marginTop: 2,
                            }}
                          >
                            {stats.nonBerParts} excluding BER
                          </div>
                          <div
                            style={{
                              color: "#3d5280",
                              fontSize: 10,
                              marginTop: 2,
                            }}
                          >
                            {stats.bonusEligibleParts} bonus eligible
                            {stats.weekendCount > 0 && (
                              <span style={{ color: "#f59e0b", marginLeft: 6 }}>
                                {stats.weekendCount} weekend
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              color: "#f0f6ff",
                              fontSize: 22,
                              fontWeight: 800,
                            }}
                          >
                            {stats.totalCommission.toFixed(2)}
                          </div>
                          <div style={{ color: "#3d5280", fontSize: 10 }}>
                            commission
                            {stats.weekendCount > 0 ? " (excl. weekend)" : ""}
                          </div>
                          {stats.weekendCount > 0 && (
                            <div
                              title="What the weekend parts would have earned. Weekend work is not eligible, so this is NOT included in the commission total."
                              style={{
                                color: "#f59e0b",
                                fontSize: 10,
                                marginTop: 2,
                              }}
                            >
                              +{stats.weekendCommission.toFixed(2)} weekend (not
                              paid)
                            </div>
                          )}
                          <div style={{ color: "#94a3b8", fontSize: 10 }}>
                            {stats.berCount} BER
                          </div>
                          {stats.ungraded > 0 && (
                            <div style={{ color: "#f59e0b", fontSize: 10 }}>
                              {stats.ungraded} ungraded
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Grade breakdown tiles */}
                      <div
                        style={{
                          display: "flex",
                          gap: 5,
                          marginTop: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        {GRADE_ORDER.filter(
                          (g) => g === "BER" || (stats.gradeCounts[g] || 0) > 0,
                        ).map((g) => (
                          <div
                            key={g}
                            style={{
                              textAlign: "center",
                              background: "#060e1e",
                              border: `1px solid ${GRADE_COLORS[g]}33`,
                              borderRadius: 7,
                              padding: "6px 10px",
                              minWidth: 42,
                            }}
                          >
                            <GradeTag grade={g} />
                            <div
                              style={{
                                color: "#f0f6ff",
                                fontSize: 14,
                                fontWeight: 800,
                                marginTop: 3,
                              }}
                            >
                              {stats.gradeCounts[g]}
                            </div>
                            <div style={{ color: "#3d5280", fontSize: 9 }}>
                              {(
                                (stats.gradeCounts[g] || 0) *
                                (gradeValues[g] || 0)
                              ).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Bonus tier bars (when days entered) */}
                      {days > 0 && avgPerDay !== null && (
                        <div
                          style={{
                            marginTop: 10,
                            display: "flex",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          {[...tiers]
                            .sort((a, b) => a.daily - b.daily)
                            .map((tier) => {
                              const hit = avgPerDay >= tier.daily;
                              const pct = Math.min(
                                100,
                                (avgPerDay / tier.daily) * 100,
                              );
                              const c = hit
                                ? "#10b981"
                                : avgPerDay >= tier.daily * 0.8
                                  ? "#f59e0b"
                                  : "#3d5280";
                              return (
                                <div
                                  key={tier.bonus}
                                  style={{
                                    flex: 1,
                                    minWidth: 90,
                                    background: "#060e1e",
                                    border: `1px solid ${c}33`,
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 4,
                                    }}
                                  >
                                    <span style={{ color: c, fontSize: 9 }}>
                                      Bonus {tier.bonus}
                                    </span>
                                    <span
                                      style={{ color: "#3d5280", fontSize: 9 }}
                                    >
                                      {tier.daily}/day
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      background: "#1a2744",
                                      borderRadius: 2,
                                      height: 3,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${pct}%`,
                                        height: "100%",
                                        background: c,
                                        borderRadius: 2,
                                        transition: "width 0.3s",
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    {/* Days worked + pace + actions */}
                    <div
                      style={{
                        padding: "10px 18px",
                        borderBottom: `1px solid ${col}11`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              color: "#3d5280",
                              fontSize: 10,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Days worked:
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={31}
                            step={0.5}
                            value={daysWorked[person.name] || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              const nextManual = new Set(manualDays);
                              nextManual.add(person.name);
                              const nextDays = {
                                ...daysWorked,
                                [person.name]: v,
                              };
                              setManualDays(nextManual);
                              setDaysWorked(nextDays);
                              persist({
                                daysWorked: nextDays,
                                manualDays: [...nextManual],
                              });
                            }}
                            placeholder=""
                            style={{
                              width: 60,
                              textAlign: "center",
                              fontSize: 13,
                              fontWeight: 700,
                              background: "#060e1e",
                              border: "1px solid #1e3056",
                              borderRadius: 6,
                              color: "#c8d8f0",
                              padding: "3px 4px",
                              fontFamily: "inherit",
                            }}
                          />
                          {rotaFilledNames.has(person.name) &&
                            daysWorked[person.name] &&
                            !manualDays.has(person.name) && (
                              <span
                                style={{
                                  color: "#a855f7",
                                  fontSize: 9,
                                  marginLeft: 4,
                                }}
                              >
                                from rota
                              </span>
                            )}
                          {daysWorkedWithToday[person.name] !== undefined &&
                            String(daysWorkedWithToday[person.name]) !==
                              String(daysWorked[person.name] || "") && (
                              <span
                                style={{
                                  color: "#4f6fa0",
                                  fontSize: 9,
                                  marginLeft: 4,
                                  whiteSpace: "nowrap",
                                }}
                                title="Days worked counts up to yesterday, which is what commission pace is based on. This shows what it would be if today were included too."
                              >
                                ({daysWorkedWithToday[person.name]} incl.
                                today)
                              </span>
                            )}
                        </div>
                        {avgPerDay !== null && (
                          <>
                            <div
                              style={{
                                background: "#060e1e",
                                border: "1px solid #1e3056",
                                borderRadius: 6,
                                padding: "4px 10px",
                                textAlign: "center",
                              }}
                            >
                              <div style={{ color: "#4f8ef7", fontSize: 9 }}>
                                AVG/DAY
                              </div>
                              <div
                                style={{
                                  color: "#f0f6ff",
                                  fontSize: 14,
                                  fontWeight: 800,
                                }}
                              >
                                {avgPerDay.toFixed(1)}
                              </div>
                              <div style={{ color: "#3d5280", fontSize: 8 }}>
                                bonus eligible
                              </div>
                            </div>
                            <div
                              style={{
                                background: "#060e1e",
                                border: "1px solid #1e3056",
                                borderRadius: 6,
                                padding: "4px 10px",
                                textAlign: "center",
                              }}
                            >
                              <div style={{ color: "#10b981", fontSize: 9 }}>
                                COMM/DAY
                              </div>
                              <div
                                style={{
                                  color: "#f0f6ff",
                                  fontSize: 14,
                                  fontWeight: 800,
                                }}
                              >
                                {avgCommPerDay.toFixed(2)}
                              </div>
                            </div>
                          </>
                        )}
                        <button
                          onClick={() => exportPersonXLSX(person, stats)}
                          style={{
                            marginLeft: "auto",
                            background: "#0a2010",
                            border: "1px solid #14532d",
                            color: "#4ade80",
                            borderRadius: 6,
                            fontSize: 9,
                            padding: "4px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Excel
                        </button>
                      </div>

                      {/* Month-end projection shown when both days worked and days left are filled */}
                      {avgPerDay !== null &&
                        daysLeft &&
                        parseInt(daysLeft) > 0 &&
                        (() => {
                          const remaining = parseInt(daysLeft);
                          const projectedExtraParts = Math.round(
                            avgPerDay * remaining,
                          );
                          const projectedExtraComm = avgCommPerDay * remaining;
                          const projectedTotalParts =
                            stats.bonusEligibleParts + projectedExtraParts;
                          const projectedTotalComm =
                            stats.totalCommission + projectedExtraComm;
                          const totalDaysWorked = days + remaining;
                          const projectedAvgPerDay =
                            totalDaysWorked > 0
                              ? projectedTotalParts / totalDaysWorked
                              : 0;
                          const tiers = getRepairTeamTiers(
                            getTeamForPerson(person),
                          );
                          const projectedTier =
                            [...tiers]
                              .sort((a, b) => b.daily - a.daily)
                              .find((t) => projectedAvgPerDay >= t.daily) ||
                            null;
                          const projectedTotalEarnings =
                            projectedTotalComm + (projectedTier?.bonus || 0);
                          const currentTier =
                            [...tiers]
                              .sort((a, b) => b.daily - a.daily)
                              .find((t) => avgPerDay >= t.daily) || null;
                          const tierChanged =
                            projectedTier?.bonus !== currentTier?.bonus;

                          return (
                            <div
                              style={{
                                marginTop: 12,
                                background: "#081224",
                                border: "1px solid #f472b633",
                                borderRadius: 8,
                                padding: "12px 14px",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f472b6",
                                  fontSize: 9,
                                  letterSpacing: 1,
                                  marginBottom: 10,
                                }}
                              >
                                MONTH-END PROJECTION {remaining} DAYS REMAINING
                                AT CURRENT PACE
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    background: "#060e1e",
                                    border: "1px solid #1e3056",
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{ color: "#4f8ef7", fontSize: 9 }}
                                  >
                                    EXTRA PARTS
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    +{projectedExtraParts}
                                  </div>
                                  <div
                                    style={{ color: "#3d5280", fontSize: 8 }}
                                  >
                                    {projectedTotalParts} total
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: "#060e1e",
                                    border: "1px solid #1e3056",
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{ color: "#a78bfa", fontSize: 9 }}
                                  >
                                    EXTRA COMM
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    +{projectedExtraComm.toFixed(2)}
                                  </div>
                                  <div
                                    style={{ color: "#3d5280", fontSize: 8 }}
                                  >
                                    {projectedTotalComm.toFixed(2)} total
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: projectedTier
                                      ? "#0a2010"
                                      : "#1a0a00",
                                    border: `1px solid ${projectedTier ? "#14532d" : "#78350f"}`,
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      color: projectedTier
                                        ? "#10b981"
                                        : "#f59e0b",
                                      fontSize: 9,
                                    }}
                                  >
                                    PROJECTED TOTAL
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {projectedTotalEarnings.toFixed(2)}
                                  </div>
                                  <div
                                    style={{
                                      color: projectedTier
                                        ? "#10b981"
                                        : "#3d5280",
                                      fontSize: 8,
                                    }}
                                  >
                                    {projectedTier
                                      ? `+GBP ${projectedTier.bonus} bonus`
                                      : "no bonus tier"}
                                  </div>
                                </div>
                              </div>
                              {/* Tier projection bars */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  flexWrap: "wrap",
                                }}
                              >
                                {[...tiers]
                                  .sort((a, b) => a.daily - b.daily)
                                  .map((tier) => {
                                    const willHit =
                                      projectedAvgPerDay >= tier.daily;
                                    const currentlyHitting =
                                      avgPerDay >= tier.daily;
                                    const pct = Math.min(
                                      100,
                                      (projectedAvgPerDay / tier.daily) * 100,
                                    );
                                    const c = willHit
                                      ? "#10b981"
                                      : projectedAvgPerDay >= tier.daily * 0.8
                                        ? "#f59e0b"
                                        : "#3d5280";
                                    const extraNeeded = willHit
                                      ? 0
                                      : Math.ceil(
                                          tier.daily * totalDaysWorked -
                                            projectedTotalParts,
                                        );
                                    return (
                                      <div
                                        key={tier.bonus}
                                        style={{
                                          flex: 1,
                                          minWidth: 90,
                                          background: "#060e1e",
                                          border: `1px solid ${c}44`,
                                          borderRadius: 6,
                                          padding: "6px 10px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: 4,
                                          }}
                                        >
                                          <span
                                            style={{ color: c, fontSize: 9 }}
                                          >
                                            Bonus {tier.bonus}
                                            {!currentlyHitting && willHit && (
                                              <span
                                                style={{
                                                  color: "#10b981",
                                                  fontSize: 8,
                                                }}
                                              >
                                                {" "}
                                                NEW
                                              </span>
                                            )}
                                          </span>
                                          <span
                                            style={{
                                              color: "#3d5280",
                                              fontSize: 9,
                                            }}
                                          >
                                            {tier.daily}/day
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            background: "#1a2744",
                                            borderRadius: 2,
                                            height: 3,
                                            marginBottom: 4,
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: `${pct}%`,
                                              height: "100%",
                                              background: c,
                                              borderRadius: 2,
                                              transition: "width 0.3s",
                                            }}
                                          />
                                        </div>
                                        {!willHit && extraNeeded > 0 && (
                                          <div
                                            style={{
                                              color: "#3d5280",
                                              fontSize: 8,
                                            }}
                                          >
                                            {extraNeeded} more needed
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                              {tierChanged && projectedTier && !currentTier && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    color: "#10b981",
                                    fontSize: 10,
                                  }}
                                >
                                  At this pace you will reach the GBP{" "}
                                  {projectedTier.bonus} bonus tier by month end!
                                </div>
                              )}
                              {tierChanged &&
                                projectedTier &&
                                currentTier &&
                                projectedTier.bonus > currentTier.bonus && (
                                  <div
                                    style={{
                                      marginTop: 8,
                                      color: "#10b981",
                                      fontSize: 10,
                                    }}
                                  >
                                    At this pace you will reach the GBP{" "}
                                    {projectedTier.bonus} bonus tier up from GBP{" "}
                                    {currentTier.bonus}!
                                  </div>
                                )}
                            </div>
                          );
                        })()}
                      {avgPerDay !== null &&
                        daysLeft !== "" &&
                        parseInt(daysLeft) === 0 &&
                        (() => {
                          const tiers = getRepairTeamTiers(
                            getTeamForPerson(person),
                          );
                          const achievedTier =
                            [...tiers]
                              .sort((a, b) => b.daily - a.daily)
                              .find((t) => avgPerDay >= t.daily) || null;
                          const finalTotal =
                            stats.totalCommission + (achievedTier?.bonus || 0);
                          return (
                            <div
                              style={{
                                marginTop: 12,
                                background: "#081224",
                                border: "1px solid #10b98144",
                                borderRadius: 8,
                                padding: "12px 14px",
                              }}
                            >
                              <div
                                style={{
                                  color: "#10b981",
                                  fontSize: 9,
                                  letterSpacing: 1,
                                  marginBottom: 10,
                                }}
                              >
                                MONTH FINAL TOTAL - NO WORKING DAYS LEFT
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <div
                                  style={{
                                    background: "#060e1e",
                                    border: "1px solid #1e3056",
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{ color: "#a78bfa", fontSize: 9 }}
                                  >
                                    COMMISSION
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {stats.totalCommission.toFixed(2)}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: achievedTier
                                      ? "#0a2010"
                                      : "#1a0a00",
                                    border: `1px solid ${achievedTier ? "#14532d" : "#78350f"}`,
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      color: achievedTier
                                        ? "#10b981"
                                        : "#f59e0b",
                                      fontSize: 9,
                                    }}
                                  >
                                    ACHIEVED BONUS
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {(achievedTier?.bonus || 0).toFixed(2)}
                                  </div>
                                  <div
                                    style={{
                                      color: achievedTier
                                        ? "#10b981"
                                        : "#3d5280",
                                      fontSize: 8,
                                    }}
                                  >
                                    {achievedTier
                                      ? `${achievedTier.daily}/day tier`
                                      : "no bonus tier"}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: "#061c14",
                                    border: "1px solid #10b98155",
                                    borderRadius: 6,
                                    padding: "6px 10px",
                                    textAlign: "center",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{ color: "#10b981", fontSize: 9 }}
                                  >
                                    FINAL TOTAL
                                  </div>
                                  <div
                                    style={{
                                      color: "#f0f6ff",
                                      fontSize: 16,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {finalTotal.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>

                    {/* Parts list */}
                    <div style={{ padding: "0 18px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            setExpandedPerson(isExpanded ? null : person.name)
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: "#3d5280",
                            cursor: "pointer",
                            fontSize: 11,
                            padding: "10px 0",
                            textAlign: "left",
                          }}
                        >
                          {isExpanded
                            ? "Hide parts list"
                            : `Show all ${person.parts.length} parts`}
                        </button>
                        {deletedCount > 0 && (
                          <button
                            onClick={() => restoreAll(person.name)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#f59e0b",
                              cursor: "pointer",
                              fontSize: 10,
                            }}
                          >
                            Restore all removed
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <div
                          style={{
                            maxHeight: 360,
                            overflowY: "auto",
                            paddingBottom: 12,
                          }}
                        >
                          {ungradedFirst(person.parts).map((p, j) => {
                            const isDeleted = deleted.has(p.idx);
                            const displayGrade = p.isBER
                              ? "BER"
                              : p.grade || "?";
                            const displayVal = p.isBER ? berValue : p.val || 0;
                            return (
                              <div
                                key={j}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "5px 0",
                                  borderBottom: "1px solid #0d1b35",
                                  opacity: isDeleted ? 0.4 : 1,
                                  background: isDeleted
                                    ? "#2a102011"
                                    : "transparent",
                                }}
                              >
                                {/* Delete / Restore toggle */}
                                {isDeleted ? (
                                  <button
                                    onClick={() =>
                                      restorePart(person.name, p.idx)
                                    }
                                    title="Restore this part"
                                    style={{
                                      background: "#0a2010",
                                      border: "1px solid #14532d",
                                      color: "#4ade80",
                                      borderRadius: 4,
                                      minWidth: 52,
                                      height: 22,
                                      cursor: "pointer",
                                      fontSize: 9,
                                      flexShrink: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    Restore
                                  </button>
                                ) : (
                                  <button
                                    onClick={() =>
                                      deletePart(person.name, p.idx)
                                    }
                                    title="Remove from commission part not counted towards earnings"
                                    style={{
                                      background: "#2a1020",
                                      border: "1px solid #3d1830",
                                      color: "#f87171",
                                      borderRadius: 4,
                                      minWidth: 52,
                                      height: 22,
                                      cursor: "pointer",
                                      fontSize: 9,
                                      flexShrink: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                                {p.isBER && (
                                  <span
                                    style={{
                                      color: "#94a3b8",
                                      fontSize: 9,
                                      flexShrink: 0,
                                    }}
                                  >
                                    BER
                                  </span>
                                )}
                                {p.isWeekend && (
                                  <span
                                    style={{
                                      color: "#f59e0b",
                                      fontSize: 9,
                                      flexShrink: 0,
                                    }}
                                  >
                                    Weekend
                                  </span>
                                )}
                                <span
                                  style={{
                                    color: isDeleted
                                      ? "#3d5280"
                                      : p.isBER
                                        ? "#94a3b8"
                                        : "#c8d8f0",
                                    fontFamily: "monospace",
                                    fontSize: 11,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textDecoration: isDeleted
                                      ? "line-through"
                                      : "none",
                                  }}
                                >
                                  {p.partId}
                                </span>
                                {p.closeDttm && (
                                  <span
                                    style={{
                                      color: isDeleted ? "#2a3a50" : "#4a6a8a",
                                      fontFamily: "monospace",
                                      fontSize: 10,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {p.closeDttm}
                                  </span>
                                )}
                                {p.priority && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontFamily: "monospace",
                                      flexShrink: 0,
                                      padding: "1px 4px",
                                      borderRadius: 3,
                                      border: `1px solid ${p.priority === "urgent" ? "#ef444466" : p.priority === "rep_req" ? "#f59e0b66" : "#4f8ef766"}`,
                                      color:
                                        p.priority === "urgent"
                                          ? "#ef4444"
                                          : p.priority === "rep_req"
                                            ? "#f59e0b"
                                            : "#4f8ef7",
                                    }}
                                  >
                                    {p.priority === "urgent"
                                      ? "Call"
                                      : p.priority === "rep_req"
                                        ? "Priority"
                                        : "Stock"}
                                  </span>
                                )}
                                <span style={{ flexShrink: 0 }}>
                                  <GradeTag grade={displayGrade} />
                                </span>
                                <span
                                  style={{
                                    color: isDeleted ? "#3d5280" : "#4f6fa0",
                                    fontFamily: "monospace",
                                    fontSize: 11,
                                    flexShrink: 0,
                                  }}
                                >
                                  {displayVal.toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Month-end projection summary */}
          {daysLeft &&
            parseInt(daysLeft) > 0 &&
            visiblePersons.some(
              (p) => parseFloat(daysWorked[p.name] || "") > 0,
            ) &&
            (() => {
              const remaining = parseInt(daysLeft);
              const rows = visiblePersons
                .map((person, idx) => {
                  const stats = getPersonStats(person);
                  const days = parseFloat(daysWorked[person.name] || "") || 0;
                  if (!days) return null;
                  const avgPD = stats.bonusEligibleParts / days;
                  const avgCD = stats.totalCommission / days;
                  const projParts =
                    stats.bonusEligibleParts + Math.round(avgPD * remaining);
                  const projComm = stats.totalCommission + avgCD * remaining;
                  const totalDays = days + remaining;
                  const projAvg = totalDays > 0 ? projParts / totalDays : 0;
                  const tiers = getRepairTeamTiers(getTeamForPerson(person));
                  const projTier =
                    [...tiers]
                      .sort((a, b) => b.daily - a.daily)
                      .find((t) => projAvg >= t.daily) || null;
                  return {
                    name: person.name,
                    col: TECH_COLORS[idx % TECH_COLORS.length],
                    projComm,
                    projTier,
                    projParts,
                    projAvg,
                  };
                })
                .filter(Boolean);
              if (!rows.length) return null;
              const totalProjComm = rows.reduce(
                (s, r) => s + r.projComm + (r.projTier?.bonus || 0),
                0,
              );
              return (
                <Card
                  style={{
                    background: "#0d1b35",
                    border: "1px solid #f472b633",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 14,
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <Label>
                      Month-End Projections {remaining} days remaining at
                      current pace
                    </Label>
                    <div
                      style={{
                        color: "#f0f6ff",
                        fontWeight: 800,
                        fontFamily: "monospace",
                        fontSize: 16,
                      }}
                    >
                      Team total: GBP {totalProjComm.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 11,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: "#3d5280",
                            fontSize: 10,
                            borderBottom: "1px solid #1a2744",
                          }}
                        >
                          <th
                            style={{ textAlign: "left", padding: "7px 10px" }}
                          >
                            ENGINEER
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            PROJ. PARTS
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            PROJ. AVG/DAY
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            PROJ. COMMISSION
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            BONUS TIER
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            PROJ. EARNINGS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.name}
                            style={{ borderTop: "1px solid #1a2744" }}
                          >
                            <td
                              style={{
                                padding: "8px 10px",
                                color: r.col,
                                fontWeight: 700,
                              }}
                            >
                              {r.name}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#7aa0d4",
                              }}
                            >
                              {r.projParts}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#f59e0b",
                              }}
                            >
                              {r.projAvg.toFixed(1)}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#a78bfa",
                                fontFamily: "monospace",
                              }}
                            >
                              {r.projComm.toFixed(2)}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: r.projTier ? "#10b981" : "#3d5280",
                              }}
                            >
                              {r.projTier ? `+GBP ${r.projTier.bonus}` : ""}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#f0f6ff",
                                fontWeight: 700,
                                fontFamily: "monospace",
                              }}
                            >
                              {(r.projComm + (r.projTier?.bonus || 0)).toFixed(
                                2,
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr
                          style={{
                            borderTop: "2px solid #1e3056",
                            background: "#081224",
                          }}
                        >
                          <td
                            style={{
                              padding: "8px 10px",
                              color: "#f472b6",
                              fontWeight: 700,
                            }}
                          >
                            TEAM TOTAL
                          </td>
                          <td
                            style={{
                              padding: "8px 10px",
                              textAlign: "right",
                              color: "#7aa0d4",
                              fontWeight: 700,
                            }}
                          >
                            {rows.reduce((s, r) => s + r.projParts, 0)}
                          </td>
                          <td></td>
                          <td
                            style={{
                              padding: "8px 10px",
                              textAlign: "right",
                              color: "#a78bfa",
                              fontWeight: 700,
                              fontFamily: "monospace",
                            }}
                          >
                            {rows
                              .reduce((s, r) => s + r.projComm, 0)
                              .toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: "8px 10px",
                              textAlign: "right",
                              color: "#10b981",
                              fontWeight: 700,
                              fontFamily: "monospace",
                            }}
                          >
                            GBP{" "}
                            {rows
                              .reduce((s, r) => s + (r.projTier?.bonus || 0), 0)
                              .toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: "8px 10px",
                              textAlign: "right",
                              color: "#f0f6ff",
                              fontWeight: 800,
                              fontFamily: "monospace",
                            }}
                          >
                            {totalProjComm.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    style={{ marginTop: 10, color: "#3d5280", fontSize: 10 }}
                  >
                    Assumes each engineer continues at their current daily pace
                    for {remaining} more working days. BER units excluded from
                    daily average.
                  </div>
                </Card>
              );
            })()}
          {daysLeft !== "" &&
            parseInt(daysLeft) === 0 &&
            visiblePersons.some(
              (p) => parseFloat(daysWorked[p.name] || "") > 0,
            ) &&
            (() => {
              const rows = visiblePersons
                .map((person, idx) => {
                  const stats = getPersonStats(person);
                  const days = parseFloat(daysWorked[person.name] || "") || 0;
                  if (!days) return null;
                  const avgPD = stats.bonusEligibleParts / days;
                  const tiers = getRepairTeamTiers(getTeamForPerson(person));
                  const achievedTier =
                    [...tiers]
                      .sort((a, b) => b.daily - a.daily)
                      .find((t) => avgPD >= t.daily) || null;
                  return {
                    name: person.name,
                    col: TECH_COLORS[idx % TECH_COLORS.length],
                    totalParts: stats.bonusEligibleParts,
                    avgPD,
                    commission: stats.totalCommission,
                    achievedTier,
                  };
                })
                .filter(Boolean);
              if (!rows.length) return null;
              const teamFinalTotal = rows.reduce(
                (s, r) => s + r.commission + (r.achievedTier?.bonus || 0),
                0,
              );
              return (
                <Card
                  style={{
                    background: "#0d1b35",
                    border: "1px solid #10b98144",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 14,
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <Label>Month Final Totals - No Working Days Left</Label>
                    <div
                      style={{
                        color: "#f0f6ff",
                        fontWeight: 800,
                        fontFamily: "monospace",
                        fontSize: 16,
                      }}
                    >
                      Team total: GBP {teamFinalTotal.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 11,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            color: "#3d5280",
                            fontSize: 10,
                            borderBottom: "1px solid #1a2744",
                          }}
                        >
                          <th
                            style={{ textAlign: "left", padding: "7px 10px" }}
                          >
                            ENGINEER
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            BONUS PARTS
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            AVG/DAY
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            COMMISSION
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            BONUS
                          </th>
                          <th
                            style={{ textAlign: "right", padding: "7px 10px" }}
                          >
                            FINAL TOTAL
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.name}
                            style={{ borderTop: "1px solid #1a2744" }}
                          >
                            <td
                              style={{
                                padding: "8px 10px",
                                color: r.col,
                                fontWeight: 700,
                              }}
                            >
                              {r.name}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#7aa0d4",
                              }}
                            >
                              {r.totalParts}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#f59e0b",
                              }}
                            >
                              {r.avgPD.toFixed(1)}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#a78bfa",
                                fontFamily: "monospace",
                              }}
                            >
                              {r.commission.toFixed(2)}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: r.achievedTier ? "#10b981" : "#3d5280",
                              }}
                            >
                              {r.achievedTier
                                ? `+GBP ${r.achievedTier.bonus}`
                                : ""}
                            </td>
                            <td
                              style={{
                                padding: "8px 10px",
                                textAlign: "right",
                                color: "#f0f6ff",
                                fontWeight: 700,
                                fontFamily: "monospace",
                              }}
                            >
                              {(
                                r.commission + (r.achievedTier?.bonus || 0)
                              ).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr
                          style={{
                            borderTop: "2px solid #1e3056",
                            background: "#081224",
                          }}
                        >
                          <td
                            style={{
                              padding: "8px 10px",
                              color: "#10b981",
                              fontWeight: 700,
                            }}
                          >
                            TEAM TOTAL
                          </td>
                          <td colSpan={4}></td>
                          <td
                            style={{
                              padding: "8px 10px",
                              textAlign: "right",
                              color: "#f0f6ff",
                              fontWeight: 800,
                              fontFamily: "monospace",
                            }}
                          >
                            {teamFinalTotal.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })()}

          {/* Summary table */}
          <Card>
            <Label>Commission Summary Table</Label>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#3d5280",
                      fontSize: 10,
                      borderBottom: "1px solid #1a2744",
                    }}
                  >
                    <th style={{ textAlign: "left", padding: "8px 10px" }}>
                      ENGINEER
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 8px" }}>
                      TEAM
                    </th>
                    {GRADE_ORDER.map((g) => (
                      <th
                        key={g}
                        style={{ textAlign: "center", padding: "8px 5px" }}
                      >
                        <GradeTag grade={g} />
                      </th>
                    ))}
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      PARTS
                    </th>
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      EX BER
                    </th>
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      BONUS
                    </th>
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      WKND
                    </th>
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      AVG/DAY
                    </th>
                    <th style={{ textAlign: "right", padding: "8px 10px" }}>
                      COMMISSION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePersons.map((person, i) => {
                    const col = TECH_COLORS[i % TECH_COLORS.length];
                    const stats = getPersonStats(person);
                    const days = parseFloat(daysWorked[person.name] || "") || 0;
                    const avg =
                      days > 0
                        ? (stats.bonusEligibleParts / days).toFixed(1)
                        : "-";
                    return (
                      <tr
                        key={person.name}
                        style={{ borderTop: "1px solid #1a2744" }}
                      >
                        <td
                          style={{
                            padding: "8px 10px",
                            color: col,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {person.name}
                        </td>
                        <td
                          style={{
                            padding: "8px 8px",
                            color: "#4f6fa0",
                            fontSize: 10,
                          }}
                        >
                          {person.team || ""}
                        </td>
                        {GRADE_ORDER.map((g) => (
                          <td
                            key={g}
                            style={{
                              padding: "8px 5px",
                              textAlign: "center",
                              color:
                                (stats.gradeCounts[g] || 0) > 0
                                  ? "#c8d8f0"
                                  : "#1e3056",
                            }}
                          >
                            {stats.gradeCounts[g] || ""}
                          </td>
                        ))}
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: "#7aa0d4",
                          }}
                        >
                          {stats.totalParts}
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: "#94a3b8",
                          }}
                        >
                          {stats.nonBerParts}
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: "#10b981",
                          }}
                        >
                          {stats.bonusEligibleParts}
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: stats.weekendCount ? "#f59e0b" : "#1e3056",
                          }}
                        >
                          {stats.weekendCount || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: "#f59e0b",
                          }}
                        >
                          {avg}
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            textAlign: "right",
                            color: "#10b981",
                            fontWeight: 700,
                            fontFamily: "monospace",
                          }}
                        >
                          {stats.totalCommission.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    style={{
                      borderTop: "2px solid #1e3056",
                      background: "#0d1b35",
                    }}
                  >
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#f472b6",
                        fontWeight: 700,
                      }}
                    >
                      TOTAL
                    </td>
                    <td></td>
                    {GRADE_ORDER.map((g) => {
                      const total = visiblePersons.reduce(
                        (s, p) => s + (getPersonStats(p).gradeCounts[g] || 0),
                        0,
                      );
                      return (
                        <td
                          key={g}
                          style={{
                            padding: "8px 5px",
                            textAlign: "center",
                            color: total ? "#7aa0d4" : "#1e3056",
                            fontWeight: 700,
                          }}
                        >
                          {total || ""}
                        </td>
                      );
                    })}
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        color: "#7aa0d4",
                        fontWeight: 700,
                      }}
                    >
                      {visiblePersons.reduce(
                        (s, p) => s + getPersonStats(p).totalParts,
                        0,
                      )}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        color: "#94a3b8",
                        fontWeight: 700,
                      }}
                    >
                      {visiblePersons.reduce(
                        (s, p) => s + getPersonStats(p).nonBerParts,
                        0,
                      )}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        color: "#10b981",
                        fontWeight: 700,
                      }}
                    >
                      {visiblePersons.reduce(
                        (s, p) => s + getPersonStats(p).bonusEligibleParts,
                        0,
                      )}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        color: "#f59e0b",
                        fontWeight: 700,
                      }}
                    >
                      {visiblePersons.reduce(
                        (s, p) => s + getPersonStats(p).weekendCount,
                        0,
                      ) || "-"}
                    </td>
                    <td></td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        color: "#10b981",
                        fontWeight: 800,
                        fontFamily: "monospace",
                      }}
                    >
                      {visiblePersons
                        .reduce(
                          (s, p) => s + getPersonStats(p).totalCommission,
                          0,
                        )
                        .toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
// Theme
const THEMES = {
  dark: {
    bg: "#02040a",
    card: "#04111f",
    cardBorder: "#143553",
    input: "#020817",
    inputBorder: "#1e4768",
    text: "#edfaff",
    textDim: "#516d91",
    textMid: "#8ecfff",
    header: "rgba(2,8,18,0.86)",
    headerBorder: "#5de7ff2e",
    accent: "#5de7ff",
    // Extended tokens
    cardHover: "#071629",
    success: "#20f6a6",
    warning: "#f59e0b",
    danger: "#ef4444",
    surface2: "#030b18",
    // Sci-fi tokens
    accentGlow: "#5de7ff40",
    neonCyan: "#5de7ff",
    neonPurple: "#20f6a6",
    gridLine: "#153554",
  },
  light: {
    bg: "#f0f5fb",
    card: "#ffffff",
    cardBorder: "#d4e0ef",
    input: "#f7fafd",
    inputBorder: "#c0d4e8",
    text: "#1a2b42",
    textDim: "#7a94b4",
    textMid: "#486080",
    header: "#1a3360",
    headerBorder: "#163060",
    accent: "#2563eb",
    // Extended tokens
    cardHover: "#f4f8fe",
    success: "#16a34a",
    warning: "#d97706",
    danger: "#dc2626",
    surface2: "#edf2f8",
  },
};

// Auto-save / Restore helpers
const SAVE_KEY = "razergrid-state-v11";

function serializeState(state) {
  try {
    const s = { ...state };
    if (s.clientPartIds) {
      const serialized = {};
      Object.entries(s.clientPartIds).forEach(([k, v]) => {
        serialized[k] = v instanceof Set ? { __set: [...v] } : v;
      });
      s.clientPartIds = serialized;
    }
    return JSON.stringify(s);
  } catch {
    return null;
  }
}

function deserializeState(json) {
  try {
    const s = JSON.parse(json);
    if (s.clientPartIds) {
      const deserialized = {};
      Object.entries(s.clientPartIds).forEach(([k, v]) => {
        deserialized[k] = v?.__set ? new Set(v.__set) : new Set();
      });
      // A save written before the lists carried a team tag cannot be trusted to
      // belong to the team now selected, so it is dropped rather than reused.
      s.clientPartIds = s.clientPartIdsTeam ? deserialized : {};
    }
    delete s.mcdPowerBIRows;
    delete s.mcdGradingLookup;
    delete s.mcdMergedParts;
    delete s.mcdCallList;
    delete s.mcdCallListFile;
    delete s.mcdAllowlist;
    return s;
  } catch {
    return null;
  }
}

const STEPS = ["SETUP", "LOAD DATA", "CALL LIST", "ALLOCATE"];

const DEFAULT_STATE = {
  techNames: ["Engineer 1", "Engineer 2", "Engineer 3"],
  workingDays: 22,
  doaCount: 0,
  tiers: TEAM_DEFAULT_TIERS["RC DCD"].map((t) => ({ ...t })),
  teamTargets: {},
  gradeValues: { ...DEFAULT_GRADE_VALUES },
  teamFilter: "RC DCD",
  customTeamFilter: "",
  powerBIRows: [],
  gradingLookup: {},
  mergedParts: [],
  callList: [],
  callListFile: null,
  ungradedPartIds: [],
  clients: [{ id: "mcd", name: "MCD", prefix: "MCD-", color: "#34d399" }],
  clientPartIds: {},
  // The team clientPartIds was fetched for. An untagged list is never used:
  // see partReferencesForTeam in clientUtils.js.
  clientPartIdsTeam: "",
  activeClient: null,
  engineerClients: {},
  engineerRotaNames: {},
  rcFile: null,
  engineerAliasesUpdatedAt: 0,
};

// Shared Day View
// Rendered when the URL contains #day=<payload> shows a single engineer's
// task list with fully functional tick-off checkboxes and running commission.
function SharedDayView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(new Set());

  useEffect(() => {
    try {
      const hash = window.location.hash;
      if (!hash.startsWith("#day=")) {
        setError("No task list data found in this link.");
        return;
      }
      const payload = hash.slice(5);
      const parsed = JSON.parse(decodeURIComponent(escape(atob(payload))));
      if (!parsed.__sharedDay) {
        setError("Invalid task list link.");
        return;
      }
      setData(parsed);
    } catch {
      setError("Could not read task list the link may be corrupted.");
    }
  }, []);

  const [rotaEntry, setRotaEntry] = useState(null);
  const [monthlyProgress, setMonthlyProgress] = useState(null);
  const [flaggedParts, setFlaggedParts] = useState({});
  const syncTimerRef = useRef(null);

  // These three run for anonymous visitors opening a #day= share link, so they
  // hit the SECURITY DEFINER functions granted to the `anon` role rather than
  // any table directly. `data.serverUrl` is still checked so links generated
  // before the migration behave exactly as they did, but it is no longer dialled.
  useEffect(() => {
    if (!data?.serverUrl || !data?.shareDate || !data?.engineerName) return;
    const { year, month } = data.shareDate;
    const rotaName = data.rotaEngineerName || data.engineerName;
    apiRoute(
      "GET",
      `/api/rota/public/${year}/${month}/${encodeURIComponent(rotaName)}`,
    )
      .then((entry) => {
        if (entry?.days) setRotaEntry(entry);
      })
      .catch(() => {});
  }, [data]);

  useEffect(() => {
    if (!data?.serverUrl || !data?.shareDate || !data?.engineerName) return;
    const { year, month } = data.shareDate;
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    const progressName = data.rotaEngineerName || data.engineerName;
    apiRoute(
      "GET",
      `/api/reports/engineer-progress-public/${monthKey}/${encodeURIComponent(progressName)}`,
    )
      .then((progress) => {
        if (progress && !progress.error) setMonthlyProgress(progress);
      })
      .catch(() => {});
  }, [data]);

  useEffect(() => {
    if (!data?.serverUrl || !data?.engineerName || !data?.shareDate) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const { year, month, day } = data.shareDate;
      const shareDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      apiRoute("POST", "/api/progress/sync", {
        shareDate,
        engineer: data.engineerName,
        dayNumber: data.dayNumber,
        completed: [...completed],
        parts: data.parts,
        gradeValues: data.gradeValues,
        tiers: data.tiers,
      }).catch(() => {});
    }, 500);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [completed, data]);

  if (error)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#060e1e",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        <div style={{ color: "#f87171", textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 24, marginBottom: 10 }}></div>
          <div>{error}</div>
        </div>
      </div>
    );

  if (!data)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#060e1e",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        <div style={{ color: "#4f8ef7" }}>Loading task list...</div>
      </div>
    );

  const gradeValues = data.gradeValues || DEFAULT_GRADE_VALUES;
  const berValue = gradeValues["BER"] || 0.1;
  const completedParts = data.parts.filter((p) => completed.has(p.serial));
  const earnedSoFar = completedParts.reduce(
    (s, p) => s + (p.isBER ? berValue : gradeValues[p.grade] || 0),
    0,
  );
  const nonBerCompleted = completedParts.filter(
    (p) => p.grade !== "BER",
  ).length;

  function togglePart(serial) {
    setCompleted((prev) => {
      const next = new Set(prev);
      next.has(serial) ? next.delete(serial) : next.add(serial);
      return next;
    });
  }

  const PRIO_SECTIONS = [
    { key: "urgent", label: " CALL LIST (URGENT)", color: "#ef4444" },
    { key: "rep_req", label: " REPAIR REQUIRED", color: "#f59e0b" },
    { key: "stock", label: " STOCK", color: "#4f8ef7" },
  ];

  function calcRotaSummary(entry, shareDate) {
    if (!entry) return null;
    const todayDayIdx = shareDate.day - 1;
    let worked = 0,
      hols = 0,
      sick = 0,
      rota = 0,
      other = 0,
      remaining = 0;
    entry.days.forEach((code, i) => {
      const isWeekend = entry.dow[i] === "S";
      const isPast = i <= todayDayIdx;
      const c = normalizeRotaCode(code);
      const delta = rotaWorkedDayDelta(code, entry.dow[i]);
      if (delta > 0) {
        if (isPast) worked += delta;
        else remaining += delta;
      }
      if (isPast) {
        if (c === "H" || c === "H/D") hols += c === "H/D" ? 0.5 : 1;
        if (c === "S" || c === "HD/S") sick += c === "HD/S" ? 0.5 : 1;
        if (c === "R") rota++;
        if (!isWeekend && (c === "O" || c === "OTHER")) other++;
      }
    });
    worked = Math.max(0, worked - other);
    return { worked, hols, sick, rota, other, remaining };
  }

  async function submitGradeFlag(part, flagState) {
    const suggestedGrade = String(flagState?.suggestedGrade || "")
      .trim()
      .toUpperCase();
    if (!suggestedGrade) {
      setFlaggedParts((prev) => ({
        ...prev,
        [part.serial]: {
          ...prev[part.serial],
          status: "form",
          error: "Choose a suggested grade first.",
        },
      }));
      return;
    }
    const { year, month, day } = data.shareDate;
    const shareDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setFlaggedParts((prev) => ({
      ...prev,
      [part.serial]: {
        ...prev[part.serial],
        status: "submitting",
        suggestedGrade,
        notes: flagState?.notes || "",
      },
    }));
    try {
      const result = await apiRoute("POST", "/api/progress/flag", {
        shareDate,
        engineer: data.rotaEngineerName || data.engineerName,
        displayEngineer: data.engineerName,
        partSerial: part.serial || part.partId,
        partId: part.partId,
        currentGrade: part.grade || "",
        suggestedGrade,
        notes: flagState?.notes || "",
      });
      if (!result || result?.ok === false) {
        throw new Error(result?.error || "Server rejected the flag.");
      }
      setFlaggedParts((prev) => ({
        ...prev,
        [part.serial]: { status: "submitted", id: result?.id },
      }));
    } catch (err) {
      setFlaggedParts((prev) => ({
        ...prev,
        [part.serial]: {
          ...prev[part.serial],
          status: "form",
          suggestedGrade,
          notes: flagState?.notes || "",
          error: err.message || "Could not submit the flag.",
        },
      }));
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#060e1e",
        fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
        color: "#c8d8f0",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;700&display=swap"
        rel="stylesheet"
      />
      <style>{`* { box-sizing: border-box; margin: 0; padding: 0; } ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #060e1e; } ::-webkit-scrollbar-thumb { background: #1a2744; border-radius: 3px; }`}</style>

      {/* Header */}
      <header
        style={{
          background: "#080f1f",
          borderBottom: "1px solid #1a2744",
          padding: "0 20px",
          height: 56,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <FaceGlyph size={34} />
        <div>
          <div style={{ color: "#f0f6ff", fontWeight: 700, fontSize: 12 }}>
            RazerGrid
          </div>
          <div style={{ color: "#3d5280", fontSize: 9, letterSpacing: 2 }}>
            ENGINEER TASK LIST {data.label}
          </div>
        </div>
        <div style={{ marginLeft: "auto", color: "#3d5280", fontSize: 10 }}>
          Day {data.dayNumber} {data.parts.length} parts assigned
        </div>
      </header>

      <main style={{ maxWidth: 700, margin: "0 auto", padding: "20px 16px" }}>
        {/* Engineer name + running commission */}
        <div
          style={{
            background: "#0d1b35",
            border: "1px solid #1e3056",
            borderRadius: 12,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div>
              <div style={{ color: "#4f8ef7", fontWeight: 800, fontSize: 20 }}>
                {data.engineerName}
              </div>
              <div style={{ color: "#3d5280", fontSize: 11, marginTop: 3 }}>
                {data.parts.length} parts {data.label}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  color: "#4f6fa0",
                  fontSize: 9,
                  letterSpacing: 1.5,
                  marginBottom: 3,
                }}
              >
                EARNED SO FAR
              </div>
              <div style={{ color: "#10b981", fontSize: 26, fontWeight: 800 }}>
                {earnedSoFar.toFixed(2)}
              </div>
              <div style={{ color: "#3d5280", fontSize: 10 }}>
                {completed.size} of {data.parts.length} ticked off
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              marginTop: 14,
              background: "#1a2744",
              borderRadius: 4,
              height: 6,
            }}
          >
            <div
              style={{
                width: `${data.parts.length > 0 ? (completed.size / data.parts.length) * 100 : 0}%`,
                height: "100%",
                background: "#10b981",
                borderRadius: 4,
                transition: "width 0.3s",
              }}
            />
          </div>
          <div style={{ color: "#3d5280", fontSize: 9, marginTop: 4 }}>
            {data.parts.length > 0
              ? Math.round((completed.size / data.parts.length) * 100)
              : 0}
            % complete
          </div>

          {/* Tier progress */}
          {data.tiers && (
            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {[...data.tiers]
                .sort((a, b) => a.daily - b.daily)
                .map((tier) => {
                  const hit = nonBerCompleted >= tier.daily;
                  const pct = Math.min(
                    100,
                    (nonBerCompleted / tier.daily) * 100,
                  );
                  const c = hit ? "#10b981" : "#3d5280";
                  return (
                    <div
                      key={tier.bonus}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        background: "#081224",
                        border: `1px solid ${c}33`,
                        borderRadius: 7,
                        padding: "8px 12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 5,
                        }}
                      >
                        <span style={{ color: c, fontSize: 9 }}>
                          {hit ? "" : ""} {tier.bonus} bonus
                        </span>
                        <span style={{ color: "#3d5280", fontSize: 9 }}>
                          {tier.daily}/day
                        </span>
                      </div>
                      <div
                        style={{
                          background: "#1a2744",
                          borderRadius: 2,
                          height: 3,
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: "100%",
                            background: c,
                            borderRadius: 2,
                            transition: "width 0.3s",
                          }}
                        />
                      </div>
                      {!hit && (
                        <div
                          style={{
                            color: "#3d5280",
                            fontSize: 9,
                            marginTop: 4,
                          }}
                        >
                          {tier.daily - nonBerCompleted} more non-BER parts
                          needed
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Saved monthly progress from Repair Centre calculator */}
        {monthlyProgress && (
          <div
            style={{
              background: "#0d1b35",
              border: "1px solid #1e3056",
              borderRadius: 12,
              padding: 20,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                color: "#4f6fa0",
                fontSize: 9,
                letterSpacing: 1.5,
                marginBottom: 12,
              }}
            >
              MONTHLY PACE - {String(monthlyProgress.month || "").toUpperCase()}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {[
                ["Total repairs", monthlyProgress.totalParts || 0, "#4f8ef7"],
                ["Bonus-counting", monthlyProgress.nonBerParts || 0, "#10b981"],
                ["BER units", monthlyProgress.berParts || 0, "#94a3b8"],
                ["Days worked", monthlyProgress.daysWorked || 0, "#a78bfa"],
                [
                  "Avg / day",
                  Number(monthlyProgress.avgPerDay || 0).toFixed(1),
                  "#f59e0b",
                ],
                [
                  "Month commission",
                  `${Number(monthlyProgress.totalCommission || 0).toFixed(2)}`,
                  "#22c55e",
                ],
              ].map(([label, value, color]) => (
                <div
                  key={label}
                  style={{
                    background: "#081224",
                    border: `1px solid ${color}33`,
                    borderRadius: 8,
                    padding: "9px 10px",
                  }}
                >
                  <div
                    style={{
                      color: "#3d5280",
                      fontSize: 8,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      color,
                      fontSize: 15,
                      fontWeight: 800,
                      marginTop: 2,
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ color: "#3d5280", fontSize: 9, marginTop: 10 }}>
              Pulled from the saved Repair Centre Earnings data. BER earns
              commission but is excluded from bonus pace.
            </div>
          </div>
        )}

        {/* Rota card */}
        {rotaEntry &&
          (() => {
            const summary = calcRotaSummary(rotaEntry, data.shareDate);
            const TILE_COLORS = {
              W: "#16a34a",
              H: "#2563eb",
              "H/D": "#60a5fa",
              S: "#d97706",
              "HD/S": "#fbbf24",
              R: "#7c3aed",
              T: "#0d9488",
              O: "#fb7185",
              OTHER: "#fb7185",
              BH: "#374151",
              OFF: "#1f2937",
              "-": "#1f2937",
            };
            return (
              <div
                style={{
                  background: "#0d1b35",
                  border: "1px solid #1e3056",
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    color: "#4f6fa0",
                    fontSize: 9,
                    letterSpacing: 1.5,
                    marginBottom: 10,
                  }}
                >
                  YOUR ROTA{" "}
                  {ROTA_MONTH_NAMES[data.shareDate.month - 1].toUpperCase()}{" "}
                  {data.shareDate.year}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px 16px",
                    marginBottom: 14,
                    fontSize: 11,
                  }}
                >
                  <span>
                    <span style={{ color: "#22c55e", fontWeight: 700 }}>
                      {summary.worked}
                    </span>{" "}
                    <span style={{ color: "#3d5280" }}>days worked</span>
                  </span>
                  {summary.hols > 0 && (
                    <span>
                      <span style={{ color: "#60a5fa", fontWeight: 700 }}>
                        {summary.hols}
                      </span>{" "}
                      <span style={{ color: "#3d5280" }}>holiday</span>
                    </span>
                  )}
                  {summary.sick > 0 && (
                    <span>
                      <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                        {summary.sick}
                      </span>{" "}
                      <span style={{ color: "#3d5280" }}>sick</span>
                    </span>
                  )}
                  {summary.rota > 0 && (
                    <span>
                      <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                        {summary.rota}
                      </span>{" "}
                      <span style={{ color: "#3d5280" }}>rota</span>
                    </span>
                  )}
                  {summary.other > 0 && (
                    <span>
                      <span style={{ color: "#fb7185", fontWeight: 700 }}>
                        -{summary.other}
                      </span>{" "}
                      <span style={{ color: "#3d5280" }}>other</span>
                    </span>
                  )}
                  <span>
                    <span style={{ color: "#94a3b8", fontWeight: 700 }}>
                      {summary.remaining}
                    </span>{" "}
                    <span style={{ color: "#3d5280" }}>days left</span>
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 3,
                    overflowX: "auto",
                    paddingBottom: 4,
                  }}
                >
                  {rotaEntry.days.map((code, i) => {
                    const dayNum = i + 1;
                    const isToday = dayNum === data.shareDate.day;
                    const isFuture = dayNum > data.shareDate.day;
                    const isWeekend = rotaEntry.dow[i] === "S";
                    const displayCode =
                      isWeekend || code === "OFF" || code === "BH" ? "" : code;
                    const bg =
                      TILE_COLORS[normalizeRotaCode(code)] || "#1f2937";
                    return (
                      <div
                        key={i}
                        style={{
                          flexShrink: 0,
                          width: 22,
                          textAlign: "center",
                          opacity: isFuture ? 0.4 : 1,
                          border: isToday
                            ? "1px solid #fff"
                            : "1px solid transparent",
                          borderRadius: 4,
                        }}
                      >
                        <div
                          style={{
                            background: bg,
                            borderRadius: 3,
                            padding: "3px 0",
                          }}
                        >
                          <div
                            style={{
                              color: "#fff",
                              fontSize: 7,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {dayNum}
                          </div>
                          <div
                            style={{
                              color: "rgba(255,255,255,0.8)",
                              fontSize: 6,
                              lineHeight: 1,
                              marginTop: 1,
                              minHeight: 7,
                            }}
                          >
                            {displayCode}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px 10px",
                    marginTop: 10,
                  }}
                >
                  {[
                    ["#16a34a", "W"],
                    ["#2563eb", "H"],
                    ["#60a5fa", "H/D"],
                    ["#d97706", "S"],
                    ["#7c3aed", "R"],
                    ["#0d9488", "T"],
                    ["#fb7185", "O"],
                  ].map(([c, l]) => (
                    <div
                      key={l}
                      style={{ display: "flex", alignItems: "center", gap: 3 }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          background: c,
                          borderRadius: 2,
                        }}
                      />
                      <span style={{ color: "#4f6fa0", fontSize: 8 }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        {/* Parts list */}
        <div
          style={{
            background: "#0d1b35",
            border: "1px solid #1e3056",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid #1a2744",
              color: "#4f6fa0",
              fontSize: 10,
              letterSpacing: 1,
            }}
          >
            TICK EACH PART AS YOU COMPLETE IT BER PARTS EARN COMMISSION BUT
            DON'T COUNT TOWARDS YOUR DAILY TARGET
          </div>
          {PRIO_SECTIONS.map((section) => {
            const sectionParts = ungradedFirst(
              data.parts.filter((p) => p.priority === section.key),
            );
            if (!sectionParts.length) return null;
            return (
              <div key={section.key}>
                <div
                  style={{
                    padding: "10px 18px 6px",
                    color: section.color,
                    fontSize: 10,
                    letterSpacing: 1,
                    background: "#081224",
                  }}
                >
                  {section.label}
                </div>
                {sectionParts.map((p, j) => {
                  const isDone = completed.has(p.serial);
                  const val = p.isBER ? berValue : gradeValues[p.grade] || 0;
                  const flagState = flaggedParts[p.serial];
                  const canFlag = !!data?.serverUrl;
                  return (
                    <div key={j} style={{ borderBottom: "1px solid #0d1b35" }}>
                      <div
                        onClick={() => togglePart(p.serial)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 18px",
                          cursor: "pointer",
                          background: isDone ? "#10b98108" : "transparent",
                          transition: "background 0.15s",
                        }}
                      >
                        {/* Big tap-friendly checkbox */}
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            border: `2px solid ${isDone ? "#10b981" : "#1e3056"}`,
                            background: isDone ? "#10b981" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "all 0.15s",
                          }}
                        >
                          {isDone && (
                            <span
                              style={{
                                color: "#fff",
                                fontSize: 14,
                                fontWeight: 900,
                              }}
                            ></span>
                          )}
                        </div>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 13,
                            flex: 1,
                            color: isDone
                              ? "#10b981"
                              : section.key === "urgent"
                                ? "#ef4444"
                                : section.key === "rep_req"
                                  ? "#c8d8f0"
                                  : "#7aa0d4",
                            textDecoration: isDone ? "line-through" : "none",
                            opacity: isDone ? 0.7 : 1,
                          }}
                        >
                          {p.partId}
                        </span>
                        {p.isBER && (
                          <span
                            style={{
                              color: "#94a3b8",
                              fontSize: 10,
                              flexShrink: 0,
                            }}
                          >
                            BER
                          </span>
                        )}
                        {p.source === "mcd" && (
                          <span
                            style={{
                              color: "#34d399",
                              fontSize: 10,
                              flexShrink: 0,
                            }}
                          >
                            MCD
                          </span>
                        )}
                        <span style={{ flexShrink: 0 }}>
                          <GradeTag grade={p.grade} />
                        </span>
                        <span
                          style={{
                            color: isDone ? "#10b981" : "#4f6fa0",
                            fontFamily: "monospace",
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {val.toFixed(2)}
                        </span>
                        {canFlag && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (flagState?.status === "submitted") return;
                              setFlaggedParts((prev) => ({
                                ...prev,
                                [p.serial]:
                                  flagState?.status === "form"
                                    ? undefined
                                    : {
                                        status: "form",
                                        suggestedGrade: GRADE_ORDER.includes(
                                          p.grade,
                                        )
                                          ? p.grade
                                          : "B",
                                        notes: "",
                                      },
                              }));
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              flexShrink: 0,
                              color:
                                flagState?.status === "submitted"
                                  ? "#22c55e"
                                  : "#4a6898",
                              fontSize: 14,
                              padding: "0 2px",
                              lineHeight: 1,
                            }}
                            title="Flag wrong grade"
                          >
                            {flagState?.status === "submitted" ? "" : ""}
                          </button>
                        )}
                      </div>
                      {(flagState?.status === "form" ||
                        flagState?.status === "submitting") && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: "#081224",
                            padding: "10px 18px 14px 58px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            alignItems: "flex-end",
                          }}
                        >
                          {flagState.error && (
                            <div
                              style={{
                                flexBasis: "100%",
                                color: "#f87171",
                                fontSize: 10,
                              }}
                            >
                              {flagState.error}
                            </div>
                          )}
                          <div>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontSize: 9,
                                marginBottom: 3,
                              }}
                            >
                              CURRENT GRADE
                            </div>
                            <div
                              style={{
                                color: "#c8d8f0",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {p.grade || ""}
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontSize: 9,
                                marginBottom: 3,
                              }}
                            >
                              SUGGEST
                            </div>
                            <select
                              value={flagState.suggestedGrade}
                              onChange={(e) =>
                                setFlaggedParts((prev) => ({
                                  ...prev,
                                  [p.serial]: {
                                    ...prev[p.serial],
                                    suggestedGrade: e.target.value,
                                  },
                                }))
                              }
                              style={{
                                background: "#0d1b35",
                                border: "1px solid #1e3056",
                                borderRadius: 5,
                                color: "#c8d8f0",
                                fontSize: 11,
                                padding: "3px 6px",
                              }}
                            >
                              {["M", "A", "B", "C", "S", "X", "BER"].map(
                                (g) => (
                                  <option key={g} value={g}>
                                    {g}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <div
                              style={{
                                color: "#4f6fa0",
                                fontSize: 9,
                                marginBottom: 3,
                              }}
                            >
                              NOTE (optional)
                            </div>
                            <input
                              value={flagState.notes}
                              onChange={(e) =>
                                setFlaggedParts((prev) => ({
                                  ...prev,
                                  [p.serial]: {
                                    ...prev[p.serial],
                                    notes: e.target.value,
                                  },
                                }))
                              }
                              placeholder="e.g. confirmed B grade in system"
                              style={{
                                background: "#0d1b35",
                                border: "1px solid #1e3056",
                                borderRadius: 5,
                                color: "#c8d8f0",
                                fontSize: 11,
                                padding: "3px 8px",
                                width: "100%",
                              }}
                            />
                          </div>
                          <button
                            onClick={() => submitGradeFlag(p, flagState)}
                            disabled={flagState.status === "submitting"}
                            style={{
                              background: "#1a3a6e",
                              border: "1px solid #2a5aae",
                              borderRadius: 6,
                              color: "#7ab4f7",
                              cursor: "pointer",
                              fontSize: 11,
                              padding: "4px 12px",
                            }}
                          >
                            {flagState.status === "submitting"
                              ? "Submitting..."
                              : "Submit"}
                          </button>
                          <button
                            onClick={() =>
                              setFlaggedParts((prev) => {
                                const n = { ...prev };
                                delete n[p.serial];
                                return n;
                              })
                            }
                            style={{
                              background: "none",
                              border: "1px solid #1e3056",
                              borderRadius: 6,
                              color: "#4a6898",
                              cursor: "pointer",
                              fontSize: 11,
                              padding: "4px 10px",
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {/* Bottom summary */}
          <div
            style={{
              padding: "14px 18px",
              background: "#081224",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <span style={{ color: "#3d5280", fontSize: 11 }}>
              {completed.size}/{data.parts.length} completed
            </span>
            <span
              style={{
                color: "#10b981",
                fontSize: 16,
                fontWeight: 800,
                fontFamily: "monospace",
              }}
            >
              {earnedSoFar.toFixed(2)} earned
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            color: "#3d5280",
            fontSize: 10,
            textAlign: "center",
          }}
        >
          Tap any part to mark it complete. Progress is saved on this device
          only.
        </div>
      </main>
    </div>
  );
}

// REPAIR CENTRE INVADERS
function RepairInvaders({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const keysRef = useRef({ left: false, right: false, space: false });
  const lastShotRef = useRef(0);

  const W = 600,
    H = 500;
  const COLS = 10,
    ROWS = 4;

  // Pixel art sprites (7-wide strings, drawn cell by cell)
  const INVADER_TYPES = [
    { type: "print", pts: 30, color: "#f59e0b", label: " PRINTER" },
    { type: "phone", pts: 20, color: "#f472b6", label: " MOBILE" },
    { type: "pcb", pts: 15, color: "#34d399", label: " PCB" },
    { type: "laptop", pts: 10, color: "#a78bfa", label: " LAPTOP" },
  ];

  function initGame() {
    const invaders = [];
    for (let r = 0; r < ROWS; r++) {
      const t = INVADER_TYPES[r] || INVADER_TYPES[3];
      for (let c = 0; c < COLS; c++) {
        invaders.push({
          x: 60 + c * 50,
          y: 55 + r * 44,
          alive: true,
          type: t,
          frame: 0,
        });
      }
    }
    return {
      player: { x: W / 2, y: H - 40, w: 44, h: 16 },
      bullets: [],
      bombs: [],
      invaders,
      barriers: [1, 2, 3, 4].map((i) => ({
        x: i * 120 - 30,
        y: H - 110,
        hp: 12,
      })),
      score: 0,
      lives: 3,
      level: 1,
      invDir: 1,
      invSpeed: 0.6,
      invTick: 0,
      invDrop: false,
      gameOver: false,
      win: false,
      paused: false,
      bombTick: 0,
      animTick: 0,
      explosions: [],
    };
  }

  useEffect(() => {
    stateRef.current = initGame();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function handleKey(e) {
      if (["ArrowLeft", "ArrowRight", " "].includes(e.key)) e.preventDefault();
      if (e.type === "keydown") {
        if (e.key === "ArrowLeft") keysRef.current.left = true;
        if (e.key === "ArrowRight") keysRef.current.right = true;
        if (e.key === " ") keysRef.current.space = true;
      }
      if (e.type === "keyup") {
        if (e.key === "ArrowLeft") keysRef.current.left = false;
        if (e.key === "ArrowRight") keysRef.current.right = false;
        if (e.key === " ") keysRef.current.space = false;
      }
    }
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKey);

    function drawPixelInvader(ctx, x, y, type, color, frame) {
      ctx.font = "13px monospace";
      ctx.textAlign = "center";
      ctx.fillStyle = color;
      // Draw differently each frame for animation feel
      const icons = {
        hdd: frame % 2 === 0 ? "" : "",
        laptop: frame % 2 === 0 ? "" : "",
        pcb: frame % 2 === 0 ? "" : "",
        phone: frame % 2 === 0 ? ")))" : "(((",
        print: frame % 2 === 0 ? "" : "",
      };
      ctx.shadowBlur = 8;
      ctx.shadowColor = color;
      ctx.fillText(icons[type.type] || "", x + 18, y + 14);
      ctx.shadowBlur = 0;
      // Legs
      ctx.fillStyle = color + "99";
      ctx.font = "9px monospace";
      ctx.fillText(frame % 2 === 0 ? "/ \\" : "\\ /", x + 18, y + 24);
    }

    function drawPlayer(ctx, p) {
      ctx.shadowBlur = 18;
      ctx.shadowColor = "#4f8ef7";
      // Cannon base
      ctx.fillStyle = "#4f8ef7";
      ctx.fillRect(p.x, p.y + 8, p.w, 8);
      ctx.fillRect(p.x + 16, p.y, 12, 10);
      ctx.fillStyle = "#7ab4f7";
      ctx.fillRect(p.x + 19, p.y - 4, 6, 6);
      ctx.shadowBlur = 0;
    }

    function drawBarrier(ctx, b) {
      if (b.hp <= 0) return;
      const alpha = b.hp / 12;
      ctx.fillStyle = `rgba(52, 211, 153, ${alpha * 0.8})`;
      ctx.shadowBlur = 6;
      ctx.shadowColor = "#34d399";
      ctx.fillRect(b.x, b.y, 44, 22);
      ctx.shadowBlur = 0;
    }

    function drawHUD(ctx, g) {
      ctx.font = "bold 13px 'IBM Plex Mono', monospace";
      ctx.fillStyle = "#4f8ef7";
      ctx.textAlign = "left";
      ctx.shadowBlur = 6;
      ctx.shadowColor = "#4f8ef7";
      ctx.fillText(`SCORE: ${g.score}`, 12, 22);
      ctx.fillStyle = "#f472b6";
      ctx.shadowColor = "#f472b6";
      ctx.textAlign = "right";
      ctx.fillText(`LEVEL ${g.level}`, W - 12, 22);
      ctx.fillStyle = "#34d399";
      ctx.shadowColor = "#34d399";
      ctx.textAlign = "center";
      ctx.fillText("".repeat(g.lives), W / 2, 22);
      ctx.shadowBlur = 0;
      // Top border line
      ctx.strokeStyle = "#1e3a6e";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, 30);
      ctx.lineTo(W, 30);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, H - 28);
      ctx.lineTo(W, H - 28);
      ctx.stroke();
      ctx.font = "9px monospace";
      ctx.fillStyle = "#1e3a6e";
      ctx.textAlign = "left";
      ctx.fillText(" MOVE SPACE FIRE ESC QUIT", 12, H - 10);
    }

    function drawExplosion(ctx, ex) {
      const age = ex.age / ex.maxAge;
      ctx.save();
      ctx.globalAlpha = 1 - age;
      ctx.shadowBlur = 20;
      ctx.shadowColor = ex.color;
      ctx.fillStyle = ex.color;
      ctx.font = `${12 + Math.round(12 * age)}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText("", ex.x, ex.y);
      ctx.restore();
    }

    function loop() {
      const g = stateRef.current;
      ctx.fillStyle = "#030a16";
      ctx.fillRect(0, 0, W, H);

      // Starfield
      ctx.fillStyle = "#ffffff";
      for (let i = 0; i < 60; i++) {
        const sx = (((i * 137 + g.animTick * 0.3) % W) + W) % W;
        const sy = ((i * 89) % (H - 40)) + 30;
        const size = i % 5 === 0 ? 1.5 : 0.8;
        ctx.globalAlpha = 0.3 + Math.sin(g.animTick * 0.05 + i) * 0.2;
        ctx.fillRect(sx, sy, size, size);
      }
      ctx.globalAlpha = 1;

      if (!g.gameOver && !g.win && !g.paused) {
        g.animTick++;

        // Move player
        const spd = 4;
        if (keysRef.current.left && g.player.x > 4) g.player.x -= spd;
        if (keysRef.current.right && g.player.x < W - g.player.w - 4)
          g.player.x += spd;

        // Shoot
        const now = Date.now();
        if (keysRef.current.space && now - lastShotRef.current > 280) {
          lastShotRef.current = now;
          g.bullets.push({
            x: g.player.x + g.player.w / 2 - 1,
            y: g.player.y - 4,
            w: 3,
            h: 10,
            spd: 7,
          });
        }

        // Move bullets
        g.bullets = g.bullets.filter((b) => {
          b.y -= b.spd;
          return b.y > -10;
        });

        // Move invaders
        g.invTick++;
        const alive = g.invaders.filter((i) => i.alive);
        const tickRate = Math.max(4, 18 - alive.length * 0.4);
        if (g.invTick >= tickRate) {
          g.invTick = 0;
          g.invDrop = false;
          const minX = Math.min(...alive.map((i) => i.x));
          const maxX = Math.max(...alive.map((i) => i.x));
          if ((g.invDir > 0 && maxX > W - 50) || (g.invDir < 0 && minX < 10)) {
            g.invDir *= -1;
            g.invDrop = true;
          }
          alive.forEach((inv) => {
            inv.x += g.invDir * (8 + g.level * 1.5);
            if (g.invDrop) inv.y += 18;
            inv.frame++;
          });
        }

        // Enemy bombs
        g.bombTick++;
        if (g.bombTick > Math.max(15, 40 - g.level * 5)) {
          g.bombTick = 0;
          if (alive.length > 0) {
            const shooter = alive[Math.floor(Math.random() * alive.length)];
            g.bombs.push({
              x: shooter.x + 16,
              y: shooter.y + 26,
              w: 3,
              h: 10,
              spd: 3 + g.level * 0.4,
            });
          }
        }
        g.bombs = g.bombs.filter((b) => {
          b.y += b.spd;
          return b.y < H;
        });

        // Bullet-invader collisions
        for (const bullet of g.bullets) {
          for (const inv of g.invaders) {
            if (!inv.alive) continue;
            if (
              bullet.x > inv.x &&
              bullet.x < inv.x + 36 &&
              bullet.y > inv.y &&
              bullet.y < inv.y + 28
            ) {
              inv.alive = false;
              bullet.y = -999;
              g.score += inv.type.pts;
              g.explosions.push({
                x: inv.x + 18,
                y: inv.y + 14,
                color: inv.type.color,
                age: 0,
                maxAge: 20,
              });
            }
          }
        }

        // Bomb-player collisions
        for (const bomb of g.bombs) {
          if (
            bomb.x > g.player.x &&
            bomb.x < g.player.x + g.player.w &&
            bomb.y > g.player.y &&
            bomb.y < g.player.y + 20
          ) {
            bomb.y = H + 99;
            g.lives--;
            g.explosions.push({
              x: g.player.x + 22,
              y: g.player.y,
              color: "#f87171",
              age: 0,
              maxAge: 30,
            });
            if (g.lives <= 0) g.gameOver = true;
          }
        }

        // Bomb-barrier collisions
        for (const bomb of g.bombs) {
          for (const bar of g.barriers) {
            if (
              bar.hp > 0 &&
              bomb.x > bar.x &&
              bomb.x < bar.x + 44 &&
              bomb.y > bar.y &&
              bomb.y < bar.y + 22
            ) {
              bomb.y = H + 99;
              bar.hp -= 3;
            }
          }
        }

        // Bullet-barrier collisions
        for (const bul of g.bullets) {
          for (const bar of g.barriers) {
            if (
              bar.hp > 0 &&
              bul.x > bar.x &&
              bul.x < bar.x + 44 &&
              bul.y > bar.y &&
              bul.y < bar.y + 22
            ) {
              bul.y = -999;
              bar.hp -= 2;
            }
          }
        }

        // Invaders reach bottom
        if (alive.some((i) => i.y > H - 60)) g.gameOver = true;

        // Win check
        if (alive.length === 0) {
          g.win = true;
          setTimeout(() => {
            stateRef.current = initGame();
            stateRef.current.score = g.score;
            stateRef.current.level = g.level + 1;
            stateRef.current.lives = g.lives;
            stateRef.current.win = false;
          }, 2200);
        }

        // Age explosions
        g.explosions = g.explosions.filter((e) => {
          e.age++;
          return e.age < e.maxAge;
        });
      }

      // Draw barriers
      g.barriers.forEach((b) => drawBarrier(ctx, b));

      // Draw invaders
      g.invaders.forEach((inv) => {
        if (!inv.alive) return;
        drawPixelInvader(
          ctx,
          inv.x,
          inv.y,
          inv.type,
          inv.type.color,
          inv.frame,
        );
      });

      // Draw player
      drawPlayer(ctx, g.player);

      // Draw bullets
      g.bullets.forEach((b) => {
        ctx.fillStyle = "#7ab4f7";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "#4f8ef7";
        ctx.fillRect(b.x, b.y, b.w, b.h);
        ctx.shadowBlur = 0;
      });

      // Draw bombs
      g.bombs.forEach((b) => {
        ctx.fillStyle = "#f87171";
        ctx.shadowBlur = 6;
        ctx.shadowColor = "#ef4444";
        ctx.fillRect(b.x, b.y, b.w, b.h);
        ctx.shadowBlur = 0;
      });

      // Draw explosions
      g.explosions.forEach((ex) => drawExplosion(ctx, ex));

      // HUD
      drawHUD(ctx, g);

      // Game over / win overlays
      if (g.gameOver || g.win) {
        ctx.fillStyle = "rgba(3,10,22,0.82)";
        ctx.fillRect(0, 0, W, H);
        ctx.textAlign = "center";
        if (g.gameOver) {
          ctx.font = "bold 32px monospace";
          ctx.fillStyle = "#f87171";
          ctx.shadowBlur = 20;
          ctx.shadowColor = "#ef4444";
          ctx.fillText("GAME OVER", W / 2, H / 2 - 20);
          ctx.font = "14px monospace";
          ctx.fillStyle = "#7aa0d4";
          ctx.shadowBlur = 0;
          ctx.fillText(`Score: ${g.score}`, W / 2, H / 2 + 18);
          ctx.font = "11px monospace";
          ctx.fillStyle = "#3d5280";
          ctx.fillText("Press SPACE to play again", W / 2, H / 2 + 50);
          if (keysRef.current.space) {
            stateRef.current = initGame();
            lastShotRef.current = 0;
          }
        }
        if (g.win) {
          ctx.font = "bold 28px monospace";
          ctx.fillStyle = "#4ade80";
          ctx.shadowBlur = 20;
          ctx.shadowColor = "#22c55e";
          ctx.fillText(`LEVEL ${g.level} CLEARED!`, W / 2, H / 2 - 16);
          ctx.font = "12px monospace";
          ctx.fillStyle = "#7aa0d4";
          ctx.shadowBlur = 0;
          ctx.fillText("Next wave loading...", W / 2, H / 2 + 20);
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    loop();
    canvasRef.current?.focus();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keyup", handleKey);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,10,22,0.92)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Title bar */}
      <div style={{ marginBottom: 14, textAlign: "center" }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            fontFamily: "monospace",
            letterSpacing: 3,
            color: "#4f8ef7",
            textShadow: "0 0 20px #4f8ef7",
          }}
        >
          REPAIR CENTRE INVADERS
        </div>
        <div
          style={{
            color: "#2a4070",
            fontSize: 10,
            letterSpacing: 2,
            marginTop: 4,
          }}
        >
          DEFEAT THE FAULTY EQUIPMENT PROTECT THE REPAIR CENTRE
        </div>
        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "center",
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          {[
            ["", "PRINTER", "#f59e0b", "30pts"],
            ["", "MOBILE", "#f472b6", "20pts"],
            ["", "PCB", "#34d399", "15pts"],
            ["", "LAPTOP", "#a78bfa", "10pts"],
          ].map(([icon, label, color, pts]) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 10,
                color,
              }}
            >
              <span>{icon}</span>
              <span style={{ color: "#3d5280" }}>{label}</span>
              <span style={{ color }}>{pts}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Canvas */}
      <div
        style={{
          position: "relative",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow:
            "0 0 60px #4f8ef733, 0 0 120px #4f8ef711, inset 0 0 40px rgba(0,0,0,0.5)",
          border: "1px solid #1e3a6e",
        }}
      >
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          tabIndex={0}
          style={{ display: "block", outline: "none" }}
        />
        {/* CRT vignette */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: 12,
            background:
              "radial-gradient(ellipse at center, transparent 60%, rgba(3,10,22,0.5) 100%)",
          }}
        />
      </div>

      {/* Mobile controls */}
      <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
        <button
          onPointerDown={() => (keysRef.current.left = true)}
          onPointerUp={() => (keysRef.current.left = false)}
          onPointerLeave={() => (keysRef.current.left = false)}
          style={{
            background: "#0a1628",
            border: "1px solid #1e3056",
            color: "#7aa0d4",
            borderRadius: 8,
            padding: "10px 22px",
            fontSize: 18,
            cursor: "pointer",
            userSelect: "none",
          }}
        ></button>
        <button
          onPointerDown={() => {
            keysRef.current.space = true;
            setTimeout(() => (keysRef.current.space = false), 150);
          }}
          style={{
            background: "linear-gradient(135deg,#1d4ed8,#4f8ef7)",
            border: "none",
            color: "#fff",
            borderRadius: 8,
            padding: "10px 28px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: 1,
            boxShadow: "0 0 16px #4f8ef755",
          }}
        >
          FIRE
        </button>
        <button
          onPointerDown={() => (keysRef.current.right = true)}
          onPointerUp={() => (keysRef.current.right = false)}
          onPointerLeave={() => (keysRef.current.right = false)}
          style={{
            background: "#0a1628",
            border: "1px solid #1e3056",
            color: "#7aa0d4",
            borderRadius: 8,
            padding: "10px 22px",
            fontSize: 18,
            cursor: "pointer",
            userSelect: "none",
          }}
        ></button>
        <button
          onClick={onClose}
          style={{
            background: "#2a1020",
            border: "1px solid #3d1830",
            color: "#f87171",
            borderRadius: 8,
            padding: "10px 18px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            marginLeft: 8,
            letterSpacing: 1,
          }}
        >
          EXIT
        </button>
      </div>
    </div>
  );
}

// RazerGrid data layer — Supabase.
// The Express server and its Cloudflare tunnel are gone. src/supabaseApi.js
// implements the same ~50 routes against Postgres; supabase/schema.sql holds
// the database, RLS policies and RPC functions.
const RAZERGRID_AUTH_KEY = "razergrid-auth-token";
// Embedded in generated share links. Nothing dials it any more — SharedDayView
// talks to Supabase directly — but the field is kept so that links generated
// before the migration keep the same payload shape and still pass the
// `canFlag` check below.
const SHARE_ORIGIN = "https://znwbcoivnvyzcsqpfuju.supabase.co";
// Working days remaining in an arbitrary month (month is 1-12). A month that has
// already finished has none left — this is what stops a July upload opened in
// August from inheriting August's remaining days and skewing the projection.
function calcWorkingDaysLeftForMonth(bankHolidays, year, month) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear || (year === currentYear && month < currentMonth))
    return 0;
  const isCurrentMonth = year === currentYear && month === currentMonth;
  const monthIdx = month - 1;
  const lastDay = new Date(year, month, 0).getDate();
  // The current month counts from tomorrow; a future month counts in full.
  const firstDay = isCurrentMonth ? now.getDate() + 1 : 1;
  const holidaySet = {};
  (bankHolidays || []).forEach((h) => {
    holidaySet[h.holiday_date] = true;
  });
  let count = 0;
  for (let d = firstDay; d <= lastDay; d++) {
    const date = new Date(year, monthIdx, d);
    const dow = date.getDay();
    // Build the key from local parts — toISOString() shifts the date in BST.
    const ds = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (dow !== 0 && dow !== 6 && !holidaySet[ds]) count++;
  }
  return count;
}

// Working days calculator uses bank holidays list. Kept as the current-month
// wrapper so useVistaServer's behaviour is unchanged.
function calcWorkingDaysLeft(bankHolidays) {
  const now = new Date();
  return calcWorkingDaysLeftForMonth(
    bankHolidays,
    now.getFullYear(),
    now.getMonth() + 1,
  );
}

function useVistaServer() {
  const [serverStatus, setServerStatus] = useState("unknown");
  const [authError, setAuthError] = useState(false);
  const [gradingLoaded, setGradingLoaded] = useState(false);
  const [gradingCount, setGradingCount] = useState(0);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [workingDaysLeft, setWorkingDaysLeft] = useState(null);

  // Probes the connection. There is no /api/health to hit any more, so a cheap
  // authenticated aggregate stands in for it: it proves the project is
  // reachable AND that RLS is letting this session read.
  async function testConnection() {
    try {
      const { data } = await supabase.auth.getSession();
      if (!data?.session) {
        setServerStatus("auth_error");
        setAuthError(true);
        return false;
      }
      const probe = await apiRoute("GET", "/api/grading/count");
      if (probe === null) {
        setServerStatus("offline");
        return false;
      }
      setServerStatus("online");
      setAuthError(false);
      return true;
    } catch {
      setServerStatus("offline");
      return false;
    }
  }

  async function loadServerData() {
    // Bank holidays
    try {
      const hols = await apiRoute("GET", "/api/bank-holidays/all");
      if (hols) {
        const activeHols = hols.filter((h) => h.active);
        setBankHolidays(activeHols);
        setWorkingDaysLeft(calcWorkingDaysLeft(activeHols));
      }
    } catch {}
    // Grading reference
    try {
      const data = await apiRoute("GET", "/api/grading/count");
      if (data) {
        setGradingCount(data.total);
        setGradingLoaded(data.total > 0);
      }
    } catch {}
  }

  function markGradingLoaded(count) {
    setGradingLoaded(true);
    if (count !== undefined) setGradingCount(count);
  }

  useEffect(() => {
    // Always calculate working days left using current date (even offline)
    setWorkingDaysLeft(calcWorkingDaysLeft([]));

    // There is nothing to discover any more — the project URL is fixed at
    // build time. Connecting is just: do we hold a valid session, and does it
    // get past RLS?
    let cancelled = false;

    async function connect() {
      setServerStatus("unknown");
      const ok = await testConnection();
      if (cancelled || !ok) return;
      loadServerData();
    }
    connect();

    // The old JWT was only checked against localStorage, so an expired token
    // simply meant a re-login. Supabase enforces the session server-side, and
    // an expired one would otherwise show up as an app full of empty tables.
    // Surface it as a disconnected state instead.
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === "SIGNED_OUT") {
        setServerStatus("auth_error");
        setAuthError(true);
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        connect();
      }
    });

    return () => {
      cancelled = true;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // Recalculate working days left whenever bank holidays load
  useEffect(() => {
    if (bankHolidays.length > 0)
      setWorkingDaysLeft(calcWorkingDaysLeft(bankHolidays));
  }, [bankHolidays]);

  // Removed with the Express server: updateApiUrl, saveCredentials,
  // setupTelegram, getClients, blockClient, createClient, getAuditLogs.
  // The API-key client registry and its audit log policed access to a shared
  // HTTP API. There is no such API now — Row Level Security enforces access at
  // the database, and the Supabase dashboard holds the request logs.

  async function changeAppPassword(currentPassword, newPassword) {
    return apiCall("POST", "/api/auth/change-password", {
      currentPassword,
      newPassword,
    });
  }

  async function getLastLogin() {
    return apiCall("GET", "/api/auth/last-login");
  }

  // The single choke point every backend call in this file goes through.
  // Previously: fetch() against the Express server. Now: a path router over
  // Supabase (src/supabaseApi.js) that returns the same JSON shapes, so none
  // of the ~40 wrappers below or their call sites needed to change.
  //
  // `timeout` is accepted and ignored — supabase-js manages its own request
  // lifecycle. Keeping the parameter means callers that pass 60000 for the big
  // history and grading pulls still compile unchanged.
  async function apiCall(method, path, body, timeout) {
    const result = await apiRoute(method, path, body);
    if (result === null) {
      // Distinguish "logged out" from "request failed": an expired session
      // should send the user back to the login screen, not look like an
      // empty database.
      try {
        const { data } = await supabase.auth.getSession();
        if (!data?.session) {
          setServerStatus("auth_error");
          setAuthError(true);
        }
      } catch {}
    }
    return result;
  }

  // Grading
  async function uploadGradingReference(records) {
    // Send in batches of 500
    const BATCH = 500;
    let totalInserted = 0,
      totalUpdated = 0;
    for (let i = 0; i < records.length; i += BATCH) {
      const batch = records.slice(i, i + BATCH);
      const r = await apiCall("POST", "/api/grading/upload", {
        records: batch,
      });
      if (r) {
        totalInserted += r.inserted || 0;
        totalUpdated += r.updated || 0;
      }
    }
    setGradingLoaded(true);
    setGradingCount(records.length);
    return {
      inserted: totalInserted,
      updated: totalUpdated,
      total: records.length,
    };
  }

  async function fetchGrading(team, excluded) {
    const params = new URLSearchParams();
    if (team && team !== "ALL") params.set("team", team);
    if (excluded !== undefined) params.set("excluded", String(excluded));
    const qs = params.toString();
    return apiCall(
      "GET",
      `/api/grading${qs ? "?" + qs : ""}`,
      undefined,
      60000,
    );
  }

  // The server used to build the workbook and stream it back. With no server,
  // the file is generated in the browser instead — same columns, same
  // filename, same UTF-8 BOM on the CSV so Excel still opens it correctly.
  async function downloadGradingExport(format) {
    return exportGrading(format === "xlsx" ? "xlsx" : "csv");
  }

  async function saveGradeOverride(partId, grade) {
    return apiCall("POST", "/api/grading/override", { part_id: partId, grade });
  }

  async function saveGradeOverrideWithTeams(partId, grade, teams) {
    return apiCall("POST", "/api/grading/override-with-teams", {
      part_id: partId,
      grade,
      teams,
    });
  }

  async function deleteGradingPart(partId) {
    return apiCall("DELETE", `/api/grading/${encodeURIComponent(partId)}`);
  }

  async function excludeGradingPart(partId) {
    return apiCall("POST", "/api/grading/exclude", { part_id: partId });
  }

  async function restoreGradingPart(partId) {
    return apiCall("POST", "/api/grading/restore", { part_id: partId });
  }

  // Stock Clients (MCD/KFC etc.)
  async function fetchStockClients() {
    return apiCall("GET", "/api/clients");
  }

  async function addOrUpdateStockClient(client) {
    return apiCall("POST", "/api/clients", client);
  }

  async function deleteStockClient(id) {
    return apiCall("DELETE", `/api/clients/${id}`);
  }

  // Part References
  async function fetchPartRefs(team, clientId) {
    return apiCall(
      "GET",
      `/api/part-references/${encodeURIComponent(team)}/${clientId}`,
    );
  }

  async function fetchAllPartRefsForTeam(team) {
    return apiCall("GET", `/api/part-references/${encodeURIComponent(team)}`);
  }

  async function uploadPartRefs(team, clientId, partIds) {
    return apiCall("POST", "/api/part-references/upload", {
      team,
      clientId,
      partIds,
    });
  }

  async function deletePartRefs(team, clientId) {
    return apiCall(
      "DELETE",
      `/api/part-references/${encodeURIComponent(team)}/${clientId}`,
    );
  }

  async function addPartRef(team, clientId, partId) {
    return apiCall("POST", "/api/part-references/add", {
      team, clientId, partId,
    });
  }

  async function removePartRef(team, clientId, partId) {
    return apiCall("POST", "/api/part-references/remove", {
      team, clientId, partId,
    });
  }

  // Every team's client allowlist at once — the Team Access view and the
  // cross-team History report both need all teams, not one at a time.
  async function fetchAllPartReferences() {
    return apiCall("GET", "/api/part-references");
  }

  // Bank Holidays
  async function fetchBankHolidays() {
    const r = await apiCall("GET", "/api/bank-holidays/all");
    if (r) setBankHolidays(r.filter((h) => h.active));
    return r;
  }

  async function toggleBankHoliday(id, active) {
    await apiCall("POST", "/api/bank-holidays/toggle", { id, active });
    fetchBankHolidays();
  }

  async function addBankHoliday(date, name) {
    await apiCall("POST", "/api/bank-holidays/add", { date, name });
    fetchBankHolidays();
  }

  async function deleteBankHoliday(id) {
    await apiCall("DELETE", `/api/bank-holidays/${id}`);
    fetchBankHolidays();
  }

  // Engineer workdays
  async function getEngineerWorkdays(engineer, month) {
    return apiCall(
      "GET",
      `/api/engineer-workdays/${encodeURIComponent(engineer)}/${month}`,
    );
  }

  // Rota
  async function fetchRotaMonths() {
    return apiCall("GET", "/api/rota/months");
  }
  async function fetchRotaForMonth(year, month) {
    return apiCall("GET", `/api/rota/${year}/${month}`);
  }
  async function uploadRota(year, month, engineers, dow) {
    return apiCall("POST", "/api/rota/upload", { year, month, engineers, dow });
  }
  async function deleteRotaMonth(year, month) {
    return apiCall("DELETE", `/api/rota/${year}/${month}`);
  }
  async function fetchEngineers() {
    return apiCall("GET", "/api/engineers");
  }
  async function saveEngineerAliases(engineer, aliases) {
    return apiCall(
      "PUT",
      `/api/engineers/${encodeURIComponent(engineer)}/aliases`,
      { aliases },
    );
  }
  async function addEngineer(engineer, section = "", source = "manual", note = "") {
    return apiCall("POST", "/api/engineers", { engineer, section, source, note });
  }
  async function removeEngineer(engineer) {
    return apiCall("DELETE", `/api/engineers/${encodeURIComponent(engineer)}`);
  }
  async function saveEngineerStatus(engineer, status, note = "") {
    return apiCall(
      "PUT",
      `/api/engineers/${encodeURIComponent(engineer)}/status`,
      { status, note },
    );
  }
  async function fetchAllocationBlocklist() {
    return apiCall("GET", "/api/allocation-blocklist");
  }
  async function addAllocationBlocklist(scope, engineer, partIds) {
    return apiCall("POST", "/api/allocation-blocklist", {
      scope,
      engineer,
      partIds,
    });
  }
  async function replaceAllocationBlocklist(global, engineers) {
    return apiCall("PUT", "/api/allocation-blocklist", { global, engineers });
  }
  async function deleteAllocationBlocklist(scope, engineer, partId) {
    const params = new URLSearchParams();
    params.set("scope", scope || "global");
    params.set("partId", partId);
    if (scope === "engineer" && engineer) params.set("engineer", engineer);
    return apiCall("DELETE", `/api/allocation-blocklist?${params.toString()}`);
  }
  async function fetchEngineerProgress(date) {
    return apiCall("GET", `/api/progress/${date}`);
  }
  async function deleteEngineerProgress(date, engineer) {
    return apiCall(
      "DELETE",
      `/api/progress/${date}/${encodeURIComponent(engineer)}`,
    );
  }
  async function fetchProgressFlags(status) {
    return apiCall(
      "GET",
      `/api/progress/flags${status ? `?status=${status}` : ""}`,
    );
  }
  async function updateProgressFlag(id, status, applyOverride) {
    return apiCall("PUT", `/api/progress/flags/${id}`, {
      status,
      applyOverride,
    });
  }
  // `serverUrlOverride` is now ignored — it came from share-link payloads that
  // embedded the tunnel URL. The parameter is kept so existing callers, and
  // links generated before the migration, keep working unchanged.
  async function fetchRotaForEngineerPublic(
    serverUrlOverride,
    year,
    month,
    engineer,
  ) {
    return apiRoute(
      "GET",
      `/api/rota/public/${year}/${month}/${encodeURIComponent(engineer)}`,
    );
  }

  // Summaries / Reports
  async function saveMonthlySummary(
    engineer,
    team,
    month,
    totalParts,
    totalCommission,
    bonusEarned,
    avgPerDay,
    daysWorked,
  ) {
    return apiCall("POST", "/api/summaries", {
      engineer,
      team,
      month,
      totalParts,
      totalCommission,
      bonusEarned,
      avgPerDay,
      daysWorked,
    });
  }

  async function saveRepairData(month, records) {
    return apiCall(
      "POST",
      "/api/repair-data",
      {
        uploadDate: new Date().toISOString().split("T")[0],
        month,
        records,
      },
      60000,
    );
  }

  async function getLeaderboard(month) {
    return apiCall("GET", `/api/leaderboard/${month}`);
  }

  async function getEngineerHistory(engineer) {
    return apiCall(
      "GET",
      `/api/reports/engineer-history/${encodeURIComponent(engineer)}`,
    );
  }

  async function getTeamOverview(month) {
    return apiCall("GET", `/api/reports/team-overview/${month}`);
  }

  async function fetchHistoryMonths() {
    return apiCall("GET", "/api/history/months");
  }

  async function fetchRepairCentreHistory(month, compareMonth, range) {
    const params = new URLSearchParams();
    if (range?.from && range?.to) {
      params.set("from", range.from);
      params.set("to", range.to);
    } else if (month) {
      params.set("month", month);
    }
    if (compareMonth) params.set("compare", compareMonth);
    const qs = params.toString();
    return apiCall(
      "GET",
      `/api/history/repair-centre${qs ? "?" + qs : ""}`,
      undefined,
      60000,
    );
  }

  async function fetchTeamTargets() {
    return apiCall("GET", "/api/team-targets");
  }

  async function saveTeamTargets(teamTargets) {
    return apiCall("PUT", "/api/team-targets", { teamTargets });
  }

  return {
    serverStatus,
    authError,
    gradingLoaded,
    gradingCount,
    markGradingLoaded,
    bankHolidays,
    workingDaysLeft,
    uploadGradingReference,
    fetchGrading,
    downloadGradingExport,
    saveGradeOverride,
    saveGradeOverrideWithTeams,
    deleteGradingPart,
    excludeGradingPart,
    restoreGradingPart,
    fetchStockClients,
    addOrUpdateStockClient,
    deleteStockClient,
    fetchPartRefs,
    fetchAllPartRefsForTeam,
    fetchAllPartReferences,
    addPartRef,
    removePartRef,
    uploadPartRefs,
    deletePartRefs,
    fetchBankHolidays,
    toggleBankHoliday,
    addBankHoliday,
    deleteBankHoliday,
    getEngineerWorkdays,
    fetchRotaMonths,
    fetchRotaForMonth,
    uploadRota,
    deleteRotaMonth,
    fetchEngineers,
    saveEngineerAliases,
    saveEngineerStatus,
    addEngineer,
    removeEngineer,
    fetchAllocationBlocklist,
    addAllocationBlocklist,
    replaceAllocationBlocklist,
    deleteAllocationBlocklist,
    fetchRotaForEngineerPublic,
    fetchEngineerProgress,
    deleteEngineerProgress,
    fetchProgressFlags,
    updateProgressFlag,
    saveMonthlySummary,
    saveRepairData,
    getLeaderboard,
    getEngineerHistory,
    getTeamOverview,
    fetchHistoryMonths,
    fetchRepairCentreHistory,
    fetchTeamTargets,
    saveTeamTargets,
    changeAppPassword,
    getLastLogin,
    apiCall,
  };
}

export const ServerContext = React.createContext(null);
function useServer() {
  return React.useContext(ServerContext);
}

// Particle Background
function ParticleBackground({ fxEnabled }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const isMobile = window.innerWidth < 640;
    const COUNT = isMobile ? 34 : 76;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r: Math.random() * 1.3 + 0.45,
      opacity: Math.random() * 0.46 + 0.18,
      hue: "93,231,255",
    }));

    function onMouseMove(e) {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    }
    window.addEventListener("mousemove", onMouseMove);

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const bg = ctx.createRadialGradient(
        canvas.width * 0.72,
        canvas.height * 0.12,
        0,
        canvas.width * 0.72,
        canvas.height * 0.12,
        Math.max(canvas.width, canvas.height) * 0.72,
      );
      bg.addColorStop(0, "rgba(93,231,255,0.09)");
      bg.addColorStop(0.3, "rgba(83,63,255,0.055)");
      bg.addColorStop(0.62, "rgba(93,231,255,0.018)");
      bg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      for (const p of particles) {
        // Mouse repel
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 140) {
          const force = ((140 - dist) / 140) * 0.4;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
        // Dampen velocity
        p.vx *= 0.97;
        p.vy *= 0.97;
        // Clamp speed
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > 1.2) {
          p.vx = (p.vx / speed) * 1.2;
          p.vy = (p.vy / speed) * 1.2;
        }

        p.x += p.vx;
        p.y += p.vy;
        // Wrap edges
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue},${p.opacity})`;
        ctx.shadowBlur = 12;
        ctx.shadowColor = `rgba(${p.hue},0.72)`;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Draw connections
      const LINK_DIST = isMobile ? 108 : 138;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < LINK_DIST) {
            const alpha = (1 - d / LINK_DIST) * 0.22;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(93,231,255,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      if (fxEnabled) rafRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [fxEnabled]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        opacity: fxEnabled ? 1 : 0,
        transition: "opacity 0.6s ease",
      }}
    />
  );
}

export default function App() {
  // Load SheetJS from CDN
  useEffect(() => {
    if (!window.XLSX) {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      document.head.appendChild(script);
    }
  }, []);

  const [step, setStep] = useState(0);
  const [tab, setTab] = useState("planner");
  const [themeKey, setThemeKey] = useState(() => {
    try {
      return localStorage.getItem("razergrid-theme") || "dark";
    } catch {
      return "dark";
    }
  });
  const theme = THEMES[themeKey] || THEMES.dark;
  const [fxEnabled, setFxEnabledState] = useState(() => {
    try {
      return localStorage.getItem("vista-fx") !== "off";
    } catch {
      return true;
    }
  });
  const [gameOpen, setGameOpen] = useState(false);
  const [bankHolidayOpen, setBankHolidayOpen] = useState(false);
  const [gradingOpen, setGradingOpen] = useState(false);
  const [rotaOpen, setRotaOpen] = useState(false);
  const [engineerManagerOpen, setEngineerManagerOpen] = useState(false);
  const [allocationBlocklistOpen, setAllocationBlocklistOpen] = useState(false);
  const [partsGradingOpen, setPartsGradingOpen] = useState(false);
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [teamTargetsOpen, setTeamTargetsOpen] = useState(false);
  const [authToken, setAuthToken] = useState(() => {
    try {
      const token = localStorage.getItem(RAZERGRID_AUTH_KEY);
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem(RAZERGRID_AUTH_KEY);
        return null;
      }
      return token;
    } catch {
      return null;
    }
  });
  const server = useVistaServer();

  // The Supabase session is the source of truth for "am I logged in", not the
  // token in localStorage.
  //
  // Without this, anyone who used the pre-Supabase app still has a
  // `razergrid-auth-token` on razergrid.com issued by the old Express server.
  // The check above only validates that token's `exp`, so a 7-day JWT from the
  // old backend sails through, the login screen is skipped, and the app renders
  // with no Supabase session behind it — every query then fails and the app
  // looks like it has no database. It is invisible on localhost, which is a
  // different origin with its own empty localStorage.
  //
  // So: if there is no Supabase session, there is no login, whatever
  // localStorage claims.
  useEffect(() => {
    let cancelled = false;

    function clearStaleLogin() {
      try {
        localStorage.removeItem(RAZERGRID_AUTH_KEY);
      } catch {}
      setAuthToken(null);
    }

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.session) clearStaleLogin();
      })
      .catch(() => {});

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;
        if (!session) clearStaleLogin();
      },
    );

    return () => {
      cancelled = true;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  function handleLogout() {
    localStorage.removeItem(RAZERGRID_AUTH_KEY);
    setAuthToken(null);
    // Also end the Supabase session, otherwise the next visitor inherits it.
    signOut();
  }

  function toggleFX() {
    const next = !fxEnabled;
    setFxEnabledState(next);
    setGlobalFX(next);
    try {
      localStorage.setItem("vista-fx", next ? "on" : "off");
    } catch {}
  }

  // Auto-save/restore state
  const [state, setStateRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) {
        const restored = deserializeState(saved);
        if (restored)
          return {
            ...DEFAULT_STATE,
            ...restored,
            teamTargets: sanitizeTeamTargets(restored.teamTargets),
          };
      }
    } catch {}
    return DEFAULT_STATE;
  });

  const setState = (patch) => setStateRaw((prev) => ({ ...prev, ...patch }));

  const saveTimer = useRef(null);
  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const serialised = serializeState(state);
      if (serialised) {
        try {
          localStorage.setItem(SAVE_KEY, serialised);
        } catch {}
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [state]);

  // Auto-fetch grading from server whenever it comes online
  useEffect(() => {
    if (!server || server.serverStatus !== "online") return;
    (async () => {
      try {
        const raw = await server.fetchGrading();
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.records)
            ? raw.records
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        if (!records.length) return;
        const lookup = {};
        records.forEach((r) => {
          const partId = (r.part_id || "").toUpperCase().trim();
          const grade = (r.grade || "").toUpperCase().trim();
          if (partId && DEFAULT_GRADE_VALUES[grade] !== undefined) {
            const teams = getEffectiveGradingTeams(r);
            lookup[partId] = { grade, team: formatGradingTeams(teams), teams };
          }
        });
        const matchedCount = Object.keys(lookup).length;
        if (matchedCount === 0) return;
        setState({ gradingLookup: lookup });
        server.markGradingLoaded(matchedCount);
      } catch {}
    })();
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!server || server.serverStatus !== "online") return;
    (async () => {
      try {
        const result = await server.fetchTeamTargets?.();
        const clean = sanitizeTeamTargets(result?.teamTargets);
        if (Object.keys(clean).length) {
          setState({ teamTargets: clean });
          return;
        }
        const local = sanitizeTeamTargets(state.teamTargets);
        if (Object.keys(local).length && server.saveTeamTargets) {
          await server.saveTeamTargets(local);
        }
      } catch {}
    })();
  }, [server?.serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTheme() {
    const next = themeKey === "dark" ? "light" : "dark";
    setThemeKey(next);
    try {
      localStorage.setItem("razergrid-theme", next);
    } catch {}
  }

  function reset() {
    setStateRaw(DEFAULT_STATE);
    setStep(0);
  }

  const T = theme;
  const isDark = themeKey === "dark";

  const cssVars = `
 @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;600;700&family=Share+Tech+Mono&family=Inter:wght@400;500;600;700;800&display=swap');

 :root {
 --bg: ${T.bg}; --card: ${T.card}; --card-border: ${T.cardBorder};
 --input: ${T.input}; --input-border: ${T.inputBorder};
 --text: ${T.text}; --text-dim: ${T.textDim}; --text-mid: ${T.textMid};
 --header: ${T.header}; --accent: ${T.accent};
 }
 * { box-sizing: border-box; margin: 0; padding: 0; }
 body {
 background: var(--bg);
 color: var(--text);
 font-family: 'Share Tech Mono', 'Fira Code', 'IBM Plex Mono', monospace;
 text-rendering: geometricPrecision;
 }

 /* Scrollbar */
 ::-webkit-scrollbar { width: 5px; height: 5px; }
 ::-webkit-scrollbar-track { background: transparent; }
 ::-webkit-scrollbar-thumb { background: #0f2a50; border-radius: 10px; }
 ::-webkit-scrollbar-thumb:hover { background: #38bdf833; }
 select option { background: var(--card); }

 /* Keyframes */
 @keyframes fadeSlideUp {
 from { opacity: 0; transform: translateY(18px); }
 to { opacity: 1; transform: translateY(0); }
 }
 @keyframes fadeIn {
 from { opacity: 0; } to { opacity: 1; }
 }
 @keyframes pulse {
 0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
 }
 @keyframes glow {
 0%, 100% { box-shadow: 0 0 8px #38bdf844; }
 50% { box-shadow: 0 0 22px #38bdf899; }
 }
 @keyframes neonPulse {
 0%, 100% { box-shadow: 0 0 6px #38bdf844, 0 0 20px #38bdf822, inset 0 0 8px #38bdf810; }
 50% { box-shadow: 0 0 16px #38bdf877, 0 0 44px #38bdf833, inset 0 0 14px #38bdf818; }
 }
 @keyframes cornerGlow {
 0%, 100% { opacity: 0.4; } 50% { opacity: 1; }
 }
 @keyframes shimmer {
 0% { background-position: -200% center; }
 100% { background-position: 200% center; }
 }
 @keyframes borderPulse {
 0%, 100% { border-color: #1e3056; }
 50% { border-color: #4f8ef766; }
 }
 @keyframes logoSpin {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
 }
 @keyframes liveDot {
 0%, 100% { box-shadow: 0 0 4px #4ade80, 0 0 8px #4ade8066; opacity:1; }
 50% { box-shadow: 0 0 10px #4ade80, 0 0 20px #4ade8099; opacity:0.7; }
 }
 @keyframes tabSlide {
 from { opacity: 0; transform: translateX(12px); }
 to { opacity: 1; transform: translateX(0); }
 }
 @keyframes numberCount {
 from { opacity: 0; transform: translateY(6px) scale(0.9); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 @keyframes cardEntrance {
 from { opacity: 0; transform: translateY(24px) scale(0.97); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 @keyframes historyRise {
 from { opacity: 0; transform: translateY(10px); filter: saturate(0.78); }
 to { opacity: 1; transform: translateY(0); filter: saturate(1); }
 }
 @keyframes progressFill {
 from { width: 0%; } to { width: var(--w, 100%); }
 }
 @keyframes checkPop {
 0% { transform: scale(0) rotate(-10deg); opacity: 0; }
 60% { transform: scale(1.3) rotate(3deg); opacity: 1; }
 100% { transform: scale(1) rotate(0deg); opacity: 1; }
 }
 @keyframes orbitSweep {
 0% { transform: translate3d(-10%, -10%, 0) rotate(0deg); opacity: 0.55; }
 50% { opacity: 0.9; }
 100% { transform: translate3d(10%, 12%, 0) rotate(360deg); opacity: 0.55; }
 }
 /* FX classes */
 ${
   fxEnabled
     ? `
 .fx-card-enter { animation: cardEntrance 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
 .fx-tab-enter { animation: tabSlide 0.3s cubic-bezier(0.22, 1, 0.36, 1) both; }
 .fx-fade-in { animation: fadeIn 0.3s ease both; }
 .fx-shimmer-text {
 background: linear-gradient(90deg, #7aa0d4 0%, #f0f6ff 30%, #38bdf8 50%, #00e5ff 60%, #38bdf8 70%, #7aa0d4 100%);
 background-size: 200% auto;
 -webkit-background-clip: text;
 -webkit-text-fill-color: transparent;
 background-clip: text;
 animation: shimmer 3s linear infinite;
 }
 .fx-live-dot { animation: liveDot 2s ease-in-out infinite; }
 .fx-glow-card { animation: glow 3s ease-in-out infinite; }
 .fx-neon-pulse { animation: neonPulse 3s ease-in-out infinite; }
 .fx-border-pulse { animation: borderPulse 2s ease-in-out infinite; }
 .fx-number { animation: numberCount 0.4s cubic-bezier(0.22,1,0.36,1) both; }
 .face-glyph-live { animation: faceAura 4.8s ease-in-out infinite; }
 .face-glyph-typing { animation: faceReact 0.42s cubic-bezier(0.22,1,0.36,1) both; }
 .face-glyph-click { animation: faceReact 0.62s cubic-bezier(0.22,1,0.36,1) both; }
 `
     : `
 .fx-card-enter, .fx-tab-enter, .fx-fade-in,
 .fx-shimmer-text, .fx-live-dot, .fx-glow-card,
 .fx-border-pulse, .fx-number { animation: none !important; }
 `
 }

 /* Header gradient line */
 .header-accent-line {
 height: 1px;
 background: linear-gradient(90deg, transparent, #0ea5e9, #5de7ff, #38bdf8, #5de7ff, #0ea5e9, transparent);
 ${fxEnabled ? "animation: shimmer 4s linear infinite;" : ""}
 background-size: 200% auto;
 box-shadow: 0 0 20px rgba(93,231,255,0.28);
 }

 /* Sci-fi grid overlay */
 .sci-fi-grid::before {
 content: '';
 position: fixed; inset: 0;
 background-image:
 linear-gradient(rgba(93,231,255,0.026) 1px, transparent 1px),
 linear-gradient(90deg, rgba(93,231,255,0.026) 1px, transparent 1px),
 radial-gradient(circle at 20% 20%, rgba(14,165,233,0.09), transparent 28%);
 background-size: 46px 46px, 46px 46px, 100% 100%;
 pointer-events: none; z-index: 1;
 }

 /* Glassmorphism card */
 .glass-card {
 background: linear-gradient(180deg, rgba(5,18,36,0.9), rgba(2,8,20,0.94)) !important;
 backdrop-filter: blur(18px) saturate(170%) !important;
 -webkit-backdrop-filter: blur(18px) saturate(170%) !important;
 border: 1px solid rgba(93,231,255,0.18) !important;
 box-shadow: 0 22px 70px rgba(0,0,0,0.42), inset 0 1px 0 rgba(125,231,255,0.08) !important;
 }

 /* Spotlight card (cursor-reactive border glow) */
 .spotlight-card { position: relative; }
 .spotlight-card::after {
 content: '';
 position: absolute; inset: -1px;
 border-radius: inherit;
 background: radial-gradient(circle at var(--mx,50%) var(--my,50%), #38bdf818, transparent 60%);
 pointer-events: none; opacity: 0;
 transition: opacity 0.3s ease;
 z-index: 0;
 }
 .spotlight-card:hover::after { opacity: 1; }

 /* HUD corner brackets */
 .hud-corners { position: relative; }
 .hud-corners::before, .hud-corners::after {
 content: '';
 position: absolute;
 width: 16px; height: 16px;
 ${fxEnabled ? "animation: cornerGlow 3s ease-in-out infinite;" : ""}
 }
 .hud-corners::before {
 top: -1px; left: -1px;
 border-top: 1px solid #5de7ff; border-left: 1px solid #5de7ff;
 }
 .hud-corners::after {
 bottom: -1px; right: -1px;
 border-bottom: 1px solid #5de7ff; border-right: 1px solid #5de7ff;
 }
 button, a, [role="button"] { cursor: pointer; }
 button {
 font-family: inherit;
 letter-spacing: 0.04em;
 }
 input, select, textarea {
 font-family: 'Fira Code', 'IBM Plex Mono', monospace;
 }
 @keyframes faceReact {
 0% { transform: translateY(0) scale(1); filter: saturate(1); }
 38% { transform: translateY(-1px) scale(1.08); filter: saturate(1.5) brightness(1.12); }
 100% { transform: translateY(0) scale(1); filter: saturate(1); }
 }
 @keyframes faceAura {
 0%, 100% { filter: drop-shadow(0 0 10px rgba(93,231,255,0.28)); }
 50% { filter: drop-shadow(0 0 18px rgba(93,231,255,0.46)); }
 }
 .face-glyph {
 background: transparent;
 box-shadow: none;
 filter: drop-shadow(0 0 14px rgba(93,231,255,0.34));
 transform-origin: center;
 transition: filter 0.2s ease;
 }
 .face-glyph svg {
 display: block;
 overflow: visible;
 }
 @media (max-width: 900px) {
 .face-lockup > div:last-child { display: none !important; }
 }

 /* Progress bars smooth fill */
 .progress-bar-fill {
 transition: ${fxEnabled ? "width 0.6s cubic-bezier(0.22,1,0.36,1)" : "none"};
 }

 /* Checkbox pop */
 .check-pop { animation: ${fxEnabled ? "checkPop 0.35s cubic-bezier(0.34,1.56,0.64,1) both" : "none"}; }

 /* Responsive nav visibility */
 /* Mobile-first: show only hamburger, hide desktop tabs + icon buttons */
 .nav-tabs-desktop { display: none !important; }
 .nav-icon-desktop { display: none !important; }
 /* Mobile menu settings section shows on all sizes inside the menu */
 .nav-icon-mobile-only { display: flex !important; }

 @media (min-width: 640px) {
 .nav-tabs-desktop { display: flex !important; }
 .nav-icon-desktop { display: flex !important; }
 /* On desktop, the hamburger menu settings section still shows useful for server config */
 }

 @media print { header, nav { display: none !important; } body { background: white; color: black; } }

 /* Mobile / Responsive */
 @media (max-width: 639px) {
 /* Prevent text zoom on inputs */
 input, select, textarea { font-size: 16px !important; }

 /* Full-width cards with tighter padding */
 .card-mobile { padding: 16px !important; border-radius: 12px !important; }

 /* Stack grids to single column */
 .grid-mobile { grid-template-columns: 1fr !important; }

 /* Stat boxes scroll horizontally */
 .statbox-row { flex-wrap: nowrap !important; overflow-x: auto; padding-bottom: 6px; -webkit-overflow-scrolling: touch; }
 .statbox-row > * { min-width: 120px !important; flex-shrink: 0 !important; }

 /* Tables scroll horizontally */
 .table-scroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }

 /* Step indicator scrolls horizontally */
 .step-indicator { overflow-x: auto !important; padding: 0 12px !important; }

 /* Engineer cards full width */
 .engineer-grid { grid-template-columns: 1fr !important; }

 /* Reduce font sizes slightly */
 .mobile-text-sm { font-size: 11px !important; }

 /* Main content padding respect safe areas */
 .main-content {
 padding: 16px 12px !important;
 padding-left: max(12px, env(safe-area-inset-left)) !important;
 padding-right: max(12px, env(safe-area-inset-right)) !important;
 padding-bottom: max(16px, env(safe-area-inset-bottom)) !important;
 }
 }

 /* Tablet (6401024px) */
 @media (min-width: 640px) and (max-width: 1024px) {
 .engineer-grid { grid-template-columns: repeat(2, 1fr) !important; }
 .statbox-row { flex-wrap: wrap; }
 }

 /* Touch improvements */
 @media (hover: none) {
 button { min-height: 44px; }
 input[type="number"], input[type="text"], select { min-height: 44px; }
 }
 `;

  // Mobile nav state bottom sheet on small screens
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // If this is a shared day link, render the lightweight task list view instead
  if (
    typeof window !== "undefined" &&
    window.location.hash.startsWith("#day=")
  ) {
    return <SharedDayView />;
  }

  if (!authToken) {
    return (
      <>
        <style>{cssVars}</style>
        <LoginScreen onLogin={setAuthToken} />
      </>
    );
  }

  return (
    <ServerContext.Provider value={server}>
      <div
        className="sci-fi-grid"
        style={{
          minHeight: "100dvh",
          background: isDark
            ? "radial-gradient(ellipse 80% 50% at 50% -6%, rgba(12,65,122,0.58) 0%, rgba(3,12,28,0.96) 42%, #02040a 100%)"
            : T.bg,
          fontFamily:
            "'Share Tech Mono', 'Fira Code', 'IBM Plex Mono', monospace",
          color: T.text,
        }}
      >
        <ParticleBackground fxEnabled={isDark && fxEnabled} />
        <link
          href="https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;600;700&family=Share+Tech+Mono&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <style>
          {`html,body{overflow-x:hidden;max-width:100vw;}`}
          {cssVars}
        </style>

        {/* Header */}
        <header
          style={{
            background: isDark
              ? "linear-gradient(180deg, rgba(3,14,31,0.94) 0%, rgba(1,6,16,0.9) 100%)"
              : T.header,
            borderBottom: `1px solid ${isDark ? "rgba(93,231,255,0.22)" : "#163060"}`,
            padding: "0 16px",
            paddingLeft: "max(16px, env(safe-area-inset-left))",
            paddingRight: "max(16px, env(safe-area-inset-right))",
            display: "flex",
            alignItems: "center",
            height: 56,
            gap: 8,
            position: "sticky",
            top: 0,
            zIndex: 100,
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.34), inset 0 -1px 0 rgba(125,231,255,0.06)"
              : "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          {/* Logo + title */}
          <FaceLockup />

          {/* Live + DB status badges */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: isDark ? "#001a2e" : "rgba(255,255,255,0.12)",
                border: `1px solid ${isDark ? "#38bdf820" : "#14532d30"}`,
                borderRadius: 20,
                padding: "3px 7px",
              }}
            >
              <span
                className={fxEnabled ? "fx-live-dot" : ""}
                style={{
                  width: 5,
                  height: 5,
                  background: "#22c55e",
                  borderRadius: "50%",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  color: "#22c55e",
                  fontSize: 7,
                  letterSpacing: 2,
                  fontWeight: 700,
                }}
              >
                LIVE
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background:
                  server.serverStatus === "online"
                    ? isDark
                      ? "#001a2e"
                      : "rgba(255,255,255,0.12)"
                    : isDark
                      ? "#1a0505"
                      : "rgba(239,68,68,0.15)",
                border: `1px solid ${server.serverStatus === "online" ? (isDark ? "#38bdf820" : "#14532d30") : "#7f1d1d40"}`,
                borderRadius: 20,
                padding: "3px 7px",
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  background:
                    server.serverStatus === "online"
                      ? "#22c55e"
                      : server.serverStatus === "offline"
                        ? "#f87171"
                        : "#4a6898",
                  borderRadius: "50%",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  color:
                    server.serverStatus === "online"
                      ? "#22c55e"
                      : server.serverStatus === "offline"
                        ? "#f87171"
                        : "#4a6898",
                  fontSize: 7,
                  letterSpacing: 2,
                  fontWeight: 700,
                }}
              >
                {server.serverStatus === "online"
                  ? "DB"
                  : server.serverStatus === "offline"
                    ? "NO DB"
                    : "DB..."}
              </span>
            </div>
          </div>

          {/* Desktop tab buttons hidden on mobile via CSS class */}
          <div
            className="nav-tabs-desktop"
            style={{ marginLeft: 8, gap: 2, alignItems: "center" }}
          >
            {[
              ["planner", "Planner"],
              ["rc", "Repair Ctr"],
              ["live", "Live"],
              ["history", "History"],
              ["stock", "STOCK"],
            ].map(([t, label]) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  background:
                    tab === t
                      ? isDark
                        ? "linear-gradient(135deg, #162a52, #1a3464)"
                        : "rgba(255,255,255,0.22)"
                      : "transparent",
                  border: `1px solid ${tab === t ? (isDark ? "#244080" : "rgba(255,255,255,0.4)") : "transparent"}`,
                  color:
                    tab === t
                      ? isDark
                        ? "#8ab8fa"
                        : "#fff"
                      : isDark
                        ? "#2e4470"
                        : "#93b4d4",
                  borderRadius: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: 10,
                  fontFamily: "inherit",
                  letterSpacing: 0.5,
                  fontWeight: tab === t ? 700 : 500,
                  transition: fxEnabled ? "all 0.18s" : "none",
                  boxShadow:
                    tab === t && fxEnabled ? "0 2px 12px #4f8ef722" : "none",
                  minHeight: 32,
                  whiteSpace: "nowrap",
                  position: "relative",
                }}
              >
                {label}
                {t === "live" && server?.pendingFlagCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      background: "#ef4444",
                      borderRadius: 8,
                      fontSize: 8,
                      padding: "1px 4px",
                      color: "#fff",
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {server.pendingFlagCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Desktop icon buttons hidden on mobile via CSS class */}
          <div
            className="nav-icon-desktop"
            style={{ gap: 4, alignItems: "center" }}
          >
            {/* FX toggle */}
            <button
              onClick={toggleFX}
              title={fxEnabled ? "Effects ON" : "Effects OFF"}
              style={{
                background: fxEnabled
                  ? isDark
                    ? "#091808"
                    : "rgba(255,255,255,0.15)"
                  : "transparent",
                border: `1px solid ${fxEnabled ? "#14532d60" : isDark ? "#142038" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 8,
                padding: "6px 9px",
                cursor: "pointer",
                fontSize: 10,
                fontFamily: "inherit",
                color: fxEnabled ? "#22c55e" : isDark ? "#2e4470" : "#93b4d4",
                minHeight: 32,
                display: "flex",
                alignItems: "center",
                gap: 4,
                letterSpacing: 0.5,
                fontWeight: 600,
              }}
            >
              {fxEnabled ? "FX ON" : "FX OFF"}
            </button>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title="Toggle theme"
              style={{
                background: isDark ? "transparent" : "rgba(255,255,255,0.15)",
                border: `1px solid ${isDark ? "#142038" : "rgba(255,255,255,0.25)"}`,
                borderRadius: 8,
                padding: "6px 9px",
                cursor: "pointer",
                minHeight: 32,
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDark ? "#4a6898" : "#b0cce8"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isDark ? (
                  <>
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </>
                ) : (
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                )}
              </svg>
            </button>

            {/* Game */}
            <button
              onClick={() => setGameOpen(true)}
              title="Repair Centre Invaders"
              style={{
                background: "transparent",
                border: `1px solid ${isDark ? "#164e63" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 8,
                padding: "6px 9px",
                cursor: "pointer",
                minHeight: 32,
                display: "flex",
                alignItems: "center",
                filter: fxEnabled
                  ? "drop-shadow(0 0 4px rgba(93,231,255,0.36))"
                  : "none",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDark ? "#5de7ff" : "#38bdf8"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="6" width="20" height="12" rx="4" />
                <line x1="12" y1="10" x2="12" y2="14" />
                <line x1="10" y1="12" x2="14" y2="12" />
                <circle cx="17.5" cy="11.5" r="0.5" fill="currentColor" />
                <circle cx="19.5" cy="13.5" r="0.5" fill="currentColor" />
              </svg>
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              title="Log out"
              style={{
                background: "transparent",
                border: `1px solid ${isDark ? "#142038" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 8,
                padding: "6px 9px",
                cursor: "pointer",
                minHeight: 32,
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDark ? "#3a5470" : "#93b4d4"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>

            <div
              style={{
                width: 1,
                height: 20,
                background: isDark ? "#142038" : "rgba(255,255,255,0.2)",
                margin: "0 2px",
              }}
            />
          </div>

          {/* Hamburger always visible */}
          <button
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            style={{
              background: mobileMenuOpen
                ? isDark
                  ? "#142240"
                  : "rgba(255,255,255,0.2)"
                : "transparent",
              border: `1px solid ${mobileMenuOpen ? (isDark ? "#244080" : "rgba(255,255,255,0.4)") : isDark ? "#142038" : "rgba(255,255,255,0.2)"}`,
              borderRadius: 8,
              padding: "8px 10px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minHeight: 36,
              justifyContent: "center",
              minWidth: 36,
              alignItems: "center",
              transition: fxEnabled ? "all 0.18s" : "none",
              flexShrink: 0,
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 16,
                  height: 1.5,
                  background: mobileMenuOpen
                    ? isDark
                      ? "#7ab4f7"
                      : "#fff"
                    : isDark
                      ? "#4a6898"
                      : "#93b4d4",
                  borderRadius: 2,
                  transition: fxEnabled
                    ? "all 0.2s cubic-bezier(0.22,1,0.36,1)"
                    : "none",
                  transform:
                    mobileMenuOpen && i === 0
                      ? "rotate(45deg) translate(3.5px, 3.5px)"
                      : mobileMenuOpen && i === 2
                        ? "rotate(-45deg) translate(3.5px, -3.5px)"
                        : "none",
                  opacity: mobileMenuOpen && i === 1 ? 0 : 1,
                }}
              />
            ))}
          </button>
        </header>

        {/* Unified menu dropdown */}
        {mobileMenuOpen && (
          <div
            style={{
              position: "sticky",
              top: 56,
              zIndex: 99,
              background: isDark ? "#060d1c" : T.header,
              borderBottom: `1px solid ${isDark ? "#102038" : "#163060"}`,
              padding: "16px 16px 20px",
              paddingLeft: "max(16px, env(safe-area-inset-left))",
              paddingRight: "max(16px, env(safe-area-inset-right))",
              maxHeight: "85dvh",
              overflowY: "auto",
            }}
          >
            {/* Mobile-only tab nav hidden on desktop where tabs are in header */}
            <div
              className="nav-tabs-mobile-menu"
              style={{ display: "flex", gap: 6, marginBottom: 16 }}
            >
              {[
                ["planner", "Planner"],
                ["rc", "Repair Ctr"],
                ["live", "Live"],
                ["history", "History"],
                ["stock", "STOCK"],
              ].map(([t, label]) => (
                <button
                  key={t}
                  onClick={() => {
                    setTab(t);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    flex: 1,
                    background:
                      tab === t
                        ? "linear-gradient(135deg, #162a52, #1a3464)"
                        : isDark
                          ? "#0a1628"
                          : "rgba(255,255,255,0.12)",
                    border: `1px solid ${tab === t ? "#244080" : isDark ? "#142038" : "rgba(255,255,255,0.2)"}`,
                    borderRadius: 10,
                    padding: "10px 8px",
                    color:
                      tab === t ? "#8ab8fa" : isDark ? "#2e4470" : "#93b4d4",
                    fontSize: 10,
                    fontFamily: "inherit",
                    fontWeight: tab === t ? 700 : 500,
                    cursor: "pointer",
                    letterSpacing: 0.5,
                    minHeight: 44,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Status chips */}
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {[
                `${state.techNames.length} engineers`,
                `${(state.mergedParts || []).length} parts`,
                state.teamFilter,
              ].map((txt) => (
                <div
                  key={txt}
                  style={{
                    background: isDark ? "#081420" : "rgba(255,255,255,0.1)",
                    border: `1px solid ${isDark ? "#142038" : "rgba(255,255,255,0.2)"}`,
                    borderRadius: 20,
                    padding: "4px 10px",
                    color: isDark ? "#3a5a90" : "#7aa8d4",
                    fontSize: 9,
                    letterSpacing: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {txt}
                </div>
              ))}
            </div>

            {/* Section divider helper */}
            {(() => {
              const Divider = ({ label }) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    margin: "14px 0 10px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background: isDark ? "#102038" : "rgba(255,255,255,0.12)",
                    }}
                  />
                  <span
                    style={{
                      color: isDark ? "#1e3454" : "rgba(255,255,255,0.3)",
                      fontSize: 8,
                      letterSpacing: 2,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background: isDark ? "#102038" : "rgba(255,255,255,0.12)",
                    }}
                  />
                </div>
              );

              return (
                <>
                  <Divider label="SERVER CONNECTION" />

                  {/* Connection status. The URL / App ID / API Key inputs and
                      the TEST button were removed with the Express server —
                      the Supabase project is fixed at build time, so there is
                      nothing left to point somewhere else. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      background: isDark ? "#040c18" : "rgba(0,0,0,0.25)",
                      border: `1px solid ${isDark ? "#142038" : "rgba(255,255,255,0.15)"}`,
                      borderRadius: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        background:
                          server.serverStatus === "online"
                            ? "#22c55e"
                            : server.serverStatus === "auth_error"
                              ? "#f59e0b"
                              : "#4a6898",
                        borderRadius: "50%",
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        color:
                          server.serverStatus === "online"
                            ? "#22c55e"
                            : server.serverStatus === "auth_error"
                              ? "#f59e0b"
                              : "#4a6898",
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      {server.serverStatus === "online"
                        ? "CONNECTED"
                        : server.serverStatus === "auth_error"
                          ? "SIGNED OUT"
                          : server.serverStatus === "unknown"
                            ? "CONNECTING..."
                            : "OFFLINE"}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: isDark ? "#1e3454" : "rgba(255,255,255,0.35)",
                        fontSize: 8,
                        letterSpacing: 1,
                      }}
                    >
                      SUPABASE
                    </span>
                  </div>


                  <Divider label="TOOLS" />

                  {/* Tool buttons */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    {[
                      {
                        label: "Grading Ref",
                        sub: server.gradingLoaded
                          ? `${server.gradingCount?.toLocaleString()} parts`
                          : "Not loaded",
                        active: server.gradingLoaded,
                        action: () => {
                          setGradingOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Bank Holidays",
                        sub: `${server.bankHolidays?.length || 0} loaded`,
                        active: false,
                        action: () => {
                          setBankHolidayOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: `Parts Grading${state.ungradedPartIds?.length > 0 ? ` (${state.ungradedPartIds.length})` : ""}`,
                        sub:
                          state.ungradedPartIds?.length > 0
                            ? `${state.ungradedPartIds.length} ungraded`
                            : "Manage grades",
                        active: (state.ungradedPartIds?.length || 0) > 0,
                        action: () => {
                          setPartsGradingOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Rota",
                        sub: "Monthly attendance",
                        active: false,
                        action: () => {
                          setRotaOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Engineers",
                        sub: "Rota names + AKA",
                        active: false,
                        action: () => {
                          setEngineerManagerOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Part Blocks",
                        sub: "Allocation exclusions",
                        active: false,
                        action: () => {
                          setAllocationBlocklistOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Clients",
                        sub: `${(state.clients || []).length} configured`,
                        active: false,
                        action: () => {
                          setClientsModalOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                      {
                        label: "Team Targets",
                        sub: `${Object.keys(state.teamTargets || {}).length} custom`,
                        active: Object.keys(state.teamTargets || {}).length > 0,
                        action: () => {
                          setTeamTargetsOpen(true);
                          setMobileMenuOpen(false);
                        },
                      },
                    ].map((btn) => (
                      <button
                        key={btn.label}
                        onClick={btn.action}
                        style={{
                          background: isDark
                            ? "#081420"
                            : "rgba(255,255,255,0.08)",
                          border: `1px solid ${btn.active ? (isDark ? "#1a3a2a" : "rgba(34,197,94,0.3)") : isDark ? "#102030" : "rgba(255,255,255,0.12)"}`,
                          borderRadius: 10,
                          padding: "12px 14px",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          minHeight: 60,
                        }}
                      >
                        <div
                          style={{
                            color: btn.active
                              ? isDark
                                ? "#22c55e"
                                : "#4ade80"
                              : isDark
                                ? "#4a6898"
                                : "#93b4d4",
                            fontSize: 11,
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                        >
                          {btn.label}
                        </div>
                        <div
                          style={{
                            color: isDark ? "#1e3454" : "rgba(255,255,255,0.3)",
                            fontSize: 9,
                            fontWeight: 500,
                          }}
                        >
                          {btn.sub}
                        </div>
                      </button>
                    ))}
                  </div>

                  <Divider label="SETTINGS" />

                  {/* Quick settings only visible on mobile where these aren't in the header */}
                  <div
                    className="nav-icon-mobile-only"
                    style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                  >
                    <button
                      onClick={toggleFX}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        background: isDark
                          ? "#081420"
                          : "rgba(255,255,255,0.08)",
                        border: `1px solid ${isDark ? "#102030" : "rgba(255,255,255,0.12)"}`,
                        borderRadius: 10,
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minHeight: 48,
                      }}
                    >
                      <span
                        style={{
                          color: fxEnabled
                            ? "#22c55e"
                            : isDark
                              ? "#2e4470"
                              : "#93b4d4",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        FX
                      </span>
                      <div style={{ textAlign: "left" }}>
                        <div
                          style={{
                            color: isDark ? "#4a6898" : "#93b4d4",
                            fontSize: 10,
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                        >
                          Effects
                        </div>
                        <div
                          style={{
                            color: isDark ? "#1e3454" : "rgba(255,255,255,0.3)",
                            fontSize: 9,
                          }}
                        >
                          {fxEnabled ? "On" : "Off"}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={toggleTheme}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        background: isDark
                          ? "#081420"
                          : "rgba(255,255,255,0.08)",
                        border: `1px solid ${isDark ? "#102030" : "rgba(255,255,255,0.12)"}`,
                        borderRadius: 10,
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minHeight: 48,
                      }}
                    >
                      <span
                        style={{
                          color: isDark ? "#4a6898" : "#93b4d4",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        TH
                      </span>
                      <div style={{ textAlign: "left" }}>
                        <div
                          style={{
                            color: isDark ? "#4a6898" : "#93b4d4",
                            fontSize: 10,
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                        >
                          Theme
                        </div>
                        <div
                          style={{
                            color: isDark ? "#1e3454" : "rgba(255,255,255,0.3)",
                            fontSize: 9,
                          }}
                        >
                          {isDark ? "Dark" : "Light"}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setGameOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        background: isDark
                          ? "#081420"
                          : "rgba(255,255,255,0.08)",
                        border: `1px solid ${isDark ? "#102030" : "rgba(255,255,255,0.12)"}`,
                        borderRadius: 10,
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minHeight: 48,
                      }}
                    >
                      <span
                        style={{
                          color: isDark ? "#5de7ff" : "#38bdf8",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        GM
                      </span>
                      <div style={{ textAlign: "left" }}>
                        <div
                          style={{
                            color: isDark ? "#4a6898" : "#93b4d4",
                            fontSize: 10,
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                        >
                          Game
                        </div>
                        <div
                          style={{
                            color: isDark ? "#1e3454" : "rgba(255,255,255,0.3)",
                            fontSize: 9,
                          }}
                        >
                          Invaders
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={handleLogout}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        background: isDark ? "#100808" : "rgba(239,68,68,0.08)",
                        border: `1px solid ${isDark ? "#2a0808" : "rgba(239,68,68,0.2)"}`,
                        borderRadius: 10,
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minHeight: 48,
                      }}
                    >
                      <span
                        style={{
                          color: isDark ? "#4a1010" : "#ef4444",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        OUT
                      </span>
                      <div style={{ textAlign: "left" }}>
                        <div
                          style={{
                            color: isDark ? "#6a2020" : "#f87171",
                            fontSize: 10,
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                        >
                          Sign Out
                        </div>
                        <div
                          style={{
                            color: isDark ? "#2a1010" : "rgba(239,68,68,0.4)",
                            fontSize: 9,
                          }}
                        >
                          Log out
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Accent line */}
        <div className="header-accent-line" />

        {tab === "planner" && (
          <StepIndicator current={step} steps={STEPS} onGo={setStep} />
        )}

        <main
          key={tab}
          className={fxEnabled ? "fx-tab-enter" : ""}
          style={{
            maxWidth: ["history", "stock"].includes(tab) ? 1400 : 1200,
            margin: "0 auto",
            padding: "20px 12px",
          }}
        >
          <StockSaveStatus />
          {tab === "planner" && (
            <>
              {step === 0 && (
                <StepSetup
                  state={state}
                  setState={setState}
                  onNext={() => setStep(1)}
                />
              )}
              {step === 1 && (
                <StepData
                  state={state}
                  setState={setState}
                  onNext={() => setStep(2)}
                  onBack={() => setStep(0)}
                />
              )}
              {step === 2 && (
                <StepCallList
                  state={state}
                  setState={setState}
                  onNext={() => setStep(3)}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && (
                <>
                <StockParsedCapture state={state} buildReport={buildParsedPartReport} />
                <StepResults
                  state={state}
                  setState={setState}
                  onBack={() => setStep(2)}
                  onReset={reset}
                />
                </>
              )}
            </>
          )}
          {tab === "rc" && (
            <RepairCentreEarnings
              state={state}
              fileState={state.rcFile}
              onFileState={(s) => setState({ rcFile: s })}
              onEngineerAliasesChanged={() =>
                setState({ engineerAliasesUpdatedAt: Date.now() })
              }
            />
          )}
          {tab === "live" && <EngineerProgressTab state={state} />}
          {tab === "stock" && <StockTab initialTeam={stockTeam(state)} server={server} parseFile={parseStockArchiveFile} buildReport={buildParsedPartReport} gradeValues={state.gradeValues} />}
          {tab === "history" && (
            <HistoryTab
              teamTargets={state.teamTargets}
              aliasRefreshVersion={state.engineerAliasesUpdatedAt}
            />
          )}
        </main>

        {/* Repair Centre Invaders game overlay */}
        {gameOpen && <RepairInvaders onClose={() => setGameOpen(false)} />}
        {bankHolidayOpen && (
          <BankHolidayManager onClose={() => setBankHolidayOpen(false)} />
        )}
        {rotaOpen && <RotaManager onClose={() => setRotaOpen(false)} />}
        {engineerManagerOpen && (
          <EngineerManager onClose={() => setEngineerManagerOpen(false)} />
        )}
        {allocationBlocklistOpen && (
          <AllocationBlocklistManager
            onClose={() => setAllocationBlocklistOpen(false)}
          />
        )}
        {gradingOpen && (
          <GradingManager state={state} onClose={() => setGradingOpen(false)} />
        )}
        {partsGradingOpen && (
          <PartsGradingModal
            state={state}
            setState={setState}
            onClose={() => setPartsGradingOpen(false)}
          />
        )}
        {clientsModalOpen && (
          <ClientsModal
            state={state}
            setState={setState}
            onClose={() => setClientsModalOpen(false)}
          />
        )}
        {teamTargetsOpen && (
          <TeamTargetsModal
            state={state}
            setState={setState}
            server={server}
            onClose={() => setTeamTargetsOpen(false)}
          />
        )}
      </div>
    </ServerContext.Provider>
  );
}
